// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { sGetRaw, sSetRaw } from "./lib/scopedStorage";
import { DARK_THEME, LIGHT_THEME, type Theme } from "./lib/theme";
import { useRealtimeSync } from "./hooks/useRealtimeSync";
import OELogo from "./components/OELogo";
import ProjectChatPanel from "./components/ProjectChatPanel";
import OEWordmark from "./components/OEWordmark";
import * as Icon from "./components/OEIcons";
import ShareDialog from "./components/ShareDialog";
import ProposalImportModal, { type EmbedItem } from "./components/ProposalImportModal";
import { CB_KEY, type CBudget, type CAreaGroup } from "./components/ConceptualBudgetEditor";
import { DEFAULT_BEST_PRACTICES, CATEGORIES as BP_CATEGORIES, ALL_DIVS as BP_ALL_DIVS, BPModal } from "./components/LessonsLibrary";
import SowEditorModal from "./components/SowEditorModal";
import BuildingCodesPanel, { type JurisdictionResult } from "./components/BuildingCodesPanel";
import AddressFields from "./components/AddressFields";
import { DrawingListPanel, type DrawingListItem } from "./components/DrawingListPanel";
// OpenItemsPanel is retired -- its entries became notes. The type stays
// while ProjectState still carries the legacy list for migration.
import { type OpenItem } from "./components/OpenItemsPanel";
import type { BestPractice, ProjectState, RFIItem, BuyoutItem, TradeSource } from "./types";
// xlsx loaded dynamically in exportExcel() to keep initial bundle small

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const UNIT_OPTIONS = _UNIT_OPTIONS;
const COST_STATUS_OPTIONS = ["","Allowance","Hard Bid","Needs Review","By Others","Excluded"];
const CLARIFICATION_STATUSES = ["By Others","Excluded"] as const;
// isClarification imported from ./lib/calculations
const ALT_TYPES = ["","Alt","Void"];
const migrateAltType = t => t==="Add Alt"||t==="Ded Alt"?"Alt":t;

import { ALL_DIVISIONS, DEFAULT_SUBS, buildFreshProject, DEFAULT_PRESETS, buildSandboxProject, LS_PRESETS_KEY_EXPORT, buildProjectFromPreset, SANDBOX_ORG_PARTNERS } from "./projectData";
import type { OrgExclusion, LaborRate } from "./lib/orgDefaults";
import { DEFAULT_LABOR_RATES, DEFAULT_ORG_EXCLUSIONS } from "./lib/orgDefaults";
import { lockedById } from "./lib/logisticsLock";
import { NAV_FIELDS, nextCell, blockEdge, leavesCell, planFillDown, type Cell as GridCell } from "./lib/gridNav";
import { rangeIds, rangeStats } from "./lib/rangeSelect";
import SelectionStatus from "./components/SelectionStatus";
import { emptyHistory, capture, changedKeys, sameKeys, looksLikeLoad, record as recordStep, stepBack, stepForward,
  describeChange, sameValue, scopeForTab, scopeOfChange, SCOPES, SCOPE_IDS, MAX_HISTORY,
  type HistoryState, type ScopeId, type Slices } from "./lib/history";
import { activePlan as logActivePlan, updateActivePlan } from "./lib/logisticsPlans";
import { isBlockSel } from "./lib/sheetBlocks";
import { copyElement, removeElement, pasteElement, nudgeElement, describeClip,
  NUDGE_STEP, NUDGE_STEP_COARSE } from "./lib/logisticsClipboard";
import type { BidScope, BidPartner, BidItem, Bid, LevelingAdjustment } from "./projectData";
import {
  type SubmittalStatus, type Submittal, type SubmittalTemplateItem, type SubmittalTemplateCustomLayer,
  DIVISION_SUBMITTAL_TEMPLATES, SUBMITTAL_STATUS_CONFIG,
  autoPopSubmittals, AddCustomTemplateItemForm,
} from "./lib/submittals";
import { supabase } from "./lib/supabase";
import {
  walkItems, isClarification, getItemTotal, calcItemCont,
  computeDivTotals, computeGrandTotal, computeSubTotals,
  computeMarkupableSubtotal, computeDivMarkupableSubtotals,
  computeAltTotals, computeContingencyByProp, computeFinal,
} from "./lib/calculations";
import { parseClipboardGrid, toClipboardGrid, planPaste, describePaste, isPasteable, PASTEABLE_FIELDS } from "./lib/clipboardGrid";
import { comparePackage, comparisonRows } from "./lib/bidComparison";
import { scopeBudget, scopeCoverage } from "./lib/scopeBudget";
import type { GcStaffRow } from "./lib/generalConditions";
import { toLineItems as gcLineItems, staleRows as gcStaleRows, GC_SUB_NAME } from "./lib/generalConditions";
import StaffingPlanModal from "./components/StaffingPlanModal";
import BcLevelingImportModal from "./components/BcLevelingImportModal";
import type { EscalationSettings } from "./lib/escalation";
import { DEFAULT_ESCALATION, computeEscalation } from "./lib/escalation";
import type { AltPackage, AltStatus } from "./lib/alternates";
import { rollUpAlternates, setPackageStatus, combineIntoPackage, splitPackage } from "./lib/alternates";
import type { ContingencyPolicy } from "./lib/contingency";
import { DEFAULT_CONTINGENCY_POLICY, lineContingencyApplies } from "./lib/contingency";
import { resolveLandingTab, hasPricedScope, loadLastTab, saveLastTab } from "./lib/landingTab";
import { showRowLine } from "./lib/rowLines";
import { gatherDecisions } from "./lib/decisions";
import { statusLinkPatch } from "./lib/statusLink";
import BuyoutTab from "./tabs/BuyoutTab";
import AlternatesTab from "./tabs/AlternatesTab";
import CashflowTab from "./tabs/CashflowTab";
import AnalysisTab from "./tabs/AnalysisTab";
import ScopeTab from "./tabs/ScopeTab";
import RfiTab from "./tabs/RfiTab";
import SubmittalsTab from "./tabs/SubmittalsTab";
import DecisionsTab from "./tabs/DecisionsTab";
import LogisticsTab from "./tabs/LogisticsTab";
import HistoryTab from "./tabs/HistoryTab";
import BidsTab from "./tabs/BidsTab";
import ScheduleTab from "./tabs/ScheduleTab";
import EstimateTab from "./tabs/EstimateTab";
import HomeTab from "./tabs/HomeTab";
import SummaryTab from "./tabs/SummaryTab";
import PropertiesTab from "./tabs/PropertiesTab";
import SettingsPanel, { type AccessRequest } from "./tabs/SettingsPanel";
import NumberInput from "./components/NumberInput";
import ErrorBoundary from "./components/ErrorBoundary";
import ResizeHandle from "./components/ResizeHandle";
import GetStartedChecklist from "./components/GetStartedChecklist";
import InfoTooltip from "./components/InfoTooltip";
import { UNIT_OPTIONS as _UNIT_OPTIONS, COL_DEFS, DEFAULT_COLS, COL_PRESETS } from "./lib/estimateDefaults";
import { safeEvalArithmetic } from "./lib/safeEval";
import { auditEvent, auditFieldChange, flushAuditQueue } from "./lib/audit";
import { suggestAlignments } from "./lib/bidPipeline";
import { reconcileAttributionAlignments } from "./lib/bidReconcile";
import HelpCenter from "./components/HelpCenter";
import ProcoreExport, { type ExportKind } from "./components/ProcoreExport";
import NotesPanel, { type NotesAnchor } from "./components/NotesPanel";
import BudgetView from "./components/BudgetView";
import { BUDGET_ENABLED } from "./lib/featureFlags";
import {
  loadNotes, addNote, setNoteResolved, deleteNote, markNotesSeen, lastSeenAt,
  loadMentionablePeople, openNotesFor, unreadCount,
  type ProjectNote, type Mentionable, type NewNote,
} from "./lib/projectNotes";
import { readBackups, addBackup, newestFor, shouldOfferRecovery } from "./lib/backups";
import { loadOrgLessons, seedOrgLessons, syncOrgLessons } from "./lib/orgLessons";
import { loadOrgPhaseGroups, seedOrgPhaseGroups, syncOrgPhaseGroups } from "./lib/orgPhaseGroups";
import { useOrgSetting } from "./hooks/useOrgSetting";
import { accountMenuEntries } from "./lib/accountMenu";
import { useEntitlements } from "./hooks/useEntitlements";
import { useOrgCostCodes } from "./hooks/useOrgCostCodes";

// Format currency — negatives shown with parentheses in red
const fmt = n => {
  if (n == null) n = 0;
  const abs = Math.abs(n);
  const str = new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:0,maximumFractionDigits:0}).format(abs);
  return n < 0 ? `(${str})` : str;
};
const fmtDec = n => {
  if (n == null) n = 0;
  const abs = Math.abs(n);
  const str = new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(abs);
  return n < 0 ? `(${str})` : str;
};
let _seq = (() => {
  try {
    const nums = [];
    const scan = obj => {
      if (obj?.itemState)
        nums.push(...Object.keys(obj.itemState).filter(k => /^i\d+$/.test(k)).map(k => parseInt(k.slice(1))));
    };
    scan(JSON.parse(sGetRaw("openestimate_v2") || "null"));
    Object.values(JSON.parse(sGetRaw("openestimate_files_v2") || "{}")).forEach(scan);
    return nums.length ? Math.max(...nums) : 2000;
  } catch { return 2000; }
})();
const uid = () => `i${++_seq}`;

import type { SiteLogistics, SiteItemType, SiteLineType, SiteIcon, SiteLine, SiteArea, SiteCircle } from "./lib/logistics";
import { SITE_ICON_CONFIG, SITE_LINE_CONFIG, SITE_AREA_CONFIG, SCALE_PRESETS } from "./lib/logistics";

// ── Preliminary Schedule ──
import type { DurationUnit, PrelimSchedule, SubSchedule } from "./lib/schedule";
import type { CostSchedule } from "./lib/costSchedule";
import { migrateSubSchedule, toSubSchedule } from "./lib/costSchedule";
import { summarizeScheduleDelta } from "./lib/scheduleDelta";
// ── Phases ──
import type { PhaseGroup } from "./lib/phaseGroups";
import { loadPhaseGroups, migratePhaseGroups, LS_PHASE_GROUPS, DEFAULT_PHASE_GROUPS } from "./lib/phaseGroups";

// ── CSI MasterFormat division plain-English tooltips ──
const CSI_DIV_DESC:Record<string,string>={
  div1:"General Requirements — Project management overhead, permits, testing, temp facilities, insurance, and general conditions costs that span all trades.",
  div2:"Existing Conditions — Demolition, hazardous material abatement, subsurface investigation, and site clearing before new construction begins.",
  div3:"Concrete — Cast-in-place foundations, slabs, structural concrete, formwork, rebar, and precast elements.",
  div4:"Masonry — Brick, CMU (block), stone, mortar, and all masonry veneer and structural wall systems.",
  div5:"Metals — Structural steel framing, metal decking, stairs, railings, miscellaneous iron, and metal fabrications.",
  div6:"Wood, Plastics & Composites — Rough carpentry, framing, sheathing, millwork, casework, finish carpentry, and composite panel systems.",
  div7:"Thermal & Moisture Protection — Roofing, waterproofing, below-grade moisture barriers, insulation, cladding, siding, and air/vapor barriers.",
  div8:"Openings — Doors, frames, hardware, windows, curtain wall, storefront, glazing, and all openings in the building envelope.",
  div9:"Finishes — Drywall, plaster, tile, stone, flooring (hardwood, carpet, LVT), painting, and specialty finish systems.",
  div10:"Specialties — Toilet accessories, signage, lockers, fire extinguishers, operable partitions, and other manufactured specialties.",
  div11:"Equipment — Commercial kitchen equipment, laboratory equipment, loading dock equipment, and other owner-furnished specialty equipment.",
  div12:"Furnishings — Window treatments, furniture, artwork, and interior furnishings typically supplied by the owner or FF&E consultant.",
  div13:"Special Construction — Pools, aquatic facilities, pre-engineered buildings, clean rooms, and other custom structure types.",
  div14:"Conveying Equipment — Elevators, escalators, dumbwaiters, lifts, and other vertical transportation systems.",
  div21:"Fire Suppression — Sprinkler systems, standpipes, foam systems, and all fire suppression infrastructure.",
  div22:"Plumbing — Domestic hot and cold water, sanitary drain/waste/vent, fixtures, and plumbing rough-in.",
  div23:"HVAC — Heating, ventilation, and air conditioning systems including mechanical equipment, ductwork, controls, and piping.",
  div25:"Integrated Automation — Building automation systems (BAS), HVAC controls, lighting controls, and integrated building management.",
  div26:"Electrical — Power distribution, switchgear, wiring, devices, lighting, and all electrical systems throughout the building.",
  div27:"Communications — Data/telecom cabling, structured wiring, AV systems, distributed antenna, and network infrastructure.",
  div28:"Electronic Safety & Security — Access control, CCTV, intrusion detection, fire alarm, and all electronic security systems.",
  div31:"Earthwork — Grading, excavation, shoring, dewatering, soil compaction, and earthwork associated with the site.",
  div32:"Exterior Improvements — Landscaping, hardscape, paving, site walls, fencing, irrigation, and all exterior site improvements.",
  div33:"Utilities — Underground site utilities including storm drain, sanitary sewer, water, gas, and dry utilities.",
};


const DEFAULT_PROPOSALS = [{id:"p1",name:"General Construction",color:0},{id:"p2",name:"Exterior Improvements",color:1}];

const TYPOLOGIES = [
  {id:"sfr",        label:"Single Family Residential"},
  {id:"multifamily",label:"Multifamily"},
  {id:"commercial", label:"Commercial / Institutional"},
  {id:"custom",     label:"Custom"},
];
const DEFAULT_TYPOLOGY_PREFS = {
  sfr:        {overhead:10,profit:8, contingency:5, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div13","div22","div23","div26","div32"]},
  multifamily:{overhead:12,profit:9, contingency:5, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div22","div23","div26","div32"]},
  commercial: {overhead:14,profit:10,contingency:7, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div10","div11","div22","div23","div25","div26","div27","div28","div31","div32"]},
  custom:     {overhead:10,profit:8, contingency:5, divisions:["div1","div2","div3","div5","div6","div7","div8","div9","div22","div23","div26"]},
};



function mkItem(id,description,opts:{qty?:number;unit?:string;unitCost?:number;proposal?:string;notes?:string}={}){
  return {type:"item",id,description,qty:opts.qty??1,unit:opts.unit||"Ea",unitCost:opts.unitCost||0,proposal:opts.proposal||"p1",notes:opts.notes||""};
}
// walkItems imported from ./lib/calculations

// ── Bid Leveling types are imported from ./projectData (single source of truth) ──

// ── RFI & Buyout types ──
// RFIItem and BuyoutItem imported from ./types

// ── Scope of Work types ──
interface ScopeDivResult { divisionId:string; narrative:string; inclusions:string[]; standardExclusions:string[] }
interface ScopeResult { introduction:string; divisions:ScopeDivResult[]; alternatesNarrative?:string; proposalsNarrative?:string }

// ── Org-level Trade Partner Address Book ──
interface OrgPartner { id:string; name:string; trade:string; contact:string; phone:string; email:string; notes:string }

// Phase rollup helpers: per-sub timing overrides describe a position RELATIVE TO the phase.
// When the phase moves, overrides translate with it. When it resizes, overrides clamp to stay inside.
function TradePartnerPicker({value,onChange,partners,onRequestAdd,placeholder="— Select trade partner —",tiny=false,T,FONT}:{
  value:string;onChange:(n:string)=>void;partners:OrgPartner[];
  onRequestAdd:(prefill:string)=>void;placeholder?:string;tiny?:boolean;T:any;FONT:string;
}){
  const [open,setOpen]=useState(false);
  const [search,setSearch]=useState("");
  const triggerRef=useRef<HTMLButtonElement>(null);
  const popRef=useRef<HTMLDivElement>(null);
  useEffect(()=>{
    if(!open)return;
    const h=(e:MouseEvent)=>{
      if(triggerRef.current?.contains(e.target as Node)||popRef.current?.contains(e.target as Node))return;
      setOpen(false);
    };
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[open]);
  const isLinked=!value||partners.some(p=>p.name.toLowerCase()===value.toLowerCase());
  const filtered=partners.filter(p=>!search||p.name.toLowerCase().includes(search.toLowerCase())||p.trade?.toLowerCase().includes(search.toLowerCase()));
  const fs=tiny?9:10;const pad=tiny?"2px 5px":"3px 6px";
  return(
    <div style={{position:"relative",flex:1,minWidth:0}}>
      <button ref={triggerRef} type="button"
        onClick={()=>{setOpen(o=>!o);setSearch("");}}
        title={!isLinked&&value?"Not in Directory — click to update":undefined}
        style={{width:"100%",fontSize:fs,padding:pad,background:T.bgInput,
          border:`1px solid ${!isLinked&&value?"#bfb04a":T.borderSecondary}`,borderRadius:3,
          color:value?T.textSecondary:T.textMuted,fontFamily:FONT,outline:"none",
          textAlign:"left" as any,cursor:"pointer",display:"flex",alignItems:"center",gap:4,minWidth:0}}>
        {!isLinked&&value&&<span style={{width:5,height:5,borderRadius:"50%",background:"#bfb04a",flexShrink:0}}/>}
        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>{value||placeholder}</span>
        <span style={{opacity:.5,fontSize:10,flexShrink:0}}>▾</span>
      </button>
      {open&&(
        <div ref={popRef} style={{position:"absolute",top:"calc(100% + 2px)",left:0,zIndex:200,
          width:280,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:3,
          boxShadow:"0 4px 16px rgba(0,0,0,.45)",overflow:"hidden"}}>
          <div style={{padding:6,borderBottom:`1px solid ${T.borderPrimary}`}}>
            <input autoFocus placeholder="Search Directory…" value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{width:"100%",fontSize:11,padding:"5px 8px",background:T.bgApp,
                border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textSecondary,
                fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}/>
          </div>
          <div style={{maxHeight:220,overflowY:"auto" as any}}>
            {partners.length===0?(
              <div style={{padding:"14px 10px",fontSize:11,color:T.textMuted,fontStyle:"italic",textAlign:"center" as any}}>
                Directory is empty.<br/>Use the button below to add the first partner.
              </div>
            ):filtered.length===0?(
              <div style={{padding:"12px 10px",fontSize:11,color:T.textMuted,fontStyle:"italic"}}>No match for "{search}"</div>
            ):filtered.map(p=>(
              <button key={p.id} onClick={()=>{onChange(p.name);setOpen(false);}}
                style={{width:"100%",textAlign:"left" as any,padding:"7px 10px",
                  background:p.name===value?`${T.borderAccent}18`:"none",border:"none",
                  borderBottom:`1px solid ${T.borderPrimary}33`,cursor:"pointer",fontFamily:FONT}}
                onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=`${T.borderAccent}10`;}}
                onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background=p.name===value?`${T.borderAccent}18`:"none";}}>
                <div style={{fontSize:11,fontWeight:600,color:T.textPrimary}}>{p.name}</div>
                {(p.trade||p.contact)&&<div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{[p.trade,p.contact].filter(Boolean).join(" · ")}</div>}
              </button>
            ))}
          </div>
          <div style={{borderTop:`1px solid ${T.borderPrimary}`,padding:6,display:"flex",gap:6}}>
            <button onClick={()=>{onRequestAdd(search.trim());setOpen(false);}}
              style={{flex:1,fontSize:10,padding:"5px 8px",background:T.bgAccentLight,
                border:`1px solid ${T.borderAccent}`,color:T.borderAccent,borderRadius:3,
                cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
              + Add{search.trim()?` "${search.trim()}"`:""} to Directory
            </button>
            {value&&<button onClick={()=>{onChange("");setOpen(false);}} title="Clear"
              style={{fontSize:10,padding:"5px 8px",background:"none",border:`1px solid ${T.borderSecondary}`,
                color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
              Clear
            </button>}
          </div>
        </div>
      )}
    </div>
  );
}

function AddPartnerToDirectoryModal({prefillName,onSave,onCancel,T,FONT}:{
  prefillName:string;onSave:(op:OrgPartner)=>void;onCancel:()=>void;T:any;FONT:string;
}){
  const [name,setName]=useState(prefillName);
  const [trade,setTrade]=useState("");
  const [contact,setContact]=useState("");
  const [phone,setPhone]=useState("");
  const [email,setEmail]=useState("");
  const [notes,setNotes]=useState("");
  const ok=name.trim().length>0;
  const fieldStyle={width:"100%",fontSize:12,padding:"6px 10px",background:T.bgApp,
    border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,
    fontFamily:FONT,outline:"none",boxSizing:"border-box" as any};
  const labelStyle={fontSize:10,color:T.textWeak,letterSpacing:".08em",
    textTransform:"uppercase" as any,marginBottom:3};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:300,
      display:"flex",alignItems:"center",justifyContent:"center"}}
      onClick={onCancel}>
      <div onClick={e=>e.stopPropagation()} style={{width:420,background:T.bgPanel,
        border:`1px solid ${T.borderPrimary}`,borderRadius:6,fontFamily:FONT}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.borderPrimary}`}}>
          <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>Add Trade Partner to Directory</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>
            This partner will be saved org-wide and available across all projects.
          </div>
        </div>
        <div style={{padding:18,display:"flex",flexDirection:"column",gap:10}}>
          <div><div style={labelStyle}>Company Name *</div>
            <input autoFocus value={name} onChange={e=>setName(e.target.value)} placeholder="Brightline Electric" style={fieldStyle}/></div>
          <div><div style={labelStyle}>Trade</div>
            <input value={trade} onChange={e=>setTrade(e.target.value)} placeholder="Electrical" style={fieldStyle}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div><div style={labelStyle}>Contact</div>
              <input value={contact} onChange={e=>setContact(e.target.value)} placeholder="John Smith" style={fieldStyle}/></div>
            <div><div style={labelStyle}>Phone</div>
              <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="(555) 123-4567" style={fieldStyle}/></div>
          </div>
          <div><div style={labelStyle}>Email</div>
            <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="john@brightline.com" style={fieldStyle}/></div>
          <div><div style={labelStyle}>Notes</div>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
              style={{...fieldStyle,resize:"vertical" as any}}/></div>
        </div>
        <div style={{padding:"12px 18px",borderTop:`1px solid ${T.borderPrimary}`,
          display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onCancel}
            style={{fontSize:11,padding:"7px 14px",background:"none",border:`1px solid ${T.borderSecondary}`,
              color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
          <button disabled={!ok} onClick={()=>{
            onSave({id:uid(),name:name.trim(),trade:trade.trim(),contact:contact.trim(),
              phone:phone.trim(),email:email.trim(),notes:notes.trim()});
          }} style={{fontSize:11,padding:"7px 18px",background:ok?T.borderAccent:T.borderSecondary,
            border:"none",color:ok?T.textOnGold:T.textMuted,borderRadius:3,
            cursor:ok?"pointer":"not-allowed",fontFamily:FONT,fontWeight:700,opacity:ok?1:.5}}>
            Add to Directory
          </button>
        </div>
      </div>
    </div>
  );
}

function bidTotal(bid:Bid){return bid.items.reduce((s,i)=>(i.qty||0)*(i.unitCost||0)+s,0);}

function matchScore(a:string,b:string):number{
  const ta=new Set((a||"").toLowerCase().trim().split(/\s+/));
  const tb=new Set((b||"").toLowerCase().trim().split(/\s+/));
  const inter=[...ta].filter(w=>tb.has(w)).length;
  const union=new Set([...ta,...tb]).size;
  return union>0?inter/union:0;
}

function reconcileItems(oldItems:BidItem[],newItems:BidItem[]){
  const THRESHOLD=0.5;
  const matched:{oldItem:BidItem;newItem:BidItem;action:string}[]=[];
  const added:{item:BidItem;action:string}[]=[];
  const removed:{item:BidItem;action:string}[]=[];
  const usedOld=new Set<string>(),usedNew=new Set<string>();
  const pairs:{old:BidItem;new_:BidItem;score:number}[]=[];
  oldItems.forEach(o=>newItems.forEach(n=>{const s=matchScore(o.description,n.description);if(s>=THRESHOLD)pairs.push({old:o,new_:n,score:s});}));
  pairs.sort((a,b)=>b.score-a.score);
  pairs.forEach(p=>{
    if(usedOld.has(p.old.id)||usedNew.has(p.new_.id))return;
    usedOld.add(p.old.id);usedNew.add(p.new_.id);
    const changed=p.old.qty!==p.new_.qty||p.old.unitCost!==p.new_.unitCost||p.old.unit!==p.new_.unit;
    matched.push({oldItem:p.old,newItem:p.new_,action:changed?"Update":"Keep"});
  });
  oldItems.filter(o=>!usedOld.has(o.id)).forEach(o=>removed.push({item:o,action:"Keep"}));
  newItems.filter(n=>!usedNew.has(n.id)).forEach(n=>added.push({item:n,action:"Add"}));
  return{matched,added,removed};
}


const LS_KEY="openestimate_v2",LS_SNAPS_KEY="oe_snapshots_v1",LS_NOTES_KEY="oe_history_notes_v1",LS_SHORTCUTS_KEY="oe_shortcuts_v1",LS_BENCHMARKS_KEY="oe_benchmarks_v1",LS_TYPOLOGY_PREFS_KEY="oe_typology_prefs_v1",LS_PRESETS_KEY="oe_presets_v1",LS_CONT_DISPLAY_KEY="oe_cont_display_v1",LS_ORG_LOGOS_KEY="oe_org_logos_v1",LS_ORG_PARTNERS_KEY="oe_org_partners_v1",LS_ORG_EXCLUSIONS_KEY="oe_org_exclusions_v1",LS_LABOR_RATES_KEY="oe_labor_rates_v1",LS_BEST_PRACTICES_KEY="oe_best_practices_v1",LS_SUBMITTALS_KEY="oe_submittals_v1",LS_SUBMITTAL_TEMPLATES_KEY="oe_submittal_templates_v1",LS_LOGISTICS_KEY="oe_logistics_v1",LS_SCHEDULE_KEY="oe_schedule_v1",LS_SUB_SCHEDULE_KEY="oe_sub_schedule_v1",LS_COST_SCHEDULE_KEY="oe_cost_schedule_v1";
const LS_BACKUP_KEY="oe_backup_v1"; // rolling 5-min local backups
const BACKUP_INTERVAL_MS=5*60*1000;

// Display names for the project tabs. Module scope because the settings
// header needs them too, and it renders above the nav that used to own them.
/** Tabs that size themselves to the viewport and scroll their own panels,
 *  rather than being a document the page scrolls. */
const FULL_BLEED_TABS=new Set(["logistics","schedule"]);

/**
 * Whether a keystroke in this element is part of one continuing edit.
 *
 * Undo steps coalesce while the same field holds focus, so typing "12500"
 * is one step rather than five, and dragging a slider is one step rather
 * than forty. A checkbox or radio is a decision each time it is clicked, so
 * each click earns its own step.
 */
const isTextEntry=(el:Element|null|undefined):boolean=>{
  if(!el)return false;
  if(el.tagName==="TEXTAREA")return true;
  if((el as HTMLElement).isContentEditable)return true;
  if(el.tagName!=="INPUT")return false;
  const t=((el as HTMLInputElement).type||"text").toLowerCase();
  return !["checkbox","radio","button","submit","reset","file"].includes(t);
};

const DEFAULT_SCHEDULE:PrelimSchedule={projectStartDate:"",totalDuration:12,durationUnit:"months",phases:[]};

const TAB_LABELS:Record<string,string>={
  estimate:"Estimate",summary:"Summary",alternates:"Alternates",
  analysis:"Analysis",history:"Snapshots",
  bids:"Bids",scope:"Scope of Work",buyout:"Buyout",
  decisions:"Decisions",submittals:"Submittals",rfi:"RFIs",schedule:"Schedule",
  logistics:"Logistics",cashflow:"Cash Flow",budget:"Budget",
  properties:"Project Info","properties-defaults":"Defaults",
};
const lsGet=(key,fallback)=>{try{const s=sGetRaw(key);return s?JSON.parse(s):fallback;}catch{return fallback;}};
// localStorage write — surfaces quota errors via window event so UI can warn the user.
let _lsWarned=false;
const lsSet=(key,val)=>{
  try{sSetRaw(key,JSON.stringify(val));_lsWarned=false;}
  catch(err:any){
    const isQuota=err?.name==="QuotaExceededError"||err?.code===22||/quota/i.test(String(err?.message));
    if(isQuota&&!_lsWarned&&typeof window!=="undefined"){
      _lsWarned=true;
      window.dispatchEvent(new CustomEvent("oe-storage-quota",{detail:{key}}));
    } else {
      console.warn("localStorage write failed:",key,err);
    }
  }
};
// Helper for cached blob URLs — revokes prior entry to prevent memory leaks.
const _revokeBlob=(u?:string)=>{ if(u&&u.startsWith("blob:")){try{URL.revokeObjectURL(u);}catch{}} };


const DEFAULT_SHORTCUTS = {
  enterAddRow: true,
  ctrlZUndo: true,
  questionShortcuts: true,
};

// safeEvalArithmetic imported from ./lib/safeEval

function CurrencyInput({value,formula,onChange,style,onEnter,negColor="#d4706a",onFocus:extFocus,onBlur:extBlur,onTab,onShiftTab,"data-oe-item":dataOeItem,"data-oe-field":dataOeField}:{value:number,formula?:string,onChange:(v:number,formula?:string)=>void,style?:any,onEnter?:()=>void,negColor?:string,onFocus?:()=>void,onBlur?:()=>void,onTab?:()=>void,onShiftTab?:()=>void,"data-oe-item"?:string,"data-oe-field"?:string}){
  const [editing,setEditing]=useState(false);
  const [raw,setRaw]=useState("");
  const [invalid,setInvalid]=useState(false);
  const ref=useRef(null);
  const invalidTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  const selectTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  useEffect(()=>()=>{
    if(invalidTimer.current)clearTimeout(invalidTimer.current);
    if(selectTimer.current)clearTimeout(selectTimer.current);
  },[]);
  // When a formula is stored, show it on focus so the user can edit the math
  // instead of seeing the pre-computed result. Falls back to the numeric value.
  const onFocus=()=>{
    const initial = formula && formula.trim() ? formula : (value!=null&&value!==0?String(value):"");
    setRaw(initial);
    setEditing(true);
    setInvalid(false);
  /**
   * Select-the-value runs on a timer because the value only becomes the raw
   * one after this render. The timer has to check it still belongs here:
   * select() FOCUSES a blurred input, so one left over from a cell you have
   * already moved on from drags the caret back -- and two of them take turns
   * doing it, forever. Arrow around the grid quickly and it would never stop.
   */
    if(selectTimer.current)clearTimeout(selectTimer.current);
    selectTimer.current=setTimeout(()=>{
      if(document.activeElement===ref.current)ref.current?.select();
    },0);
    extFocus?.();
  };
  const commitBlur=()=>{
    if(selectTimer.current){clearTimeout(selectTimer.current);selectTimer.current=null;}
    setEditing(false);
    const s=String(raw).trim();
    let result=0;
    let storedFormula: string | undefined;
    // Formula evaluation — uses safe recursive descent parser, no eval/Function()
    const evalResult = s ? safeEvalArithmetic(s) : null;
    if (evalResult !== null) { result = evalResult; storedFormula = s; }
    else {
      const parsed = parseFloat(s.replace(/[^0-9.-]/g, ""));
      // Unreadable entry — flash the field instead of zeroing silently
      if (s && (isNaN(parsed) || !isFinite(parsed))) {
        setInvalid(true);
        if(invalidTimer.current)clearTimeout(invalidTimer.current);
        invalidTimer.current=setTimeout(()=>setInvalid(false),2500);
      }
      result = parsed || 0;
      storedFormula = undefined;
    }
    onChange(isNaN(result)||!isFinite(result)?0:result, storedFormula);
    extBlur?.();
  };
  const hasFormula = !!(formula && formula.trim());
  const displayVal = editing ? raw : fmtDec(value??0);
  const isNeg = !editing && (value??0) < 0;
  return <input ref={ref} className="input-field" value={displayVal}
    data-oe-item={dataOeItem} data-oe-field={dataOeField}
    title={invalid ? "Couldn't read that as a number — value reset to 0" : (hasFormula && !editing ? `= ${formula}` : undefined)}
    onChange={e=>setRaw(e.target.value)} onFocus={onFocus} onBlur={commitBlur}
    onKeyDown={e=>{
      if(e.key==="Enter"){ref.current?.blur();onEnter&&onEnter();}
      if(e.key==="Tab"&&!e.shiftKey&&onTab){e.preventDefault();ref.current?.blur();setTimeout(()=>onTab(),0);}
      if(e.key==="Tab"&&e.shiftKey&&onShiftTab){e.preventDefault();ref.current?.blur();setTimeout(()=>onShiftTab(),0);}
    }}
    style={{textAlign:"right",color:isNeg?negColor:undefined,fontStyle:hasFormula&&!editing?"italic":"normal",...style,...(invalid?{outline:"1.5px solid #e05252",outlineOffset:-1,background:"#e0525218"}:{})}}/>;
}

// NumberInput and ResizeHandle imported from ./components/

// ── PDF Page renderer (single page) ──────────────────────────────────────
const PdfPage=React.memo(function PdfPage({doc,pageNum}:{doc:any,pageNum:number}){
  const ref=React.useRef<HTMLCanvasElement>(null);
  React.useEffect(()=>{
    if(!doc)return;
    let cancelled=false;let task:any=null;
    (async()=>{
      try{
        const page=await doc.getPage(pageNum);
        if(cancelled)return;
        const canvas=ref.current;if(!canvas)return;
        const vp=page.getViewport({scale:1.5});
        canvas.width=vp.width;canvas.height=vp.height;
        const ctx=canvas.getContext("2d");if(!ctx)return;
        task=page.render({canvasContext:ctx,viewport:vp});
        await task.promise;
      }catch(e:any){if(!cancelled&&e?.name!=="RenderingCancelledException")console.warn("pdf page err",e);}
    })();
    return()=>{cancelled=true;task?.cancel?.();};
  },[doc,pageNum]);
  return <canvas ref={ref} style={{display:"block",width:"100%",height:"auto"}}/>;
});

// ── PDF Pane (multi-page scrollable viewer) ───────────────────────────────
const PdfPane=React.memo(function PdfPane({url}:{url:string}){
  const [st,setSt]=React.useState<{doc:any,n:number}|null>(null);
  const [loading,setLoading]=React.useState(true);
  const [err,setErr]=React.useState<string|null>(null);
  React.useEffect(()=>{
    if(!url)return;
    let cancelled=false;
    setSt(null);setLoading(true);setErr(null);
    (async()=>{
      try{
        const pdfjs=await import("pdfjs-dist");
        const wm=await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        pdfjs.GlobalWorkerOptions.workerSrc=wm.default;
        const doc=await pdfjs.getDocument({url}).promise;
        if(cancelled)return;
        setSt({doc,n:doc.numPages});setLoading(false);
      }catch(e:any){if(!cancelled){setErr("Could not load PDF.");setLoading(false);}}
    })();
    return()=>{cancelled=true;};
  },[url]);
  if(loading)return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#999",fontSize:13}}>Loading PDF…</div>;
  if(err||!st)return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"#c04848",fontSize:12,padding:24,textAlign:"center"as any}}>{err||"Unavailable"}</div>;
  return(
    <div style={{overflowY:"auto",height:"100%",background:"#2a2a2a",padding:"16px 20px"}}>
      {Array.from({length:st.n}).map((_,i)=>(
        <div key={i} style={{marginBottom:10,boxShadow:"0 2px 12px #0008",borderRadius:3,overflow:"hidden",background:"#fff"}}>
          <PdfPage doc={st.doc} pageNum={i+1}/>
        </div>
      ))}
    </div>
  );
});

// COL_DEFS, DEFAULT_COLS, COL_PRESETS imported from ./lib/estimateDefaults

function getFileSummary(f){
  let sub=0;
  (f.divisions||[]).forEach(div=>div.subcategories.forEach(s2=>[...walkItems(s2.rows)].forEach(item=>{
    const li=(f.itemState||{})[item.id]||{};
    if(li.included&&(!li.altType||(li.altType==="Alt"&&li.altStatus==="Accepted")))sub+=(li.qty||0)*(li.unitCost||0);
  })));
  const mm=f.markupMode||"both";
  const oh=mm==="profit"?0:sub*((f.overhead||0)/100);
  const pr=mm==="overhead"||mm==="combined"?0:sub*((f.profit||0)/100);
  const bc=sub+oh+pr,co=bc*((f.contingency||0)/100),total=bc+co;
  return{total,costPerSF:(f.buildingArea||0)>0?total/f.buildingArea:0};
}

// getItemTotal and calcItemCont imported from ./lib/calculations

function exportCSV(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode="both"){
  const rows=[["OpenEstimate Export","","","","","","",""],
    ["Project",projectName,"","Date",new Date().toLocaleDateString(),"","",""],
    ["","","","","","","",""],
    ["Division","Subcategory","Cost Code","Description","Qty","Unit","Unit Cost","Total","Package","Cost Status","Alt Type","Notes"]
  ];
  divisions.forEach(div=>{
    div.subcategories.forEach(sub=>{
      [...walkItems(sub.rows)].forEach(item=>{
        const li=itemState[item.id]||{};
        const tot=(li.qty||0)*(li.unitCost||0);
        rows.push([div.name,sub.name,sub.costCode||"",li.description||"",li.qty||0,li.unit||"",li.unitCost||0,tot,
          proposals.find(p=>p.id===li.proposal)?.name||"",li.costStatus||"",li.altType||"",li.notes||""]);
      });
    });
  });
  rows.push(["","","","","","","","","","","",""]);
  rows.push(["Subtotal","","","","","","",final.subtotal]);
  if(markupMode==="combined")rows.push([`OH&P (${overhead}%)","","","","","","`,final.overhead]);
  else{
    if(markupMode!=="profit")rows.push([`Overhead (${overhead}%)","","","","","","`,final.overhead]);
    if(markupMode!=="overhead")rows.push([`Profit (${profit}%)","","","","","","`,final.profit]);
  }
  rows.push([`Contingency","","","","","","`,final.contingency]);
  rows.push(["TOTAL ESTIMATE","","","","","","",final.total]);
  const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const a=document.createElement("a");
  a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
  a.download=`${projectName.replace(/[^a-z0-9]/gi,"_")}_estimate.csv`;
  document.body.appendChild(a);a.click();document.body.removeChild(a);
}

/** Parse CSV rows from a string — handles quoted fields and newlines inside quotes */
function parseCSV(text:string):string[][]{
  const rows:string[][]=[];let row:string[]=[],field="",inQ=false;
  for(let i=0;i<text.length;i++){
    const c=text[i],n=text[i+1];
    if(inQ){if(c==='"'&&n==='"'){field+='"';i++;}else if(c==='"'){inQ=false;}else{field+=c;}}
    else if(c==='"'){inQ=true;}
    else if(c===','){row.push(field);field="";}
    else if(c==='\r'&&n==='\n'){row.push(field);rows.push(row);row=[];field="";i++;}
    else if(c==='\n'||c==='\r'){row.push(field);rows.push(row);row=[];field="";}
    else{field+=c;}
  }
  if(field||row.length)row.push(field);
  if(row.length)rows.push(row);
  return rows;
}

/** Parse an OpenEstimate CSV export and return new divisions + itemState */
function importCSV(text:string,existingDivisions:any[],existingProposals:any[]):{divisions:any[],itemState:Record<string,any>,imported:number,skipped:number}|null{
  const rows=parseCSV(text);
  // Find the header row: Division,Subcategory,Cost Code,Description,Qty,Unit,Unit Cost,...
  const headerIdx=rows.findIndex(r=>r[0]?.toLowerCase().includes("division")&&r[3]?.toLowerCase().includes("desc"));
  if(headerIdx<0)return null;
  const header=rows[headerIdx].map(h=>h.toLowerCase().trim());
  const col=(name:string)=>header.findIndex(h=>h.includes(name));
  const cDiv=col("division"),cSub=col("subcategor"),cDesc=col("desc"),cQty=col("qty"),cUnit=col("unit");
  const cUC=col("unit cost"),cPkg=col("package"),cStatus=col("cost status"),cAlt=col("alt"),cNotes=col("notes");
  if(cDiv<0||cDesc<0)return null;

  const newDivs=existingDivisions.map(d=>({...d,subcategories:d.subcategories.map(s=>({...s,rows:[...s.rows]}))}));
  const newItemState:Record<string,any>={};
  let imported=0,skipped=0;

  const dataRows=rows.slice(headerIdx+1).filter(r=>r.length>cDesc&&r[cDesc]?.trim()&&!r[0]?.toLowerCase().includes("subtotal")&&!r[0]?.toLowerCase().includes("total")&&!r[0]?.toLowerCase().includes("overhead")&&!r[0]?.toLowerCase().includes("profit")&&!r[0]?.toLowerCase().includes("contingency"));

  dataRows.forEach(r=>{
    const divName=(cDiv>=0?r[cDiv]:"").trim();
    const subName=(cSub>=0?r[cSub]:"General").trim()||"General";
    const desc=(r[cDesc]||"").trim();
    if(!desc){skipped++;return;}

    // Find or use first matching division
    let targetDiv=newDivs.find(d=>d.name.toLowerCase().includes(divName.toLowerCase().slice(0,8)));
    if(!targetDiv)targetDiv=newDivs[0];
    if(!targetDiv){skipped++;return;}

    let targetSub=targetDiv.subcategories.find((s:any)=>s.name.toLowerCase().includes(subName.toLowerCase().slice(0,8)));
    if(!targetSub){targetSub=targetDiv.subcategories[0];}
    if(!targetSub){skipped++;return;}

    const proposalName=(cPkg>=0?r[cPkg]:"").trim();
    const proposalId=existingProposals.find(p=>p.name.toLowerCase()===proposalName.toLowerCase())?.id||"";

    let _seq2=Date.now();const newId=`i${++_seq2}`;
    // Generate proper ID using the global uid-style approach
    const id=`i${Math.floor(Math.random()*9e9)+1e9}`;
    const qty=parseFloat((cQty>=0?r[cQty]:"1")||"1")||1;
    const uc=parseFloat((cUC>=0?r[cUC]:r[cQty+1]||"0")||"0")||0;
    const unit=(cUnit>=0?r[cUnit]:"LS")||"LS";
    const costStatus=(cStatus>=0?r[cStatus]:"").trim()||"";
    const altType=(cAlt>=0?r[cAlt]:"").trim()||"";
    const notes=(cNotes>=0?r[cNotes]:"").trim()||"";

    targetSub.rows.push({type:"item",id});
    newItemState[id]={description:desc,qty,unit,unitCost:uc,included:true,costStatus,altType:migrateAltType(altType),proposal:proposalId,notes};
    imported++;
  });

  return{divisions:newDivs,itemState:newItemState,imported,skipped};
}

async function exportExcel(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode="both"){
  const XLSX=await import("xlsx");
  const header=["Division","Subcategory","Cost Code","Description","Qty","Unit","Unit Cost","Total","Package","Cost Status","Alt Type","Notes"];
  const dataRows=[];
  divisions.forEach(div=>{
    div.subcategories.forEach(sub=>{
      [...walkItems(sub.rows)].forEach(item=>{
        const li=itemState[item.id]||{};
        const tot=(li.qty||0)*(li.unitCost||0);
        dataRows.push([div.name,sub.name,sub.costCode||"",li.description||"",li.qty||0,li.unit||"",li.unitCost||0,tot,
          proposals.find(p=>p.id===li.proposal)?.name||"",li.costStatus||"",li.altType||"",li.notes||""]);
      });
    });
  });
  const summaryRows=[
    [],
    ["Subtotal","","","","","","",final.subtotal],
    ...(markupMode==="combined"?[[`OH&P (${overhead}%)`,"","","","","","",final.overhead]]:[
      ...(markupMode!=="profit"?[[`Overhead (${overhead}%)`,"","","","","","",final.overhead]]:[]),
      ...(markupMode!=="overhead"?[[`Profit (${profit}%)`,"","","","","","",final.profit]]:[]),
    ]),
    [`Contingency (${contingency}%)`,"","","","","","",final.contingency],
    ["TOTAL ESTIMATE","","","","","","",final.total],
  ];
  const ws=XLSX.utils.aoa_to_sheet([
    ["OpenEstimate Export","","","","","","",""],
    ["Project",projectName,"","Date",new Date().toLocaleDateString()],
    [],
    header,
    ...dataRows,
    ...summaryRows,
  ]);
  // Set column widths
  ws["!cols"]=[{wch:22},{wch:28},{wch:10},{wch:36},{wch:8},{wch:8},{wch:12},{wch:14},{wch:20},{wch:14},{wch:10},{wch:24}];
  const wb=XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb,ws,"Estimate");
  XLSX.writeFile(wb,`${projectName.replace(/[^a-z0-9]/gi,"_")}_estimate.xlsx`);
}

// ── Org Partner CSV/Excel Import Parser ────────────────────────────────────
// Accepts a File (CSV or XLSX), returns OrgPartner[] from detected columns.
async function parsePartnerFile(file:File):Promise<OrgPartner[]>{
  const COLS:{key:keyof OrgPartner;aliases:string[]}[]=[
    {key:"name",   aliases:["name","company","firm","subcontractor","sub","trade partner","contractor","vendor"]},
    {key:"trade",  aliases:["trade","division","csi","specialty","scope","category"]},
    {key:"contact",aliases:["contact","contact name","person","representative","rep"]},
    {key:"phone",  aliases:["phone","telephone","cell","mobile","ph"]},
    {key:"email",  aliases:["email","e-mail","mail"]},
    {key:"notes",  aliases:["notes","remarks","comment","comments","description"]},
  ];
  const normalize=(s:string)=>String(s||"").trim().toLowerCase();

  let rows:{[k:string]:string}[]=[];

  if(file.name.toLowerCase().endsWith(".csv")){
    const text=await file.text();
    const lines=text.split(/\r?\n/).filter(l=>l.trim());
    if(lines.length<2)return[];
    const headers=lines[0].split(",").map(h=>h.replace(/^"|"$/g,"").trim().toLowerCase());
    for(let i=1;i<lines.length;i++){
      const cells=lines[i].split(",").map(c=>c.replace(/^"|"$/g,"").trim());
      const row:{[k:string]:string}={};
      headers.forEach((h,j)=>{ row[h]=cells[j]||""; });
      rows.push(row);
    }
  } else {
    // XLSX
    const XLSX=await import("xlsx");
    const buf=await file.arrayBuffer();
    const wb=XLSX.read(buf,{type:"array"});
    const ws=wb.Sheets[wb.SheetNames[0]];
    const data:string[][]=XLSX.utils.sheet_to_json(ws,{header:1,defval:""}) as string[][];
    if(data.length<2)return[];
    const headers=data[0].map((h:any)=>String(h||"").trim().toLowerCase());
    for(let i=1;i<data.length;i++){
      const row:{[k:string]:string}={};
      headers.forEach((h,j)=>{ row[h]=String(data[i][j]||"").trim(); });
      rows.push(row);
    }
  }

  // Map headers to OrgPartner fields
  const headerKeys=Object.keys(rows[0]||{});
  const fieldMap:Partial<Record<keyof OrgPartner,string>>={};
  COLS.forEach(({key,aliases})=>{
    const found=headerKeys.find(h=>aliases.some(a=>normalize(h)===a||normalize(h).includes(a)));
    if(found)fieldMap[key]=found;
  });

  return rows
    .filter(r=>Object.values(r).some(v=>v.trim()))
    .map(r=>({
      id:Math.random().toString(36).slice(2),
      name:   r[fieldMap.name   ||""]||"",
      trade:  r[fieldMap.trade  ||""]||"",
      contact:r[fieldMap.contact||""]||"",
      phone:  r[fieldMap.phone  ||""]||"",
      email:  r[fieldMap.email  ||""]||"",
      notes:  r[fieldMap.notes  ||""]||"",
    }))
    .filter(p=>p.name.trim());
}

// ── QuickBooks CSV Export ───────────────────────────────────────────────────
// Matches QuickBooks Online estimate/invoice CSV import template.
// Columns: No, Customer, Date, Item(Product/Service), Description, Qty, Rate, Amount, Class
function exportQuickBooks(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode="both"){
  const date=new Date().toLocaleDateString("en-US");
  const header=["No","Customer","Date","Item(Product/Service)","Description","Qty","Rate","Amount","Class","Memo"];
  const rows=[header];
  let lineNum=1;
  divisions.forEach(div=>{
    div.subcategories.forEach(sub=>{
      [...walkItems(sub.rows)].forEach(item=>{
        const li=itemState[item.id]||{};
        if(!li.description&&!li.qty&&!li.unitCost)return;
        const qty=li.qty||0;
        const rate=li.unitCost||0;
        const amount=qty*rate;
        // QB "Item" maps to Division/Subcategory — used as service item name
        const qbItem=`${div.name} / ${sub.name}`;
        const pkg=proposals.find(p=>p.id===li.proposal)?.name||"";
        rows.push([
          `EST-${String(lineNum).padStart(3,"0")}`,
          projectName,
          date,
          qbItem,
          li.description||"",
          qty,
          rate,
          amount,
          pkg,        // QB "Class" = proposal/package name
          li.notes||"",
        ]);
        lineNum++;
      });
    });
  });
  // Summary footer rows (non-import, informational)
  rows.push([]);
  rows.push(["","","","","Subtotal","","",final.subtotal,"",""]);
  if(markupMode==="combined"){if(overhead>0)rows.push(["","","","",`OH&P (${overhead}%)","","`,final.overhead,"",""]);}
  else{
    if(markupMode!=="profit"&&overhead>0)rows.push(["","","","",`Overhead (${overhead}%)","","`,final.overhead,"",""]);
    if(markupMode!=="overhead"&&profit>0)rows.push(["","","","",`Profit (${profit}%)","","`,final.profit,"",""]);
  }
  if(contingency>0)rows.push(["","","","",`Contingency (${contingency}%)","","`,final.contingency,"",""]);
  rows.push(["","","","","TOTAL ESTIMATE","","",final.total,"",""]);
  const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const a=document.createElement("a");
  a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
  a.download=`${projectName.replace(/[^a-z0-9]/gi,"_")}_quickbooks.csv`;
  document.body.appendChild(a);a.click();document.body.removeChild(a);
}

// ── Scope of Work — Payload Builder ────────────────────────────────────────────
function buildScopePayload(
  projectName:string, buildingArea:number, duration:number,
  overhead:number, profit:number, contingency:number, markupMode:string,
  final:any, divisions:any[], itemState:Record<string,any>,
  proposals:any[], divTotals:Record<string,{byProp:Record<string,number>;total:number}>,
  bids:any[], bidPartners:any[]
){
  const propMap:Record<string,string>={};
  proposals.forEach(p=>{propMap[p.id]=p.name;});

  const activeDivisions=divisions.filter(div=>(divTotals[div.id]?.total||0)!==0);

  const divPayloads=activeDivisions.map(div=>{
    const subcategories:any[]=[];
    const allItems:any[]=[];

    div.subcategories.forEach((sub:any)=>{
      let subTotal=0;
      let itemCount=0;
      const subItems:any[]=[];
      ;[...walkItems(sub.rows)].forEach(row=>{
        const li=itemState[row.id]||{};
        if(li.included===false)return;
        const qty=li.qty||0;
        const uc=li.unitCost||0;
        const total=qty*uc;
        subTotal+=total;
        itemCount++;
        subItems.push({
          description:li.description||row.label||"",
          qty,unit:li.unit||"LS",unitCost:uc,total,
          costStatus:li.costStatus||"",
          altType:li.altType||"",
          proposal:propMap[li.proposal]||"",
        });
      });
      if(itemCount>0){
        subcategories.push({name:sub.name,subtotal:subTotal,itemCount});
        allItems.push(...subItems);
      }
    });

    // Top 6 items by cost for the AI prompt
    const topItems=allItems
      .filter(i=>!i.altType)
      .sort((a,b)=>Math.abs(b.total)-Math.abs(a.total))
      .slice(0,6);

    // Alternates for this division
    const altItems=allItems.filter(i=>i.altType==="Alt");

    return{
      id:div.id,name:div.name,code:div.code,
      subtotal:divTotals[div.id]?.total||0,
      costPerSF:buildingArea>0?(divTotals[div.id]?.total||0)/buildingArea:0,
      subcategories,topItems,altItems,
    };
  });

  // Collect all alternates across divisions
  const addAlts:any[]=[],dedAlts:any[]=[];
  divisions.forEach(div=>{
    div.subcategories.forEach((sub:any)=>{
      ;[...walkItems(sub.rows)].forEach(row=>{
        const li=itemState[row.id]||{};
        if(li.altType!=="Alt"||li.included===false)return;
        const total=(li.qty||0)*(li.unitCost||0);
        const entry={description:li.description||"",amount:total,division:div.name};
        if(total<0)dedAlts.push(entry);else addAlts.push(entry);
      });
    });
  });

  const proposalTotals=proposals.map(p=>{
    let t=0;
    divisions.forEach(div=>{
      div.subcategories.forEach((sub:any)=>{
        ;[...walkItems(sub.rows)].forEach(row=>{
          const li=itemState[row.id]||{};
          if(li.proposal===p.id&&li.included!==false&&!li.altType){
            t+=(li.qty||0)*(li.unitCost||0);
          }
        });
      });
    });
    return{id:p.id,name:p.name,total:t};
  }).filter(p=>p.total!==0);

  return{
    projectName,buildingArea,duration,overhead,profit,contingency,markupMode,final,
    proposals:proposalTotals,
    divisions:divPayloads,
    alternates:{add:addAlts,ded:dedAlts},
    tradePartnerCount:bidPartners.length,
    proposalReviewCount:bids.filter((b:any)=>b.status!=="Rejected").length,
  };
}

// ── Scope of Work — DOCX Assembler ─────────────────────────────────────────────
async function assembleScopeDocx(
  projectName:string,
  buildingAddress:string,
  payload:ReturnType<typeof buildScopePayload>,
  scopeResult:any,
  scopeExclusions:Record<string,string>,
  buildingArea:number,
  final:any,
  drawingList:DrawingListItem[]=[],
){
  const {
    Document,Packer,Paragraph,TextRun,HeadingLevel,AlignmentType,
    Table,TableRow,TableCell,WidthType,BorderStyle,
  } = await import("docx");

  const today=new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});
  const fmtD=(n:number)=>"$"+n.toLocaleString("en-US");
  const fmtSFD=(n:number)=>"$"+n.toFixed(2)+"/SF";

  const spacer=()=>new Paragraph({text:""});
  const rule=()=>new Paragraph({
    children:[new TextRun({text:"\u2014".repeat(60),color:"C8CAD0",size:16})],
    spacing:{before:80,after:80},
  });

  const heading=(text:string,level:typeof HeadingLevel[keyof typeof HeadingLevel])=>
    new Paragraph({text,heading:level,spacing:{before:400,after:160}});

  const body=(text:string)=>
    new Paragraph({children:[new TextRun({text,size:22})],spacing:{before:0,after:160}});

  const bullet=(text:string,level=0)=>
    new Paragraph({
      children:[new TextRun({text,size:22})],
      bullet:{level},
      spacing:{before:40,after:40},
    });

  const sectionLabel=(text:string,color="444444")=>
    new Paragraph({
      children:[new TextRun({text:text.toUpperCase(),bold:true,size:18,color,characterSpacing:60})],
      spacing:{before:280,after:100},
    });

  const costRow=(label:string,value:string,sub?:string)=>
    new Paragraph({
      children:[
        new TextRun({text:`${label}: `,size:20,bold:true}),
        new TextRun({text:value,size:20}),
        ...(sub?[new TextRun({text:`  ${sub}`,size:18,color:"888888"})]:[]),
      ],
      spacing:{before:40,after:40},
    });

  const borderStyle={style:BorderStyle.SINGLE,size:1,color:"E0E0E0"};
  const noBorder={style:BorderStyle.NONE,size:0,color:"FFFFFF"};

  const summaryTable=()=>new Table({
    width:{size:100,type:WidthType.PERCENTAGE},
    rows:[
      new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Total Estimate",bold:true,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:fmtD(final.total),bold:true,size:24,color:"1A7A6E"})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder}}),
      ]}),
      ...(buildingArea>0?[new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Building Area / Cost per SF",bold:true,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:`${buildingArea.toLocaleString()} SF  ·  ${fmtSFD(final.costPerSF)}`,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder}}),
      ]})]:[]),
      ...(final.overhead!==0?[new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:payload.markupMode==="combined"?`OH&P (${payload.overhead}%)`:`Overhead (${payload.overhead}%)`,bold:true,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:fmtD(final.overhead),size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder}}),
      ]})]:[]),
      ...(final.profit!==0?[new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:`Profit (${payload.profit}%)`,bold:true,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:fmtD(final.profit),size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder}}),
      ]})]:[]),
      ...(payload.contingency>0?[new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:`Contingency (${payload.contingency}%)`,bold:true,size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:fmtD(final.contingency),size:20})]})],borders:{top:noBorder,bottom:borderStyle,left:noBorder,right:noBorder}}),
      ]})]:[]),
      ...(payload.tradePartnerCount>0?[new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Trade Partners Engaged",bold:true,size:20})]})],borders:{top:noBorder,bottom:noBorder,left:noBorder,right:noBorder},width:{size:50,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:`${payload.tradePartnerCount} partner${payload.tradePartnerCount!==1?"s":""}  ·  ${payload.proposalReviewCount} proposal${payload.proposalReviewCount!==1?"s":""} reviewed`,size:20})]})],borders:{top:noBorder,bottom:noBorder,left:noBorder,right:noBorder}}),
      ]})]:[]),
    ],
  });

  const children:any[]=[];

  // ── Cover ──
  children.push(new Paragraph({
    children:[new TextRun({text:"SCOPE OF WORK",bold:true,size:28,characterSpacing:120,color:"1A7A6E"})],
    alignment:AlignmentType.CENTER,spacing:{before:0,after:120},
  }));
  children.push(new Paragraph({
    children:[new TextRun({text:projectName||"Construction Project",bold:true,size:52,color:"111111"})],
    alignment:AlignmentType.CENTER,spacing:{before:0,after:buildingAddress?80:200},
  }));
  if(buildingAddress){children.push(new Paragraph({
    children:[new TextRun({text:buildingAddress,size:24,color:"555555"})],
    alignment:AlignmentType.CENTER,spacing:{before:0,after:200},
  }));}
  children.push(new Paragraph({
    children:[new TextRun({text:`Prepared: ${today}`,size:20,color:"888888"})],
    alignment:AlignmentType.CENTER,spacing:{before:0,after:40},
  }));
  children.push(spacer());children.push(summaryTable());
  children.push(spacer());children.push(rule());

  // ── Contract Documents / Basis of Estimate ──
  if(drawingList.length>0){
    children.push(spacer());children.push(rule());
    children.push(heading("Contract Documents — Basis of Estimate",HeadingLevel.HEADING_1));
    children.push(body("The following drawing sheets and specifications were used as the basis of this estimate. Any changes to the issued drawing set after the date of this document may affect scope and cost."));
    children.push(spacer());

    // Group by discipline
    const disciplineOrder=["Architectural","Structural","Civil","Mechanical","Electrical","Plumbing","Fire Protection","Landscape","Interiors","Other"];
    const byDisc:Record<string,DrawingListItem[]>={};
    for(const sheet of drawingList){
      const disc=sheet.discipline||"Other";
      if(!byDisc[disc])byDisc[disc]=[];
      byDisc[disc].push(sheet);
    }

    const discBorderStyle={style:BorderStyle.SINGLE,size:1,color:"E0E0E0"};
    const discNoBorder={style:BorderStyle.NONE,size:0,color:"FFFFFF"};

    for(const disc of disciplineOrder){
      const sheets=byDisc[disc];
      if(!sheets||sheets.length===0)continue;
      children.push(sectionLabel(disc,"1A7A6E"));

      // Build table: Sheet | Title | Rev | Date
      const headerRow=new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Sheet",bold:true,size:18,color:"555555"})]})],borders:{top:discBorderStyle,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder},width:{size:12,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Title",bold:true,size:18,color:"555555"})]})],borders:{top:discBorderStyle,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder},width:{size:58,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Rev",bold:true,size:18,color:"555555"})]})],borders:{top:discBorderStyle,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder},width:{size:12,type:WidthType.PERCENTAGE}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:"Date",bold:true,size:18,color:"555555"})]})],borders:{top:discBorderStyle,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder},width:{size:18,type:WidthType.PERCENTAGE}}),
      ]});

      const dataRows=sheets.map(s=>new TableRow({children:[
        new TableCell({children:[new Paragraph({children:[new TextRun({text:s.sheet||"",size:18})]})],borders:{top:discNoBorder,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:s.title||"",size:18})]})],borders:{top:discNoBorder,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:s.revision||"",size:18})]})],borders:{top:discNoBorder,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder}}),
        new TableCell({children:[new Paragraph({children:[new TextRun({text:s.date||"",size:18})]})],borders:{top:discNoBorder,bottom:discBorderStyle,left:discNoBorder,right:discNoBorder}}),
      ]}));

      children.push(new Table({
        width:{size:100,type:WidthType.PERCENTAGE},
        rows:[headerRow,...dataRows],
      }));
      children.push(spacer());
    }
  }

  // ── Project Overview ──
  children.push(heading("Project Overview",HeadingLevel.HEADING_1));
  children.push(body(scopeResult.introduction||""));

  // ── Per-division sections ──
  for(const divResult of (scopeResult.divisions||[])){
    const divPayload=payload.divisions.find(d=>d.id===divResult.divisionId);
    if(!divPayload)continue;

    children.push(spacer());children.push(rule());
    children.push(new Paragraph({
      children:[
        new TextRun({text:divPayload.code.toUpperCase()+" · ",size:18,color:"888888",bold:true}),
        new TextRun({text:divPayload.name.toUpperCase(),bold:true,size:24,color:"111111"}),
      ],
      spacing:{before:320,after:60},
    }));
    children.push(new Paragraph({
      children:[
        new TextRun({text:fmtD(divPayload.subtotal),bold:true,size:28,color:"1A7A6E"}),
        ...(divPayload.costPerSF>0?[new TextRun({text:`  ·  ${fmtSFD(divPayload.costPerSF)}`,size:20,color:"888888"})]:[]),
      ],
      spacing:{before:0,after:200},
    }));
    children.push(body(divResult.narrative||""));

    // Inclusions
    children.push(sectionLabel("Inclusions","2C7A6E"));
    for(const inc of (divResult.inclusions||[])){children.push(bullet(inc));}

    // Exclusions — from user-entered textarea
    children.push(sectionLabel("Exclusions","8B1A1A"));
    const userExcl=(scopeExclusions[divResult.divisionId]||"").trim();
    if(userExcl){
      const excLines=userExcl.split("\n").map(l=>l.trim()).filter(Boolean);
      for(const line of excLines){children.push(bullet(line));}
    }else{
      children.push(new Paragraph({
        children:[new TextRun({text:"[To be determined by estimator]",italics:true,color:"AAAAAA",size:20})],
        spacing:{before:40,after:40},
      }));
    }
  }

  // ── Alternates ──
  if(scopeResult.alternatesNarrative&&(payload.alternates.add.length>0||payload.alternates.ded.length>0)){
    children.push(spacer());children.push(rule());
    children.push(heading("Alternates",HeadingLevel.HEADING_1));
    children.push(body(scopeResult.alternatesNarrative));
    if(payload.alternates.add.length>0){
      children.push(sectionLabel("Add Alternates","2C5A7A"));
      for(const a of payload.alternates.add){children.push(bullet(`${a.description} — ${fmtD(a.amount)}`));}
    }
    if(payload.alternates.ded.length>0){
      children.push(sectionLabel("Deductive Alternates","8B1A1A"));
      for(const a of payload.alternates.ded){children.push(bullet(`${a.description} — (${fmtD(Math.abs(a.amount))})`));}
    }
  }

  // ── Proposals ──
  if(scopeResult.proposalsNarrative&&payload.proposals.length>1){
    children.push(spacer());children.push(rule());
    children.push(heading("Proposal Packages",HeadingLevel.HEADING_1));
    children.push(body(scopeResult.proposalsNarrative));
    for(const p of payload.proposals){
      children.push(costRow(p.name,fmtD(p.total)));
    }
  }

  // ── Disclaimer ──
  children.push(spacer());children.push(rule());children.push(spacer());
  children.push(new Paragraph({
    children:[new TextRun({
      text:"This Scope of Work document describes the general scope of the construction estimate as of the date prepared above. It is intended to inform the project owner of anticipated scope and cost and does not constitute a construction contract. Final contract documents, including the owner-contractor agreement and construction drawings, shall govern in all cases. All costs are estimates and subject to change based on final drawings, specifications, and market conditions.",
      italics:true,size:18,color:"888888",
    })],
    alignment:AlignmentType.CENTER,
    spacing:{before:0,after:200},
  }));

  const doc=new Document({
    creator:"OpenEstimate",
    title:`${projectName} — Scope of Work`,
    description:`Scope of Work prepared on ${today}`,
    styles:{
      default:{
        heading1:{
          run:{bold:true,size:28,color:"111111",font:"Calibri"},
          paragraph:{spacing:{before:400,after:160}},
        },
        heading2:{
          run:{bold:true,size:24,color:"1A7A6E",font:"Calibri"},
          paragraph:{spacing:{before:320,after:120}},
        },
      },
    },
    sections:[{
      properties:{
        page:{margin:{top:1080,bottom:1080,left:1080,right:1080}},
      },
      children,
    }],
  });

  return await Packer.toBlob(doc);
}

const PRINT_SHEETS=[
  {id:"estimate",label:"Estimate",desc:"Line items by division"},
  {id:"summary",label:"Summary",desc:"Division-by-proposal grid"},
  {id:"proposals",label:"Packages",desc:"Per-proposal breakdown"},
  {id:"analysis",label:"Analysis",desc:"Cost confidence by status"},
  {id:"history",label:"Snapshots",desc:"Cost progression & comparisons"},
  {id:"alternates",label:"Alternates",desc:"Add / Deduct / Rejected"},
  {id:"bids",label:"Bid Comparison",desc:"Levelled bids, one package per page"},
  {id:"decisions",label:"Open Decisions",desc:"What is still unresolved, for the meeting"},
];


function PrintModal({activeTab,initialSheets,projectName,buildingArea,duration,overhead,profit,contingency,markupMode="both",proposals,proposalAreas,divisions,itemState,final,grandTotal,divTotals,contingencyByProp,altTotals,projectSnapshots,currentSummary,getPC,orgLogo,costSFCents=false,bidScopes=[],bidPartners=[],bids=[],levelingAdjustments=[],rfis=[],onClose}:{activeTab?:string,initialSheets?:string[]|null,projectName:string,buildingArea:number,duration:number,overhead:number,profit:number,contingency:number,markupMode?:string,proposals:any[],proposalAreas:any,divisions:any[],itemState:any,final:any,grandTotal:any,divTotals:any,contingencyByProp:any,altTotals:any,projectSnapshots:any[],currentSummary:any,getPC:any,orgLogo:string,costSFCents?:boolean,bidScopes?:any[],bidPartners?:any[],bids?:any[],levelingAdjustments?:any[],rfis?:any[],onClose:()=>void}){
  const sfFor=(p:string)=>proposalAreas[p]>0?proposalAreas[p]:buildingArea;
  const fmtSF=(n:number)=>costSFCents?fmtDec(n):fmt(n);

  // ── Controls state ──
  const printable=PRINT_SHEETS.map(s=>s.id);
  const initSheets=initialSheets?.filter(s=>printable.includes(s));
  const [checked,setChecked]=useState<{[k:string]:boolean}>(()=>{
    const m:{[k:string]:boolean}={};
    PRINT_SHEETS.forEach(s=>{
      m[s.id]=initSheets&&initSheets.length>0?initSheets.includes(s.id):(s.id===(printable.includes(activeTab||"")?activeTab:"estimate"));
    });
    return m;
  });
  const selectedSheets=PRINT_SHEETS.filter(s=>checked[s.id]).map(s=>s.id);
  const allOn=PRINT_SHEETS.every(s=>checked[s.id]);
  const noneOn=!PRINT_SHEETS.some(s=>checked[s.id]);

  // ── Print CSS ──
  const PS=`
    @page{margin:0.5in;}
    @media screen{.pm-print-body{display:none!important;}}
    @media print{
      body *{visibility:hidden;}
      .pm-print-body,.pm-print-body *{visibility:visible;}
      .pm-print-body{display:block!important;position:absolute!important;top:0!important;left:0!important;width:100%!important;background:#fff!important;overflow:visible!important;color:#000!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .pm-sidebar,.pm-preview-area,.pm-preview-header{display:none!important;visibility:hidden!important;}
      .print-sheet{page-break-before:always;}
      .print-sheet:first-of-type{page-break-before:auto;}
    }
    .pm-print-body{font-family:${FONT};font-size:11px;background:#fff;color:#111;margin:0;padding:0;}
    .print-sheet{padding:24px 32px;}
    .print-sheet h2{font-size:16px;margin:0 0 2px;color:#1a1a1a;font-weight:700;}
    .print-sheet h3{font-size:14px;margin:0 0 4px;color:#222;font-weight:700;}
    .print-sheet .sheet-meta{color:#555;font-size:10px;margin-bottom:20px;}
    .print-sheet table{width:100%;border-collapse:collapse;margin-bottom:24px;}
    .print-sheet th{text-align:left;font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#666;padding:5px 8px;border-bottom:2px solid #ccc;}
    .print-sheet td{padding:5px 8px;border-bottom:1px solid #e5e5e5;font-size:11px;color:#222;}
    .print-sheet .right{text-align:right;}
    .print-sheet .neg{color:#b91c1c;}
    .print-sheet .div-code{color:#555;font-size:10px;}
    .print-sheet .sub-name{color:#444;font-size:10px;}
    .print-sheet .notes{color:#777;font-size:10px;}
    .print-sheet .add-alt{color:#1a5c8a;}
    .print-sheet .ded-alt{color:#7a2a7a;}
    .print-sheet .summary-label{color:#333;}
    .print-sheet .summary-total-label{font-size:14px;font-weight:700;color:#111;}
    .print-sheet .summary-total-value{font-size:16px;font-weight:700;color:#111;}
    .print-sheet .card{border:1px solid #ddd;border-radius:6px;padding:16px 20px;margin-bottom:14px;}
    .print-sheet .card-title{font-size:13px;font-weight:600;color:#222;margin-bottom:8px;}
    .print-sheet .stat-row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #eee;font-size:12px;}
    .print-sheet .stat-label{color:#333;}
    .print-sheet .footer-total{display:flex;justify-content:space-between;padding:10px 0 4px;border-top:2px solid #222;margin-top:4px;}
    /* The credit line. The sheet belongs to whoever prepared it, so this is
       deliberately quiet and at the foot: the tool, not the author. Before
       this the most-seen artifact the product makes carried no identity at
       all -- it reached the owner's table reading as a printout. */
    .print-sheet .made-with{display:flex;align-items:center;gap:5px;margin-top:18px;padding-top:7px;
      border-top:1px solid #dddddd;color:#999999;font-size:10px;letter-spacing:.08em;text-transform:uppercase;}
    .print-sheet .made-with svg{display:block;flex-shrink:0;}
  `;

  /** The mark, at the one size a printed sheet can spare for it. */
  const PrintCredit=()=>(
    <div className="made-with">
      <svg width="17" height="8" viewBox="0 0 256 120" aria-hidden="true">
        <circle cx="60" cy="60" r="40" fill="none" stroke="#999999" strokeWidth="16"/>
        <g fill="#999999">
          <rect x="132" y="16" width="72" height="16"/>
          <rect x="132" y="52" width="60" height="16"/>
          <rect x="132" y="88" width="72" height="16"/>
        </g>
      </svg>
      OpenEstimate
    </div>
  );

  // ── Print Header ──
  const PrintHeader=({title,meta}:{title:string,meta:string})=>(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:orgLogo?4:0}}>
      <div style={{flex:1,paddingRight:12}}>
        <h2 style={{margin:"0 0 2px"}}>{title}</h2>
        <div className="sheet-meta" style={{marginBottom:orgLogo?8:20}}>{meta}</div>
      </div>
      {orgLogo&&<img src={orgLogo} alt="Logo" style={{maxHeight:48,maxWidth:160,objectFit:"contain",flexShrink:0,marginTop:2}}/>}
    </div>
  );

  /* ── ESTIMATE SHEET ── */
  const EstimateSheet=()=>{
    const alts:{add:any[],ded:any[]}={add:[],ded:[]};
    divisions.forEach(div=>div.subcategories.forEach(sub=>[...walkItems(sub.rows)].forEach(item=>{
      const li=itemState[item.id]||{};
      if(li.altType==="Add Alt") alts.add.push({...li,desc:li.description,divName:div.name,total:(li.qty||0)*(li.unitCost||0)});
      if(li.altType==="Ded Alt") alts.ded.push({...li,desc:li.description,divName:div.name,total:(li.qty||0)*(li.unitCost||0)});
    })));
    return (
      <div className="print-sheet">
        <PrintHeader title={`Cost Estimate — ${projectName}`} meta={`${buildingArea>0?`${buildingArea.toLocaleString()} SF · `:""}${duration} Months · Printed ${new Date().toLocaleDateString()}`}/>
        <table>
          <thead><tr>
            <th>Division</th><th>Subcategory</th><th>Description</th>
            <th className="right">Qty</th><th>Unit</th><th className="right">Unit Cost</th><th className="right">Total</th><th>Package</th><th>Status</th>
          </tr></thead>
          <tbody>
            {divisions.map(div=>div.subcategories.map((sub:any,si:number)=>{
              const items=[...walkItems(sub.rows)].filter((item:any)=>{const li=itemState[item.id]||{};return li.included&&(!li.altType||(li.altType==="Alt"&&li.altStatus==="Accepted"));});
              if(!items.length&&si===0) return null;
              return items.map((item:any,ii:number)=>{
                const li=itemState[item.id]||{};
                const tot=(li.qty||0)*(li.unitCost||0);
                const pName=proposals.find((p:any)=>p.id===li.proposal)?.name||"";
                return <tr key={item.id}>
                  <td className="div-code">{ii===0&&si===0?div.code:""}</td>
                  <td className="sub-name">{ii===0?sub.name:""}</td>
                  <td>{li.description||""}{li.notes&&<div className="notes">{li.notes}</div>}</td>
                  <td className="right">{li.qty}</td><td>{li.unit}</td>
                  <td className={`right${(li.unitCost||0)<0?" neg":""}`}>{fmtDec(li.unitCost||0)}</td>
                  <td className={`right${tot<0?" neg":""}`} style={{fontWeight:500}}>{tot!==0?fmt(tot):"--"}</td>
                  <td style={{fontSize:10,color:"#444"}}>{pName}</td>
                  <td style={{fontSize:10,color:"#555"}}>{li.costStatus||""}</td>
                </tr>;
              });
            }))}
          </tbody>
        </table>
        {(alts.add.length>0||alts.ded.length>0)&&(<>
          <h3>Alternates</h3>
          <table>
            <thead><tr><th>Type</th><th>Description</th><th>Division</th><th className="right">Total</th></tr></thead>
            <tbody>
              {alts.add.map((a,i)=><tr key={i}><td className="add-alt">Add Alt</td><td>{a.desc}</td><td className="div-code">{a.divName}</td><td className="right add-alt">+{fmt(a.total)}</td></tr>)}
              {alts.ded.map((a,i)=><tr key={i}><td className="ded-alt">Ded Alt</td><td>{a.desc}</td><td className="div-code">{a.divName}</td><td className="right neg">{fmt(-Math.abs(a.total))}</td></tr>)}
            </tbody>
          </table>
        </>)}
        <div style={{maxWidth:380,marginLeft:"auto"}}>
          {([["Subtotal – Direct Costs",final.subtotal],
            ...(markupMode==="combined"?[[`OH&P (${overhead}%)`,final.overhead]]:[
              ...(markupMode!=="profit"?[[`Overhead (${overhead}%)`,final.overhead]]:[]),
              ...(markupMode!=="overhead"?[[`Profit (${profit}%)`,final.profit]]:[]),
            ]),
            ["Before Contingency",final.beforeCont],
            [`Contingency (${contingency}%)`,final.contingency],
          ] as [string,number][]).map(([l,v])=>(
            <div key={l} className="stat-row"><span className="summary-label">{l}</span><span className={v<0?"neg":""}>{fmt(v)}</span></div>
          ))}
          <div className="footer-total">
            <span className="summary-total-label">Total Estimate</span>
            <span className={`summary-total-value${final.total<0?" neg":""}`}>{fmt(final.total)}</span>
          </div>
          {buildingArea>0&&<div style={{display:"flex",justifyContent:"space-between",paddingTop:3,fontSize:11,color:"#555"}}>
            <span>Cost per SF</span><span>{fmtSF(final.costPerSF)} / SF</span>
          </div>}
        </div>
      </div>
    );
  };

  /* ── SUMMARY SHEET ── */
  const SummarySheet=()=>(
    <div className="print-sheet">
      <PrintHeader title={`Cost Summary — ${projectName}`} meta={`${buildingArea>0?`${buildingArea.toLocaleString()} SF · `:""}${duration} Months · Printed ${new Date().toLocaleDateString()}`}/>
      <table>
        <thead><tr>
          <th>Division</th>
          {proposals.map(p=><th key={p.id} className="right">{p.name}</th>)}
          <th className="right">Div Total</th>
          <th className="right">$/SF</th>
        </tr></thead>
        <tbody>
          {divisions.map(div=>{
            const dt=divTotals[div.id];if(!dt)return null;
            const divContMap=contingencyByProp.byDivByProp[div.id]||{};
            const divContTotal=Object.values(divContMap).reduce((a:number,v:any)=>a+v,0) as number;
            const displayTotal=dt.total+divContTotal;
            const dSF=displayTotal!==0?Object.entries(dt.byProp).reduce((a:number,[p,v]:any)=>a+(sfFor(p)*Math.abs(v)),0)/Math.abs(dt.total||1):buildingArea;
            const csf=dSF>0&&displayTotal!==0?displayTotal/dSF:0;
            return (
              <tr key={div.id}>
                <td><span className="div-code" style={{marginRight:6}}>{div.code}</span>{div.name}</td>
                {proposals.map(p=>{const combined=(dt.byProp[p.id]||0)+(divContMap[p.id]||0);
                  return <td key={p.id} className={`right${combined<0?" neg":""}`}>{combined!==0?fmt(combined):"--"}</td>;})}
                <td className={`right${displayTotal<0?" neg":""}`} style={{fontWeight:600}}>{displayTotal!==0?fmt(displayTotal):"--"}</td>
                <td className="right" style={{color:"#555"}}>{csf!==0?fmtSF(csf):"--"}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{borderTop:"2px solid #999"}}>
            <td style={{fontWeight:600,color:"#333"}}>Subtotal</td>
            {proposals.map(p=>{const combined=(grandTotal.byProp[p.id]||0)+(contingencyByProp.byProp[p.id]||0);
              return <td key={p.id} className={`right${combined<0?" neg":""}`} style={{fontWeight:600}}>{combined!==0?fmt(combined):"--"}</td>;})}
            <td className="right" style={{fontWeight:700,fontSize:13}}>{fmt(grandTotal.subtotal+contingencyByProp.total)}</td>
            <td className="right" style={{color:"#555"}}>{buildingArea>0?fmtSF((grandTotal.subtotal+contingencyByProp.total)/buildingArea):"--"}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{maxWidth:380,marginLeft:"auto"}}>
        {([["Subtotal – Direct Costs",final.subtotal],
          ...(markupMode==="combined"?[[`OH&P (${overhead}%)`,final.overhead]]:[
            ...(markupMode!=="profit"?[[`Overhead (${overhead}%)`,final.overhead]]:[]),
            ...(markupMode!=="overhead"?[[`Profit (${profit}%)`,final.profit]]:[]),
          ]),
          ["Before Contingency",final.beforeCont],
          [`Contingency (${contingency}%)`,final.contingency],
        ] as [string,number][]).map(([l,v])=>(
          <div key={l} className="stat-row"><span className="summary-label">{l}</span><span className={v<0?"neg":""}>{fmt(v)}</span></div>
        ))}
        <div className="footer-total">
          <span className="summary-total-label">Total Estimate</span>
          <span className={`summary-total-value${final.total<0?" neg":""}`}>{fmt(final.total)}</span>
        </div>
        {buildingArea>0&&<div style={{display:"flex",justifyContent:"space-between",paddingTop:3,fontSize:11,color:"#555"}}>
          <span>Cost per SF (blended)</span><span>{fmtSF(final.costPerSF)} / SF</span>
        </div>}
      </div>
    </div>
  );

  /* ── PROPOSALS SHEET ── */
  const ProposalsSheet=()=>(
    <div className="print-sheet">
      <PrintHeader title={`Proposals — ${projectName}`} meta={`Cost breakdown by proposal / contract package · Printed ${new Date().toLocaleDateString()}`}/>
      {proposals.map(prop=>{
        const total=grandTotal.byProp[prop.id]||0;
        const pct=grandTotal.subtotal!==0?(total/Math.abs(grandTotal.subtotal))*100:0;
        const sf=sfFor(prop.id),csf=sf>0&&total!==0?total/sf:0;
        const items=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].filter(item=>{
          const li=itemState[item.id]||{};return li.included&&li.proposal===prop.id&&getItemTotal(li,itemState)!==0&&!li.altType;
        }).map(item=>({id:item.id,desc:itemState[item.id]?.description||item.description,total:getItemTotal(itemState[item.id]||{},itemState),code:div.code}))));
        return (
          <div key={prop.id} className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div className="card-title">{prop.name}</div>
                <div style={{fontSize:10,color:"#666"}}>{pct.toFixed(0)}% of direct cost · {sf.toLocaleString()} SF basis</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:16,fontWeight:700,color:total<0?"#b91c1c":"#111"}}>{fmt(total)}</div>
                {csf!==0&&<div style={{fontSize:11,color:"#555"}}>{fmtSF(csf)} / SF</div>}
              </div>
            </div>
            {items.length>0?(
              <table>
                <thead><tr><th>Div</th><th>Item</th><th className="right">Total</th><th className="right">$/SF</th></tr></thead>
                <tbody>
                  {items.map((item,i)=>(
                    <tr key={i}>
                      <td className="div-code">{item.code}</td>
                      <td>{item.desc}</td>
                      <td className={`right${item.total<0?" neg":""}`}>{fmt(item.total)}</td>
                      <td className="right" style={{color:"#555"}}>{sf>0?fmtSF(item.total/sf):""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ):<div style={{fontSize:11,color:"#888",fontStyle:"italic"}}>No included line items with cost assigned.</div>}
          </div>
        );
      })}
    </div>
  );

  /* ── ANALYSIS SHEET ── */
  const AnalysisSheet=()=>{
    const SK=["Hard Bid","Allowance","Needs Review","Other"];
    const CW={"Hard Bid":1,"Allowance":.5,"Needs Review":0,"Other":.25};
    const SC={"Hard Bid":{color:"#2d8a4e",label:"Hard Bid"},"Allowance":{color:"#2a6a9a",label:"Allowance"},"Needs Review":{color:"#9a7a2a",label:"Needs Review"},"Other":{color:"#555",label:"Other (unset)"}};
    const allItems=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].map(item=>{
      const li=itemState[item.id]||{};
      const total=li.included!==false?(li.qty||0)*(li.unitCost||0):0;
      const status=li.costStatus&&SK.includes(li.costStatus)?li.costStatus:"Other";
      return {total,status,desc:li.description||item.description,div:div.code,sub:sub.name};
    }).filter(i=>i.total>0)));
    const buckets={};SK.forEach(k=>{buckets[k]=0;});allItems.forEach(i=>{buckets[i.status]=(buckets[i.status]||0)+i.total;});
    const grandSum=Object.values(buckets).reduce((a:number,v:any)=>a+v,0) as number;
    const score=grandSum>0?Math.round((SK.reduce((a:number,k:string)=>a+(buckets[k]||0)*CW[k],0)/grandSum)*100):null;
    return (
      <div className="print-sheet">
        <PrintHeader title={`Cost Confidence — ${projectName}`} meta={`Breakdown of estimate certainty by cost status · Printed ${new Date().toLocaleDateString()}`}/>
        {score!==null&&<div style={{marginBottom:20,padding:"12px 18px",border:"1px solid #ddd",borderRadius:6,display:"inline-block"}}>
          <span style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Confidence Score: </span>
          <span style={{fontSize:24,fontWeight:700,color:score>=75?"#2d8a4e":score>=45?"#9a7a2a":"#b91c1c"}}>{score}</span>
          <span style={{fontSize:11,color:"#666",marginLeft:8}}>({score>=75?"High":score>=45?"Moderate":"Low"})</span>
        </div>}
        <table>
          <thead><tr><th>Status</th><th className="right">Amount</th><th className="right">% of Total</th><th className="right">Weight</th><th className="right">Items</th></tr></thead>
          <tbody>
            {SK.map(k=>{const val=buckets[k]||0;const pct=grandSum>0?(val/grandSum)*100:0;const cnt=allItems.filter(i=>i.status===k).length;
              if(!val)return null;
              return <tr key={k}><td style={{color:SC[k].color,fontWeight:600}}>{SC[k].label}</td>
                <td className="right">{fmt(val)}</td><td className="right">{pct.toFixed(0)}%</td>
                <td className="right" style={{color:"#555"}}>{(CW[k]*100).toFixed(0)}%</td>
                <td className="right" style={{color:"#555"}}>{cnt}</td></tr>;
            })}
          </tbody>
          <tfoot><tr style={{borderTop:"2px solid #999"}}><td style={{fontWeight:600}}>Total</td><td className="right" style={{fontWeight:600}}>{fmt(grandSum)}</td><td className="right">100%</td><td/><td className="right" style={{color:"#555"}}>{allItems.length}</td></tr></tfoot>
        </table>
        {SK.map(k=>{const items=allItems.filter(i=>i.status===k);if(!items.length)return null;
          const statusTotal=items.reduce((a,i)=>a+i.total,0);
          return (
            <div key={k} style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:10,height:10,borderRadius:3,background:SC[k].color}}/>
                <span style={{fontSize:12,fontWeight:600,color:SC[k].color}}>{SC[k].label}</span>
                <span style={{fontSize:11,color:"#666"}}>{items.length} item{items.length!==1?"s":""}</span>
                <span style={{marginLeft:"auto",fontSize:12,fontWeight:600,color:SC[k].color}}>{fmt(statusTotal)}</span>
              </div>
              <table>
                <thead><tr><th>Description</th><th>Division</th><th>Subcategory</th><th className="right">Amount</th></tr></thead>
                <tbody>{items.map((item,i)=><tr key={i}><td>{item.desc||"—"}</td><td className="div-code">{item.div}</td><td className="sub-name">{item.sub}</td><td className={`right${item.total<0?" neg":""}`}>{fmt(item.total)}</td></tr>)}</tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  /* ── ALTERNATES SHEET ── */
  const AlternatesSheet=()=>(
    <div className="print-sheet">
      <PrintHeader title={`Alternates — ${projectName}`} meta={`Add, Deduct, and Rejected alternates · Printed ${new Date().toLocaleDateString()}`}/>
      <div style={{display:"flex",gap:20,marginBottom:24,flexWrap:"wrap"}}>
        <div style={{border:"1px solid #ddd",borderRadius:6,padding:"12px 18px",flex:1,minWidth:140}}>
          <div style={{fontSize:10,color:"#1a5c8a",letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>Add Alternates</div>
          <div style={{fontSize:16,fontWeight:700,color:"#1a5c8a"}}>{fmt(altTotals.addTotal)}</div>
          <div style={{fontSize:10,color:"#666",marginTop:3}}>{altTotals.add.length} item{altTotals.add.length!==1?"s":""}</div>
        </div>
        <div style={{border:"1px solid #ddd",borderRadius:6,padding:"12px 18px",flex:1,minWidth:140}}>
          <div style={{fontSize:10,color:"#7a2a7a",letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>Deduct Alternates</div>
          <div style={{fontSize:16,fontWeight:700,color:"#b91c1c"}}>{fmt(-altTotals.dedTotal)}</div>
          <div style={{fontSize:10,color:"#666",marginTop:3}}>{altTotals.ded.length} item{altTotals.ded.length!==1?"s":""}</div>
        </div>
        <div style={{border:"1px solid #ddd",borderRadius:6,padding:"12px 18px",flex:1,minWidth:140}}>
          <div style={{fontSize:10,color:"#555",letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>Base Bid</div>
          <div style={{fontSize:16,fontWeight:700,color:"#111"}}>{fmt(final.total)}</div>
          <div style={{fontSize:10,color:"#666",marginTop:3}}>All markups applied</div>
        </div>
      </div>
      {altTotals.add.length>0&&(<>
        <h3 style={{color:"#1a5c8a"}}>Add Alternates</h3>
        <table>
          <thead><tr><th>#</th><th>Description</th><th>Status</th><th>Division</th><th className="right">Total</th></tr></thead>
          <tbody>{altTotals.add.map((a,i)=><tr key={i}><td style={{color:"#1a5c8a",fontWeight:700}}>A{i+1}</td><td>{a.desc||"Untitled"}</td><td style={{fontSize:10,color:"#555"}}>{a.altStatus||"TBD"}</td><td className="div-code">{a.div}</td><td className="right add-alt" style={{fontWeight:600}}>+{fmt(a.amt)}</td></tr>)}</tbody>
          <tfoot><tr style={{borderTop:"2px solid #1a5c8a22"}}><td colSpan={4} style={{color:"#1a5c8a",fontSize:11}}>Total Add Alternates</td><td className="right" style={{fontWeight:700,color:"#1a5c8a"}}>{fmt(altTotals.addTotal)}</td></tr></tfoot>
        </table>
      </>)}
      {altTotals.ded.length>0&&(<>
        <h3 style={{color:"#7a2a7a"}}>Deduct Alternates</h3>
        <table>
          <thead><tr><th>#</th><th>Description</th><th>Status</th><th>Division</th><th className="right">Amount</th></tr></thead>
          <tbody>{altTotals.ded.map((a,i)=><tr key={i}><td style={{color:"#7a2a7a",fontWeight:700}}>D{i+1}</td><td>{a.desc||"Untitled"}</td><td style={{fontSize:10,color:"#555"}}>{a.altStatus||"TBD"}</td><td className="div-code">{a.div}</td><td className="right neg" style={{fontWeight:600}}>{fmt(-a.amt)}</td></tr>)}</tbody>
          <tfoot><tr style={{borderTop:"2px solid #7a2a7a22"}}><td colSpan={4} style={{color:"#7a2a7a",fontSize:11}}>Total Deduct Alternates</td><td className="right neg" style={{fontWeight:700}}>{fmt(-altTotals.dedTotal)}</td></tr></tfoot>
        </table>
      </>)}
      {altTotals.voided.length>0&&(<>
        <h3 style={{color:"#888"}}>Rejected</h3>
        <table style={{opacity:.8}}>
          <thead><tr><th>#</th><th>Description</th><th>Division</th><th className="right">Amount</th></tr></thead>
          <tbody>{altTotals.voided.map((a,i)=><tr key={i}><td style={{color:"#888",fontWeight:700}}>V{i+1}</td><td>{a.desc||"Untitled"}</td><td className="div-code">{a.div}</td><td className="right" style={{color:"#888"}}>{fmt(a.amt)}</td></tr>)}</tbody>
        </table>
      </>)}
    </div>
  );

  /* ── HISTORY SHEET ── */
  const HistorySheet=()=>{
    const curTotal=Object.values(currentSummary).reduce((a:number,v:any)=>a+v.total,0) as number;
    return (
      <div className="print-sheet">
        <PrintHeader title={`Snapshots — ${projectName}`} meta={`Cost progression & comparisons · Printed ${new Date().toLocaleDateString()}`}/>
        {projectSnapshots.length===0?<div style={{fontSize:12,color:"#888"}}>No snapshots saved.</div>:(
          <table>
            <thead><tr><th>Snapshot</th><th>Date</th><th className="right">Total</th><th className="right">Delta vs Current</th></tr></thead>
            <tbody>
              {projectSnapshots.map(snap=>{
                const snapSummary=(() => {try{const s=snap.state||{};const is=s.itemState||{};let t=0;(s.divisions||[]).forEach(d=>d.subcategories.forEach(sub=>[...walkItems(sub.rows)].forEach(item=>{const li=is[item.id]||{};if(li.included!==false&&!li.altType)t+=(li.qty||0)*(li.unitCost||0);})));return t;}catch{return 0;}})();
                const delta=curTotal-snapSummary;
                return <tr key={snap.id}>
                  <td style={{fontWeight:500}}>{snap.label}</td>
                  <td style={{color:"#555"}}>{new Date(snap.date).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"})}</td>
                  <td className={`right${snapSummary<0?" neg":""}`} style={{fontWeight:600}}>{fmt(snapSummary)}</td>
                  <td className={`right${delta>0?" neg":""}`} style={{color:delta>0?"#b91c1c":delta<0?"#2d8a4e":"#555"}}>{delta>0?"+":""}{fmt(delta)}</td>
                </tr>;
              })}
              <tr style={{borderTop:"2px solid #999"}}><td style={{fontWeight:600}}>Current</td><td style={{color:"#555"}}>{new Date().toLocaleDateString()}</td><td className="right" style={{fontWeight:700}}>{fmt(curTotal)}</td><td className="right" style={{color:"#555"}}>—</td></tr>
            </tbody>
          </table>
        )}
      </div>
    );
  };

  /* ── EXECUTIVE SUMMARY SHEET ── */
  const ExecutiveSummarySheet=()=>{
    const sfForP=p=>proposalAreas[p]>0?proposalAreas[p]:buildingArea;
    const divRowsP=divisions.map((div,i)=>({div,total:divTotals[div.id]?.total||0})).filter(r=>r.total>0).sort((a,b)=>b.total-a.total).slice(0,8);
    const maxDivP=Math.max(...divRowsP.map(r=>r.total),1);
    const BAR_COLORS_P=["#1a5c8a","#2d8a4e","#8a7a1a","#8a4a1a","#6a1a8a","#1a4a8a","#8a4a2a","#4a8a1a"];
    return (
      <div className="print-sheet">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",paddingBottom:14,marginBottom:18,borderBottom:"2px solid #222"}}>
          <div>
            <h2 style={{margin:0}}>{projectName}</h2>
            <div className="sheet-meta" style={{margin:"4px 0 0"}}>Executive Summary · {new Date().toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"})}</div>
          </div>
          <div style={{textAlign:"right",fontSize:10,color:"#555"}}>
            {buildingArea>0&&<div>{buildingArea.toLocaleString()} SF</div>}
            <div>{duration} Month{duration!==1?"s":""}</div>
          </div>
        </div>
        {/* KPI row */}
        <div style={{display:"flex",gap:12,marginBottom:18}}>
          {[
            {label:"Total Estimate",value:fmt(final.total)},
            {label:"Cost per SF",value:buildingArea>0?fmtSF(final.costPerSF)+" / SF":"—"},
            {label:"Duration",value:`${duration} Month${duration!==1?"s":""}`},
          ].map(({label,value})=>(
            <div key={label} className="card" style={{flex:1,padding:"12px 14px"}}>
              <div style={{fontSize:10,color:"#888",letterSpacing:".12em",textTransform:"uppercase",marginBottom:6}}>{label}</div>
              <div style={{fontSize:16,fontWeight:700,color:"#111"}}>{value}</div>
            </div>
          ))}
        </div>
        {/* Proposals */}
        {proposals.length>0&&(
          <div style={{marginBottom:18}}>
            <div style={{fontSize:10,color:"#888",letterSpacing:".12em",textTransform:"uppercase",marginBottom:8}}>Packages</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {proposals.map((prop,pi)=>{
                const ptotal=grandTotal.byProp[prop.id]||0;
                const sf=sfForP(prop.id);
                const csf=sf>0&&ptotal!==0?ptotal/sf:0;
                const pct=grandTotal.subtotal!==0?(ptotal/Math.abs(grandTotal.subtotal))*100:0;
                const col=BAR_COLORS_P[pi%BAR_COLORS_P.length];
                return (
                  <div key={prop.id} className="card" style={{flex:1,padding:"10px 14px",borderTop:`3px solid ${col}`}}>
                    <div style={{fontSize:10,color:col,letterSpacing:".1em",textTransform:"uppercase",fontWeight:600,marginBottom:6}}>{prop.name}</div>
                    <div style={{fontSize:16,fontWeight:700,color:"#111",marginBottom:4}}>{fmt(ptotal)}</div>
                    <div style={{fontSize:10,color:"#666"}}>{csf>0?`${fmtSF(csf)}/sf · `:""}{pct.toFixed(0)}% of total</div>
                    <div style={{height:3,background:"#eee",borderRadius:3,marginTop:8,overflow:"hidden"}}>
                      <div style={{width:`${Math.min(100,Math.abs(pct))}%`,height:"100%",background:col,borderRadius:3}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Division bar chart */}
        {divRowsP.length>0&&(
          <div style={{marginBottom:18}}>
            <div style={{fontSize:10,color:"#888",letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Cost by Division</div>
            {divRowsP.map(({div,total},i)=>{
              const pct=Math.round((total/maxDivP)*100);
              const col=BAR_COLORS_P[i%BAR_COLORS_P.length];
              const csf=buildingArea>0?total/buildingArea:0;
              return (
                <div key={div.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontSize:10}}>
                  <div style={{width:150,textAlign:"right",color:"#333",flexShrink:0}}>{div.code} — {div.name.length>18?div.name.slice(0,17)+"…":div.name}</div>
                  <div style={{flex:1,background:"#eee",borderRadius:3,height:14,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:3}}/>
                  </div>
                  <div style={{width:80,textAlign:"right",color:"#111",fontWeight:600,flexShrink:0}}>{fmt(total)}</div>
                  {csf>0&&<div style={{width:64,textAlign:"right",color:"#888",flexShrink:0}}>{fmtSF(csf)}/sf</div>}
                </div>
              );
            })}
          </div>
        )}
        {/* Totals footer */}
        <div className="card" style={{maxWidth:380,marginLeft:"auto",padding:"14px 18px"}}>
          {([["Subtotal",final.subtotal],
            ...(markupMode==="combined"?[[`OH&P (${overhead}%)`,final.overhead]]:[
              ...(markupMode!=="profit"?[[`Overhead (${overhead}%)`,final.overhead]]:[]),
              ...(markupMode!=="overhead"?[[`Profit (${profit}%)`,final.profit]]:[]),
            ]),
            ["Contingency",final.contingency]] as [string,number][]).map(([lbl,val])=>(
            <div key={lbl} className="stat-row"><span className="stat-label">{lbl}</span><span>{fmt(val)}</span></div>
          ))}
          <div className="footer-total">
            <span className="summary-total-label">Total Estimate</span>
            <span className="summary-total-value">{fmt(final.total)}</span>
          </div>
          {buildingArea>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#888",marginTop:4}}><span>Cost per SF</span><span>{fmtSF(final.costPerSF)}</span></div>}
        </div>
      </div>
    );
  };

  // ── Bid Comparison ─────────────────────────────────────────────────────────
  // The sheet a bid review is actually read off, in a room, by people who
  // will act on it. One package per page, bidders cheapest first, and the
  // levelling plugs shown as their own block so nobody has to take on trust
  // why the low bid is not the lowest number.
  const BidsSheet=()=>{
    const packages=(bidScopes||[])
      .map(sc=>comparePackage(sc,bids||[],bidPartners||[],levelingAdjustments||[],
        scopeBudget(sc,divisions,itemState)))
      .filter(c=>c.partners.length>0);
    if(!packages.length)return(
      <div className="print-sheet">
        <h2>Bid Comparison</h2>
        <div className="sheet-meta">{projectName}</div>
        <p style={{fontSize:11,color:"#666"}}>No packages have bids yet.</p>
      </div>
    );
    return(<>
      {packages.map(cmp=>{
        const rows=comparisonRows(cmp,bids||[]);
        const under=cmp.deltaVsBudget<=0;
        return(
          <div className="print-sheet" key={cmp.scope.id}>
            <h2>{cmp.scope.name}</h2>
            <div className="sheet-meta">
              {projectName} · {scopeCoverage(cmp.scope,divisions)} · {cmp.partners.length} bidder{cmp.partners.length!==1?"s":""} · {new Date().toLocaleDateString()}
            </div>

            <div style={{display:"flex",gap:10,marginBottom:16}}>
              <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
                <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Estimate (Budget)</div>
                <div style={{fontSize:16,fontWeight:700}}>{cmp.budget>0?fmt(cmp.budget):"—"}</div>
              </div>
              <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
                <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Low Levelled Bid</div>
                <div style={{fontSize:16,fontWeight:700}}>{fmt(cmp.lowLeveled)}</div>
              </div>
              {cmp.budget>0&&(
                <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
                  <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>{under?"Under Budget":"Over Budget"}</div>
                  <div style={{fontSize:16,fontWeight:700}} className={under?"":"neg"}>
                    {fmt(Math.abs(cmp.deltaVsBudget))}
                    <span style={{fontSize:10,fontWeight:600,marginLeft:6,color:"#666"}}>
                      {Math.abs((cmp.deltaVsBudget/cmp.budget)*100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
              <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
                <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Spread</div>
                <div style={{fontSize:16,fontWeight:700}}>{cmp.spreadPct.toFixed(1)}%</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  {/* No Budget column. The estimate is not broken down the
                      way a bidder breaks down their proposal, so there is no
                      honest per-line figure to put here -- the first attempt
                      printed the package's whole budget against line one,
                      which reads as that line costing $145,000. The budget
                      belongs against the total, where it is comparable. */}
                  <th style={{width:"38%"}}>Line Item</th>
                  {cmp.partners.map(p=>(
                    <th key={p.partnerId} className="right">
                      {p.name}{p.isLow?" ★":""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i)=>(
                  <tr key={i}>
                    <td>{r.isAdjustment?<em>{r.label}</em>:r.label}</td>
                    {r.amounts.map((a2,j)=>(
                      <td key={j} className="right" style={r.isAdjustment?{color:"#7a2a7a"}:undefined}>
                        {a2==null?"—":fmt(a2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:"2px solid #999"}}>
                  <td style={{fontWeight:700}}>Bid as submitted</td>
                  {cmp.partners.map(p=><td key={p.partnerId} className="right">{fmt(p.base)}</td>)}
                </tr>
                <tr>
                  <td style={{color:"#7a2a7a"}}>Levelling adjustments</td>
                  {cmp.partners.map(p=><td key={p.partnerId} className="right" style={{color:"#7a2a7a"}}>{p.adjustments?fmt(p.adjustments):"—"}</td>)}
                </tr>
                <tr>
                  <td className="summary-total-label">Levelled total</td>
                  {cmp.partners.map(p=>(
                    <td key={p.partnerId} className="right summary-total-value">
                      {fmt(p.leveled)}
                      {p.deltaFromLow>0&&<div style={{fontSize:10,fontWeight:400,color:"#777"}}>+{fmt(p.deltaFromLow)}</div>}
                    </td>
                  ))}
                </tr>
                {cmp.budget>0&&(
                  <tr>
                    <td style={{color:"#555"}}>Estimate (budget) for this package</td>
                    <td className="right" colSpan={cmp.partners.length} style={{color:"#555"}}>
                      {fmt(cmp.budget)} &nbsp;·&nbsp; {scopeCoverage(cmp.scope,divisions)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>

            {/* Said once per page, because a printout leaves the building and
                the reader will not have the app open beside it. */}
            <div style={{fontSize:10,color:"#777",marginTop:-12,lineHeight:1.5}}>
              ★ lowest levelled total. Levelling adjustments are amounts entered for scope a bidder did
              not carry, so every column is priced for the same work — which is why the lowest submitted
              bid and the lowest levelled bid are not always the same firm.
            </div>
          </div>
        );
      })}
    </>);
  };

  // ── Open Decisions ─────────────────────────────────────────────────────────
  // The page is built for the owner/architect meeting, and the meeting runs
  // off paper. window.print() could not produce it: the tab's content sits in
  // a 598px scroller inside two overflow:hidden boxes with body overflow
  // hidden, so it printed roughly a third of the list and silently dropped
  // the rest -- in light-on-dark, onto white paper.
  const DecisionsSheet=()=>{
    const d=gatherDecisions(divisions,itemState,altTotals,rfis);
    const Group=({title,note,rows}:{title:string;note:string;rows:any[]})=>rows.length?(
      <>
        <h3 style={{marginTop:18}}>{title} <span style={{fontWeight:400,color:"#777",fontSize:11}}>· {rows.length}</span></h3>
        <div style={{fontSize:10,color:"#777",marginBottom:6}}>{note}</div>
        <table>
          <thead><tr><th>Item</th><th>Division</th><th className="right">Amount</th></tr></thead>
          <tbody>
            {rows.map((r:any,i:number)=>(
              <tr key={i}>
                <td>{r.desc||r.subject||"Untitled"}</td>
                <td className="div-code">{r.div||(r.number?`RFI #${r.number}`:"")}</td>
                <td className="right">{r.amt?fmt(r.amt):r.costImpact?fmt(r.costImpact):r.scheduleDays?`${r.scheduleDays}d`:"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ):null;
    return(
      <div className="print-sheet">
        <h2>Open Decisions</h2>
        <div className="sheet-meta">
          {projectName} · {new Date().toLocaleDateString()} · {d.count} outstanding
        </div>
        <div style={{display:"flex",gap:10,marginBottom:10}}>
          <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
            <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Open decisions</div>
            <div style={{fontSize:16,fontWeight:700}}>{d.count}</div>
          </div>
          <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
            <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Carried at an uncommitted price</div>
            <div style={{fontSize:16,fontWeight:700}}>{fmt(d.exposure)}</div>
          </div>
          <div className="card" style={{flex:1,padding:"10px 14px",margin:0}}>
            <div style={{fontSize:10,color:"#666",letterSpacing:".1em",textTransform:"uppercase"}}>Alternate swing</div>
            <div style={{fontSize:16,fontWeight:700}}>{fmt(d.altSwing)}</div>
          </div>
        </div>
        {d.count===0&&<p style={{fontSize:11,color:"#666"}}>Nothing outstanding.</p>}
        <Group title="Allowances" rows={d.allowances}
          note="Known scope at a price nobody has committed to yet." />
        <Group title="Needs Review" rows={d.needsReview}
          note="Priced, but flagged by the estimator as not yet settled." />
        <Group title="Alternates still open" rows={d.altTBD}
          note="Accepted or rejected, these move the total by the swing above." />
        <Group title="Open RFIs" rows={d.openRfis}
          note="Questions out to the design team that carry cost or schedule." />
      </div>
    );
  };

  const sheetMap:any={executive:ExecutiveSummarySheet,bids:BidsSheet,decisions:DecisionsSheet,estimate:EstimateSheet,summary:SummarySheet,proposals:ProposalsSheet,analysis:AnalysisSheet,alternates:AlternatesSheet,history:HistorySheet};

  return (
    <>
      <style>{PS}</style>

      {/* ── Modal backdrop ── */}
      <div style={{position:"fixed",inset:0,zIndex:500,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
        <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:6,padding:"28px 32px",width:420,fontFamily:FONT,boxShadow:"0 12px 40px rgba(0,0,0,.25)"}}>
          <div style={{fontSize:16,fontWeight:700,color:"#1a1a1a",marginBottom:3}}>Print / Export PDF</div>
          <div style={{fontSize:11,color:"#888",marginBottom:22,lineHeight:1.5}}>Select which sheets to include. Use <strong>Save as PDF</strong> in the browser dialog to export, or choose a printer.</div>

          {/* Sheets */}
          <div style={{fontSize:10,color:"#888",letterSpacing:".1em",textTransform:"uppercase" as const,marginBottom:8,fontWeight:600}}>Sheets to include</div>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #e5e5e5",cursor:"pointer",marginBottom:4}}>
            <input type="checkbox" checked={allOn} onChange={()=>setChecked(Object.fromEntries(PRINT_SHEETS.map(s=>[s.id,!allOn])))} style={{accentColor:"#1f2235",width:15,height:15}}/>
            <span style={{fontSize:12,fontWeight:600,color:"#333"}}>Select All</span>
          </label>
          {PRINT_SHEETS.map(s=>(
            <label key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0 7px 8px",cursor:"pointer"}}>
              <input type="checkbox" checked={!!checked[s.id]} onChange={()=>setChecked(p=>({...p,[s.id]:!p[s.id]}))} style={{accentColor:"#1f2235",width:15,height:15,flexShrink:0}}/>
              <div>
                <div style={{fontSize:12,fontWeight:500,color:"#222"}}>{s.label}</div>
                <div style={{fontSize:10,color:"#888"}}>{s.desc}</div>
              </div>
            </label>
          ))}

          {/* Actions */}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:22,paddingTop:16,borderTop:"1px solid #e5e5e5"}}>
            <button onClick={onClose} style={{background:"none",border:"1px solid #ccc",color:"#555",cursor:"pointer",padding:"8px 18px",borderRadius:3,fontSize:12,fontFamily:FONT}}>Cancel</button>
            <button disabled={noneOn} onClick={()=>window.print()}
              style={{background:noneOn?"#ccc":"#1f2235",border:"none",color:"#fff",cursor:noneOn?"not-allowed":"pointer",padding:"8px 24px",borderRadius:3,fontSize:12,fontFamily:FONT,fontWeight:600}}>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* ── Print content: hidden on screen, visible when printing ── */}
      <div className="pm-print-body">
        {selectedSheets.map(id=>{const Sheet=sheetMap[id];return Sheet?<Sheet key={id}/>:null;})}
      </div>
    </>
  );
}

/* ── Memoized row wrappers — fresh-closure ref + data-prop memo.
   renderRef holds the latest render closure; React.memo skips re-render when every
   data prop is unchanged, so typing in one row no longer re-renders the whole table.
   Every runtime-varying value a row's visual output reads MUST appear in the props. */
const _rowMemoEqual=(a:any,b:any)=>{
  for(const k in a){
    if(k==="renderRef"||k==="tradeGroupInfo")continue; // tradeGroupInfo compared via tgKey
    const av=a[k],bv=b[k];
    if(Array.isArray(av)&&Array.isArray(bv)){
      if(av.length!==bv.length)return false;
      for(let i=0;i<av.length;i++)if(!Object.is(av[i],bv[i]))return false;
      continue;
    }
    if(!Object.is(av,bv))return false;
  }
  return true;
};
const MemoItemRow=React.memo(
  (p:any)=>p.renderRef.current(p.item,p.divId,p.subId,p.inGroupId,p.tradeGroupInfo),
  _rowMemoEqual
);
const MemoGroupRow=React.memo(
  (p:any)=>p.renderRef.current(p.group,p.divId,p.subId),
  _rowMemoEqual
);

/* ────────────────────────────────────────────────── MAIN ── */
export default function OpenEstimate({user,signOut,cloudProjectId,cloudStorage,onBackToProjects,initialTab,isSandbox,initialState,initialLocked,userOrgs=[],currentOrgId=null}:{user?:{id:string,email:string,displayName:string,avatarUrl?:string}|null,signOut?:()=>Promise<void>,cloudProjectId?:string,cloudStorage?:any,onBackToProjects?:()=>void,initialTab?:string,isSandbox?:boolean,initialState?:any,initialLocked?:boolean,userOrgs?:Array<{id:string;name:string}>,currentOrgId?:string|null}={}){
  // ── Theme ──
  const [isDarkMode, setIsDarkMode] = useState<boolean>(()=>{
    try{ const s=sGetRaw("oe_dark_mode"); return s===null?true:s==="true"; }catch{ return true; }
  });
  const T: Theme = isDarkMode ? DARK_THEME : LIGHT_THEME;
  useEffect(()=>{ try{ sSetRaw("oe_dark_mode", String(isDarkMode)); }catch{} }, [isDarkMode]);
  useEffect(()=>{ document.body.style.background = T.bgApp; }, [isDarkMode]);

  // ── Responsive breakpoints ──
  const [winW,setWinW]=useState(()=>typeof window!=="undefined"?window.innerWidth:1440);
  useEffect(()=>{const fn=()=>setWinW(window.innerWidth);window.addEventListener("resize",fn,{passive:true});return()=>window.removeEventListener("resize",fn);},[]);
  const isMobile=winW<640;
  const isTablet=winW<1024;
  const hPad=isMobile?"16px":isTablet?"24px":"32px";

  // ── Org Logos (per-org map stored as {[orgId]: base64} in localStorage) ──
  const [orgLogos,setOrgLogosLocal]=useState<{[orgId:string]:string}>(()=>lsGet(LS_ORG_LOGOS_KEY,{})||{});
  useEffect(()=>{ lsSet(LS_ORG_LOGOS_KEY,orgLogos); },[orgLogos]);
  const setOrgLogo=(orgId:string,logo:string)=>setOrgLogos(prev=>({...prev,[orgId]:logo}));
  const clearOrgLogo=(orgId:string)=>setOrgLogos(prev=>{const n={...prev};delete n[orgId];return n;});
  // Active logo = logo for current org (or empty for personal projects)
  const activeLogo=currentOrgId?(orgLogos[currentOrgId]||""):"";

  // ── Org Trade Partner Address Book (cross-project, localStorage) ──
  const [orgPartners,setOrgPartners]=useState<OrgPartner[]>(()=>lsGet(LS_ORG_PARTNERS_KEY,[])||[]);
  useEffect(()=>{ lsSet(LS_ORG_PARTNERS_KEY,orgPartners); },[orgPartners]);

  // Supabase-backed org directory trade partners — the canonical source when cloud is available.
  // Falls back to localStorage orgPartners when offline / no Supabase.
  const [supabasePartners,setSupabasePartners]=useState<OrgPartner[]>([]);
  const [supabasePartnersLoaded,setSupabasePartnersLoaded]=useState(false);
  useEffect(()=>{
    if(!supabase||!user?.id)return;
    supabase.from("org_directory_companies")
      .select("id,company,role,phone,notes,org_directory_people(name,title,phone,email)")
      .order("company")
      .then(({data,error})=>{
        if(error){console.warn("org_directory load:",error);return;}
        const mapped:OrgPartner[]=(data||[]).map((c:any)=>{
          const primary=c.org_directory_people?.[0];
          return{id:c.id,name:c.company||"",trade:c.role||"",
            contact:primary?.name||"",phone:primary?.phone||c.phone||"",
            email:primary?.email||"",notes:c.notes||""};
        });
        setSupabasePartners(mapped);
        setSupabasePartnersLoaded(true);
      });
  },[user?.id]);

  // ── Admin: beta access request management ──
  const ADMIN_EMAIL_LIST=(import.meta.env.VITE_ADMIN_EMAILS||"adb5174@gmail.com").split(",").map((e:string)=>e.trim().toLowerCase());
  const isAdmin=!!(user?.email&&ADMIN_EMAIL_LIST.includes(user.email.toLowerCase()));
  const [accessRequests,setAccessRequests]=useState<AccessRequest[]>([]);
  const [accessRequestsLoading,setAccessRequestsLoading]=useState(false);

  useEffect(()=>{
    if(!isAdmin||isSandbox||!supabase)return;
    setAccessRequestsLoading(true);
    supabase.auth.getSession().then(({data:{session}})=>{
      const token=session?.access_token||"";
      return fetch("/api/approve-access",{headers:{Authorization:`Bearer ${token}`}});
    }).then(r=>r.json())
      .then(d=>{setAccessRequests(d.requests||[]);})
      .catch(e=>console.warn("access requests fetch:",e))
      .finally(()=>setAccessRequestsLoading(false));
  },[isAdmin,isSandbox]);

  const handleApproveRequest=async(email:string)=>{
    if(!supabase)return;
    const {data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token||"";
    const r=await fetch("/api/approve-access",{
      method:"POST",
      headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({action:"approve",email}),
    });
    const d=await r.json().catch(()=>({} as any));
    if(r.ok){
      setAccessRequests(prev=>prev.map(req=>req.email.toLowerCase()===email.toLowerCase()?{...req,status:"approved" as const}:req));
      showToast(d.emailSent===false?`Approved ${email} — but the email failed to send`:`Approved ${email} — signup link sent`);
    } else {
      showToast(d.error||"Failed to approve request");
    }
  };

  const handleRejectRequest=async(email:string)=>{
    if(!supabase)return;
    const {data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token||"";
    const r=await fetch("/api/approve-access",{
      method:"POST",
      headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({action:"reject",email}),
    });
    if(r.ok){
      setAccessRequests(prev=>prev.map(req=>req.email.toLowerCase()===email.toLowerCase()?{...req,status:"rejected" as const}:req));
      showToast(`Rejected ${email}`);
    } else {
      showToast("Failed to reject request");
    }
  };

  const handleInviteUser=async(name:string,email:string)=>{
    if(!supabase)return;
    const {data:{session}}=await supabase.auth.getSession();
    const token=session?.access_token||"";
    const r=await fetch("/api/approve-access",{
      method:"POST",
      headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({action:"invite",name,email}),
    });
    const d=await r.json().catch(()=>({} as any));
    if(r.ok){
      // Add to local list as an approved invite for history
      const newReq:AccessRequest={id:uid(),name:name||email,email,message:"VIP invite",status:"approved",created_at:new Date().toISOString()};
      setAccessRequests(prev=>[newReq,...prev.filter(r=>r.email.toLowerCase()!==email.toLowerCase())]);
      showToast(d.emailSent===false?`Invited ${email} — but the email failed to send`:`Invite sent to ${email}`);
    } else {
      // The endpoint used to report success whatever the database did, so a
      // failed write looked identical to a good one until the page reloaded.
      showToast(d.error||"Failed to send invite");
    }
  };

  // addSupabasePartner: inserts into org_directory_companies and refreshes the list
  const addSupabasePartner=async(op:OrgPartner)=>{
    if(!supabase){setOrgPartners(prev=>[...prev,op]);return;}
    const{error,data}=await supabase.from("org_directory_companies")
      .insert({company:op.name,role:op.trade||"Trade Partner",phone:op.phone,notes:op.notes})
      .select("id").single();
    if(error){console.warn("addSupabasePartner:",error);setOrgPartners(prev=>[...prev,op]);return;}
    if(op.contact||op.email){
      await supabase.from("org_directory_people")
        .insert({company_id:data.id,name:op.contact||"",title:"",phone:op.phone||"",email:op.email||"",notes:""});
    }
    // Refresh list
    setSupabasePartners(prev=>[...prev,{...op,id:data.id}]);
  };

  // In sandbox mode: use in-memory demo list so the real directory stays clean.
  // In cloud mode: use Supabase org directory (the canonical source).
  // Offline/localStorage: fall back to orgPartners.
  const pickerPartners=isSandbox
    ? SANDBOX_ORG_PARTNERS
    : supabasePartnersLoaded
      ? supabasePartners
      : orgPartners;

  // updateOrgPartner: patch an existing partner in Supabase org directory or localStorage fallback.
  const updateOrgPartner=async(updated:OrgPartner)=>{
    if(supabase&&supabasePartnersLoaded){
      await supabase.from("org_directory_companies")
        .update({company:updated.name,role:updated.trade||"Trade Partner",phone:updated.phone,notes:updated.notes})
        .eq("id",updated.id);
      const{data:people}=await supabase.from("org_directory_people").select("id").eq("company_id",updated.id);
      if(people?.length){
        await supabase.from("org_directory_people")
          .update({name:updated.contact||"",email:updated.email||"",phone:updated.phone||""})
          .eq("company_id",updated.id);
      } else if(updated.contact||updated.email){
        await supabase.from("org_directory_people")
          .insert({company_id:updated.id,name:updated.contact||"",title:"",phone:updated.phone||"",email:updated.email||"",notes:""});
      }
      setSupabasePartners(prev=>prev.map(p=>p.id===updated.id?updated:p));
    } else {
      setOrgPartners(prev=>prev.map(p=>p.id===updated.id?updated:p));
    }
  };

  // deleteOrgPartner: removes from Supabase org directory or localStorage fallback.
  const deleteOrgPartner=async(id:string)=>{
    if(supabase&&supabasePartnersLoaded){
      await supabase.from("org_directory_companies").delete().eq("id",id);
      setSupabasePartners(prev=>prev.filter(p=>p.id!==id));
    } else {
      setOrgPartners(prev=>prev.filter(p=>p.id!==id));
    }
  };

  // addOrgPartner: creates in Supabase org directory (or localStorage) and returns the actual assigned ID.
  const addOrgPartner=async(op:OrgPartner):Promise<string>=>{
    if(supabase&&!isSandbox){
      const{error,data}=await supabase.from("org_directory_companies")
        .insert({company:op.name,role:op.trade||"Trade Partner",phone:op.phone||"",notes:op.notes||""})
        .select("id").single();
      if(!error&&data){
        const finalId=data.id as string;
        if(op.contact||op.email){
          await supabase.from("org_directory_people")
            .insert({company_id:finalId,name:op.contact||"",title:"",phone:op.phone||"",email:op.email||"",notes:""});
        }
        setSupabasePartners(prev=>[...prev,{...op,id:finalId}]);
        return finalId;
      }
    }
    // Offline / sandbox fallback
    setOrgPartners(prev=>[...prev,op]);
    return op.id;
  };

  // ── Org Exclusions Library (cross-project) + per-project exclusions ──
  const [orgExclusions,setOrgExclusionsLocal]=useState<OrgExclusion[]>(()=>{const s=lsGet(LS_ORG_EXCLUSIONS_KEY,null);return s??DEFAULT_ORG_EXCLUSIONS;});
  useEffect(()=>{ lsSet(LS_ORG_EXCLUSIONS_KEY,orgExclusions); },[orgExclusions]);
  const [laborRates,setLaborRatesLocal]=useState<LaborRate[]>(()=>{const s=lsGet(LS_LABOR_RATES_KEY,null);return s??DEFAULT_LABOR_RATES;});
  useEffect(()=>{ lsSet(LS_LABOR_RATES_KEY,laborRates); },[laborRates]);
  const [laborRatePopover,setLaborRatePopover]=useState<{itemId:string;anchorRect:DOMRect}|null>(null);
  const [laborPickerSub,setLaborPickerSub]=useState<string|null>(null);
  const [projectExclusions,setProjectExclusions]=useState<string[]>(()=>{
    // initState is defined further down; replicate its read logic here to avoid TDZ error
    if(initialState?.projectExclusions) return initialState.projectExclusions;
    try{ const s=lsGet(LS_KEY,null); return s?.projectExclusions||[]; }catch{ return []; }
  });
  const [newExclusionText,setNewExclusionText]=useState("");
  // Import modal state
  const [showOrgPartnerImport,setShowOrgPartnerImport]=useState(false);
  const [orgPartnerImportRows,setOrgPartnerImportRows]=useState<OrgPartner[]>([]);
  const [orgPartnerImportStep,setOrgPartnerImportStep]=useState<"upload"|"preview">("upload");
  const [orgPartnerImportError,setOrgPartnerImportError]=useState("");
  const [orgPartnerSearch,setOrgPartnerSearch]=useState("");
  const orgPartnerFileRef=useRef<HTMLInputElement>(null);
  // null = closed; {prefill, onAdded} = open
  const [addPartnerModal,setAddPartnerModal]=useState<{prefill:string;onAdded:(op:OrgPartner)=>void}|null>(null);

  const getCostStyle = (s: string) => (T.costStatus as any)[s] || null;

  // Custom status dropdown — replaces native <select> so dark-mode popup is fully themed
  const StatusSelect=({id,value,onChange}:{id:string;value:string;onChange:(v:string)=>void})=>{
    const cs=getCostStyle(value);
    const isOpen=statusDropdownId===id;
    const CHEVRON="url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")";
    return (
      <div style={{position:"relative",width:"100%"}}>
        <div onClick={e=>{
            e.stopPropagation();
            if(isOpen){setStatusDropdownId(null);setStatusMenuAt(null);return;}
            const r=(e.currentTarget as HTMLElement).getBoundingClientRect();
            const h=COST_STATUS_OPTIONS.length*26+6;
            const below=window.innerHeight-r.bottom;
            setStatusMenuAt({
              top: below<h&&r.top>h ? r.top-h-2 : r.bottom+2,
              left: Math.min(r.left, window.innerWidth-Math.max(r.width,150)-8),
              width: r.width,
            });
            setStatusDropdownId(id);
          }}
          style={{fontSize:10,padding:"3px 18px 3px 5px",borderRadius:3,cursor:"pointer",
            fontFamily:FONT,textAlign:"center",userSelect:"none",
            background:cs?cs.badgeBg:T.bgInput,
            color:cs?cs.badgeText:T.textSecondary,
            border:cs?`1px solid ${cs.leftBar}55`:`1px solid ${T.borderSecondary}`,
            fontWeight:cs?600:400,
            backgroundImage:CHEVRON,backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {value||"-- Status --"}
        </div>
        {/* Portalled to the body. position:fixed is measured against the
            nearest transformed ancestor rather than the viewport, and every
            tab is wrapped in .fade-in, whose animation leaves a transform on
            the element -- so a "fixed" menu was being positioned inside the
            tab and opened off the bottom of the screen. The keyframe no
            longer ends on a transform either, but the portal is what makes
            this independent of any ancestor anyone adds later. */}
        {isOpen&&statusMenuAt&&createPortal(
          <>
            <div style={{position:"fixed",inset:0,zIndex:8990}} onClick={()=>{setStatusDropdownId(null);setStatusMenuAt(null);}}/>
            {/* fontFamily is not optional here. A portal renders under
                document.body, outside the app root that sets the type stack,
                so anything it does not declare falls back to the browser
                default -- which is a serif. The trigger said Inter and the
                menu it opened said Times. */}
            <div style={{position:"fixed",top:statusMenuAt.top,left:statusMenuAt.left,minWidth:Math.max(statusMenuAt.width,150),zIndex:8991,
              background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:3,fontFamily:FONT,
              boxShadow:"0 6px 20px rgba(0,0,0,.55)",overflow:"hidden"}}>
              {COST_STATUS_OPTIONS.map(o=>{
                const ocs=o?getCostStyle(o):null;
                const isSel=o===value;
                return(
                  <div key={o}
                    onClick={e=>{e.stopPropagation();onChange(o);setStatusDropdownId(null);setStatusMenuAt(null);}}
                    style={{padding:"7px 12px",fontSize:10,cursor:"pointer",whiteSpace:"nowrap",
                      background:isSel?(ocs?ocs.badgeBg:`${T.borderAccent}18`):"transparent",
                      color:ocs?ocs.badgeText:T.textWeak,
                      fontWeight:ocs||isSel?600:400,
                      borderBottom:`1px solid ${T.borderSecondary}22`}}
                    onMouseEnter={e=>(e.currentTarget as HTMLElement).style.filter="brightness(1.15)"}
                    onMouseLeave={e=>(e.currentTarget as HTMLElement).style.filter=""}>
                    {o||"— Clear status —"}
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}
      </div>
    );
  };

  const getPC=useCallback((pid,props)=>{
    const p=props.find(x=>x.id===pid);
    if(!p)return{bg:T.bgAccentFaint,text:T.textMuted,bar:T.textMuted};
    return T.proposalColors[p.color % T.proposalColors.length];
  },[T]);

  const initState=useMemo(()=>{
    if(initialState&&initialState.divisions&&initialState.itemState)return initialState;
    const saved=lsGet(LS_KEY,null);
    if(saved&&saved.divisions&&saved.itemState)return saved;
    const ds=ALL_DIVISIONS.map(d=>({id:d.id,enabled:d.enabled}));
    const ss=Object.fromEntries(Object.entries(DEFAULT_SUBS).map(([k,v])=>[k,v.map(s=>({...s}))]));
    const {divisions,itemState}=buildFreshProject(ds,ss);
    return {projectName:"XXX Rd.",buildingArea:0,duration:12,overhead:10,profit:8,contingency:5,
      proposals:DEFAULT_PROPOSALS,proposalAreas:{},divisionSettings:ds,subSettings:ss,divisions,itemState};
  },[]);

  const [projectName,setProjectName]=useState(initState.projectName);
  const [address,setAddress]=useState(initState.address||"");
  const [geoQuery,setGeoQuery]=useState("");
  const [costSFCents,setCostSFCents]=useState<boolean>(true); // always show cents — option removed
  const [buildingArea,setBuildingArea]=useState(initState.buildingArea);
  const [duration,setDuration]=useState(initState.duration);
  const [overhead,setOverhead]=useState(initState.overhead);
  const [profit,setProfit]=useState(initState.profit);
  const [markupMode,setMarkupMode]=useState<"both"|"overhead"|"profit"|"combined">(initState.markupMode||"both");
  // Per-division markup overrides: {[divId]: {overhead: number|null, profit: number|null}}
  const [divisionMarkups,setDivisionMarkups]=useState<Record<string,{overhead:number|null,profit:number|null}>>(initState.divisionMarkups||{});
  const [contingency,setContingency]=useState(initState.contingency);
  /** Alternate packages. A line points at one through altPackageId. */
  const [altPackages,setAltPackages]=useState<AltPackage[]>(()=>initState.alternates||[]);

  const [contingencyPolicy,setContingencyPolicy]=useState<ContingencyPolicy>(
    ()=>({...DEFAULT_CONTINGENCY_POLICY,...(initState.contingencyPolicy||{})}));
  const [escalation,setEscalation]=useState<EscalationSettings>(
    ()=>({...DEFAULT_ESCALATION,...(initState.escalation||{})}));
  // The staffing plan that produced Division 01's Project Staffing rows. Kept
  // so re-opening shows what was written rather than a fresh default, and so
  // the estimate can say when the schedule has moved away from it.
  const [staffPlan,setStaffPlan]=useState<GcStaffRow[]>(()=>initState.staffPlan||[]);
  const [showStaffingPlan,setShowStaffingPlan]=useState(false);
  const [showBcImport,setShowBcImport]=useState(false);
  /** Whether a line's own contingency setting counts under the current
   *  policy. In project-reserve mode it does not, and the column says so
   *  rather than letting somebody tune a number that is not in the total. */
  const contLive=lineContingencyApplies(contingencyPolicy);
  const contDeadNote="This estimate carries a single project reserve, so per-line contingency is held but not counted. Setup › Defaults › How contingency is carried.";
  const [proposals,setProposals]=useState(initState.proposals||DEFAULT_PROPOSALS);
  const [tradeSources,setTradeSources]=useState<{id:string;tradePartner:string;estimateDate:string;notes:string}[]>(initState.tradeSources||[]);
  // ── Bid Leveling state ──
  const [bidScopes,setBidScopes]=useState<BidScope[]>(initState.bidScopes||[]);
  const [bidPartners,setBidPartners]=useState<BidPartner[]>(initState.bidPartners||[]);
  const [bids,setBids]=useState<Bid[]>(initState.bids||[]);
  const [levelingAdjustments,setLevelingAdjustments]=useState<LevelingAdjustment[]>(initState.levelingAdjustments||[]);
  // ── RFI & Buyout state ──
  const [rfis,setRfis]=useState<RFIItem[]>(initState.rfis||[]);
  const [buyoutItems,setBuyoutItems]=useState<BuyoutItem[]>(initState.buyoutItems||[]);
  const [editingBuyoutCell,setEditingBuyoutCell]=useState<{id:string,field:"budget"|"contract"}|null>(null);
  // Deduplicated assignee list for RFI fields — org directory people first, then companies
  const rfiAssigneeOptions=useMemo(()=>{
    const seen=new Set<string>();
    const opts:{value:string;hint:string}[]=[];
    for(const p of pickerPartners){
      if(p.contact&&!seen.has(p.contact)){seen.add(p.contact);opts.push({value:p.contact,hint:`${p.name}${p.trade?" — "+p.trade:""}`});}
    }
    for(const p of pickerPartners){
      if(p.name&&!seen.has(p.name)){seen.add(p.name);opts.push({value:p.name,hint:p.trade||""});}
    }
    return opts;
  },[pickerPartners]);

  // effectiveBidPartners: merged list for BidsTab rendering — legacy project partners
  // + any org-level partners referenced by bids (created after the org-roster migration).
  // BidsTab uses this to resolve all partner names without needing to know about the two-tier system.
  const effectiveBidPartners=React.useMemo<BidPartner[]>(()=>{
    const base=[...bidPartners];
    bids.forEach(bid=>{
      if(!base.find(bp=>bp.id===bid.partnerId)){
        const op=(pickerPartners as any[]).find((p:any)=>p.id===bid.partnerId);
        if(op)base.push({id:op.id,name:op.name,trade:op.trade||"",contact:op.contact||"",phone:op.phone||"",email:op.email||"",notes:op.notes||""});
      }
    });
    return base;
  },[bidPartners,bids,pickerPartners]);

  const [activeRfiId,setActiveRfiId]=useState<string|null>(null);
  const [rfiStatusFilter,setRfiStatusFilter]=useState<"all"|"open"|"answered"|"closed"|"void"|"overdue">("all");
  const [rfiSearch,setRfiSearch]=useState("");
  const [rfiItemPickerFor,setRfiItemPickerFor]=useState<string|null>(null);
  const [rfiItemSearch,setRfiItemSearch]=useState("");
  const [rfiQuickCreate,setRfiQuickCreate]=useState<{itemId:string;divLabel:string;subLabel:string;divId:string;subId:string;itemDesc:string;subject:string;question:string;assignedTo:string;costImpactType:"none"|"tbd"|"add"|"deduct";costImpact:string;priority:"normal"|"high"|"urgent";specSection:string;drawingRef:string}|null>(null);
  const [rfiJumpHighlight,setRfiJumpHighlight]=useState<string|null>(null);
  const [activeBidScope,setActiveBidScope]=useState<string|null>(null);
  const [bidSubTab,setBidSubTab]=useState<"compare"|"history">("compare");
  // selectedTradePartner removed — was never read or written
  const [historyDiffA,setHistoryDiffA]=useState<string|null>(null);
  const [historyDiffB,setHistoryDiffB]=useState<string|null>(null);
  const [bidSnapModalId,setBidSnapModalId]=useState<string|null>(null);
  const [snapExpandedDivs,setSnapExpandedDivs]=useState<Set<string>>(new Set());
  const [historyHoverBid,setHistoryHoverBid]=useState<{bid:Bid;x:number;y:number}|null>(null);
  const [historyScopeFilter,setHistoryScopeFilter]=useState<string|null>(null);
  const [bidChartHover,setBidChartHover]=useState<{bid:Bid;x:number;y:number}|null>(null);
  const [bidChartScopeFilter,setBidChartScopeFilter]=useState<string|null>(null);
  const [bidChartRangeMode,setBidChartRangeMode]=useState<"all"|"iteration"|"custom">("all");
  const [bidChartCustomStart,setBidChartCustomStart]=useState<string>("");
  const [bidChartCustomEnd,setBidChartCustomEnd]=useState<string>("");
  const [bidChartW,setBidChartW]=useState(900);
  const [collapsedHistoryPartners,setCollapsedHistoryPartners]=useState<Set<string>>(new Set());

  const [showBidImport,setShowBidImport]=useState(false);
  const [bidImportScopeId,setBidImportScopeId]=useState<string|null>(null);
  const [showDocScopePicker,setShowDocScopePicker]=useState(false);

  const [bidImportDate,setBidImportDate]=useState<string>(()=>new Date().toISOString().slice(0,10));
  const bidPdfCache=useRef<Map<string,string>>(new Map()); // bidId → objectURL
  const pendingBidFileRef=useRef<File|null>(null);
  const [showBidEmbed,setShowBidEmbed]=useState(false);
  const [bidEmbedSource,setBidEmbedSource]=useState<{items:BidItem[];bidId:string;partnerId:string}|null>(null);
  const [bidEmbedDivId,setBidEmbedDivId]=useState<string>("");
  const [bidEmbedSubId,setBidEmbedSubId]=useState<string>("");
  const [bidEmbedProposal,setBidEmbedProposal]=useState<string>("");
  const [expandedBidId,setExpandedBidId]=useState<string|null>(null);
  const [newBidPartnerName,setNewBidPartnerName]=useState("");
  const [newBidPartnerTrade,setNewBidPartnerTrade]=useState("");
  const [manualBidItems,setManualBidItems]=useState<BidItem[]>([]);
  const [bidImportPartnerId,setBidImportPartnerId]=useState<string>("");
  const [bidImportStage,setBidImportStage]=useState<"idle"|"scanning"|"preview"|"error"|"manual"|"attach">("idle");
  const [bidImportPdfName,setBidImportPdfName]=useState("");
  const [bidImportError,setBidImportError]=useState("");
  const [bidImportIsDragging,setBidImportIsDragging]=useState(false);
  const [bidImportMode,setBidImportMode]=useState<"ai"|"attach">("ai");
  const [bidAttachTotal,setBidAttachTotal]=useState(0);
  const [bidAttachNotes,setBidAttachNotes]=useState("");
  const [pdfViewerOpen,setPdfViewerOpen]=useState(false);
  const [pdfViewerItems,setPdfViewerItems]=useState<{bidId:string,url:string,title:string}[]>([]);
  const [comparePdfIds,setComparePdfIds]=useState<string[]>([]); // up to 2 bid IDs selected for side-by-side
  const [bidAlignments,setBidAlignments]=useState<Record<string,Record<string,string>>>(initState.bidAlignments||{});
  const [hiddenPartners,setHiddenPartners]=useState<Record<string,string[]>>({});
  const [dragBidItemIds,setDragBidItemIds]=useState<string[]|null>(null);
  const [dropTargetEstItemId,setDropTargetEstItemId]=useState<string|null>(null);
  const [expandedAlignRows,setExpandedAlignRows]=useState<Set<string>>(()=>new Set());
  // ── SOW Editor ──
  const [showSowEditor,setShowSowEditor]=useState<{divisionId:string;divisionName:string;tradeName:string;tradeContact:string;scopeId?:string}|null>(null);
  // ── Scope management ──
  const [editingScopeId,setEditingScopeId]=useState<string|null>(null);
  const [editingScopeName,setEditingScopeName]=useState("");
  const [showNewScope,setShowNewScope]=useState(false);
  const [newScopeName,setNewScopeName]=useState("");
  const [newScopeDivIds,setNewScopeDivIds]=useState<string[]>([]);
  // ── Partner management panel ──
  const [showPartnersPanel,setShowPartnersPanel]=useState(false);
  const [bidNavMode,setBidNavMode]=useState<"list"|"tree">("list");
  const [bidTreeExpanded,setBidTreeExpanded]=useState<string[]>([]);
  const [editingPartnerId,setEditingPartnerId]=useState<string|null>(null);
  const [partnerEditDraft,setPartnerEditDraft]=useState<BidPartner>({id:"",name:"",trade:"",contact:"",phone:"",email:"",notes:""});
  const [proposalAreas,setProposalAreas]=useState(initState.proposalAreas||{});
  const [divisionSettings,setDivisionSettings]=useState(initState.divisionSettings||ALL_DIVISIONS.map(d=>({id:d.id,enabled:d.enabled})));
  const [subSettings,setSubSettings]=useState(initState.subSettings||{});
  const [divisions,setDivisionsRaw]=useState(initState.divisions);
  const [itemState,setItemStateRaw]=useState(initState.itemState);
  const [activeDiv,setActiveDiv]=useState(initState.divisions[0]?.id||"div1");
  // Which division the continuous estimate is scrolled to. Separate from
  // activeDiv on purpose — see the note in EstimateTab's scroll-spy effect.
  const [visibleDiv,setVisibleDiv]=useState<string|null>(null);
  // Continuous estimate view — stacks every division into one scroll instead
  // of paging one at a time. Presentational only; editing is unchanged.
  const [continuousEstimate,setContinuousEstimate]=useState(()=>{try{return sGetRaw("oe_continuous_estimate")==="true";}catch{return false;}});
  useEffect(()=>{try{sSetRaw("oe_continuous_estimate",String(continuousEstimate));}catch{}},[continuousEstimate]);
  // Sidebar navigation. In the continuous view the sidebar stops being a filter
  // and becomes an index: clicking jumps to the division instead of swapping the
  // table out from under you. scroll-margin-top on the marker keeps the target
  // clear of the sticky header.
  const goToDiv=useCallback((id:string)=>{
    setActiveDiv(id);
    if(!continuousEstimate)return;
    requestAnimationFrame(()=>{
      document.getElementById("oe-div-"+id)?.scrollIntoView({behavior:"smooth",block:"start"});
    });
  },[continuousEstimate]);
  const [activeTab,setActiveTab]=useState(initialTab||"home");

  /**
   * Where a link brought you from, so there is a way back.
   *
   * Most cross-tab links in this app are consulted, not travelled: you click
   * an RFI on Decisions to see what it says, a line item to check a number, a
   * markup to try a different one. The reading takes five seconds and the
   * trip back took four clicks through two tab rows -- which is enough
   * friction that people stop following the links, and the links were the
   * point.
   *
   * One breadcrumb for every such jump, rather than the per-feature returns
   * this started as. It is set by navigation that a link caused and cleared by
   * navigation the user performed, which is the whole distinction: clicking a
   * tab is a decision to be somewhere, and nobody wants to be offered a way
   * back from a place they chose.
   */
  const [linkReturn,setLinkReturn]=useState<{tab:string;at:number}|null>(null);

  const navTo=useCallback((tab:string)=>{
    setLinkReturn(prev=>{
      const from=activeTab;
      if(!from||from===tab||from==="home"||from.startsWith("settings-"))return prev;
      // Following a second link does not bury the first: the breadcrumb keeps
      // pointing at where the reading started, which is where you want to end
      // up. Re-pointing it at each hop turns one Back into a chain of them.
      return prev??{tab:from,at:Date.now()};
    });
    setActiveTab(tab);
    window.history.pushState({oeTab:tab},"",window.location.hash);
  },[activeTab]);

  // Retire the breadcrumb on arrival, and whenever the project itself is
  // left. Deliberately not "did the user click a tab?" tracked through a ref:
  // the first version did that, and clicking a tab you are already on leaves
  // activeTab unchanged, so the effect that resets the flag never runs and the
  // stale flag eats the next link you follow. Tab clicks now clear it
  // themselves, where the intent is actually known.
  useEffect(()=>{
    if(activeTab==="home"||activeTab.startsWith("settings-")){setLinkReturn(null);return;}
    setLinkReturn(prev=>(prev&&prev.tab===activeTab)?null:prev);
  },[activeTab]);

  /**
   * The settings jump, now just a link like any other.
   *
   * This used to keep its own return state and render its own back link on
   * Project Defaults -- which, once linkReturn existed, put two identical
   * "Back to Summary" buttons on the same page, forty pixels apart. One
   * mechanism, in the chrome, for every link.
   */
  const goSetting=useCallback((tab:string)=>{navTo(tab);},[navTo]);

  // Where to return from Settings. Settings is reachable from deep links all
  // over the app (the exclusions footer, the account menu, Presets), and its
  // only exit was the logo, which drops you at the project picker -- several
  // navigations away from the work you were doing.
  const settingsReturnRef=useRef<string|null>(null);
  useEffect(()=>{
    if(activeTab==="home"){settingsReturnRef.current=null;return;}
    if(!activeTab.startsWith("settings-"))settingsReturnRef.current=activeTab;
  },[activeTab]);

  const [descWidth,setDescWidth]=useState(260);
  const [expandedSubs,setExpandedSubs]=useState({});
  // Which divisions are rolled up in the continuous view. Held here rather
  // than inside the tab so switching tabs and coming back does not throw the
  // shape of the page away.
  const [collapsedDivs,setCollapsedDivs]=useState<Record<string,boolean>>({});
  const [expandedGrps,setExpandedGrps]=useState({});
  const [editSubId,setEditSubId]=useState(null);
  const [globalFilter,setGlobalFilter]=useState("All"); // single package filter — applies across all divisions
  const [showFileMenu,setShowFileMenu]=useState(false);
  const [showExportSub,setShowExportSub]=useState(false);
  const [showCopyDialog,setShowCopyDialog]=useState(false);
  const [copyDialogName,setCopyDialogName]=useState("");
  const [ctxMenu,setCtxMenu]=useState<{x:number;y:number;itemId:string;divId:string;subId:string}|null>(null);
  const [ctxShowMoveSub,setCtxShowMoveSub]=useState(false);
  const [aiImporting,setAiImporting]=useState(false);
  const entitlements=useEntitlements(user?.id);
  // Org's standard subcategory catalog — drives the subcategory picker and the
  // rename/cost-code guards. Falls back to the built-ins when there's no org.
  const orgCostCodes=useOrgCostCodes(currentOrgId);
  const aiFileRef=useRef<HTMLInputElement>(null);
  const bidImportFileRef=useRef<HTMLInputElement>(null);
  const subTemplateImportRef=useRef<HTMLInputElement>(null);
  const [showShareDialog,setShowShareDialog]=useState(false);
  const [showProjectChat,setShowProjectChat]=useState(false);
  const [showProcoreDialog,setShowProcoreDialog]=useState(false);
  // ── Scope of Work state ──
  const [scopeData,setScopeData]=useState<ScopeResult|null>(null);
  const [scopeLoading,setScopeLoading]=useState(false);
  const [scopeExclusions,setScopeExclusions]=useState<Record<string,string>>({});
  const [importTarget,setImportTarget]=useState<{divId:string;divName:string;subId:string;subName:string}|null>(null);
  const [hoveredSubId,setHoveredSubId]=useState<string|null>(null);
  const [procoreCompanies,setProcoreCompanies]=useState<{id:number,name:string}[]>([]);
  const [selectedCompanyId,setSelectedCompanyId]=useState<number|null>(null);
  const [procoreExporting,setProcoreExporting]=useState(false);
  const [procoreProjects,setProcoreProjects]=useState<{id:number,name:string,number:string,active:boolean}[]>([]);
  const [selectedProcoreProjectId,setSelectedProcoreProjectId]=useState<number|null>(null);
  // Default is to land in a project the team already set up. Creating a second
  // one next to it is the rarer, more destructive choice, so it is opt-in.
  const [procoreCreateNew,setProcoreCreateNew]=useState(false);
  const [procoreLoadingProjects,setProcoreLoadingProjects]=useState(false);
  // Integrations settings tab state
  const [procoreConnected,setProcoreConnected]=useState<boolean|null>(null); // null=unchecked
  const [procoreConnecting,setProcoreConnecting]=useState(false);
  const [toast,setToast]=useState("");
  const [toastUndo,setToastUndo]=useState(false);
  const [searchQuery,setSearchQuery]=useState("");
  const [searchOpen,setSearchOpen]=useState(false);
  const [projectSnapshots,setProjectSnapshots]=useState(()=>{
    // Cloud mode: always start empty — correct snapshots load from Supabase per-project
    if(cloudProjectId)return [];
    // Local mode: migrate from old global key, then fall back to project state
    const old=lsGet(LS_SNAPS_KEY,null);
    if(old&&old.length>0){lsSet(LS_SNAPS_KEY,[]);return old;}
    return initState.projectSnapshots||[];
  });
  const [deletedSnapshots,setDeletedSnapshots]=useState<any[]>([]);
  const [showDeletedSnaps,setShowDeletedSnaps]=useState(false);
  const [selectedArchivedIds,setSelectedArchivedIds]=useState<Set<string>>(new Set());
  // Track the project owner — used to gate snapshot deletion
  const [projectOwnerId,setProjectOwnerId]=useState<string|null>(null);
  // Project state, so a variance explanation reaches whoever reads the
  // comparison next. The old per-user list is adopted once.
  const [historyNotes,setHistoryNotes]=useState<Record<string,string>>(()=>initState.historyNotes??lsGet(LS_NOTES_KEY,{}));
  const [isLocked,setIsLocked]=useState(initialLocked||false);
  const [lockedLabel,setLockedLabel]=useState<string|null>(null);
  const [lockedDate,setLockedDate]=useState<string|null>(null);
  // Tracks that the current view is a snapshot (not the live project)
  // Used by "Return to Current Estimate" to know to reload from cloud
  const isViewingSnapshot=useRef(false);
  const [snapLabel,setSnapLabel]=useState("");
  const [showSnapDialog,setShowSnapDialog]=useState(false);
  const [divMarkupExpanded,setDivMarkupExpanded]=useState(false);
const [sidebarCollapsed,setSidebarCollapsed]=useState(false);
  const [snapPreviewId,setSnapPreviewId]=useState<string|null>(null);
  const [renamingSnapId,setRenamingSnapId]=useState<string|null>(null);
  const [deltaPopupId,setDeltaPopupId]=useState<string|null>(null);
  const [deltaPopupPos,setDeltaPopupPos]=useState<{x:number;top:number;bottom:number}>({x:0,top:0,bottom:0});
  const [lineItemPopup,setLineItemPopup]=useState<{code:string;label:string;x:number;top:number;bottom:number}|null>(null);
  const [renameSnapLabel,setRenameSnapLabel]=useState("");
  const [renameSnapDate,setRenameSnapDate]=useState("");
  const [linkedBudgetId,setLinkedBudgetId]=useState<string|null>(initState.linkedBudgetId||null);
  const [showBudgetPicker,setShowBudgetPicker]=useState(false);
  const [expandedMobileRow,setExpandedMobileRow]=useState<string|null>(null);
  const [visibleCols,setVisibleCols]=useState(DEFAULT_COLS);
  const [showColMenu,setShowColMenu]=useState(false);
  const [bidLinkFor,setBidLinkFor]=useState<string|null>(null); // item id awaiting Hard Bid link
  const [bidLinkOtherOpen,setBidLinkOtherOpen]=useState(false); // "Other Divisions" section expanded
  const [showPrintDialog,setShowPrintDialog]=useState(false);
  const [printInitialSheets,setPrintInitialSheets]=useState<string[]|null>(null);
  const [showShortcuts,setShowShortcuts]=useState(false);
  const [shortcuts,setShortcuts]=useState(()=>lsGet(LS_SHORTCUTS_KEY,DEFAULT_SHORTCUTS));
  // Analysis: two selectable sides (left = snapshot id or "current", right = snapshot id or "current")
  const [analysisLeft,setAnalysisLeft]=useState("current");
  const [analysisRight,setAnalysisRight]=useState(null); // null = none selected yet
  const [checklistDismissed,setChecklistDismissed]=useState(()=>lsGet("oe_checklist_dismissed_v1",false));
  const [statusDropdownId,setStatusDropdownId]=useState<string|null>(null);
  /**
   * Where the open status menu should be drawn, in viewport coordinates.
   *
   * The menu used to be absolutely positioned inside the row, which meant the
   * estimate table's own horizontal scroll container clipped it: on the last
   * row of a subcategory you saw the top two pixels of "Clear status" and
   * nothing else. z-index cannot escape an ancestor that clips, so the menu is
   * fixed instead, measured from the trigger at the moment it opens -- and
   * flipped above the trigger when there is not room below, which is exactly
   * the last-row case where this was visible.
   *
   * Held here rather than inside StatusSelect because that component is
   * re-created on every parent render, so state inside it would be lost on
   * each keystroke.
   */
  const [statusMenuAt,setStatusMenuAt]=useState<{top:number;left:number;width:number}|null>(null);
  // A fixed menu does not travel with the row underneath it, so scrolling
  // closes it rather than leaving it stranded over the wrong line.
  useEffect(()=>{
    if(!statusDropdownId)return;
    const shut=()=>{setStatusDropdownId(null);setStatusMenuAt(null);};
    window.addEventListener("scroll",shut,true);
    window.addEventListener("resize",shut);
    return()=>{window.removeEventListener("scroll",shut,true);window.removeEventListener("resize",shut);};
  },[statusDropdownId]);
  const [collapsedAnalysisDivs,setCollapsedAnalysisDivs]=useState<Set<string>>(new Set());
  const [collapsedAnalysisSubs,setCollapsedAnalysisSubs]=useState<Set<string>>(new Set());
  const [analysisStatusFilter,setAnalysisStatusFilter]=useState<string|null>(null);
  const [presets,setPresetsLocal]=useState(()=>{const saved=lsGet(LS_PRESETS_KEY,null);return saved||DEFAULT_PRESETS;});
  const [showNewProjDialog,setShowNewProjDialog]=useState(false);
  const [expandedProjects,setExpandedProjects]=useState(new Set());
  const [newProjName,setNewProjName]=useState("");
  const [newProjPresetId,setNewProjPresetId]=useState("preset_sfr");
  const [editingPresetId,setEditingPresetId]=useState(null);
  const [newPresetName,setNewPresetName]=useState("");
  const [showNewPresetForm,setShowNewPresetForm]=useState(false);
  const [showAccountMenu,setShowAccountMenu]=useState(false);
  const [editDisplayName,setEditDisplayName]=useState(user?.displayName||"");
  const [profileSaving,setProfileSaving]=useState(false);
  const [profileMsg,setProfileMsg]=useState("");
  // Org/collaboration state
  const [orgs,setOrgs]=useState([]);
  const [orgsLoading,setOrgsLoading]=useState(false);
  const [newOrgName,setNewOrgName]=useState("");
  const [inviteEmails,setInviteEmails]=useState({});
  const [inviteRoles,setInviteRoles]=useState({});
  const [orgMsg,setOrgMsg]=useState("");
  const [ownershipRequests,setOwnershipRequests]=useState({});
  // Confirmation modal state: {title, message, confirmText, inputRequired, onConfirm} or null
  const [confirmAction,setConfirmAction]=useState(null);
  const [confirmInput,setConfirmInput]=useState("");
  // Project sharing state
  const [sharingProject,setSharingProject]=useState(null);
  const [projectShares,setProjectShares]=useState({});
  const [shareEmail,setShareEmail]=useState("");
  const [shareRole,setShareRole]=useState("viewer");
  const [contingencyDisplay,setContingencyDisplay]=useState(()=>lsGet(LS_CONT_DISPLAY_KEY,"included"));
  // ── Submittals ──
  // Submittals belong to the project. Where a project predates that, the old
  // global list is adopted once so nobody loses a log they can currently see;
  // nothing writes back to that key again, so the projects diverge from here.
  const [submittals,setSubmittals]=useState<Submittal[]>(()=>{
    if(Array.isArray(initState?.submittals))return initState.submittals;
    try{const v=sGetRaw(LS_SUBMITTALS_KEY);return v?JSON.parse(v):[];}catch{return [];}
  });
  const [subFilterStatus,setSubFilterStatus]=useState<SubmittalStatus|"all">("all");
  const [subFilterDiv,setSubFilterDiv]=useState("all");
  const [subDraft,setSubDraft]=useState<Partial<Submittal>|null>(null);
  const [subEditId,setSubEditId]=useState<string|null>(null);
  const [subAutoPopDone,setSubAutoPopDone]=useState(false);
  // ── Drawing Review / Project Intake ──
  const [drawingList,setDrawingList]=useState<DrawingListItem[]>(()=>{
    const saved=lsGet(LS_KEY,null);
    return saved?.drawingList||[];
  });
  const [projectNotes,setProjectNotes]=useState<string>(()=>{
    const saved=lsGet(LS_KEY,null);
    return saved?.projectNotes||"";
  });
  const [openItems,setOpenItems]=useState<OpenItem[]>(()=>{
    const saved=lsGet(LS_KEY,null);
    return saved?.openItems||[];
  });
  // Lets the Ctrl+Shift+O shortcut and the Summary card reach the notes
  // panel without hoisting openNotes above the state it depends on.
  const openNotesRef=useRef<(()=>void)|null>(null);
  const [migratedOpenItems,setMigratedOpenItems]=useState<boolean>(()=>!!initState.migratedOpenItems);
  const [showHelp,setShowHelp]=useState(false);
  // Procore RFI export. Only offered on a cloud project: the export ledger
  // that stops a second run duplicating everything is keyed to the project id.
  const [procoreExportKind,setProcoreExportKind]=useState<ExportKind|null>(null);

  // ── Internal notes ──────────────────────────────────────────────────
  // Distinct from itemState[].notes, which PRINTS on client documents. These
  // never leave the building, carry an author and a resolution, and hang off
  // whatever they are about. They live in their own table rather than project
  // state: two people writing inside one autosave window must not lose a
  // comment to last-write-wins.
  const [notes,setNotes]=useState<ProjectNote[]>([]);
  const [notesPeople,setNotesPeople]=useState<Mentionable[]>([]);
  const [notesSeenAt,setNotesSeenAt]=useState<string|null>(null);
  const [notesAnchor,setNotesAnchor]=useState<NotesAnchor|null>(null);
  const [notesOpen,setNotesOpen]=useState(false);
  const notesEnabled=!!(cloudProjectId&&user&&!isSandbox);

  // Open Items was the same feature without an anchor: a project-level list
  // of attributed, resolvable things to do. Two of those is one too many, so
  // the old entries become notes and the panel goes away. Carried over once
  // per project, guarded by a flag in project state and by a body match, so a
  // second device cannot duplicate the list.
  //
  // author_id has to be whoever runs the migration -- the insert policy
  // requires it to match auth.uid(), and the old rows only ever stored a
  // display name. The original name is preserved in author_name, so the
  // history reads correctly even though "Mine" counts them as the migrator's.
  const migrateOpenItems=useCallback(async(existing:ProjectNote[])=>{
    if(!cloudProjectId||!user||migratedOpenItems||openItems.length===0)return;
    const seen=new Set(existing.map(n=>n.body.trim()));
    const pending=openItems.filter(i=>i.text?.trim()&&!seen.has(i.text.trim()));
    try{
      for(const item of pending){
        const created=await addNote(
          {projectId:cloudProjectId,body:item.text.trim(),anchorKind:"project"},
          user.id,item.author||user.displayName||"Someone",
        );
        if(item.resolved)await setNoteResolved(created.id,true,user.id);
      }
      setMigratedOpenItems(true);
      if(pending.length>0)showToast(`${pending.length} open item${pending.length===1?"":"s"} moved into Notes`);
    }catch(e:any){console.warn("open items migration:",e?.message||e);}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[cloudProjectId,user,migratedOpenItems,openItems]);

  const [notesError,setNotesError]=useState<string>("");
  const refreshNotes=useCallback(async()=>{
    if(!cloudProjectId)return;
    try{setNotes(await loadNotes(cloudProjectId));setNotesError("");}
    catch(e:any){
      // Swallowing this to a console.warn made a missing table look like an
      // empty list, which is indistinguishable from "nothing to show".
      const msg=e?.message||String(e);
      console.error("[notes] load failed:",e);
      setNotesError(/relation|does not exist/i.test(msg)
        ? "Notes storage is not set up yet — run migration 034 (project_notes) in Supabase."
        : msg);
    }
  },[cloudProjectId]);

  useEffect(()=>{
    if(!notesEnabled||!cloudProjectId||!user)return;
    loadNotes(cloudProjectId)
      .then(async rows=>{ setNotes(rows); setNotesError(""); await migrateOpenItems(rows); await refreshNotes(); })
      .catch(()=>refreshNotes());
    lastSeenAt(cloudProjectId,user.id).then(setNotesSeenAt).catch(()=>{});
    loadMentionablePeople(currentOrgId).then(setNotesPeople).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[notesEnabled,cloudProjectId,currentOrgId]);

  const openNotes=(anchor:NotesAnchor|null)=>{
    setNotesAnchor(anchor);
    setNotesOpen(true);
    // Opening the panel is what "seen" means; the badge clears from here.
    if(cloudProjectId&&user){
      markNotesSeen(cloudProjectId,user.id).catch(()=>{});
      setNotesSeenAt(new Date().toISOString());
    }
  };

  openNotesRef.current=()=>openNotes(null);

  const handleAddNote=async(n:Omit<NewNote,"projectId">)=>{
    if(!cloudProjectId||!user)return;
    try{
      await addNote({...n,projectId:cloudProjectId},user.id,user.displayName||user.email||"Someone");
      await refreshNotes();
    }catch(e:any){showToast(e?.message||"Could not add that note");}
  };
  const handleResolveNote=async(id:string,resolved:boolean)=>{
    if(!user)return;
    try{await setNoteResolved(id,resolved,user.id);await refreshNotes();}
    catch(e:any){showToast(e?.message||"Could not update that note");}
  };
  const handleDeleteNote=async(id:string)=>{
    try{await deleteNote(id);await refreshNotes();}
    catch(e:any){showToast(e?.message||"Could not delete that note");}
  };

  const notesUnread=user?unreadCount(notes,user.id,notesSeenAt):0;

  // Nothing adapts notes into the old Open Item shape any more: Decisions and
  // Summary are client-facing and must not receive internal notes at all,
  // rather than receive them and choose not to render them.
  const [showAllowancePanel,setShowAllowancePanel]=useState(false);
  // Project Info / Defaults subtab is derived from activeTab (Setup group).
  const [jurisdictionInfo,setJurisdictionInfo]=useState<JurisdictionResult|null>(()=>{
    const saved=lsGet(LS_KEY,null);
    return saved?.jurisdictionInfo||null;
  });
  const [submittalTemplates,setSubmittalTemplatesLocal]=useState<SubmittalTemplateCustomLayer>(()=>{try{const v=sGetRaw(LS_SUBMITTAL_TEMPLATES_KEY);return v?JSON.parse(v):{custom:{},disabled:{}};}catch{return{custom:{},disabled:{}};}});
  const [subTemplateEditorExpanded,setSubTemplateEditorExpanded]=useState(false);
  const [subTemplateExpandedDiv,setSubTemplateExpandedDiv]=useState<string|null>(null);
  // ── Site Logistics ──
  const [siteLogistics,setSiteLogistics]=useState<SiteLogistics>(()=>{
    // In sandbox mode, use the demo logistics bundled with the sandbox project —
    // never read from or write to localStorage (which holds the user's real site plan).
    if(isSandbox&&(initialState as any)?.siteLogistics){
      return (initialState as any).siteLogistics as SiteLogistics;
    }
    // This project's plan first. The global key below is where plans lived
    // before they belonged to a project; it is read so that one saved then is
    // not orphaned now, and never written again.
    const inProject=(()=>{try{return lsGet(LS_KEY,null)?.siteLogistics||null;}catch{return null;}})();
    if(inProject)return {...inProject,
      imageDataUrl:inProject.imageDataUrl??null,icons:inProject.icons??[],lines:inProject.lines??[],
      areas:inProject.areas??[],circles:inProject.circles??[],notes:inProject.notes??"",
      scale:inProject.scale??{preset:"none",feetPerPx:null}} as SiteLogistics;
    try{
      const v=sGetRaw(LS_LOGISTICS_KEY);
      // Spread first, then fill in what has to exist. Listing the fields
      // instead dropped every one added since it was written -- the plans, the
      // text boxes, the dimensions, the sheet blocks, the title block, the
      // drawing's resolution, the markup size -- so a plan came back from
      // local storage as the subset somebody thought of in 2025.
      if(v){const p=JSON.parse(v);return{...p,
        imageDataUrl:p.imageDataUrl??null,icons:p.icons??[],lines:p.lines??[],
        areas:p.areas??[],circles:p.circles??[],notes:p.notes??"",
        scale:p.scale??{preset:"none",feetPerPx:null}};}
    }catch{}
    return{imageDataUrl:null,icons:[],lines:[],areas:[],circles:[],notes:"",scale:{preset:"none",feetPerPx:null}};
  });
  const [logisticsView,setLogisticsView]=useState<"plan"|"map">("plan");
  const [mapLocked,setMapLocked]=useState(false);
  const [mapViewMode,setMapViewMode]=useState<"aerial"|"street">("aerial");
  const [mapLocLabel,setMapLocLabel]=useState("");
  const [logisticsTool,setLogisticsTool]=useState<string>("select");
  const [logDrawStyle,setLogDrawStyle]=useState<{strokeColor?:string;strokeWidth?:number;strokeDash?:string}>({});
  const [selectedLogId,setSelectedLogId]=useState<string|null>(null);
  const [lineInProgress,setLineInProgress]=useState<{x:number;y:number}[]>([]);
  const [linePreview,setLinePreview]=useState<{x:number;y:number}|null>(null);
  const [editingLogLabel,setEditingLogLabel]=useState<string|null>(null);
  const [logSvgDims,setLogSvgDims]=useState({w:800,h:550});
  const logSvgRef=useRef<SVGSVGElement>(null);
  // kind says which collection the id belongs to: areas and circles were
  // selectable but immovable because only icons and line vertices were ever
  // wired up.
  const logDragRef=useRef<{kind:"icon"|"area"|"circle"|"text"|"line";id:string;ox:number;oy:number;startX:number;startY:number}|null>(null);
  const logVertexDragRef=useRef<{kind:"line"|"area";id:string;vertIdx:number}|null>(null);
  // Resizing an item is its own gesture: the pointer is saying where a
  // corner goes, not where the item goes, so it cannot share the move ref.
  const logResizeRef=useRef<{id:string;handle:"nw"|"ne"|"sw"|"se"}|null>(null);
  // Turning is its own gesture again: the pointer is saying which way the
  // thing faces, not where it is or how big it is.
  const logRotateRef=useRef<{kind:"icon"|"text"|"area"|"line";id:string;
    start:number;base:number;points?:{x:number;y:number}[]}|null>(null);
  // Pulling a label clear of a clash. Its own gesture: the pointer is saying
  // where the words go, not where the thing they name goes.
  const logLabelRef=useRef<{id:string;ox:number;oy:number}|null>(null);
  // What was copied or cut on the plan, and how many times it has been pasted,
  // so a run of pastes steps away instead of piling up in one spot.
  const logClipRef=useRef<{clip:any;pastes:number}|null>(null);
  const [areaInProgress,setAreaInProgress]=useState<{x:number;y:number}[]>([]);
  const [areaPreview,setAreaPreview]=useState<{x:number;y:number}|null>(null);
  const [circleCenter,setCircleCenter]=useState<{x:number;y:number}|null>(null);
  const [circlePreviewR,setCirclePreviewR]=useState<number>(0);
  const [calibPoints,setCalibPoints]=useState<{x:number;y:number}[]>([]);
  const [calibInput,setCalibInput]=useState("");
  const [logPanelCollapsed,setLogPanelCollapsed]=useState<{[k:string]:boolean}>({});
  // ── Schedule ──
  // The schedule belongs to the project. It used to live only in two global
  // localStorage keys -- so it never reached the cloud, never made it into a
  // snapshot, and every project on the machine shared one schedule. A cloud
  // project now reads its own (or starts empty); the global keys serve only
  // the sandbox and local work, where there is no project to attach to.
  const [schedule,setSchedule]=useState<PrelimSchedule>(()=>{
    if(initialState?.schedule)return initialState.schedule as PrelimSchedule;
    if(cloudProjectId)return DEFAULT_SCHEDULE;
    try{const v=sGetRaw(LS_SCHEDULE_KEY);if(v){const p=JSON.parse(v);if(!p.durationUnit)return{...p,totalDuration:Math.round((p.totalWeeks||52)/4.333),durationUnit:"months" as DurationUnit};return p;}}catch{}
    return DEFAULT_SCHEDULE;
  });
  // Cost schedule. Bands, keyed by division / subcategory / item id, resolved
  // most-specific-first. Migrated once from the old single-window-per-
  // subcategory store, which stays on disk untouched so an older build still
  // opens the project.
  const [costSchedule,setCostSchedule]=useState<CostSchedule>(()=>{
    if(initialState?.costSchedule)return initialState.costSchedule as CostSchedule;
    if(cloudProjectId)return {};
    const saved=lsGet(LS_COST_SCHEDULE_KEY,null) as CostSchedule|null;
    if(saved&&Object.keys(saved).length>0)return saved;
    return migrateSubSchedule(lsGet(LS_SUB_SCHEDULE_KEY,{}) as SubSchedule);
  });
  const subSchedule=useMemo(()=>toSubSchedule(costSchedule),[costSchedule]);
  const [phaseGroups,setPhaseGroups]=useState<PhaseGroup[]>(()=>migratePhaseGroups(loadPhaseGroups()));
  const orgPhasesId=(!isSandbox&&user&&currentOrgId)?currentOrgId:null;
  // What the server last confirmed, so a write is a diff rather than a wipe.
  const phasesServerRef=useRef<PhaseGroup[]>([]);
  const phasesLoadedRef=useRef<string|null>(null);
  const phasesTimerRef=useRef<any>(null);

  useEffect(()=>{
    if(!orgPhasesId||phasesLoadedRef.current===orgPhasesId)return;
    phasesLoadedRef.current=orgPhasesId;
    let cancelled=false;
    (async()=>{
      try{
        let rows=await loadOrgPhaseGroups(orgPhasesId);
        // A new org gets the default set, so its first schedule groups
        // sensibly instead of dropping every division into one bucket.
        if(rows.length===0)rows=await seedOrgPhaseGroups(orgPhasesId,DEFAULT_PHASE_GROUPS);
        if(cancelled)return;
        phasesServerRef.current=rows;
        if(rows.length)setPhaseGroups(rows);
      }catch(e:any){
        if(!cancelled)showToast(`Could not load the org phases: ${e.message||e}`);
      }
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orgPhasesId]);

  // Renaming and recolouring fire on every keystroke, so the write is
  // debounced: the screen updates immediately and the org sees one save per
  // edit rather than one per character.
  const updatePhaseGroups=useCallback((next:React.SetStateAction<PhaseGroup[]>)=>{
    setPhaseGroups(prev=>{
      const resolved=typeof next==="function"?(next as (p:PhaseGroup[])=>PhaseGroup[])(prev):next;
      if(!orgPhasesId)return resolved;
      if(phasesTimerRef.current)clearTimeout(phasesTimerRef.current);
      phasesTimerRef.current=setTimeout(()=>{
        const before=phasesServerRef.current.length?phasesServerRef.current:prev;
        syncOrgPhaseGroups(orgPhasesId,before,resolved).then(({items,errors})=>{
          phasesServerRef.current=items;
          if(items.length)setPhaseGroups(items);
          if(errors.length)showToast(errors[0]);
        }).catch((e:any)=>showToast(`Phases did not save: ${e.message||e}`));
      },800);
      return resolved;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orgPhasesId]);
  // Per-phase duration display unit (independent of the project-level unit)
  const [schedDetail,setSchedDetail]=useState<{phaseId:string;scope:"phase"|"division"|"subcategory";divId?:string;subId?:string}|null>(null);
  const [cfDetail,setCfDetail]=useState<{monthKey:string;label:string;weekStart:number;weekEnd:number}|null>(null);
  // Esc closes detail modals
  useEffect(()=>{
    const h=(e:KeyboardEvent)=>{
      if(e.key==="Escape"){
        setSchedDetail(null);
        setCfDetail(null);
      }
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[]);

  // ── Undo / redo ──────────────────────────────────────────────
  // One history for the whole project -- see lib/history.
  //
  // There were five stacks here, routed by whichever tab was open: the
  // estimate grid, and one each for the schedule, logistics, submittals and
  // bids. So Ctrl+Z meant a different thing depending on where you were
  // standing, and everything edited outside those five tabs -- escalation,
  // markup, the staffing plan, packages, exclusions, the whole of Setup --
  // had no undo at all. Set an escalation rate, walk to the summary to see
  // what it did, and the only way back was to remember the old number.
  //
  // The journal is kept by WATCHING the project's state rather than by
  // remembering to call a function before each edit. That is the point: a
  // snapshot call nobody adds is an edit nobody can undo, which is exactly
  // how the gap opened. The recorder lives further down, where every slice
  // it watches has been declared.
  //
  // The five snapshot functions keep their names because some sixty call
  // sites use them, but all they do now is mark a boundary: whatever happens
  // next begins its own step instead of folding into the one before it.
  // One journal per scope -- see SCOPES in lib/history. Undo reaches only the
  // scope the tab you are on belongs to, because a change you cannot see is
  // not one you can check afterwards.
  const emptyJournals=()=>Object.fromEntries(SCOPE_IDS.map(id=>[id,emptyHistory()])) as Record<ScopeId,HistoryState>;
  const historyRef=useRef<Record<ScopeId,HistoryState>>(emptyJournals());
  const prevSlicesRef=useRef<Slices|null>(null);
  const skipHistoryRef=useRef(false);     // an undo, a redo, or a remote write
  const resetHistoryRef=useRef(false);    // a different project just arrived
  const historyBarrierRef=useRef(false);  // next change starts its own step
  const lastEditElRef=useRef<Element|null>(null);
  // One press-drag-release is one gesture, however many times it writes state.
  // Dragging an icon across the site plan calls setSiteLogistics on every
  // mouse move; without this, crossing the drawing would fill all sixty steps
  // with one drag and throw away everything done before it.
  const gestureRef=useRef({id:0,down:false});
  const lastGestureRef=useRef(-1);
  useEffect(()=>{
    const down=()=>{gestureRef.current={id:gestureRef.current.id+1,down:true};};
    const up=()=>{gestureRef.current.down=false;};
    // A pointerup can be missed -- released outside the window, swallowed by
    // a native drag, lost to an alt-tab mid-drag. A press that never ends
    // would fold every later edit into one undo step and say nothing about
    // it, so the next move with no button held clears it, as does leaving
    // the window. Nothing downstream has to trust that pointerup arrived.
    const move=(e:PointerEvent)=>{if(gestureRef.current.down&&e.buttons===0)gestureRef.current.down=false;};
    window.addEventListener("pointerdown",down,true);
    window.addEventListener("pointerup",up,true);
    window.addEventListener("pointercancel",up,true);
    window.addEventListener("pointermove",move,true);
    window.addEventListener("blur",up);
    return()=>{
      window.removeEventListener("pointerdown",down,true);
      window.removeEventListener("pointerup",up,true);
      window.removeEventListener("pointercancel",up,true);
      window.removeEventListener("pointermove",move,true);
      window.removeEventListener("blur",up);
    };
  },[]);
  const markHistoryBoundary=useCallback(()=>{historyBarrierRef.current=true;},[]);
  const scheduleSnapshot=markHistoryBoundary;
  const logisticsSnapshot=markHistoryBoundary;
  const submittalsSnapshot=markHistoryBoundary;
  const bidsSnapshot=markHistoryBoundary;

  // ── Best Practices / Lessons Learned ──
  // Lessons belong to the organisation, not the browser. localStorage is the
  // fallback for sandbox and signed-out use only; with an org, the library is
  // read from and written to Supabase and every edit is live for the team at
  // once -- no draft state, by decision.
  const [bestPractices,setBestPractices]=useState<BestPractice[]>(()=>{const s=lsGet(LS_BEST_PRACTICES_KEY,[]);return s.length>0?s:DEFAULT_BEST_PRACTICES;});
  const orgLessonsId=(!isSandbox&&user&&currentOrgId)?currentOrgId:null;
  // What the server last confirmed, so a write is a diff rather than a wipe:
  // two people editing different lessons must not erase one another.
  const lessonsServerRef=useRef<BestPractice[]>([]);
  const lessonsLoadedRef=useRef<string|null>(null);

  useEffect(()=>{
    if(!orgLessonsId||lessonsLoadedRef.current===orgLessonsId)return;
    lessonsLoadedRef.current=orgLessonsId;
    let cancelled=false;
    (async()=>{
      try{
        let rows=await loadOrgLessons(orgLessonsId);
        // A brand-new org gets the built-in library, so the team's first look
        // at Lessons is useful rather than empty.
        if(rows.length===0)rows=await seedOrgLessons(orgLessonsId,user!.id,DEFAULT_BEST_PRACTICES);
        if(cancelled)return;
        lessonsServerRef.current=rows;
        setBestPractices(rows);
      }catch(e:any){
        if(!cancelled)showToast(`Could not load the org lessons library: ${e.message||e}`);
      }
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orgLessonsId]);

  // Every mutation in Settings goes through this one setter, so persistence
  // lives here rather than at three call sites that could forget it.
  const updateBestPractices=useCallback((next:React.SetStateAction<BestPractice[]>)=>{
    setBestPractices(prev=>{
      const resolved=typeof next==="function"?(next as (p:BestPractice[])=>BestPractice[])(prev):next;
      if(!orgLessonsId||!user){
        try{sSetRaw(LS_BEST_PRACTICES_KEY,JSON.stringify(resolved));}catch{}
        return resolved;
      }
      const before=lessonsServerRef.current.length?lessonsServerRef.current:prev;
      syncOrgLessons(orgLessonsId,user.id,before,resolved).then(({items,errors})=>{
        lessonsServerRef.current=items;
        setBestPractices(items);
        if(errors.length)showToast(errors[0]);
      }).catch((e:any)=>showToast(`Lessons did not save: ${e.message||e}`));
      return resolved;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orgLessonsId,user]);
  // (no global persist -- submittals ride in fullState with everything else)
  useEffect(()=>{try{sSetRaw(LS_SUBMITTAL_TEMPLATES_KEY,JSON.stringify(submittalTemplates));}catch{}},[submittalTemplates]);
  // (no global persist -- the site plan rides in fullState with the project
  //  it belongs to; the initialiser adopts the old shared key once)
  useEffect(()=>{if(cloudProjectId)return;try{sSetRaw(LS_SCHEDULE_KEY,JSON.stringify(schedule));}catch{}},[schedule,cloudProjectId]);
  useEffect(()=>{if(cloudProjectId)return;try{sSetRaw(LS_COST_SCHEDULE_KEY,JSON.stringify(costSchedule));}catch{}},[costSchedule,cloudProjectId]);
  // localStorage is the fallback for sandbox and signed-out use. With an org,
  // phases live in Supabase -- see updatePhaseGroups below.
  useEffect(()=>{if(orgPhasesId)return;try{sSetRaw(LS_PHASE_GROUPS,JSON.stringify(phaseGroups));}catch{}},[phaseGroups,orgPhasesId]);
  useEffect(()=>{
    const el=logSvgRef.current; if(!el)return;
    const obs=new ResizeObserver(entries=>{const r=entries[0].contentRect;setLogSvgDims({w:r.width,h:r.height});});
    obs.observe(el); return()=>obs.disconnect();
  },[activeTab,logisticsView]); // re-observe when switching tabs OR between plan/map view
  const [bpPanelCollapsed,setBpPanelCollapsed]=useState<Record<string,boolean>>({});
  const [bpNotedSet,setBpNotedSet]=useState<Set<string>>(()=>new Set());
  // Org settings panel state (separate from in-estimate panel)
  const [bpOrgFilterCat,setBpOrgFilterCat]=useState<string>("all");
  const [bpOrgEditItem,setBpOrgEditItem]=useState<Partial<BestPractice>|null>(null);
  const [bpOrgDeleteConfirmId,setBpOrgDeleteConfirmId]=useState<string|null>(null);
  // Reset panel state when division changes (show fresh for each div)
  useEffect(()=>{ setBpPanelCollapsed({}); setBpNotedSet(new Set()); },[activeDiv]);
  // Index best practices by division id
  const bpByDiv=useMemo(()=>{
    const map:Record<string,BestPractice[]>={};
    bestPractices.forEach(bp=>{ bp.divisionTags.forEach(d=>{ map[d]=[...(map[d]||[]),bp]; }); });
    return map;
  },[bestPractices]);
  const [bdCollapsed,setBdCollapsed]=useState<Record<string,boolean>>({});
  const [pickingPctForId,setPickingPctForId]=useState(null); // item id being configured for % unit
  const [pickerSel,setPickerSel]=useState([]);               // selected target item ids
  // Cell range selection (Excel-style status bar)
  const [selField,setSelField]=useState(null);        // "qty"|"unitCost"|"total"|"contingency"
  const [selAnchorId,setSelAnchorId]=useState(null);  // item id where drag started
  const [selCurrentId,setSelCurrentId]=useState(null);// item id at current drag position
  const [selDragging,setSelDragging]=useState(false);
  const [selExcluded,setSelExcluded]=useState<Set<string>>(new Set()); // ctrl+clicked out of selection
  // Row selection (for copy/cut/paste/drag — separate from cell selection)
  const [rowSelIds,setRowSelIds]=useState<Set<string>>(new Set());
  const [bulkTradeInput,setBulkTradeInput]=useState("");
  const [rowSelAnchorId,setRowSelAnchorId]=useState<string|null>(null);
  // Clipboard for row copy/cut
  const clipboardRef=useRef<{mode:"copy"|"cut",items:{rowObj:any,itemData?:any,itemDataMap?:Record<string,any>,isGroup:boolean}[]}|null>(null);
  // Drag-and-drop reorder
  const [dndActive,setDndActive]=useState(false);
  const [dndTargetId,setDndTargetId]=useState<string|null>(null);
  const [dndPosition,setDndPosition]=useState<"before"|"after"|"into">("after");
  // Sub-header drop target: when set, the drop appends rows to the end of that
  // subcategory (used when the user releases the drag over a sub header).
  const [dndTargetSubId,setDndTargetSubId]=useState<string|null>(null);
  const dndSourceRef=useRef<{ids:Set<string>}|null>(null);

  const remoteChangeRef=useRef(false); // a collaborator's write, not this user's edit
  const tabBarRef=useRef<HTMLDivElement|null>(null);
  const tabDrag=useRef<{active:boolean;didDrag:boolean;startX:number;scrollLeft:number}>({active:false,didDrag:false,startX:0,scrollLeft:0});
  const [cloudLoading,setCloudLoading]=useState(!!cloudProjectId);
  const [cloudSaveStatus,setCloudSaveStatus]=useState<"saved"|"saving"|"failed"|"">("");
  const [lastSavedAt,setLastSavedAt]=useState<number|null>(null);
  const [saveAgoTick,setSaveAgoTick]=useState(0);
  const [recoveryBackup,setRecoveryBackup]=useState<{ts:number;projectName?:string;state:any}|null>(null);

  // ── Real-time collaboration ──
  const realtime=useRealtimeSync({
    projectId:cloudProjectId,
    userId:user?.id||"",
    displayName:user?.displayName||"",
    enabled:!!cloudProjectId&&!isSandbox&&!isLocked,
  }) as any;
  const cloudLoaded=useRef(false);

    // Load project from Supabase on mount (cloud mode)
  useEffect(()=>{
    if(!cloudProjectId||!cloudStorage||cloudLoaded.current)return;
    cloudLoaded.current=true;
    // If initialState was provided (e.g. snapshot), skip DB load — state already hydrated
    if(initialState){setActiveTab(landOn(initialState,initialTab));setCloudLoading(false);return;}
    cloudStorage.loadProjectById(cloudProjectId).then((s:any)=>{
      if(!s)return;
      // A different project just arrived. Nothing before it belongs in this
      // project's history, and stepping back into it would write one job's
      // numbers over another's.
      resetHistoryRef.current=true;
      if(s.projectName)setProjectName(s.projectName);
      if(s.address!=null)setAddress(s.address||"");
      if(s.buildingArea!=null)setBuildingArea(s.buildingArea);
      if(s.duration!=null)setDuration(s.duration);
      if(s.overhead!=null)setOverhead(s.overhead);
      if(s.profit!=null)setProfit(s.profit);
      if(s.contingency!=null)setContingency(s.contingency);
      setAltPackages(s.alternates||[]);
      setContingencyPolicy({...DEFAULT_CONTINGENCY_POLICY,...(s.contingencyPolicy||{})});
      setEscalation({...DEFAULT_ESCALATION,...(s.escalation||{})});
      setStaffPlan(s.staffPlan||[]);
      if(s.markupMode)setMarkupMode(s.markupMode);
      setProposals(s.proposals?.length?s.proposals:DEFAULT_PROPOSALS);
      setProposalAreas(s.proposalAreas||{});
      setDivisionSettings(s.divisionSettings||[]);
      setSubSettings(s.subSettings||{});
      setDivisionsRaw(s.divisions||[]);
      setActiveDiv((s.divisions||[])[0]?.id||"div1");
      setItemStateRaw(s.itemState||{});
      // Procurement + ancillary state (persisted in extra_state — migration 025).
      // Before that column existed these silently failed to load in cloud mode.
      if(s.divisionMarkups)setDivisionMarkups(s.divisionMarkups);
      if(s.tradeSources)setTradeSources(s.tradeSources);
      if(s.bidScopes)setBidScopes(s.bidScopes);
      if(s.bidPartners)setBidPartners(s.bidPartners);
      if(s.bids)setBids(s.bids);
      if(s.levelingAdjustments)setLevelingAdjustments(s.levelingAdjustments);
      if(s.bidAlignments)setBidAlignments(s.bidAlignments);
      if(s.costSFCents!=null)setCostSFCents(s.costSFCents);
      if(s.rfis)setRfis(s.rfis);
      if(s.submittals)setSubmittals(s.submittals);
      if(s.siteLogistics)setSiteLogistics(s.siteLogistics);
      if(s.historyNotes)setHistoryNotes(s.historyNotes);
      if(s.migratedOpenItems)setMigratedOpenItems(true);
      if(s.buyoutItems)setBuyoutItems(s.buyoutItems);
      if(s.projectExclusions)setProjectExclusions(s.projectExclusions);
      setSchedule(s.schedule||DEFAULT_SCHEDULE);setCostSchedule(s.costSchedule||{});
      if(s.drawingList)setDrawingList(s.drawingList);
      if(s.projectNotes!=null)setProjectNotes(s.projectNotes);
      if(s.openItems)setOpenItems(s.openItems);
      if(s.jurisdictionInfo)setJurisdictionInfo(s.jurisdictionInfo);
      // Load owner ID (used to gate snapshot deletion)
      if(s.ownerId)setProjectOwnerId(s.ownerId);
      // Load active snapshots
      cloudStorage.listSnapshots(cloudProjectId).then((snaps:any[])=>{
        if(snaps?.length)setProjectSnapshots(snaps.map((s:any)=>({id:s.id,label:s.label,date:s.created_at,locked:true,state:s.state})));
      });
      // Load soft-deleted snapshots (owner only — archive view)
      if(cloudStorage.listDeletedSnapshots){
        cloudStorage.listDeletedSnapshots(cloudProjectId).then((snaps:any[])=>{
          setDeletedSnapshots((snaps||[]).map((s:any)=>({id:s.id,label:s.label,date:s.created_at,deletedAt:s.deleted_at,locked:true,state:s.state})));
        });
      }
      setActiveTab(landOn(s,initialTab));
      setCloudLoading(false);
    }).catch(()=>setCloudLoading(false));
  },[cloudProjectId,cloudStorage]);

  // Assigned further down, the moment fullState exists, and read only from
  // inside effects -- which run after the render body, so it is always the
  // current one by then.
  const backupStateRef=useRef<()=>any>(()=>({}));

  // Auto-save: cloud (debounced 1.5s) or localStorage
  useEffect(()=>{
    if(cloudLoading)return; // don't save while initial load is in progress
    if(isSandbox){
      // Sandbox mode — never save anywhere
    } else if(isLocked){
      // Locked (viewing a snapshot) — never auto-save, preserves the live project
    } else if(cloudProjectId&&cloudStorage){
      setCloudSaveStatus("saving");
      const t=setTimeout(()=>{
        // Everything the project is, from the one list -- except the site
        // logistics plan, which saves itself below. An uploaded drawing is
        // several megabytes of data URL, and sending it again every time
        // somebody edits a unit price would be re-uploading a PDF on every
        // keystroke.
        const {siteLogistics:_plan,...state}=backupStateRef.current();
        cloudStorage.saveProjectById(cloudProjectId,state)
          .then(()=>{setCloudSaveStatus("saved");setLastSavedAt(Date.now());setSaveAgoTick(t=>t+1);})
          .catch(()=>setCloudSaveStatus("failed"));
      },1500);
      return()=>clearTimeout(t);
    } else {
      // Locally the plan goes in the project, not in a key of its own: one
      // key shared by every project is how one client's site plan used to
      // turn up on another client's job.
      lsSet(LS_KEY,backupStateRef.current());
    }
  // The payload is read at save time from the one list, so these only decide
  // when a save fires. Submittals, history notes and the rest were changing
  // without one.
  },[projectName,address,buildingArea,duration,overhead,profit,contingency,contingencyPolicy,escalation,staffPlan,altPackages,markupMode,divisionMarkups,proposals,proposalAreas,divisionSettings,subSettings,divisions,itemState,projectSnapshots,tradeSources,bidScopes,bidPartners,bids,levelingAdjustments,bidAlignments,costSFCents,rfis,buyoutItems,projectExclusions,drawingList,projectNotes,openItems,jurisdictionInfo,schedule,costSchedule,submittals,historyNotes,linkedBudgetId,migratedOpenItems,siteLogistics,cloudProjectId,cloudStorage,cloudLoading,isSandbox,isLocked]);

  /**
   * The site logistics plan, saved on its own.
   *
   * It was in no save payload at all: the loader looked for it in the project
   * and in oe_logistics_v1, the type had carried the field for months, and
   * nothing ever wrote it either place. So an uploaded site plan and every
   * mark on it survived only inside a snapshot, and came back empty on the
   * next load. Nobody was doing anything wrong.
   *
   * On its own key and its own patch because it is the one part of a project
   * that is megabytes: sending it with every edit would re-upload the drawing
   * on every keystroke, and locally a drawing too big for the quota would
   * take the whole project's save down with it instead of just itself.
   */
  useEffect(()=>{
    if(cloudLoading||isSandbox||isLocked)return;
    const t=setTimeout(()=>{
      // Cloud only. The local save above already carries it, inside the
      // project, where it belongs.
      if(cloudProjectId&&cloudStorage){
        cloudStorage.saveProjectById(cloudProjectId,{siteLogistics})
          .catch((e:any)=>console.warn("[autosave] site plan save failed:",e?.message));
      }
    },2000);
    return()=>clearTimeout(t);
  },[siteLogistics,cloudProjectId,cloudStorage,cloudLoading,isSandbox,isLocked]);
  // Open items save immediately (no debounce) so collaborators see updates instantly
  useEffect(()=>{
    if(cloudLoading||isSandbox||isLocked)return;
    if(cloudProjectId&&cloudStorage){
      // Patch just the openItems key — avoids triggering a full re-save
      cloudStorage.saveProjectById(cloudProjectId,{openItems}).catch((e:any)=>console.warn("[autosave] openItems patch failed:",e?.message));
    } else {
      // localStorage: merge into existing saved state
      try{
        const existing=lsGet(LS_KEY,{});
        lsSet(LS_KEY,{...existing,openItems});
      }catch{}
    }
  },[openItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick every 5s while "saved" so "Saved Xs ago" stays current
  useEffect(()=>{
    if(cloudSaveStatus!=="saved")return;
    const id=setInterval(()=>setSaveAgoTick(t=>t+1),5000);
    return()=>clearInterval(id);
  },[cloudSaveStatus]);

  // ── Periodic local backup (every 5 min, keep last 3) ──────────────────────
  // Which project a backup belongs to. Cloud projects have a real id; the
  // local single-file mode has exactly one project, so a constant is honest.
  const backupPid=cloudProjectId||"local";
  useEffect(()=>{
    if(cloudLoading||isSandbox)return;
    const run=()=>{
      try{
        const entry={ts:Date.now(),pid:backupPid,projectName:backupStateRef.current()?.projectName,state:backupStateRef.current()};
        lsSet(LS_BACKUP_KEY,addBackup(readBackups(lsGet(LS_BACKUP_KEY,null)),entry));
      }catch(e){console.warn("[backup] periodic backup failed:",e);}
    };
    const id=setInterval(run,BACKUP_INTERVAL_MS);
    return()=>clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[cloudLoading,isSandbox,backupPid]);

  // Flush pending field-level audit entries when the tab closes or the
  // project unmounts — otherwise the last debounced edit would be lost.
  useEffect(()=>{
    window.addEventListener("beforeunload",flushAuditQueue);
    return()=>{
      window.removeEventListener("beforeunload",flushAuditQueue);
      flushAuditQueue();
    };
  },[]);

  // "Attribution implies alignment": once per project load, heal any estimate
  // line that's credited to a partner (sourceBidId / tradeName) but whose bid
  // was never aligned — so the budget chip and the comparison column agree.
  const reconcileDone=useRef(false);
  useEffect(()=>{
    if(cloudLoading||reconcileDone.current||!bids.length)return;
    reconcileDone.current=true;
    const next=reconcileAttributionAlignments({bids,bidPartners,itemState,bidAlignments});
    if(next)setBidAlignments(next);
  },[cloudLoading,bids,bidPartners,itemState,bidAlignments]);

  // Redirect away from gated tabs when a base-plan user lands on one
  useEffect(()=>{
    const gated=[
      ...(!entitlements.canUseBids?["bids","scope","buyout"]:[]),
      ...(!entitlements.canUseRfi?["rfi"]:[]),
      ...(!entitlements.canUseCashflow?["cashflow"]:[]),
    ];
    if(gated.includes(activeTab))setActiveTab("estimate");
  },[entitlements.canUseBids,entitlements.canUseRfi,entitlements.canUseCashflow,activeTab]);

  /** Every tab this user can actually reach, so a landing choice cannot
   *  send them somewhere that renders nothing. */
  const availableTabs=useMemo(()=>[
    "summary","estimate","alternates","analysis","history",
    ...(entitlements.canUseBids?["bids","scope","buyout"]:[]),
    "decisions","submittals",
    ...(entitlements.canUseRfi?["rfi"]:[]),
    "schedule",
    ...(entitlements.canUseCashflow?["cashflow"]:[]),
    "logistics","properties","properties-defaults",
  ],[entitlements.canUseBids,entitlements.canUseRfi,entitlements.canUseCashflow]);

  /** Decide where a project opens, given what it holds and where it was left. */
  const landOn=useCallback((state:any,explicit?:string|null)=>{
    const divs=state?.divisions, items=state?.itemState;
    // Counted from the state being loaded rather than from this render's
    // memos, which have not caught up yet at the moment a project opens.
    const alts=computeAltTotals(divs||[],items||{});
    return resolveLandingTab({
      explicit,
      remembered:loadLastTab(cloudProjectId,sGetRaw),
      hasItems:hasPricedScope(divs,items),
      openDecisions:gatherDecisions(divs,items,alts,state?.rfis).count,
      available:availableTabs,
    });
  },[cloudProjectId,availableTabs]);

  // Remember where this project is, so tomorrow starts where today ended.
  // "home" and the settings-* screens are app chrome rather than places in a
  // project, and reopening onto one of them would be a worse answer than the
  // default.
  useEffect(()=>{
    if(!cloudProjectId||isSandbox)return;
    if(activeTab==="home"||activeTab.startsWith("settings-"))return;
    saveLastTab(cloudProjectId,activeTab,sGetRaw,sSetRaw);
  },[cloudProjectId,isSandbox,activeTab]);

  // Offer recovery when this project's own backup holds more than what loaded.
  //
  // Two bugs lived here. It read backups[0] -- the newest entry from whatever
  // project was last open -- and offered it whoever you were, which is how one
  // client's estimate could be restored into another's. And it compared
  // against the LOCAL project store even in cloud mode, measuring an unrelated
  // project. It now reads only this project's bucket and compares against what
  // is actually on screen.
  //
  // It also ran on [] with an early return while cloudLoading, so it never
  // fired for a cloud project at all -- the data-loss safety net was local
  // only. The ref keeps it to one check now that it waits for the load.
  const recoveryCheckedRef=useRef(false);
  useEffect(()=>{
    if(cloudLoading||isSandbox||recoveryCheckedRef.current)return;
    recoveryCheckedRef.current=true;
    try{
      const mine=newestFor(readBackups(lsGet(LS_BACKUP_KEY,null)),backupPid);
      if(shouldOfferRecovery(mine,Object.keys(itemState||{}).length))setRecoveryBackup(mine as any);
    }catch{}
  // itemState is read as it stands once loading finishes, not tracked.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[cloudLoading,isSandbox,backupPid]);

  // (no global persist -- historyNotes ride in fullState with the project)
  useEffect(()=>lsSet(LS_SHORTCUTS_KEY,shortcuts),[shortcuts]);
  useEffect(()=>lsSet(LS_PRESETS_KEY,presets),[presets]);
  useEffect(()=>lsSet(LS_CONT_DISPLAY_KEY,contingencyDisplay),[contingencyDisplay]);

  // Cell selection — global mouseup ends drag; Escape clears
  useEffect(()=>{
    const onUp=()=>setSelDragging(false);
    const onKey=e=>{if(e.key==="Escape"){setSelField(null);setSelAnchorId(null);setSelCurrentId(null);setSelExcluded(new Set());setRowSelIds(new Set());setRowSelAnchorId(null);clipboardRef.current=null;setDeltaPopupId(null);setLineItemPopup(null);setBidSnapModalId(null);setSnapExpandedDivs(new Set());setStatusDropdownId(null);}};
    window.addEventListener("mouseup",onUp);
    window.addEventListener("keydown",onKey);
    return()=>{window.removeEventListener("mouseup",onUp);window.removeEventListener("keydown",onKey);};
  },[]);
  // Clear selection when the user switches division or tab
  useEffect(()=>{setSelField(null);setSelAnchorId(null);setSelCurrentId(null);setSelExcluded(new Set());setRowSelIds(new Set());setRowSelAnchorId(null);},[activeDiv,activeTab]);
  // ── Tab browser history (back/forward) ──
  useEffect(()=>{
    const onPop=(e:PopStateEvent)=>{if(e.state?.oeTab)setActiveTab(e.state.oeTab);};
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);

  // $/SF formatter — whole dollars by default, cents when costSFCents is enabled
  const fmtSF=(n:number)=>costSFCents?fmtDec(n):fmt(n);
  /**
   * Say what just happened, and when it is undoable, offer the way back.
   *
   * The Undo button is how a destructive action stops needing a dialog in
   * front of it: do the thing, say so, and leave the door open. It runs the
   * same scoped undo Ctrl+Z does -- this is the discoverable version of a
   * keystroke most people never learn.
   *
   * The timer is cleared first, because two toasts in quick succession used
   * to share the second one's lifetime: the first one's timer would fire and
   * clear the message that had replaced it.
   */
  const toastTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  const showToast=(msg:string,durationMs=2200,offerUndo=false)=>{
    if(toastTimer.current)clearTimeout(toastTimer.current);
    setToast(msg);setToastUndo(offerUndo);
    toastTimer.current=setTimeout(()=>{setToast("");setToastUndo(false);},durationMs);
  };
  useEffect(()=>()=>{if(toastTimer.current)clearTimeout(toastTimer.current);},[]);

  // ── Shared org libraries ────────────────────────────────────────────
  // Each of these was a private copy in one browser, so a firm of five had
  // five different sets of what were meant to be its standards. These setters
  // keep the name the raw useState ones had, so every existing call site --
  // savePreset, deletePreset, the template import, the logo helpers -- gains
  // persistence without being touched.
  const orgLibsId=(!isSandbox&&user&&currentOrgId)?currentOrgId:null;
  const orgUserId=user?.id??null;
  const setPresets=useOrgSetting(orgLibsId,orgUserId,"presets",presets,setPresetsLocal,showToast);
  const setLaborRates=useOrgSetting(orgLibsId,orgUserId,"labor_rates",laborRates,setLaborRatesLocal,showToast);
  const setOrgExclusions=useOrgSetting(orgLibsId,orgUserId,"org_exclusions",orgExclusions,setOrgExclusionsLocal,showToast);
  const setSubmittalTemplates=useOrgSetting(orgLibsId,orgUserId,"submittal_templates",submittalTemplates,setSubmittalTemplatesLocal,showToast);
  // The logo map is keyed by org across every org this user belongs to.
  // Storing the whole map in one org's row would put another org's logo
  // inside it, so only this org's slice travels.
  const setOrgLogos=useOrgSetting(orgLibsId,orgUserId,"org_logo",orgLogos,setOrgLogosLocal,showToast,{
    toStored:(map:{[k:string]:string})=>(orgLibsId?map[orgLibsId]??null:null),
    fromStored:(logo:any,current:{[k:string]:string})=>(orgLibsId&&typeof logo==="string"?{...current,[orgLibsId]:logo}:current),
  });

  // Surface localStorage quota exhaustion to the user (instead of silently dropping saves).
  useEffect(()=>{
    const onQuota=()=>showToast("⚠ Local storage is full — please sync to cloud or clear old snapshots.");
    window.addEventListener("oe-storage-quota",onQuota);
    return ()=>window.removeEventListener("oe-storage-quota",onQuota);
  },[]);

  // Revoke cached blob URLs on unmount so PDFs imported during a session don't leak memory.
  useEffect(()=>()=>{
    bidPdfCache.current.forEach(u=>_revokeBlob(u));
    bidPdfCache.current.clear();
  },[]);

  /**
   * The estimate grid's own snapshot hooks, kept for their call sites.
   *
   * They used to build the undo stack themselves. The recorder does that now
   * by watching state, so all three of these only shape where one step ends
   * and the next begins: focusing a cell starts a fresh step, and leaving it
   * closes that step off. Typing within one cell still collapses to a single
   * undo -- the recorder coalesces changes while the same field holds focus.
   */
  const snapshot=markHistoryBoundary;
  const beginCellEdit=markHistoryBoundary;
  const endCellEdit=markHistoryBoundary;

  // Assigned once the slices and their setters exist, further down. The
  // keyboard handler reaches them through refs so it does not have to be
  // rebound every time any of them changes.
  const undoRef=useRef<()=>void>(()=>{});
  const redoRef=useRef<()=>void>(()=>{});

  useEffect(()=>{
    const h=e=>{
      const tag=document.activeElement?.tagName;
      const inInput=tag==="INPUT"||tag==="TEXTAREA";
      // Undo and redo are the project's, not the tab's. Whichever tab you are
      // standing on, Ctrl+Z steps back through what was done to the project in
      // the order it was done -- and says what it undid, because the change it
      // reverses is often on a screen you are not looking at.
      if(shortcuts.ctrlZUndo&&(e.ctrlKey||e.metaKey)&&e.key==="z"&&!e.shiftKey){
        if(inInput)return;   // let a focused field keep its own text undo
        e.preventDefault();
        undoRef.current();
      }
      // Redo answers to Ctrl+Shift+Z as well as Ctrl+Y: Shift+Z is the binding
      // on macOS and in most editors, and it used to do nothing at all.
      if(shortcuts.ctrlZUndo&&(e.ctrlKey||e.metaKey)&&(e.key==="y"||(e.shiftKey&&e.key.toLowerCase()==="z"))){
        if(inInput)return;
        e.preventDefault();
        redoRef.current();
      }
      if(shortcuts.questionShortcuts&&e.key==="?"&&!e.ctrlKey&&!e.metaKey){
        if(inInput)return;
        setShowShortcuts(v=>!v);
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="f"){
        e.preventDefault();
        setSearchOpen(o=>!o);
        setTimeout(()=>document.getElementById("oe-search-input")?.focus(),50);
      }
      // Ctrl+Shift+O used to open the Open Items drawer; it opens Notes now,
      // which is where those items went.
      if((e.ctrlKey||e.metaKey)&&e.shiftKey&&e.key==="O"){
        e.preventDefault();
        openNotesRef.current?.();
      }
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[shortcuts]);

  // ── Logistics keyboard shortcuts (ESC, Delete) ──
  useEffect(()=>{
    if(activeTab!=="logistics")return;
    const selectedLogIdRaw=selectedLogId;
    const h=(e:KeyboardEvent)=>{
      const tag=(document.activeElement as HTMLElement)?.tagName;
      if(tag==="INPUT"||tag==="TEXTAREA")return;
      // A selected block is not an element: deleting, nudging or copying one
      // would snapshot the plan and change nothing.
      const selectedLogId=isBlockSel(selectedLogIdRaw)?null:selectedLogIdRaw;
      if(e.key==="Escape"){
        e.preventDefault();
        // Two stages, which is what every drawing tool does: the first Escape
        // abandons whatever is half-drawn, and once there is nothing in
        // progress the next one lets go of the selection. There was no second
        // stage at all, so once something was selected it stayed selected.
        const busy=logisticsTool!=="select"||lineInProgress.length>0
          ||areaInProgress.length>0||circleCenter!==null||calibPoints.length>0;
        setLogisticsTool("select");
        setLineInProgress([]);setLinePreview(null);
        setAreaInProgress([]);setAreaPreview(null);
        setCircleCenter(null);setCirclePreviewR(0);
        setCalibPoints([]);
        if(!busy)setSelectedLogId(null);
      }
      // The keys people already press. A drawing tool that cannot copy,
      // paste or nudge with the arrows is one they have to think about
      // instead of use.
      const mod=e.ctrlKey||e.metaKey;
      if(mod&&(e.key==="c"||e.key==="x")&&selectedLogId){
        const open=logActivePlan(siteLogistics as any);
        const clip=copyElement(open as any,selectedLogId);
        if(clip){
          e.preventDefault();
          if(e.key==="x"&&lockedById(open as any,selectedLogId)){
            showToast("That one is locked — unlock it first");return;
          }
          logClipRef.current={clip,pastes:0};
          if(e.key==="x"){
            logisticsSnapshot();
            setSiteLogistics(prev=>updateActivePlan(prev as any,
              pl=>removeElement(pl as any,selectedLogId) as any) as any);
            setSelectedLogId(null);
          }
          showToast(e.key==="x"?`Cut ${describeClip(clip)}`:`Copied ${describeClip(clip)}`);
        }
      }
      if(mod&&e.key==="v"&&logClipRef.current){
        e.preventDefault();
        const slot=logClipRef.current;
        slot.pastes+=1;
        const id=uid();
        logisticsSnapshot();
        setSiteLogistics(prev=>updateActivePlan(prev as any,
          pl=>pasteElement(pl as any,slot.clip,id,slot.pastes).sl as any) as any);
        setSelectedLogId(id);
      }
      // Duplicate is paste without disturbing what is on the clipboard.
      if(mod&&e.key==="d"&&selectedLogId){
        const clip=copyElement(logActivePlan(siteLogistics as any) as any,selectedLogId);
        if(clip){
          e.preventDefault();
          const id=uid();
          logisticsSnapshot();
          setSiteLogistics(prev=>updateActivePlan(prev as any,
            pl=>pasteElement(pl as any,clip,id,1).sl as any) as any);
          setSelectedLogId(id);
        }
      }
      if(selectedLogId&&!mod&&e.key.startsWith("Arrow")){
        e.preventDefault();
        if(lockedById(logActivePlan(siteLogistics as any) as any,selectedLogId))return;
        const step=e.shiftKey?NUDGE_STEP_COARSE:NUDGE_STEP;
        const dx=e.key==="ArrowLeft"?-step:e.key==="ArrowRight"?step:0;
        const dy=e.key==="ArrowUp"?-step:e.key==="ArrowDown"?step:0;
        logisticsSnapshot();
        setSiteLogistics(prev=>updateActivePlan(prev as any,
          pl=>nudgeElement(pl as any,selectedLogId,dx,dy) as any) as any);
      }
      if((e.key==="Delete"||e.key==="Backspace")&&selectedLogId){
        e.preventDefault();
        if(lockedById(logActivePlan(siteLogistics as any) as any,selectedLogId)){
          showToast("That one is locked — unlock it first");return;
        }
        logisticsSnapshot();
        setSiteLogistics(prev=>updateActivePlan(prev as any,pl=>({...pl,
          icons:pl.icons.filter(x=>x.id!==selectedLogId),
          lines:pl.lines.filter(x=>x.id!==selectedLogId),
          areas:pl.areas.filter(x=>x.id!==selectedLogId),
          circles:pl.circles.filter(x=>x.id!==selectedLogId),
          texts:(pl.texts||[]).filter(x=>x.id!==selectedLogId),
          dimensions:(pl.dimensions||[]).filter(x=>x.id!==selectedLogId),
        })) as any);
        setSelectedLogId(null);
      }
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[activeTab,selectedLogId,logisticsTool,lineInProgress,areaInProgress,circleCenter,calibPoints,siteLogistics,uid]);

  const setDivisions=useCallback(fn=>{if(isLocked)return;setDivisionsRaw(fn);},[isLocked]);
  const setItemState=useCallback(fn=>{if(isLocked)return;setItemStateRaw(fn);},[isLocked]);

  /**
   * Refuse the caret while a snapshot is open.
   *
   * CSS stops the pointer; it cannot stop Tab. The first version set
   * tabindex="-1" on the controls it could find, which missed every input
   * React rendered afterwards -- expanding a subcategory produced fresh,
   * focusable fields. A listener on the container survives re-renders
   * because the container does: anything matching a value field is blurred
   * the moment it takes focus, so there is nowhere for a keystroke to land.
   */
  useEffect(()=>{
    if(!isLocked)return;
    const VALUE_FIELDS="table input,table textarea,table select,.ghost-input,.input-field,[contenteditable]";
    const onFocus=(e:FocusEvent)=>{
      const el=e.target as HTMLElement|null;
      if(el?.closest?.(".oe-readonly")&&el.matches?.(VALUE_FIELDS))el.blur();
    };
    document.addEventListener("focusin",onFocus,true);
    return()=>document.removeEventListener("focusin",onFocus,true);
  },[isLocked]);
  // Recent local edits — used to detect when a collaborator's remote change
  // overwrites something this user just touched (concurrent-edit conflict).
  const recentLocalEdits=useRef<Map<string,number>>(new Map());
  const CONFLICT_WINDOW_MS=30000;
  const noteLocalEdit=(key:string)=>{
    const m=recentLocalEdits.current;
    m.set(key,Date.now());
    if(m.size>200){ // prune old entries
      const cutoff=Date.now()-CONFLICT_WINDOW_MS;
      for(const [k,t] of m)if(t<cutoff)m.delete(k);
    }
  };
  const wasRecentlyEditedLocally=(key:string)=>{
    const t=recentLocalEdits.current.get(key);
    return t!=null&&Date.now()-t<CONFLICT_WINDOW_MS;
  };

  const upd=useCallback((id,field,val)=>{
    // Field-level audit: capture before/after for "who changed this number".
    // Debounced in lib/audit so rapid edits collapse to one entry. Cloud
    // projects only — backupStateRef returns the current state at call time.
    if(cloudProjectId&&!isSandbox){
      const cur=backupStateRef.current?.()?.itemState?.[id];
      auditFieldChange(cloudProjectId,id,String(field),cur?.[field],val,cur?.description||undefined);
    }
    noteLocalEdit(`${id}|${field}`);
    setItemState(p=>({...p,[id]:{...p[id],[field]:val}}));
    realtime.broadcastCellUpdate?.(id,field,val);
  },[setItemState,realtime,cloudProjectId,isSandbox]);

  // Audited markup setters — markup % changes move the whole estimate, so
  // they're exactly what a reviewer asks about ("who changed overhead?").
  // Only the Properties tab gets these; remote-sync handlers use the raw
  // setters so a collaborator's change isn't double-logged locally.
  const mkAuditedMarkupSetter=useCallback((field:"overhead"|"profit"|"contingency",rawSetter:(v:number)=>void,current:number)=>{
    return (v:number)=>{
      if(cloudProjectId&&!isSandbox&&v!==current){
        auditEvent("markup.update","project",cloudProjectId,{field,oldValue:current,newValue:v});
      }
      rawSetter(v);
    };
  },[cloudProjectId,isSandbox]);
  const auditedSetOverhead=useMemo(()=>mkAuditedMarkupSetter("overhead",setOverhead,overhead),[mkAuditedMarkupSetter,overhead]);
  const auditedSetProfit=useMemo(()=>mkAuditedMarkupSetter("profit",setProfit,profit),[mkAuditedMarkupSetter,profit]);
  const auditedSetContingency=useMemo(()=>mkAuditedMarkupSetter("contingency",setContingency,contingency),[mkAuditedMarkupSetter,contingency]);

  // One-click award: leveling sheet → buyout row, with audit attribution.
  // Contract amount comes from the leveled total — no manual re-entry.
  const awardBid=useCallback((args:{scopeName:string,scopeDivisionIds:string[],partnerName:string,bidId:string,leveledTotal:number,budget:number})=>{
    const dup=buyoutItems.find(b=>b.scope===args.scopeName&&(b.status==="awarded"||b.status==="executed"));
    // Only the conflict is worth stopping for. Confirming the award itself
    // asked the user to agree with the button they had just pressed; what
    // follows says what happened and offers the way back out of it.
    if(dup&&!window.confirm(`"${args.scopeName}" already has an awarded buyout row (${dup.vendor||"no vendor"}). Award again anyway?`))return;
    snapshot();
    const row={id:uid(),scope:args.scopeName,divisionId:args.scopeDivisionIds[0]||"",subId:undefined,
      budgetAmount:args.budget,vendor:args.partnerName,bidId:args.bidId,
      contractAmount:args.leveledTotal,contractNumber:"",contractDate:new Date().toISOString().slice(0,10),
      status:"awarded" as const,notes:"Awarded from bid leveling"};
    setBuyoutItems(p=>[...p,row]);
    if(cloudProjectId&&!isSandbox){
      auditEvent("bid.award","project",cloudProjectId,{field:"contractAmount",oldValue:args.budget,newValue:args.leveledTotal,itemDesc:`${args.scopeName} → ${args.partnerName}`});
    }
    showToast(`Awarded ${args.scopeName} to ${args.partnerName} — tracked in Buyout`,5000);
    setActiveTab("buyout");
  },[buyoutItems,cloudProjectId,isSandbox,snapshot]);


  // ── Remote change handlers (real-time collaboration) ──
  useEffect(()=>{
    if(!realtime._onCellUpdateRef)return;
    const collabName=(remoteUserId:string)=>
      realtime.collaborators?.find(c=>c.userId===remoteUserId)?.displayName||"A collaborator";
    realtime._onCellUpdateRef.current=(u)=>{
      remoteChangeRef.current=true;
      // Conflict visibility: if this user edited the same cell in the last
      // 30s, the remote value just overwrote their work — say so instead of
      // losing it silently. (Full merge/CRDT is future work; visibility now.)
      if(wasRecentlyEditedLocally(`${u.itemId}|${u.field}`)){
        const desc=backupStateRef.current?.()?.itemState?.[u.itemId]?.description;
        showToast(`⚠ ${collabName(u.userId)} also changed ${u.field}${desc?` on “${desc}”`:""} — their value applied. Check it.`,8000);
      }
      setItemStateRaw(prev=>({...prev,[u.itemId]:{...prev[u.itemId],[u.field]:u.value}}));
    };
    realtime._onScalarUpdateRef.current=(u)=>{
      remoteChangeRef.current=true;
      const setters={projectName:setProjectName,buildingArea:setBuildingArea,duration:setDuration,overhead:setOverhead,profit:setProfit,contingency:setContingency,markupMode:setMarkupMode};
      const setter=setters[u.field];
      if(setter)setter(u.value);
    };
    realtime._onStructureUpdateRef.current=(u)=>{
      remoteChangeRef.current=true;
      // A structure update replaces the whole tree — if the user made ANY
      // edit in the last 30s, warn that it may have been superseded.
      const cutoff=Date.now()-CONFLICT_WINDOW_MS;
      let hadRecent=false;
      for(const t of recentLocalEdits.current.values()){if(t>=cutoff){hadRecent=true;break;}}
      if(hadRecent){
        showToast(`⚠ ${collabName(u.userId)} restructured the estimate — your recent edits may have been overwritten. Verify them.`,8000);
      }
      setDivisionsRaw(u.divisions);setItemStateRaw(u.itemState);
    };
  },[realtime]);

  /**
   * Everything that is this project.
   *
   * There used to be two lists of that, and they had drifted. This one fed
   * snapshots and the local backup; the autosave below kept its own, and each
   * was missing things the other had. What the autosave was missing was the
   * site logistics plan -- the uploaded drawing and every mark on it -- so a
   * plan survived only inside a snapshot and was gone on the next load. The
   * type had carried the field for a while and the loader had always looked
   * for it; nothing ever wrote it.
   *
   * So there is one list now, and both savers use it.
   */
  const fullState=():ProjectState=>({projectName,address,buildingArea,duration,overhead,profit,contingency,contingencyPolicy,escalation,staffPlan,markupMode,divisionMarkups,
    proposals,alternates:altPackages,proposalAreas,divisionSettings,subSettings,divisions,itemState,
    projectSnapshots,linkedBudgetId,bidAlignments,bidScopes,bidPartners,bids,levelingAdjustments,tradeSources,projectExclusions,rfis,buyoutItems,
    schedule,costSchedule,submittals,siteLogistics,historyNotes,migratedOpenItems,
    costSFCents,drawingList,projectNotes,openItems,jurisdictionInfo});
  backupStateRef.current=fullState;

  // ── The undo recorder ────────────────────────────────────────────
  /**
   * Every slice Ctrl+Z moves, under the names lib/history knows them by.
   *
   * The same values fullState() saves, so what is undoable and what is saved
   * cannot drift apart -- history.test.ts reads fullState() out of this file
   * and fails if a field appears there without anyone deciding whether an
   * undo should move it.
   */
  const undoSlices:Slices={projectName,address,buildingArea,duration,overhead,profit,contingency,
    contingencyPolicy,escalation,staffPlan,markupMode,divisionMarkups,proposals,alternates:altPackages,
    proposalAreas,divisionSettings,subSettings,divisions,itemState,linkedBudgetId,bidAlignments,
    bidScopes,bidPartners,bids,levelingAdjustments,tradeSources,projectExclusions,rfis,buyoutItems,
    schedule,costSchedule,submittals,siteLogistics,historyNotes,costSFCents,drawingList,projectNotes,
    openItems,jurisdictionInfo};
  const undoSlicesRef=useRef<Slices>(undoSlices);
  undoSlicesRef.current=undoSlices;

  const UNDO_SETTERS:Record<string,(v:any)=>void>={
    projectName:setProjectName,address:setAddress,buildingArea:setBuildingArea,duration:setDuration,
    overhead:setOverhead,profit:setProfit,contingency:setContingency,contingencyPolicy:setContingencyPolicy,
    escalation:setEscalation,staffPlan:setStaffPlan,markupMode:setMarkupMode,divisionMarkups:setDivisionMarkups,
    proposals:setProposals,alternates:setAltPackages,proposalAreas:setProposalAreas,
    divisionSettings:setDivisionSettings,subSettings:setSubSettings,divisions:setDivisionsRaw,
    itemState:setItemStateRaw,linkedBudgetId:setLinkedBudgetId,bidAlignments:setBidAlignments,
    bidScopes:setBidScopes,bidPartners:setBidPartners,bids:setBids,levelingAdjustments:setLevelingAdjustments,
    tradeSources:setTradeSources,projectExclusions:setProjectExclusions,rfis:setRfis,buyoutItems:setBuyoutItems,
    schedule:setSchedule,costSchedule:setCostSchedule,submittals:setSubmittals,siteLogistics:setSiteLogistics,
    historyNotes:setHistoryNotes,costSFCents:setCostSFCents,drawingList:setDrawingList,
    projectNotes:setProjectNotes,openItems:setOpenItems,jurisdictionInfo:setJurisdictionInfo,
  };

  /**
   * Watch the project and write down what moved.
   *
   * Runs after every render and compares references -- thirty-nine of them,
   * which costs nothing -- rather than relying on each edit to announce
   * itself. An edit that forgets to call a snapshot function is an edit that
   * cannot be undone, and that is precisely how every field outside the
   * estimate grid ended up with no undo at all.
   *
   * What goes into the journal is the state BEFORE the change, which is where
   * one Ctrl+Z should land you.
   */
  useEffect(()=>{
    const now=undoSlicesRef.current;
    const prev=prevSlicesRef.current;
    prevSlicesRef.current=now;
    if(!prev)return;                       // first pass: nothing to compare against
    if(resetHistoryRef.current){           // a different project just loaded
      resetHistoryRef.current=false;
      historyRef.current=emptyJournals();
      return;
    }
    const touched=changedKeys(prev,now);
    if(!touched.length)return;
    // An undo, a redo, or a collaborator's write. None of them is this user
    // making a change, and recording them would let Ctrl+Z fight itself.
    if(skipHistoryRef.current||remoteChangeRef.current){
      skipHistoryRef.current=false;remoteChangeRef.current=false;
      return;
    }
    // A new object is not a new value. Leaving a cell commits it whether or
    // not it changed, handing back a fresh itemState holding what it already
    // held; filed by reference that is a step that undoes nothing, and one
    // Ctrl+Z doing visibly nothing costs more trust than the feature earns.
    const keys=touched.filter(k=>!sameValue(prev[k],now[k]));
    if(!keys.length)return;
    // Backstop for a load path that did not set resetHistoryRef. Recording
    // one would leave the PREVIOUS project sitting one Ctrl+Z away, ready to
    // be written over this one.
    if(looksLikeLoad(keys)){historyRef.current=emptyJournals();return;}
    // Two things count as one continuing edit: the same field still holding
    // focus, and a pointer still held down from the same press.
    const el=document.activeElement;
    const typing=isTextEntry(el);
    const g=gestureRef.current;
    const sameField=typing&&el===lastEditElRef.current;
    const sameGesture=g.down&&lastGestureRef.current===g.id;
    const coalesce=!historyBarrierRef.current&&(sameField||sameGesture);
    historyBarrierRef.current=false;
    lastEditElRef.current=typing?el:null;
    lastGestureRef.current=g.id;
    const scope=scopeOfChange(keys);
    if(!scope)return;                      // nothing undoable moved
    const journal=historyRef.current[scope];
    const last=journal.past[journal.past.length-1]??null;
    // When this change is folding into the step before it, there is nothing
    // new to clone -- reuse that step's frame rather than copying the slice
    // again on every keystroke.
    const frame=(coalesce&&last&&sameKeys(last.keys,keys))
      ?{state:last.state,raw:last.raw}
      :capture(prev,keys);
    historyRef.current={...historyRef.current,
      [scope]:recordStep(journal,{...frame,tab:activeTab,keys,at:Date.now()},{coalesce,max:MAX_HISTORY})};
  });

  /** Put a step back on screen, and say what that did. */
  const stepHistoryTo=(dir:"back"|"forward")=>{
    if(isLocked){showToast("Viewing a snapshot — there is nothing here to undo");return;}
    // Only what this tab can show. Undoing a site-plan drag from the Summary
    // would change a drawing on the strength of a toast, which is the thing
    // this is here to prevent rather than to do.
    const scope=scopeForTab(activeTab);
    if(!scope){showToast("There is nothing to undo on this screen");return;}
    const h=historyRef.current[scope];
    const side=dir==="back"?h.past:h.future;
    const target=side[side.length-1]??null;
    if(!target){
      showToast(`${dir==="back"?"Nothing to undo":"Nothing to redo"} in ${SCOPES[scope].label}`);
      return;
    }
    // Only the slices this step touched: they are the only ones it can put
    // back, and the only ones worth copying for the journey the other way.
    const cur=capture(undoSlicesRef.current,target.keys);
    const moved=(dir==="back"?stepBack:stepForward)(h,cur)!;
    historyRef.current={...historyRef.current,[scope]:moved.history};
    const toWrite=target.keys;
    const what=describeChange(cur.state,moved.entry.state,target.keys);
    skipHistoryRef.current=true;
    historyBarrierRef.current=true;        // the next real edit starts fresh
    lastEditElRef.current=null;
    lastGestureRef.current=-1;
    for(const k of toWrite)UNDO_SETTERS[k]?.(moved.entry.state[k]);
    // Name the change, and name the tab when it is not the one you are on.
    // An undo you cannot see is worse than none: afterwards you no longer
    // know what the estimate says, which is the thing this is here to fix.
    const where=moved.entry.tab&&moved.entry.tab!==activeTab
      ?`  · ${TAB_LABELS[moved.entry.tab]||moved.entry.tab}`:"";
    showToast(`${dir==="back"?"↶":"↷"} ${what}${where}`,3400);
  };
  undoRef.current=()=>stepHistoryTo("back");
  redoRef.current=()=>stepHistoryTo("forward");


  const restoreFromBackup=(backup:{ts:number;state:any})=>{
    const f=backup.state;
    if(!f)return;
    resetHistoryRef.current=true;
    if(f.projectName)setProjectName(f.projectName);
    if(f.buildingArea!=null)setBuildingArea(f.buildingArea);
    if(f.duration!=null)setDuration(f.duration);
    if(f.overhead!=null)setOverhead(f.overhead);
    if(f.profit!=null)setProfit(f.profit);
    if(f.contingency!=null)setContingency(f.contingency);
    setAltPackages(f.alternates||[]);
    setContingencyPolicy({...DEFAULT_CONTINGENCY_POLICY,...(f.contingencyPolicy||{})});
    setEscalation({...DEFAULT_ESCALATION,...(f.escalation||{})});
    setStaffPlan(f.staffPlan||[]);
    if(f.markupMode)setMarkupMode(f.markupMode);
    if(f.proposals?.length)setProposals(f.proposals);
    if(f.proposalAreas)setProposalAreas(f.proposalAreas);
    if(f.divisionSettings?.length)setDivisionSettings(f.divisionSettings);
    if(f.subSettings)setSubSettings(f.subSettings);
    if(f.divisions?.length)setDivisionsRaw(f.divisions);
    if(f.itemState)setItemStateRaw(migrateItemStateAltTypes(f.itemState));
    setRecoveryBackup(null);
    showToast("Backup restored ✓");
  };

  const migrateItemStateAltTypes=is=>{
    if(!is)return is;
    const out={...is};
    for(const k of Object.keys(out)){
      if(out[k]?.altType&&(out[k].altType==="Add Alt"||out[k].altType==="Ded Alt"))
        out[k]={...out[k],altType:"Alt"};
    }
    return out;
  };
  const handleExportCSV=()=>{exportCSV(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode);showToast("Exported CSV");setShowFileMenu(false);};
  const handleExportExcel=async()=>{setShowFileMenu(false);await exportExcel(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode);showToast("Exported Excel");};

  // ── CSV Import ──
  const csvImportRef=useRef<HTMLInputElement>(null);
  const handleImportCSV=(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{
      const text=ev.target?.result as string;
      const result=importCSV(text,divisions,proposals);
      if(!result){showToast("Could not parse CSV — check format.");return;}
      if(result.imported===0){showToast("No items imported. Check column headers match OpenEstimate format.");return;}
      snapshot();
      setDivisionsRaw(result.divisions);
      setItemStateRaw(prev=>({...prev,...result.itemState}));
      showToast(`Imported ${result.imported} item${result.imported!==1?"s":""}${result.skipped>0?` (${result.skipped} skipped)`:""}  — Ctrl+Z to undo`);
    };
    reader.readAsText(file);
    e.target.value="";
  };
  const handleExportQuickBooks=()=>{exportQuickBooks(projectName,divisions,itemState,proposals,final,overhead,profit,contingency,markupMode);showToast("Exported QuickBooks CSV");setShowFileMenu(false);setShowExportSub(false);};
  // ── Scope of Work handlers ──
  const handleGenerateScope=async()=>{
    setScopeLoading(true);
    showToast("Claude is writing your Scope of Work…");
    try{
      const {supabase:sb}=await import("./lib/supabase");
      if(!sb){showToast("Connection required to generate Scope of Work");setScopeLoading(false);return;}
      const payload=buildScopePayload(projectName,buildingArea,duration,overhead,profit,contingency,markupMode,final,divisions,itemState,proposals,divTotals,bids,bidPartners);
      const {data,error}=await sb.functions.invoke("generate-scope-of-work",{body:payload});
      if(error){
        let detail=error.message;
        try{const body=await (error as any).context?.json?.();detail=body?.error||body?.detail||body?.message||detail;}catch{}
        throw new Error(detail);
      }
      if(data?.error)throw new Error(data.error);
      setScopeData(data as ScopeResult);
      showToast("Scope of Work generated — review and edit exclusions below");
    }catch(e:any){
      showToast(`Error: ${e.message||"Failed to generate scope"}`);
    }finally{
      setScopeLoading(false);
    }
  };
  const handleDownloadScope=async()=>{
    if(!scopeData)return;
    showToast("Building Word document…");
    try{
      const payload=buildScopePayload(projectName,buildingArea,duration,overhead,profit,contingency,markupMode,final,divisions,itemState,proposals,divTotals,bids,bidPartners);
      const blob=await assembleScopeDocx(projectName,address,payload,scopeData,scopeExclusions,buildingArea,final,drawingList);
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url;
      a.download=`${(projectName||"Scope of Work").replace(/[^a-z0-9 ]/gi,"_")} — Scope of Work.docx`;
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("Downloaded Scope of Work");
    }catch(e:any){
      showToast(`Error: ${e.message||"Failed to build document"}`);
    }
  };

  // ── Open Items handlers ──
  const currentUserName=user?.displayName||(user?.email?.split("@")[0])||"You";
  const currentUserInitials=(currentUserName.trim().split(/\s+/).map((w:string)=>w[0]).join("").toUpperCase().slice(0,2))||"?";

  const handleAddOpenItem=(text:string)=>{
    const item:OpenItem={
      id:`oi_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      text:text.trim(),
      author:currentUserName,
      authorInitials:currentUserInitials,
      createdAt:new Date().toISOString(),
      resolved:false,
    };
    setOpenItems(prev=>[item,...prev]);
  };
  const handleResolveItem=(id:string)=>{
    setOpenItems(prev=>prev.map(i=>i.id===id?{...i,resolved:true,resolvedAt:new Date().toISOString(),resolvedBy:currentUserName}:i));
  };
  const handleUnresolveItem=(id:string)=>{
    setOpenItems(prev=>prev.map(i=>i.id===id?{...i,resolved:false,resolvedAt:undefined,resolvedBy:undefined}:i));
  };
  const handleDeleteOpenItem=(id:string)=>{
    setOpenItems(prev=>prev.filter(i=>i.id!==id));
  };

  // ── Procore export ──
  const handleProcoreExport=async()=>{
    setShowFileMenu(false);setShowExportSub(false);
    try{
      const {supabase:sb}=await import("./lib/supabase");
      const {data:{session}}=await sb.auth.getSession();
      if(!session?.access_token){showToast("Please sign in to use Procore export");return;}
      const authH={"Authorization":`Bearer ${session.access_token}`};
      // Check connection status
      const statusRes=await fetch("/api/procore-data?action=status",{headers:authH});
      const statusData=await statusRes.json();
      if(!statusData.connected){
        // Get the OAuth URL from the server — nonce-based, JWT never touches a redirect URL
        const initRes=await fetch("/api/procore-auth-init",{method:"POST",headers:authH});
        if(!initRes.ok){showToast("Failed to initiate Procore connection");return;}
        const{url}=await initRes.json();
        window.location.href=url;
        return;
      }
      // Fetch companies
      const compRes=await fetch("/api/procore-data?action=companies",{headers:authH});
      const compData=await compRes.json();
      if(!compData.connected||!compData.companies?.length){showToast("No Procore companies found");return;}
      setProcoreCompanies(compData.companies);
      setSelectedCompanyId(compData.companies[0].id);
      setShowProcoreDialog(true);
    }catch(e:any){showToast(e.message||"Failed to connect to Procore");}
  };
  // Connect-only flow for the Integrations settings tab (no export)
  const handleProcoreConnect=async()=>{
    setProcoreConnecting(true);
    try{
      const {supabase:sb}=await import("./lib/supabase");
      const {data:{session}}=await sb.auth.getSession();
      if(!session?.access_token){showToast("Please sign in to connect Procore");setProcoreConnecting(false);return;}
      const authH={"Authorization":`Bearer ${session.access_token}`};
      const initRes=await fetch("/api/procore-auth-init",{method:"POST",headers:authH});
      if(!initRes.ok){showToast("Failed to initiate Procore connection");setProcoreConnecting(false);return;}
      const{url}=await initRes.json();
      window.location.href=url;
    }catch(e:any){showToast(e.message||"Failed to connect to Procore");setProcoreConnecting(false);}
  };
  const handleProcoreSubmit=async()=>{
    if(!selectedCompanyId)return;
    if(!procoreCreateNew&&!selectedProcoreProjectId){showToast("Choose a Procore project to export into");return;}
    setProcoreExporting(true);
    try{
      const {supabase:sb}=await import("./lib/supabase");
      const {data:{session}}=await sb.auth.getSession();
      if(!session?.access_token){showToast("Session expired");setProcoreExporting(false);return;}
      const res=await fetch("/api/procore-export",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${session.access_token}`},
        body:JSON.stringify({
          companyId:selectedCompanyId,
          projectId:procoreCreateNew?null:selectedProcoreProjectId,
          createProject:procoreCreateNew,
          projectName,divisions,itemState,overhead,profit,contingency,
          // Lets the server flag a mismatch rather than quietly writing a
          // different number from the one on screen.
          expectedTotal:final.total||0,
        }),
      });
      const data=await res.json();
      if(!res.ok)throw new Error(data.error||"Export failed");
      let msg=`Exported to Procore — ${data.lineItemsCreated} budget lines created.`;
      if(data.unmatchedCodes>0)msg+=` ${data.unmatchedCodes} need coding in Procore.`;
      if(data.warnings?.length)msg+=` (${data.warnings.length} warning${data.warnings.length===1?"":"s"})`;
      showToast(msg);
      if(data.warnings?.length)console.warn("Procore export warnings:",data.warnings);
      setShowProcoreDialog(false);
    }catch(e:any){showToast(e.message||"Procore export failed");}
    finally{setProcoreExporting(false);}
  };
  // Procore projects for the chosen company — the list the export targets.
  useEffect(()=>{
    if(!showProcoreDialog||!selectedCompanyId)return;
    let cancelled=false;
    (async()=>{
      setProcoreLoadingProjects(true);
      setProcoreProjects([]);setSelectedProcoreProjectId(null);
      try{
        const {supabase:sb}=await import("./lib/supabase");
        const {data:{session}}=await sb.auth.getSession();
        if(!session?.access_token)return;
        const res=await fetch(`/api/procore-data?action=projects&companyId=${selectedCompanyId}`,
          {headers:{"Authorization":`Bearer ${session.access_token}`}});
        const data=await res.json();
        if(cancelled)return;
        const list=(data.projects||[]).filter((p:any)=>p.active);
        setProcoreProjects(list);
        // Pre-select the project whose name matches this estimate, which is
        // usually the one meant — but never silently create one.
        const match=list.find((p:any)=>p.name?.trim().toLowerCase()===projectName.trim().toLowerCase());
        setSelectedProcoreProjectId(match?match.id:(list[0]?.id??null));
        setProcoreCreateNew(list.length===0);
      }catch{ if(!cancelled)setProcoreProjects([]); }
      finally{ if(!cancelled)setProcoreLoadingProjects(false); }
    })();
    return()=>{cancelled=true;};
  },[showProcoreDialog,selectedCompanyId]);

  // Check for Procore OAuth callback on mount
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const pc=params.get("procore");
    if(pc==="connected"){showToast("Connected to Procore!");setProcoreConnected(true);history.replaceState(null,"",window.location.pathname);}
    else if(pc==="error"){const r=params.get("reason")||"unknown";showToast(`Procore connection failed: ${r}`);history.replaceState(null,"",window.location.pathname);}
  },[]);
  // Fetch Procore connection status when Integrations tab is opened
  useEffect(()=>{
    if(activeTab!=="settings-integrations"||procoreConnected!==null)return;
    (async()=>{
      try{
        const {supabase:sb}=await import("./lib/supabase");
        const {data:{session}}=await sb.auth.getSession();
        if(!session?.access_token){setProcoreConnected(false);return;}
        const res=await fetch("/api/procore-data?action=status",{headers:{"Authorization":`Bearer ${session.access_token}`}});
        const data=await res.json();
        setProcoreConnected(!!data.connected);
      }catch{setProcoreConnected(false);}
    })();
  },[activeTab]);
  const handleSaveAsNewProject=()=>{
    if(!cloudStorage)return;
    setShowFileMenu(false);
    const defaultName=projectName.match(/\s+copy(\s+\d+)?$/i)?projectName:`${projectName} Copy`;
    setCopyDialogName(defaultName);
    setShowCopyDialog(true);
  };
  const handleConfirmCopy=async()=>{
    if(!cloudStorage)return;
    const name=copyDialogName.trim()||`${projectName} Copy`;
    setShowCopyDialog(false);
    showToast("Creating copy…");
    const state={...fullState(),projectName:name};
    const newId=await cloudStorage.createProject(state);
    if(newId){showToast(`"${name}" created — opening project picker`);setTimeout(()=>onBackToProjects?.(),800);}
    else showToast("Failed to create copy — try again");
  };
  const handleAiImport=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    e.target.value="";
    // Size check (3MB max due to Vercel 4.5MB body limit + base64 overhead)
    if(file.size>3*1024*1024){showToast("File too large — 3 MB maximum");return;}
    setAiImporting(true);
    try{
      const ext=file.name.split(".").pop()?.toLowerCase()||"";
      let content:string;let fileType:string;
      if(ext==="pdf"){
        // Extract text client-side to avoid Vercel timeout with large PDF payloads
        const pdfjs=await import("pdfjs-dist");
        const workerMod=await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        pdfjs.GlobalWorkerOptions.workerSrc=workerMod.default;
        const buf=await file.arrayBuffer();
        const pdf=await pdfjs.getDocument({data:buf}).promise;
        let text="";
        for(let i=1;i<=pdf.numPages;i++){const page=await pdf.getPage(i);const tc=await page.getTextContent();text+=tc.items.map((item:any)=>item.str).join(" ")+"\n";}
        content=text;
        fileType="text";
      }else{
        // Excel or CSV — parse to CSV text via xlsx
        const XLSX=await import("xlsx");
        const buf=await file.arrayBuffer();
        const wb=XLSX.read(buf,{type:"array"});
        const sheet=wb.Sheets[wb.SheetNames[0]];
        content=XLSX.utils.sheet_to_csv(sheet);
        fileType=ext==="csv"?"csv":"excel";
      }
      // Get auth token
      const {supabase:sb}=await import("./lib/supabase");
      const {data:{session}}=await sb.auth.getSession();
      if(!session?.access_token){showToast("Please sign in to use AI Import");setAiImporting(false);return;}
      // Call API (streaming SSE)
      const resp=await fetch("/api/ai-import",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${session.access_token}`},
        body:JSON.stringify({content,fileType,fileName:file.name}),
      });
      if(!resp.ok){
        const err=await resp.json().catch(()=>({error:"Import failed"}));
        throw new Error(err.error||"Import failed");
      }
      // Read SSE stream
      const reader=resp.body!.getReader();
      const decoder=new TextDecoder();
      let buf2="",result:any=null,importWarnings:any=null;
      while(true){
        const{done,value}=await reader.read();
        if(done)break;
        buf2+=decoder.decode(value,{stream:true});
        const lines=buf2.split("\n");
        buf2=lines.pop()||"";
        for(const line of lines){
          if(!line.startsWith("data: "))continue;
          try{
            const evt=JSON.parse(line.slice(6));
            if(evt.type==="complete"){result=evt.projectState;importWarnings=evt.warnings||null;}
            if(evt.type==="error")throw new Error(evt.error);
          }catch(pe:any){if(pe.message&&!pe.message.includes("JSON"))throw pe;}
        }
      }
      if(!result)throw new Error("No result received from AI");
      // Surface server-side sanitization warnings — flagged items are marked Needs Review
      if(importWarnings&&(importWarnings.invalidNumbers||importWarnings.suspectValues||importWarnings.duplicatesRemoved)){
        const n=(importWarnings.invalidNumbers||0)+(importWarnings.suspectValues||0);
        const d=importWarnings.duplicatesRemoved||0;
        showToast(`Import flagged ${n} item(s) as Needs Review${d?` and removed ${d} duplicate(s)`:""} — please verify`);
      }
      // Save as new cloud project
      if(cloudStorage){
        const newId=await cloudStorage.createProject(result);
        if(newId){
          showToast(`Imported "${result.projectName}" successfully`);
          if(onBackToProjects)onBackToProjects();
        }else throw new Error("Failed to save project");
      }else{
        // Offline fallback: load directly into current view
        resetHistoryRef.current=true;
        setProjectName(result.projectName||file.name);
        setBuildingArea(result.buildingArea||0);setDuration(result.duration||12);
        setOverhead(result.overhead||10);setProfit(result.profit||8);setContingency(result.contingency||5);setMarkupMode(result.markupMode||"both");
        setProposals(result.proposals||[{id:"p1",name:"General Construction",color:0}]);
        setProposalAreas(result.proposalAreas||{});
        if(result.divisionSettings)setDivisionSettings(result.divisionSettings);
        if(result.subSettings)setSubSettings(result.subSettings);
        setDivisionsRaw(result.divisions||[]);setItemStateRaw(result.itemState||{});
        setProjectExclusions(result.projectExclusions||[]);
        setActiveDiv(result.divisions?.[0]?.id||"div1");setActiveTab("summary");
        showToast(`Imported "${result.projectName}" successfully`);
      }
    }catch(err:any){
      console.error("AI import error:",err);
      showToast(err.message||"Import failed — please try again");
    }finally{setAiImporting(false);}
  };
  const handleNewProject=()=>{
    const preset=presets.find(p=>p.id===newProjPresetId)||presets[0]||DEFAULT_PRESETS[0];
    const divSettings=ALL_DIVISIONS.map(d=>({id:d.id,enabled:(preset.divisions||[]).includes(d.id)}));
    const ss=Object.fromEntries(Object.entries(DEFAULT_SUBS).map(([k,v])=>[k,v.map(s=>({...s}))]));
    const {divisions:divs,itemState:is}=buildFreshProject(divSettings,ss);
    resetHistoryRef.current=true;
    setProjectName(newProjName.trim()||"New Project");setBuildingArea(0);
    setDuration(12);setOverhead(preset.overhead||10);setProfit(preset.profit||8);setContingency(preset.contingency||5);
    setProposalAreas({});setDivisionSettings(divSettings);setSubSettings(ss);
    setDivisionsRaw(divs);setItemStateRaw(is);
    // A brand-new project has nothing to summarise, and an empty summary
    // reads as broken where an empty grid reads as a form.
    setActiveDiv(divs[0]?.id);setActiveTab("estimate");setShowFileMenu(false);
    setShowNewProjDialog(false);setNewProjName("");
    showToast("New project created");
  };
  const handleOpenSandbox=()=>{
    const sb=buildSandboxProject();
    resetHistoryRef.current=true;
    setProjectName(sb.projectName);setBuildingArea(sb.buildingArea);setDuration(sb.duration);
    setOverhead(sb.overhead);setProfit(sb.profit);setContingency(sb.contingency);
    setProposals(sb.proposals);setProposalAreas(sb.proposalAreas||{});
    setDivisionsRaw(sb.divisions);setItemStateRaw(sb.itemState);
    if(sb.projectSnapshots)setProjectSnapshots(sb.projectSnapshots);
    // Bid / procurement data
    if(sb.bidScopes)setBidScopes(sb.bidScopes);
    if(sb.bidPartners)setBidPartners(sb.bidPartners);
    if(sb.bids)setBids(sb.bids);
    if(sb.rfis)setRfis(sb.rfis);
    // Logistics plan (fake site plan + icons/zones for demo)
    if(sb.siteLogistics)setSiteLogistics(sb.siteLogistics as SiteLogistics);
    setActiveDiv(sb.divisions[0]?.id);setActiveTab("summary");
    // Seed the org-wide directory with sandbox partners if it's currently empty,
    // so the trade-partner picker is populated when exploring the demo project.
    setOrgPartners(prev=>prev.length>0?prev:SANDBOX_ORG_PARTNERS);
    showToast("Demo project loaded");
  };
  const savePreset=(preset)=>setPresets(prev=>prev.map(p=>p.id===preset.id?preset:p));
  const addPreset=(name)=>{
    if(!name.trim())return;
    const id="preset_"+Date.now();
    setPresets(prev=>[...prev,{id,name:name.trim(),overhead:10,profit:8,contingency:5,divisions:[...DEFAULT_PRESETS[0].divisions]}]);
    setNewPresetName("");setShowNewPresetForm(false);setEditingPresetId(id);
  };
  const deletePreset=(id)=>setPresets(prev=>prev.filter(p=>p.id!==id||p.builtIn));
  const duplicatePreset=(p)=>{
    const id="preset_"+Date.now();
    setPresets(prev=>[...prev,{...p,id,name:p.name+" (Copy)",builtIn:false}]);
    setEditingPresetId(id);
  };
  const summarizeState=useCallback(state=>{
    const byCode:Record<string,{code:string;label:string;total:number}>={};
    if(!state?.divisions||!state?.itemState)return byCode;
    state.divisions.forEach(div=>{
      div.subcategories.forEach(sub=>{
        const code=sub.costCode||"00-000";
        const label=`${sub.costCode||"--"} — ${sub.name} (${div.code})`;
        let total=0;
        [...walkItems(sub.rows)].forEach(item=>{
          const li=state.itemState[item.id]||{};
          if(li.included!==false)total+=(li.qty||0)*(li.unitCost||0);
        });
        if(!byCode[code])byCode[code]={code,label,total:0};
        byCode[code].total+=total;
      });
    });
    return byCode;
  },[]);

  const currentSummary=useMemo(()=>summarizeState({divisions,itemState}),[divisions,itemState,summarizeState]);

  // Build a labeled list of selectable "versions" for analysis — oldest first, current last
  const analysisOptions = useMemo(()=>{
    const snaps=[...projectSnapshots]
      .sort((a,b)=>((a.date||a.createdAt||"") < (b.date||b.createdAt||"") ? -1 : 1))
      .map(s=>({id:s.id,label:s.label,date:s.date,summary:summarizeState(s.state),isLive:false}));
    return [...snaps,{id:"current",label:`Current — ${projectName}`,summary:currentSummary,isLive:true,date:null}];
  },[projectSnapshots,currentSummary,projectName,summarizeState]);

  const saveSnapshot=async()=>{
    const label=snapLabel.trim()||`${projectName} — ${new Date().toLocaleDateString()}`;
    const fs=fullState();
    const snapState=JSON.parse(JSON.stringify({...fs,projectSnapshots:undefined}));
    if(cloudProjectId&&cloudStorage){
      try{
        await cloudStorage.saveSnapshot(cloudProjectId,label,snapState);
        const fresh=await cloudStorage.listSnapshots(cloudProjectId);
        setProjectSnapshots(fresh.map((s:any)=>({id:s.id,label:s.label,date:s.created_at,locked:true,state:s.state})));
        setSnapLabel("");setShowSnapDialog(false);showToast(`Snapshot "${label}" saved`);
      }catch(e:any){
        showToast(`⚠ Snapshot failed: ${e?.message||"unknown error"}`);
      }
    } else {
      const snap={id:uid(),label,date:new Date().toISOString(),locked:true,state:snapState};
      setProjectSnapshots(prev=>[snap,...prev]);
      setSnapLabel("");setShowSnapDialog(false);showToast(`Snapshot "${label}" saved`);
    }
  };
  const deleteSnapshot=async(id:string)=>{
    if(cloudProjectId&&cloudStorage){
      if(cloudStorage.softDeleteSnapshotById){
        const result=await cloudStorage.softDeleteSnapshotById(id);
        if(!result.ok){
          if(result.migrationMissing){
            showToast("⚠ Run migration 010 in Supabase SQL Editor to enable archiving");
          } else {
            showToast("⚠ Run migration 011 in Supabase SQL Editor to enable archiving");
          }
          return;
        }
      }
      const fresh=await cloudStorage.listSnapshots(cloudProjectId);
      setProjectSnapshots(fresh.map((s:any)=>({id:s.id,label:s.label,date:s.created_at,locked:true,state:s.state})));
      if(cloudStorage.listDeletedSnapshots){
        const deleted=await cloudStorage.listDeletedSnapshots(cloudProjectId);
        setDeletedSnapshots((deleted||[]).map((s:any)=>({id:s.id,label:s.label,date:s.created_at,deletedAt:s.deleted_at,locked:true,state:s.state})));
      }
    } else {
      setProjectSnapshots(prev=>prev.filter((s:any)=>s.id!==id));
    }
    if(analysisLeft===id)setAnalysisLeft("current");
    if(analysisRight===id)setAnalysisRight(null);
    showToast("Snapshot archived");
  };
  const restoreSnapshot=async(id:string)=>{
    if(!cloudProjectId||!cloudStorage||!cloudStorage.restoreSnapshotById)return;
    try{
      await cloudStorage.restoreSnapshotById(id);
      const fresh=await cloudStorage.listSnapshots(cloudProjectId);
      setProjectSnapshots(fresh.map((s:any)=>({id:s.id,label:s.label,date:s.created_at,locked:true,state:s.state})));
      const deleted=await cloudStorage.listDeletedSnapshots(cloudProjectId);
      setDeletedSnapshots((deleted||[]).map((s:any)=>({id:s.id,label:s.label,date:s.created_at,deletedAt:s.deleted_at,locked:true,state:s.state})));
      showToast("Snapshot restored");
    }catch(e:any){
      showToast(`⚠ Restore failed: ${e?.message||"unknown error"}`);
    }
  };
  const renameSnapshot=async(id:string,newLabel:string,newDate?:string)=>{
    const trimmed=newLabel.trim();if(!trimmed)return;
    if(cloudProjectId&&cloudStorage){
      try{
        await cloudStorage.renameSnapshotById(id,trimmed);
        const fresh=await cloudStorage.listSnapshots(cloudProjectId);
        setProjectSnapshots(fresh.map((s:any)=>({id:s.id,label:s.label,date:s.created_at,locked:true,state:s.state})));
        setRenamingSnapId(null);setRenameSnapLabel("");setRenameSnapDate("");
        showToast(`Snapshot updated`);
      }catch(e:any){
        showToast(`⚠ Rename failed: ${e?.message||"unknown error"}`);
      }
    } else {
      const updatedDate=newDate?new Date(newDate+"T00:00:00Z").toISOString():undefined;
      setProjectSnapshots(prev=>prev.map((s:any)=>s.id===id?{...s,label:trimmed,...(updatedDate?{date:updatedDate}:{})}:s));
      setRenamingSnapId(null);setRenameSnapLabel("");setRenameSnapDate("");
      showToast(`Snapshot updated`);
    }
  };
  const handleOpenArchive=(snap)=>{
    const f=snap.state;if(!f)return;
    resetHistoryRef.current=true;
    // Keep the project's own name. Snapshots taken before the name was
    // stored have none of their own, and the old fallback put the literal
    // word "Archived" in the title bar -- throwing away the one piece of
    // context the header exists to give, to say something the amber
    // "Viewing Snapshot - Read Only" banner is already saying louder.
    setProjectName(f.projectName||projectName||"Untitled");setBuildingArea(f.buildingArea||0);
    setDuration(f.duration||12);setOverhead(f.overhead||10);
    setProfit(f.profit||8);setContingency(f.contingency||5);setMarkupMode(f.markupMode||"both");
    setProposals(f.proposals||DEFAULT_PROPOSALS);setProposalAreas(f.proposalAreas||{});
    setDivisionSettings(f.divisionSettings||ALL_DIVISIONS.map(d=>({id:d.id,enabled:d.enabled})));
    setSubSettings(f.subSettings||{});
    setDivisionsRaw(f.divisions);setItemStateRaw(migrateItemStateAltTypes(f.itemState));
    setProjectExclusions(f.projectExclusions||[]);
    setSchedule(f.schedule||DEFAULT_SCHEDULE);setCostSchedule(f.costSchedule||{});
    setActiveDiv(f.divisions[0]?.id||"div1");setActiveTab("summary");
    setIsLocked(true);setLockedLabel(snap.label||null);setLockedDate(snap.date||null);
    isViewingSnapshot.current=true;
    showToast(`Viewing snapshot: "${snap.label}"`);
  };
  const handleReturnToCurrentEstimate=async()=>{
    if(cloudProjectId&&cloudStorage){
      // Reload the live project state from Supabase — guarantees freshest version
      const s=await cloudStorage.loadProjectById(cloudProjectId);
      if(s){
        resetHistoryRef.current=true;
        if(s.projectName)setProjectName(s.projectName);
        if(s.address!=null)setAddress(s.address||"");
        if(s.buildingArea!=null)setBuildingArea(s.buildingArea);
        if(s.duration!=null)setDuration(s.duration);
        if(s.overhead!=null)setOverhead(s.overhead);
        if(s.profit!=null)setProfit(s.profit);
        if(s.contingency!=null)setContingency(s.contingency);
        setAltPackages(s.alternates||[]);
        setContingencyPolicy({...DEFAULT_CONTINGENCY_POLICY,...(s.contingencyPolicy||{})});
      setEscalation({...DEFAULT_ESCALATION,...(s.escalation||{})});
      setStaffPlan(s.staffPlan||[]);
        if(s.markupMode)setMarkupMode(s.markupMode);
        setProposals(s.proposals?.length?s.proposals:DEFAULT_PROPOSALS);
        setProposalAreas(s.proposalAreas||{});
        setDivisionSettings(s.divisionSettings||[]);
        setSubSettings(s.subSettings||{});
        setDivisionsRaw(s.divisions||[]);
        setItemStateRaw(s.itemState||{});
        if(s.projectExclusions)setProjectExclusions(s.projectExclusions);
        setSchedule(s.schedule||DEFAULT_SCHEDULE);setCostSchedule(s.costSchedule||{});
        setActiveDiv((s.divisions||[])[0]?.id||"div1");
      }
    }
    setIsLocked(false);setLockedLabel(null);setLockedDate(null);
    isViewingSnapshot.current=false;
    setActiveTab("summary");
    showToast("Returned to current estimate");
  };
  /**
   * Files a parsed estimate into this project's history.
   *
   * Locked on arrival: an imported version is a record of something that was
   * already issued, not a draft, and editing it would quietly rewrite history.
   */
  const importVersion=(state:any,label:string,dateISO:string)=>{
    if(!state?.divisions||!state?.itemState){showToast("That file did not contain an estimate");return;}
    const snap={
      id:uid(),label,date:dateISO,locked:true,
      state:{
        divisions:state.divisions,itemState:state.itemState,
        projectName:state.projectName||projectName,
        buildingArea:state.buildingArea||0,duration:state.duration||12,
        overhead:state.overhead??10,profit:state.profit??8,contingency:state.contingency??5,
        proposals:state.proposals||DEFAULT_PROPOSALS,proposalAreas:state.proposalAreas||{},
        divisionSettings:state.divisionSettings||[],subSettings:state.subSettings||{},
      },
    };
    // Newest first, matching how snapshots are listed everywhere else.
    setProjectSnapshots(prev=>[snap,...prev].sort((a,b)=>(Date.parse(b.date)||0)-(Date.parse(a.date)||0)));
  };
  const downloadJSON=(data:unknown,filename:string)=>{
    const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=filename;a.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000);
  };
  const exportSnapshot=snap=>downloadJSON(snap,`${snap.label.replace(/[^a-z0-9]/gi,"_")}.oe-snapshot.json`);

  /* ── Add Best Practice to Estimate ── */
  const handleAddBpToEstimate=useCallback((bp:BestPractice)=>{
    snapshot();
    const divData=divisions.find(d=>d.id===activeDiv);
    if(!divData?.subcategories?.length)return;
    const sub=divData.subcategories[0];
    const newId=uid();
    const newItem=mkItem(newId,bp.description,{qty:1,unit:"LS",unitCost:0,proposal:proposals[0]?.id||"p1",notes:bp.sourceNote||""});
    setDivisions((p:any)=>p.map((d:any)=>d.id!==activeDiv?d:{...d,subcategories:d.subcategories.map((s:any,i:number)=>i===0?{...s,rows:[...s.rows,newItem]}:s)}));
    setItemState((p:any)=>({...p,[newId]:{included:true,qty:1,unit:"LS",unitCost:0,description:bp.description,costStatus:"Needs Review",proposal:proposals[0]?.id||"p1",notes:bp.sourceNote||""}}));
    setExpandedSubs((p:any)=>({...p,[sub.id]:true}));
    setBpNotedSet(prev=>new Set([...prev,bp.id]));
  },[snapshot,divisions,activeDiv,proposals,setDivisions,setItemState]);

  /* mutations */
  const addSub=(divId,preset?:{name:string;costCode:string})=>{
    snapshot();
    const newSub={id:uid(),name:preset?.name||"New Subcategory",costCode:preset?.costCode||"00-000",rows:[]};
    setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:[...d.subcategories,newSub]}));
    setExpandedSubs(p=>({...p,[newSub.id]:true}));setEditSubId(newSub.id);
  };
  const removeSub=(divId,subId)=>{snapshot();setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.filter(s=>s.id!==subId)}));};
  const renameSub=(divId,subId,name)=>setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,name})}));
  const updateSubCostCode=(divId,subId,costCode)=>setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,costCode})}));
  // Pin one subcategory to a phase other than its division's, or clear it back
  // to following the division. Lives on the subcategory, so it persists,
  // snapshots and syncs with the estimate for free.
  const setSubPhase=(divId:string,subId:string,phaseId?:string)=>{snapshot();setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,phaseId})}));};

  const handleProposalEmbed=useCallback((divId:string,subId:string,embedItems:Omit<EmbedItem,"include">[],sourceInfo?:{tradePartner:string;estimateDate:string})=>{
    snapshot();
    // Resolve or create a TradeSource if sourceInfo provided
    let sourceId="";
    if(sourceInfo?.tradePartner?.trim()){
      setTradeSources(prev=>{
        const existing=prev.find(s=>s.tradePartner.trim().toLowerCase()===sourceInfo.tradePartner.trim().toLowerCase());
        if(existing){sourceId=existing.id;return prev;}
        sourceId=uid();
        return[...prev,{id:sourceId,tradePartner:sourceInfo.tradePartner.trim(),estimateDate:sourceInfo.estimateDate||"",notes:""}];
      });
    }
    const newRows=embedItems.map(it=>{const id=uid();return mkItem(id,it.description,{qty:it.qty,unit:it.unit,unitCost:it.unitCost,proposal:it.proposal,notes:it.notes});});
    const newStates:Record<string,any>={};
    embedItems.forEach((it,idx)=>{
      newStates[newRows[idx].id]={qty:it.qty,unitCost:it.unitCost,included:true,unit:it.unit,proposal:it.proposal,description:it.description,notes:it.notes,costStatus:"",altType:it.altType||"",altStatus:it.altStatus||"",sourceId};
    });
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return{...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        return{...s,rows:[...s.rows,...newRows]};
      })};
    }));
    setItemState(p=>({...p,...newStates}));
    setExpandedSubs(p=>({...p,[subId]:true}));
    showToast(`${embedItems.length} line item${embedItems.length!==1?"s":""} embedded${sourceInfo?.tradePartner?` from ${sourceInfo.tradePartner}`:""}`);
  },[snapshot,showToast]);

  const handleBidPdf=useCallback(async(file:File,mode?:"ai"|"attach")=>{
    if(file.type!=="application/pdf"){setBidImportError("Please select a PDF file.");setBidImportStage("error");return;}
    pendingBidFileRef.current=file; // cache for storage upload after bid is saved
    setBidImportPdfName(file.name);setBidImportError("");
    // ── Attach-only mode: skip AI, go straight to confirm screen ──
    const effectiveMode=mode??bidImportMode;
    if(effectiveMode==="attach"){
      setBidAttachTotal(0);setBidAttachNotes("");
      setBidImportStage("attach" as any);
      return;
    }
    setBidImportStage("scanning");
    try{
      const base64=await new Promise<string>((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=()=>resolve((reader.result as string).split(",")[1]);
        reader.onerror=reject;
        reader.readAsDataURL(file);
      });
      const {supabase:sb}=await import("./lib/supabase");
      if(!sb)throw new Error("Supabase not configured.");
      const scope=bidScopes.find(s=>s.id===bidImportScopeId);
      const divName=scope?.divisionIds[0]?(divisions.find(d=>d.id===scope.divisionIds[0])?.name||""):"";
      const {data,error}=await sb.functions.invoke("parse-proposal",{
        body:{pdfBase64:base64,subcategoryName:scope?.name||"",divisionName:divName},
      });
      if(error)throw new Error(error.message||"Edge function error");
      if(data.error)throw new Error(data.error+(data.detail?`: ${data.detail}`:""));
      const parsed:BidItem[]=(data.items||[]).map((it:any)=>({
        id:uid(),description:it.description||"",qty:it.qty||1,unit:it.unit||"LS",unitCost:it.unitCost||0,notes:it.notes||"",
      }));
      setManualBidItems(parsed.length>0?parsed:[{id:uid(),description:"",qty:1,unit:"LS",unitCost:0,notes:""}]);
      setBidImportStage("preview");
    }catch(e:any){
      setBidImportError(String(e?.message||e));
      setBidImportStage("error");
    }
  },[bidImportScopeId,bidScopes,divisions]);

  // ── Upload PDF to Supabase Storage (non-blocking, cloud users only) ──
  const uploadProposalToStorage=useCallback(async(file:File,bid:Bid)=>{
    if(!cloudProjectId||!user?.id)return;
    try{
      const{supabase:sb}=await import("./lib/supabase");
      if(!sb)return;
      const scope=bidScopes.find(s=>s.id===bid.scopeId);
      const partner=findPartner(bid.partnerId);
      const safe=(s:string)=>s.replace(/[^a-z0-9._-]/gi,"_").slice(0,60);
      const datePfx=bid.date.slice(0,10);
      const path=`${user.id}/${cloudProjectId}/${safe(scope?.name||"scope")}/${safe(partner?.name||"partner")}/${datePfx}_${safe(file.name)}`;
      // Create bucket if it doesn't exist yet (no-op if already exists)
      await sb.storage.createBucket("proposals",{public:false}).catch(()=>{});
      const{error}=await sb.storage.from("proposals").upload(path,file,{upsert:true,contentType:"application/pdf"});
      if(error){console.warn("PDF upload failed:",error.message);return;}
      // Patch the bid with its storage path
      setBids(prev=>prev.map(b=>b.id===bid.id?{...b,pdfStoragePath:path}:b));
    }catch(err){console.warn("PDF upload error:",err);}
  },[cloudProjectId,user?.id,bidScopes,bidPartners]);

  // ── Resolve a bid's PDF to a viewable URL (cache → static demo URL → Supabase) ──
  const resolveBidPdfUrl=useCallback(async(bid:Bid):Promise<string|null>=>{
    const cached=bidPdfCache.current.get(bid.id);
    if(cached)return cached;
    // Static demo PDFs served from /public (no auth required)
    if(bid.pdfUrl){
      bidPdfCache.current.set(bid.id,bid.pdfUrl);
      return bid.pdfUrl;
    }
    if(bid.pdfStoragePath){
      try{
        const{supabase:sb}=await import("./lib/supabase");
        if(!sb)return null;
        const{data,error}=await sb.storage.from("proposals").createSignedUrl(bid.pdfStoragePath,3600);
        if(!error&&data?.signedUrl){
          bidPdfCache.current.set(bid.id,data.signedUrl); // cache the signed URL
          return data.signedUrl;
        }
      }catch{}
    }
    return null;
  },[]);

  // ── Open in-app PDF viewer for one or two bids ──
  const openBidPdfViewer=useCallback(async(bidIds:string[])=>{
    const items=await Promise.all(bidIds.map(async(bidId)=>{
      const bid=bids.find(b=>b.id===bidId);if(!bid)return null;
      const partner=findPartner(bid.partnerId);
      const title=`${partner?.name||"Unknown"} · v${bid.version} · ${bid.date.slice(0,10)}`;
      const url=await resolveBidPdfUrl(bid);
      if(!url)return null;
      return{bidId,url,title};
    }));
    const valid=items.filter(Boolean) as{bidId:string,url:string,title:string}[];
    if(!valid.length){showToast("No PDF stored for selected bid(s)");return;}
    setPdfViewerItems(valid);setPdfViewerOpen(true);
  },[bids,bidPartners,resolveBidPdfUrl,showToast]);

  // ── View a stored bid PDF (opens in-app viewer) ──
  const viewBidPdf=useCallback(async(bid:Bid)=>{
    await openBidPdfViewer([bid.id]);
  },[openBidPdfViewer]);

  // ── General conditions from the staffing plan ──────────────────────────────
  //
  // Writes ordinary line items into one subcategory of Division 01, replacing
  // whatever that subcategory held. Scoped to that bucket on purpose: a user's
  // own Division 01 rows -- permits, bonds, the things that are not staff --
  // are never touched, and re-running after a schedule change is therefore
  // safe rather than destructive.
  const applyStaffingPlan=(rows:GcStaffRow[])=>{
    snapshot();
    const lines=gcLineItems(rows);
    const prop=proposals[0]?.id||"p1";
    const built=lines.map(l=>({item:mkItem(uid(),l.description,{qty:l.qty,unit:l.unit,unitCost:l.unitCost,proposal:prop}),l}));
    setDivisions(p=>p.map(d=>{
      if(d.code!=="Div 01")return d;
      const subs=d.subcategories.some(x=>x.name===GC_SUB_NAME)
        ? d.subcategories
        : [...d.subcategories,{id:uid(),name:GC_SUB_NAME,costCode:"01-300",rows:[]}];
      return {...d,subcategories:subs.map(sub=>
        sub.name!==GC_SUB_NAME?sub:{...sub,rows:built.map(b=>b.item)})};
    }));
    setItemState(p=>{
      const next={...p};
      for(const b of built){
        next[b.item.id]={qty:b.l.qty,unitCost:b.l.unitCost,included:true,unit:b.l.unit,
          proposal:prop,description:b.l.description,notes:"",costStatus:"",altType:""};
      }
      return next;
    });
    setStaffPlan(rows);
    setShowStaffingPlan(false);
    showToast(`Division 01 → ${GC_SUB_NAME}: ${lines.length} line${lines.length===1?"":"s"} written`);
  };
  /** Rows whose months no longer match the project duration. Said, never fixed. */
  const staffPlanStale=useMemo(()=>gcStaleRows(staffPlan,duration),[staffPlan,duration]);

  const addItem=useCallback((divId,subId,groupId=null)=>{
    snapshot();
    const newId=uid();const blank=mkItem(newId,"");
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return {...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        if(groupId)return{...s,rows:s.rows.map(r=>r.type==="group"&&r.id===groupId?{...r,children:[...r.children,blank]}:r)};
        return{...s,rows:[...s.rows,blank]};
      })};
    }));
    setItemState(p=>({...p,[newId]:{qty:1,unitCost:0,included:true,unit:"Ea",proposal:proposals[0]?.id||"p1",description:"",notes:"",costStatus:"",altType:""}}));
    setExpandedSubs(p=>({...p,[subId]:true}));
    if(cloudProjectId&&!isSandbox)auditEvent("item.create","project",cloudProjectId);
    setTimeout(()=>{
      const el=document.querySelector(`[data-itemid="${newId}"] .desc-input`) as HTMLElement | null;
      if(el)el.focus();
    },60);
  },[snapshot,setDivisions,setItemState,proposals,cloudProjectId,isSandbox]);

  const removeItem=(divId,subId,itemId)=>{
    snapshot();
    if(cloudProjectId&&!isSandbox){
      const cur=itemState[itemId];
      auditEvent("item.delete","project",cloudProjectId,{itemDesc:cur?.description||undefined,field:"unitCost",oldValue:cur?cur.qty*cur.unitCost:undefined});
    }
    const prune=rows=>rows.filter(r=>r.id!==itemId).map(r=>r.type==="group"?{...r,children:r.children.filter(c=>c.id!==itemId)}:r);
    setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,rows:prune(s.rows)})}));
    setItemState(p=>{const n={...p};delete n[itemId];return n;});
  };
  const cloneItem=(divId:string,subId:string,itemId:string)=>{
    const li=itemState[itemId];if(!li)return;
    snapshot();
    const newId=uid();
    const newState={...li,description:(li.description||"")+" (copy)"};
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return{...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        const insertAfter=(rows:any[]):any[]=>{
          const result:any[]=[];
          for(const r of rows){
            result.push(r);
            if(r.id===itemId)result.push(mkItem(newId,newState.description,{qty:li.qty,unit:li.unit,unitCost:li.unitCost,proposal:li.proposal,notes:li.notes}));
            if(r.type==="group"&&r.children){const last=result[result.length-1];result[result.length-1]={...last,children:insertAfter(last.children)};}
          }
          return result;
        };
        return{...s,rows:insertAfter(s.rows)};
      })};
    }));
    setItemState(p=>({...p,[newId]:newState}));
  };
  const addLaborItem=useCallback((divId:string,subId:string,rate:{id:string;role:string;rate:number})=>{
    snapshot();
    const newId=uid();const blank=mkItem(newId,rate.role);
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return{...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        return{...s,rows:[...s.rows,blank]};
      })};
    }));
    setItemState(p=>({...p,[newId]:{qty:1,unitCost:rate.rate,included:true,unit:"hr",proposal:proposals[0]?.id||"p1",description:rate.role,notes:"",costStatus:"",altType:""}}));
    setExpandedSubs(p=>({...p,[subId]:true}));
    setTimeout(()=>{const el=document.querySelector(`[data-itemid="${newId}"] .desc-input`);if(el)(el as HTMLElement).focus();},60);
  },[snapshot,setDivisions,setItemState,proposals]);

  const addGroup=(divId,subId)=>{
    snapshot();
    const gid=uid();const g={type:"group",id:gid,name:"New Group",children:[]};
    setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,rows:[...s.rows,g]})}));
    setExpandedGrps(p=>({...p,[gid]:true}));setExpandedSubs(p=>({...p,[subId]:true}));
  };
  // Dissolve a group: lift its children back to the parent subcategory rows in place of the group.
  // Items are preserved — only the group wrapper is removed.
  const removeGroup=(divId,subId,groupId)=>{snapshot();setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,rows:s.rows.flatMap(r=>r.id===groupId?(r.children||[]):r)})}))};
  const renameGroup=(divId,subId,groupId,name)=>setDivisions(p=>p.map(d=>d.id!==divId?d:{...d,subcategories:d.subcategories.map(s=>s.id!==subId?s:{...s,rows:s.rows.map(r=>r.type==="group"&&r.id===groupId?{...r,name}:r)})}));

  const indentItemToGroup=(divId,subId,itemId)=>{
    snapshot();
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return {...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        const idx=s.rows.findIndex(r=>r.id===itemId);if(idx<0)return s;
        const item=s.rows[idx];const prev=idx>0?s.rows[idx-1]:null;
        if(prev&&prev.type==="group"){
          const nr=s.rows.filter((_,i)=>i!==idx).map(r=>r.type==="group"&&r.id===prev.id?{...r,children:[...r.children,item]}:r);
          setExpandedGrps(p2=>({...p2,[prev.id]:true}));return{...s,rows:nr};
        }else{
          const gid=uid();const ng={type:"group",id:gid,name:"Group",children:[item]};
          const nr=[...s.rows];nr[idx]=ng;
          setExpandedGrps(p2=>({...p2,[gid]:true}));return{...s,rows:nr};
        }
      })};
    }));
  };
  const unindentItemFromGroup=(divId,subId,groupId,itemId)=>{
    snapshot();
    setDivisions(p=>p.map(d=>{
      if(d.id!==divId)return d;
      return {...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==subId)return s;
        const item=[...walkItems(s.rows)].find(r=>r.id===itemId);if(!item)return s;
        const gIdx=s.rows.findIndex(r=>r.id===groupId);
        const ng={...s.rows[gIdx],children:s.rows[gIdx].children.filter(c=>c.id!==itemId)};
        const nr=[...s.rows];nr[gIdx]=ng;nr.splice(gIdx+1,0,item);
        return{...s,rows:nr};
      })};
    }));
  };

  /* ── Row clipboard & reorder helpers ── */
  const findRowById=useCallback((targetId:string)=>{
    for(const div of divisions){
      for(const sub of div.subcategories){
        for(let i=0;i<sub.rows.length;i++){
          const r=sub.rows[i];
          if(r.id===targetId)return{row:r,divId:div.id,subId:sub.id,parentGroupId:null,index:i};
          if(r.type==="group"){
            for(let j=0;j<r.children.length;j++){
              if(r.children[j].id===targetId)return{row:r.children[j],divId:div.id,subId:sub.id,parentGroupId:r.id,index:j};
            }
          }
        }
      }
    }
    return null;
  },[divisions]);

  const insertRowsAfter=useCallback((targetDivId:string,targetSubId:string,afterId:string,newRows:any[])=>{
    setDivisions(p=>p.map(d=>{
      if(d.id!==targetDivId)return d;
      return{...d,subcategories:d.subcategories.map(s=>{
        if(s.id!==targetSubId)return s;
        // Check if afterId is inside a group
        for(let i=0;i<s.rows.length;i++){
          const r=s.rows[i];
          if(r.id===afterId){
            const nr=[...s.rows];nr.splice(i+1,0,...newRows);return{...s,rows:nr};
          }
          if(r.type==="group"){
            const ci=r.children.findIndex(c=>c.id===afterId);
            if(ci>=0){
              // Insert after item inside group — only items go into groups, groups go after the group
              const items=newRows.filter(nr=>nr.type==="item");
              const groups=newRows.filter(nr=>nr.type==="group");
              const newChildren=[...r.children];newChildren.splice(ci+1,0,...items);
              const updatedRows=[...s.rows];updatedRows[i]={...r,children:newChildren};
              if(groups.length)updatedRows.splice(i+1,0,...groups);
              return{...s,rows:updatedRows};
            }
          }
        }
        // Fallback: append to end
        return{...s,rows:[...s.rows,...newRows]};
      })};
    }));
  },[setDivisions]);

  const removeRowsByIds=useCallback((idSet:Set<string>)=>{
    setDivisions(p=>p.map(d=>({...d,subcategories:d.subcategories.map(s=>({
      ...s,rows:s.rows.filter(r=>{
        if(idSet.has(r.id))return false;
        return true;
      }).map(r=>r.type==="group"?{...r,children:r.children.filter(c=>!idSet.has(c.id))}:r)
    }))})));
  },[setDivisions]);

  /* computed */
  const rowsTotal=useCallback(rows=>{
    let t=0;
    for(const r of rows){if(r.type==="group")t+=rowsTotal(r.children);else{const li=itemState[r.id]||{};if(li.included&&(!li.altType||(li.altType==="Alt"&&li.altStatus==="Accepted"))&&!isClarification(li.costStatus||""))t+=getItemTotal(li,itemState);}}
    return t;
  },[itemState]);

  const rowsContTotal=useCallback(rows=>{
    let t=0;
    for(const r of rows){if(r.type==="group")t+=rowsContTotal(r.children);else{const li=itemState[r.id]||{};if(li.included&&(!li.altType||(li.altType==="Alt"&&li.altStatus==="Accepted")))t+=calcItemCont(li,contingency,itemState);}}
    return t;
  },[itemState,contingency]);

  const divTotals=useMemo(()=>computeDivTotals(divisions,itemState,proposals),[itemState,divisions,proposals]);


  // Subcategory-level totals for schedule phase linking
  const subTotals=useMemo(()=>computeSubTotals(divisions,itemState),[itemState,divisions]);

  const grandTotal=useMemo(()=>computeGrandTotal(divTotals),[divTotals]);

  // Subtotal of items that participate in OH/profit markup (excludes items flagged excludeFromMarkup)
  const markupableSubtotal=useMemo(()=>computeMarkupableSubtotal(divisions,itemState),[divisions,itemState]);

  // Alternates: deduct alternates are treated as negative values
  const altTotals=useMemo(()=>computeAltTotals(divisions,itemState),[divisions,itemState]);

  // ── Alternates as decisions ────────────────────────────────────────────────
  // The same lines, gathered into the packages an owner is actually asked
  // about. A line that has never been grouped rolls up as a package of one, so
  // this reads existing projects without anybody migrating anything.
  const altRollups=useMemo(()=>rollUpAlternates(divisions,itemState,altPackages),[divisions,itemState,altPackages]);
  // The undo stack holds divisions+itemState, not the package list, so undoing
  // a combine puts the lines back and leaves the package record behind. That
  // package now owns no lines, and rollUpAlternates only emits packages that
  // do -- so it disappears from the UI and is dropped the next time the
  // project is rewritten. Harmless, and cheaper than widening every undo frame.
  const applyAlt=(r:{packages:AltPackage[];itemState:Record<string,any>})=>{
    if(isLocked)return;
    snapshot();
    setAltPackages(r.packages);
    setItemState(r.itemState);
  };
  const onAltStatus=(pkgId:string,status:AltStatus)=>applyAlt(setPackageStatus(altPackages,itemState,pkgId,status));
  const onAltSplit=(pkgId:string)=>applyAlt(splitPackage(altPackages,itemState,pkgId));
  const onAltRename=(pkgId:string,name:string)=>{
    if(isLocked)return;
    setAltPackages(p=>p.map(x=>x.id===pkgId?{...x,name:name.trim()||x.name}:x));
  };
  const onAltCombine=(pkgIds:string[],name:string)=>{
    const picked=altRollups.filter(r=>pkgIds.includes(r.pkg.id));
    const lineIds=picked.flatMap(r=>r.lines.map(l=>l.id));
    // The combined scope inherits a decision only when every piece already
    // carried the same one. Mixed answers become one open question, which is
    // the honest reading: nobody has decided the thing that now exists.
    const statuses=new Set(picked.map(r=>r.pkg.status));
    const status:AltStatus=statuses.size===1?[...statuses][0]:"TBD";
    // Packages that had their own record are absorbed; their lines move.
    const kept=altPackages.filter(p=>!pkgIds.includes(p.id));
    applyAlt(combineIntoPackage(kept,itemState,lineIds,name,status));
  };

  // What every line is carrying, whether or not the policy counts it. The
  // totals need the raw figure in order to say "$445,827 set on lines,
  // excluded by the current mode" rather than silently dropping it.
  const contingencyByPropRaw=useMemo(()=>computeContingencyByProp(divisions,itemState,contingency,proposals),[divisions,itemState,contingency,proposals]);

  // What the sheet is allowed to show. Zeroing it here rather than at each of
  // the dozen places that read it is what stops a column of per-line
  // contingency appearing beside a project reserve it does not add up to --
  // the numbers were real, but nothing on screen summed to anything.
  const contingencyByProp=useMemo(()=>contLive?contingencyByPropRaw
    :{byProp:{} as Record<string,number>,byDivByProp:{} as Record<string,Record<string,number>>,byDiv:{} as Record<string,number>,total:0},
    [contLive,contingencyByPropRaw]);

  // Sum of per-proposal areas (0 when none are set)
  const sumPropAreas=useMemo(()=>proposals.reduce((a,p)=>a+(proposalAreas[p.id]||0),0),[proposals,proposalAreas]);

  // ── Cost-confidence rollup ($-weighted) ──
  // Each cost-status bucket carries a weight: Hard Bid = full confidence,
  // Allowance = half, Other (unset) = quarter, Needs Review = none.
  // The score is the weighted sum / total $ → 0..100. Helps estimators see at a
  // glance how much of their number is firm vs. still soft.
  const confidenceRollup=useMemo(()=>{
    const buckets={"Hard Bid":0,"Allowance":0,"Needs Review":0,"Other":0} as Record<string,number>;
    const weights={"Hard Bid":1,"Allowance":.5,"Needs Review":0,"Other":.25} as Record<string,number>;
    let total=0;
    divisions.forEach(div=>div.subcategories.forEach(sub=>[...walkItems(sub.rows||[])].forEach(item=>{
      const li=itemState[item.id]||{};
      if(!li.included||(li.altType&&!(li.altType==="Alt"&&li.altStatus==="Accepted"))||isClarification(li.costStatus||""))return;
      const amt=getItemTotal(li,itemState);
      const k=li.costStatus&&buckets[li.costStatus]!==undefined?li.costStatus:"Other";
      buckets[k]+=amt;total+=amt;
    })));
    const score=total>0?Math.round((Object.entries(buckets).reduce((s,[k,v])=>s+v*weights[k],0)/total)*100):0;
    return{buckets,total,score};
  },[divisions,itemState]);

  // ── RFI cost-impact rollup by division ──
  // Sums up RFI.costImpact per division based on the originally linked items.
  // Distinguishes pending (open/answered) vs resolved (closed) impact so PMs can
  // see live exposure separately from realized changes.
  const rfiImpactByDivision=useMemo(()=>{
    const itemDivLookup=new Map<string,string>();
    divisions.forEach(div=>div.subcategories.forEach(sub=>[...walkItems(sub.rows||[])].forEach(item=>{
      itemDivLookup.set(item.id,div.id);
    })));
    const out:Record<string,{open:number;answered:number;closed:number;pending:number;resolved:number;count:number}>={};
    rfis.forEach(rfi=>{
      if(rfi.status==="void"||!rfi.linkedItemIds?.length)return;
      const impact=rfi.costImpact||0;
      // Distribute impact equally across linked items' divisions (most RFIs link to one item).
      const divIds=Array.from(new Set(rfi.linkedItemIds.map(id=>itemDivLookup.get(id)).filter(Boolean) as string[]));
      if(divIds.length===0)return;
      const share=impact/divIds.length;
      divIds.forEach(divId=>{
        if(!out[divId])out[divId]={open:0,answered:0,closed:0,pending:0,resolved:0,count:0};
        out[divId].count+=1;
        if(rfi.status==="open"){out[divId].open+=1;out[divId].pending+=share;}
        else if(rfi.status==="answered"){out[divId].answered+=1;out[divId].pending+=share;}
        else if(rfi.status==="closed"){out[divId].closed+=1;out[divId].resolved+=share;}
      });
    });
    const totals=Object.values(out).reduce((a,v)=>({pending:a.pending+v.pending,resolved:a.resolved+v.resolved,count:a.count+v.count,open:a.open+v.open}),{pending:0,resolved:0,count:0,open:0});
    return{byDiv:out,totals};
  },[rfis,divisions]);

  // Blended SF denominator: use sum of per-proposal areas when set; fallback to buildingArea
  const blendedSF=sumPropAreas>0?sumPropAreas:(buildingArea||0);

  // Per-division markupable subtotals (excludes excludeFromMarkup items)
  const divMarkupableSubtotals=useMemo(()=>computeDivMarkupableSubtotals(divisions,itemState),[divisions,itemState]);

  // Escalation, priced to the midpoint of construction and read from the
  // schedule, so it follows the job when the job moves.
  const escLive=useMemo(
    ()=>computeEscalation(grandTotal.subtotal,escalation,schedule?.projectStartDate,duration),
    [grandTotal.subtotal,escalation,schedule?.projectStartDate,duration]);
  const final=useMemo(()=>computeFinal(
    grandTotal.subtotal,markupableSubtotal,divMarkupableSubtotals,
    divisions,divisionMarkups,overhead,profit,markupMode,
    contingencyByPropRaw.total,blendedSF,contingencyPolicy,contingencyByPropRaw.byDiv,
    escLive.amount,
  ),[grandTotal,markupableSubtotal,divMarkupableSubtotals,divisions,divisionMarkups,overhead,profit,markupMode,contingencyByPropRaw,blendedSF,contingencyPolicy,escLive.amount]);

  const sfFor=p=>proposalAreas[p]>0?proposalAreas[p]:buildingArea;
  const activeDivData=divisions.find(d=>d.id===activeDiv)||divisions[0];
  // What the sidebar highlights: where you are scrolled to in the continuous
  // view, otherwise the division you selected.
  const sidebarDiv=(continuousEstimate&&visibleDiv)||activeDiv;
  // Keep the tracked division in the sidebar's own viewport. The sidebar list
  // scrolls independently of the estimate, so on a long project the highlight
  // would otherwise sit off-screen exactly when it is meant to tell you where
  // you are. "nearest" moves it the minimum amount, so it does not yank the
  // list around while the user is scrolling the sidebar themselves.
  const sidebarActiveRef=useRef<HTMLDivElement|null>(null);
  useEffect(()=>{
    if(!continuousEstimate)return;
    sidebarActiveRef.current?.scrollIntoView({block:"nearest"});
  },[sidebarDiv,continuousEstimate]);

  // ── Get Started checklist conditions ─────────────────────────────────────────
  const checklistHasName=projectName.trim().length>0&&projectName!=="New Project"&&projectName!=="Untitled";
  const checklistHasItem=useMemo(()=>Object.values(itemState).some((v:any)=>(v.qty>0||v.unitCost>0)),[itemState]);
  const checklistHasPackage=useMemo(()=>Object.values(itemState).some((v:any)=>!!v.proposal),[itemState]);
  const checklistHasMarkup=overhead>0||profit>0;
  const showChecklist=!checklistDismissed&&activeTab==="estimate"&&!sidebarCollapsed&&!isMobile;

  // ── Project chat context document ────────────────────────────────────────────
  const projectContextDoc=useMemo(()=>{
    const fmtC=(n:number)=>`$${Math.round(n).toLocaleString()}`;
    const parts:string[]=[];
    parts.push(`PROJECT: ${projectName}${address?`  |  ${address}`:""}`);
    parts.push(`Total: ${fmtC(final.total)}${buildingArea>0?`  |  $${(final.total/buildingArea).toFixed(0)}/SF  |  ${buildingArea.toLocaleString()} SF`:""}`);
    parts.push(`Subtotal: ${fmtC(final.subtotal)}  |  Overhead: ${overhead}%  |  Profit: ${profit}%  |  Contingency: ${contingency}%`);
    if(duration>0)parts.push(`Duration: ${duration} months`);
    if(proposals.length>1)parts.push(`Packages: ${proposals.map((p:any)=>p.name).join(", ")}`);
    // Division totals
    const divTotRows=divisions.map(div=>{
      let tot=0;
      for(const sub of div.subcategories||[]){
        for(const row of walkItems(sub.rows||[])){
          const it=itemState[row.id];
          if(!it||!it.included)continue;
          tot+=(it.qty||0)*(it.unitCost||0);
        }
      }
      return{name:(div.name||div.id).replace(/^Division \d+\s*[–-]\s*/,"").replace(/^Div \d+\s*[–-]\s*/,""),total:tot};
    }).filter(d=>d.total>0);
    if(divTotRows.length){
      parts.push("Division totals:");
      divTotRows.forEach(d=>parts.push(`  ${d.name}: ${fmtC(d.total)}${buildingArea>0?` ($${(d.total/buildingArea).toFixed(0)}/SF)`:""}`));
    }
    // Line items
    const lines:string[]=[];
    for(const div of divisions){
      const divShort=(div.name||div.id).replace(/^Division \d+\s*[–-]\s*/,"").replace(/^Div \d+\s*[–-]\s*/,"");
      for(const sub of div.subcategories||[]){
        for(const row of walkItems(sub.rows||[])){
          const it=itemState[row.id];
          if(!it||!it.included)continue;
          const qty=it.qty||0,uc=it.unitCost||0,tot=qty*uc;
          if(tot<=0)continue;
          let line=`  [${divShort}] ${it.description||"—"}: ${qty} ${it.unit||"LS"} @ $${uc.toLocaleString()}/${it.unit||"LS"} = ${fmtC(tot)}`;
          if(it.tradeName)line+=`  (carrier: ${it.tradeName})`;
          if(it.costStatus&&it.costStatus!=="Hard Bid")line+=`  [${it.costStatus}]`;
          if(proposals.length>1){const p=proposals.find((p:any)=>p.id===it.proposal);if(p)line+=`  {${p.name}}`;}
          lines.push(line);
        }
      }
    }
    if(lines.length){
      const sorted=lines.slice().sort((a,b)=>{
        const tot=(l:string)=>{const m=l.match(/= \$([0-9,]+)/);return m?parseInt(m[1].replace(/,/g,""),10):0;};
        return tot(b)-tot(a);
      });
      parts.push(`Line items (${sorted.length}):`);
      parts.push(...sorted.slice(0,300));
      if(sorted.length>300)parts.push(`  ... and ${sorted.length-300} more smaller items`);
    }
    // Trade partners
    if(bidPartners.length){
      parts.push("Trade partners:");
      bidPartners.forEach((p:any)=>{
        const partnerBids=(bids as any[]).filter((b:any)=>b.partnerId===p.id);
        const scopes=[...new Set(partnerBids.flatMap((b:any)=>{const sc=(bidScopes as any[]).find((s:any)=>s.id===b.scopeId);return sc?[sc.name]:[];}))];
        parts.push(`  ${p.name}${scopes.length?` → ${scopes.join(", ")}`:""}`)
      });
    }
    // RFIs
    if(rfis?.length){
      parts.push(`RFIs (${rfis.length} total):`);
      rfis.slice(0,50).forEach((r:any)=>parts.push(`  #${r.number||"?"} [${r.status||"Open"}] ${r.subject||"—"}${r.costImpact?` — cost impact: ${fmtC(r.costImpact)}`:""}${r.scheduleDays?` — schedule impact: ${r.scheduleDays}d`:""}`));
    }
    return parts.join("\n");
  },[projectName,address,buildingArea,duration,final,overhead,profit,contingency,proposals,divisions,itemState,bidPartners,bids,bidScopes,rfis]);

  // Bid partner name helper — must be at component scope (used in embed modal outside the bids IIFE)
  // Resolves against org-level pickerPartners first (new bids), then project-level bidPartners (legacy).
  const findPartner=(pid:string):BidPartner|OrgPartner|null=>
    (pickerPartners as any[]).find((p:any)=>p.id===pid)||bidPartners.find(p=>p.id===pid)||null;
  const pName=(pid:string)=>findPartner(pid)?.name||"Unknown";

  const navigateToItem=useCallback((itemId)=>{
    let divId=null,subId=null;
    divisions.forEach(div=>div.subcategories.forEach(sub=>{
      if([...walkItems(sub.rows)].some(r=>r.id===itemId)){divId=div.id;subId=sub.id;}
    }));
    if(!divId)return;
    navTo("estimate");
    setActiveDiv(divId);
    setExpandedSubs(p=>({...p,[subId]:true}));
    setTimeout(()=>{
      const el=document.querySelector(`[data-itemid="${itemId}"]`);
      if(el)el.scrollIntoView({behavior:"smooth",block:"center"});
    },120);
  },[divisions,navTo]);


  // ── Conceptual Budget Range (for KPI indicator) ──
  const budgetRange=useMemo(()=>{
    if(!linkedBudgetId)return null;
    try{
      const raw=sGetRaw(CB_KEY);
      const budgets:CBudget[]=raw?JSON.parse(raw):[];
      const b=budgets.find(x=>x.id===linkedBudgetId);
      if(!b)return null;
      const groups=b.areaGroups||[];
      const siteItems=b.siteItems||[];
      let low=0,high=0;
      groups.forEach(g=>{
        const sf=g.subAreas.reduce((s,a)=>s+(a.sf||0),0);
        low+=sf*(g.lowRate||0);high+=sf*(g.highRate||0);
      });
      siteItems.forEach(si=>{low+=(si.low||0);high+=(si.high||0);});
      return low>0||high>0?{low,high,name:b.name}:null;
    }catch{return null;}
  },[linkedBudgetId]);

  // Ordered flat list of visible item IDs in the current division (for range selection)
  const visibleItemIds=useMemo(()=>{
    const ids=[];
    if(!activeDivData)return ids;
    activeDivData.subcategories.forEach(sub=>{
      if(expandedSubs[sub.id]===false)return;
      const walk=rows=>rows.forEach(r=>{
        if(r.type==="group"){if(expandedGrps[r.id]!==false)walk(r.children);}
        else ids.push(r.id);
      });
      walk(sub.rows);
    });
    return ids;
  },[activeDivData,expandedSubs,expandedGrps]);

  /** The same rows, cut into the subcategories they sit in -- what Ctrl+Arrow
   *  jumps between. */
  const visibleBlocks=useMemo(()=>{
    const blocks:string[][]=[];
    if(!activeDivData)return blocks;
    activeDivData.subcategories.forEach(sub=>{
      if(expandedSubs[sub.id]===false)return;
      const ids:string[]=[];
      const walk=(rows:any[])=>rows.forEach((r:any)=>{
        if(r.type==="group"){if(expandedGrps[r.id]!==false)walk(r.children);}
        else ids.push(r.id);
      });
      walk(sub.rows);
      blocks.push(ids);
    });
    return blocks;
  },[activeDivData,expandedSubs,expandedGrps]);

  // Visible row IDs including groups (for row selection range)
  const visibleRowIds=useMemo(()=>{
    const ids:string[]=[];
    if(!activeDivData)return ids;
    activeDivData.subcategories.forEach(sub=>{
      if(expandedSubs[sub.id]===false)return;
      sub.rows.forEach(r=>{
        ids.push(r.id);
        if(r.type==="group"&&expandedGrps[r.id]!==false)r.children.forEach(c=>ids.push(c.id));
      });
    });
    return ids;
  },[activeDivData,expandedSubs,expandedGrps]);

  const selectedIds=useMemo(()=>
    selField?new Set(rangeIds(visibleItemIds,selAnchorId,selCurrentId,selExcluded)):new Set<string>(),
  [selField,selAnchorId,selCurrentId,visibleItemIds,selExcluded]);

  const selStatus=useMemo(()=>{
    if(!selField||!selectedIds.size)return null;
    const amountOf=(id:string)=>{
      const li=itemState[id]||{};
      if(selField==="qty")return li.qty||0;
      if(selField==="unitCost")return li.unitCost||0;
      if(selField==="total")return getItemTotal(li,itemState);
      if(selField==="contingency")return calcItemCont(li,contingency,itemState);
      return 0;
    };
    return{...rangeStats([...selectedIds] as string[],amountOf),field:selField};
  },[selField,selectedIds,itemState,contingency]);

  const startSel=(itemId,field,e)=>{
    // Skip interactive controls so editing inputs / opening dropdowns / clicking
    // duplicate-and-delete buttons doesn't accidentally start a cell selection.
    if(e&&(e.target.tagName==="SELECT"||e.target.tagName==="BUTTON"||e.target.tagName==="INPUT"||e.target.tagName==="TEXTAREA"))return;
    setRowSelIds(new Set());setRowSelAnchorId(null); // cell click clears row selection
    if(e&&(e.ctrlKey||e.metaKey)){
      // Ctrl+click: toggle item out of (or back into) the current selection
      if(selField===field){
        setSelExcluded(prev=>{const n=new Set(prev);n.has(itemId)?n.delete(itemId):n.add(itemId);return n;});
      }
      return;
    }
    if(e&&e.shiftKey&&selField===field&&selAnchorId){
      // Shift+click: extend range from existing anchor to this item
      setSelCurrentId(itemId);setSelDragging(false);
      return;
    }
    // Normal click: fresh selection
    setSelField(field);setSelAnchorId(itemId);setSelCurrentId(itemId);
    setSelExcluded(new Set());setSelDragging(true);
  };
  const extendSel=(itemId,field)=>{if(selDragging&&selField===field)setSelCurrentId(itemId);};
  const isSel=(itemId,field)=>selectedIds.has(itemId)&&selField===field;

  // ── Row selection (for copy/cut/paste/drag) ──
  // ── Talking to Excel ───────────────────────────────────────────────────────
  //
  // The row clipboard above moves rows between places inside this app. This
  // moves VALUES in and out of it, because an estimator's other window is a
  // spreadsheet and a column of forty quantities has to arrive by paste
  // rather than by typing.
  //
  // Selection here is a single column -- selField plus a run of rows -- so a
  // copy is one column of text and a paste fills down from the anchor,
  // spreading right across consecutive fields when the pasted block is wider.

  // Where the keyboard selection currently sits. Written by the arrow-key
  // handler and re-synced whenever the selection changes by other means
  // (a click, a drag, Escape), so the two never drift apart.
  const selHeadRef=useRef<string|null>(null);
  const selAnchorRef=useRef<string|null>(null);
  useEffect(()=>{
    selHeadRef.current=selCurrentId||selAnchorId;
    selAnchorRef.current=selAnchorId;
  },[selCurrentId,selAnchorId]);

  /** The selected cells, top to bottom, as the grid shows them. */
  const orderedSelIds=useMemo(
    ()=>visibleItemIds.filter(id=>selectedIds.has(id)),
    [visibleItemIds,selectedIds]);

  /** One cell's value as text, for the clipboard. */
  const cellText=useCallback((id:string,field:string):string=>{
    const li=itemState[id]||{};
    if(field==="total")return String(getItemTotal(li,itemState));
    if(field==="contingency")return String(calcItemCont(li,contingency,itemState));
    const v=li[field];
    return v==null?"":String(v);
  },[itemState,contingency]);

  /** The selected column, as one column of text. */
  const cellsAsText=useCallback(()=>{
    if(!selField||!orderedSelIds.length)return "";
    return toClipboardGrid(orderedSelIds.map(id=>[cellText(id,selField)]));
  },[selField,orderedSelIds,cellText]);

  /** Whole rows, so a row copy is also worth something in a spreadsheet. */
  const rowsAsText=useCallback((ids:string[])=>
    toClipboardGrid(ids.map(id=>PASTEABLE_FIELDS.map(f=>cellText(id,f)))),
    [cellText]);

  /**
   * A paste from outside, applied as one undo step.
   *
   * Driven by the real paste event rather than by reading the clipboard on a
   * keystroke: the event hands over the text with no permission prompt, which
   * navigator.clipboard.readText does not.
   */
  const applyExternalPaste=useCallback((text:string)=>{
    if(!selField||!selAnchorId||!isPasteable(selField))return false;
    const grid=parseClipboardGrid(text);
    if(!grid.length)return false;
    const plan=planPaste(grid,selField,selAnchorId,visibleItemIds);
    if(!plan.rowsWritten){
      const why=describePaste(plan);
      showToast(why||"Nothing in the clipboard landed in the grid");
      return true; // handled: do not fall through to the row paste
    }
    snapshot();
    setItemState(prev=>{
      const next={...prev};
      for(const [id,fields] of Object.entries(plan.edits)) next[id]={...next[id],...fields};
      return next;
    });
    const skipped=describePaste(plan);
    showToast(`Pasted ${plan.rowsWritten} row${plan.rowsWritten===1?"":"s"}${skipped?` · ${skipped}`:""}`,
      skipped?4200:2200);
    return true;
  },[selField,selAnchorId,visibleItemIds,snapshot]);

  const clearRowSel=()=>{setRowSelIds(new Set());setRowSelAnchorId(null);};
  const bulkUpd=(field:string,value:any)=>{
    snapshot();
    setItemState(prev=>{const next={...prev};rowSelIds.forEach(id=>{next[id]={...(next[id]||{})}; (next[id] as any)[field]=value;});return next;});
  };
  const bulkAltUpd=(val:string)=>{
    snapshot();
    setItemState(prev=>{
      const next={...prev};
      rowSelIds.forEach(id=>{
        const li=next[id]||{};
        if(val==="Alt-Accepted") next[id]={...li,altType:"Alt",altStatus:"Accepted"};
        else if(val==="Alt") next[id]={...li,altType:"Alt",altStatus:"TBD"};
        else if(val==="Void") next[id]={...li,altType:"Void",altStatus:"Void"};
        else next[id]={...li,altType:"",altStatus:""};
      });
      return next;
    });
  };
  const clearCellSel=()=>{setSelField(null);setSelAnchorId(null);setSelCurrentId(null);setSelExcluded(new Set());};
  const startRowSel=(rowId:string,e:React.MouseEvent)=>{
    // No e.preventDefault() — it would block native drag initiation
    clearCellSel(); // row selection clears cell selection
    if(e.ctrlKey||e.metaKey){
      setRowSelIds(prev=>{const n=new Set(prev);n.has(rowId)?n.delete(rowId):n.add(rowId);return n;});
      if(!rowSelAnchorId)setRowSelAnchorId(rowId);
      return;
    }
    if(e.shiftKey&&rowSelAnchorId){
      const ai=visibleRowIds.indexOf(rowSelAnchorId);
      const ci=visibleRowIds.indexOf(rowId);
      if(ai>=0&&ci>=0){
        const lo=Math.min(ai,ci),hi=Math.max(ai,ci);
        setRowSelIds(new Set(visibleRowIds.slice(lo,hi+1)));
      }
      return;
    }
    setRowSelIds(new Set([rowId]));setRowSelAnchorId(rowId);
  };
  const isRowSel=(rowId:string)=>rowSelIds.has(rowId);
  const isCutPending=(rowId:string)=>clipboardRef.current?.mode==="cut"&&clipboardRef.current.items.some(i=>i.rowObj.id===rowId||(i.isGroup&&i.rowObj.children?.some(c=>c.id===rowId)));

  // ── Copy / Cut / Paste ──
  const handleCopy=useCallback(()=>{
    if(!rowSelIds.size)return;
    const orderedIds=visibleRowIds.filter(id=>rowSelIds.has(id));
    // Don't duplicate group children if group itself is selected
    const groupIds=new Set<string>();
    const items:any[]=[];
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type==="group"){
        groupIds.add(id);
        const groupClone=JSON.parse(JSON.stringify(found.row));
        const itemDataMap={};
        [...walkItems(found.row.children)].forEach(item=>{itemDataMap[item.id]=JSON.parse(JSON.stringify(itemState[item.id]||{}));});
        items.push({rowObj:groupClone,itemDataMap,isGroup:true});
      }
    });
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type!=="group"&&!found.parentGroupId||
         (found.parentGroupId&&!groupIds.has(found.parentGroupId))){
        items.push({rowObj:JSON.parse(JSON.stringify(found.row)),itemData:JSON.parse(JSON.stringify(itemState[id]||{})),isGroup:false});
      }
    });
    clipboardRef.current={mode:"copy",items};
    showToast(`Copied ${orderedIds.length} row(s)`);
  },[rowSelIds,visibleRowIds,findRowById,itemState]);

  const handleCut=useCallback(()=>{
    if(!rowSelIds.size)return;
    const orderedIds=visibleRowIds.filter(id=>rowSelIds.has(id));
    const groupIds=new Set<string>();
    const items:any[]=[];
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type==="group"){
        groupIds.add(id);
        const groupClone=JSON.parse(JSON.stringify(found.row));
        const itemDataMap={};
        [...walkItems(found.row.children)].forEach(item=>{itemDataMap[item.id]=JSON.parse(JSON.stringify(itemState[item.id]||{}));});
        items.push({rowObj:groupClone,itemDataMap,isGroup:true});
      }
    });
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type!=="group"&&!found.parentGroupId||
         (found.parentGroupId&&!groupIds.has(found.parentGroupId))){
        items.push({rowObj:JSON.parse(JSON.stringify(found.row)),itemData:JSON.parse(JSON.stringify(itemState[id]||{})),isGroup:false});
      }
    });
    clipboardRef.current={mode:"cut",items};
    showToast(`Cut ${orderedIds.length} row(s)`);
    // Force re-render for cut visual
    setRowSelIds(new Set(rowSelIds));
  },[rowSelIds,visibleRowIds,findRowById,itemState]);

  const handlePaste=useCallback(()=>{
    const clip=clipboardRef.current;if(!clip||!clip.items.length)return;
    snapshot();
    // Find insertion point: prefer selected rows that are VISIBLE in current division,
    // else fall back to last visible row (which is always in activeDiv).
    // This ensures cross-division paste lands in the currently visible division.
    const selInCurrentDiv=[...rowSelIds].filter(id=>visibleRowIds.includes(id));
    const targetId=selInCurrentDiv.pop()||visibleRowIds[visibleRowIds.length-1];
    if(!targetId)return;
    const target=findRowById(targetId);if(!target)return;

    if(clip.mode==="copy"){
      const idMap:Record<string,string>={};
      const newItemStateEntries:Record<string,any>={};
      const newRows=clip.items.map(entry=>{
        if(entry.isGroup){
          const newGroupId=uid();
          const newChildren=entry.rowObj.children.map(child=>{
            const newId=uid();idMap[child.id]=newId;
            newItemStateEntries[newId]={...entry.itemDataMap[child.id]};
            return{...child,id:newId};
          });
          return{type:"group" as const,id:newGroupId,name:entry.rowObj.name,children:newChildren};
        }else{
          const newId=uid();idMap[entry.rowObj.id]=newId;
          newItemStateEntries[newId]={...entry.itemData};
          return{...entry.rowObj,id:newId};
        }
      });
      // Remap pctTargetIds
      Object.values(newItemStateEntries).forEach((data:any)=>{
        if(data.pctTargetIds)data.pctTargetIds=data.pctTargetIds.map(id=>idMap[id]||id);
      });
      insertRowsAfter(target.divId,target.subId,targetId,newRows);
      setItemState(p=>({...p,...newItemStateEntries}));
      showToast(`Pasted ${clip.items.length} row(s)`);
    }else{
      // Cut mode — atomic remove + insert in single setDivisions call
      const cutIds=new Set(clip.items.map(e=>e.rowObj.id));
      clip.items.forEach(e=>{if(e.isGroup)e.rowObj.children.forEach(c=>cutIds.add(c.id));});
      const rows=clip.items.map(e=>e.rowObj);
      const _targetDiv=target.divId;const _targetSub=target.subId;const _targetId=targetId;
      setDivisions(prev=>{
        // Step 1: Remove cut rows
        const cleaned=prev.map(d=>({...d,subcategories:d.subcategories.map(s=>({
          ...s,rows:s.rows.filter(r=>!cutIds.has(r.id))
            .map(r=>r.type==="group"?{...r,children:r.children.filter(c=>!cutIds.has(c.id))}:r)
        }))}));
        // Step 2: Insert at target
        return cleaned.map(d=>{
          if(d.id!==_targetDiv)return d;
          return{...d,subcategories:d.subcategories.map(s=>{
            if(s.id!==_targetSub)return s;
            for(let i=0;i<s.rows.length;i++){
              const r=s.rows[i];
              if(r.id===_targetId){const nr=[...s.rows];nr.splice(i+1,0,...rows);return{...s,rows:nr};}
              if(r.type==="group"){
                const ci=r.children.findIndex(c=>c.id===_targetId);
                if(ci>=0){
                  const items=rows.filter(rr=>rr.type==="item");
                  const groups=rows.filter(rr=>rr.type==="group");
                  const nc=[...r.children];nc.splice(ci+1,0,...items);
                  const ur=[...s.rows];ur[i]={...r,children:nc};
                  if(groups.length)ur.splice(i+1,0,...groups);
                  return{...s,rows:ur};
                }
              }
            }
            return{...s,rows:[...s.rows,...rows]};
          })};
        });
      });
      showToast(`Moved ${clip.items.length} row(s)`);
      clipboardRef.current=null;
    }
    clearRowSel();
  },[rowSelIds,visibleRowIds,findRowById,snapshot,insertRowsAfter,removeRowsByIds,setItemState]);

  const handleMoveToDivision=useCallback((srcItemId:string,targetDivId:string)=>{
    // Move the right-clicked row (and any other selected rows) to the first sub of targetDivId
    const idsToMove=new Set(rowSelIds.size>0?[...rowSelIds]:[srcItemId]);
    const targetDiv=divisions.find(d=>d.id===targetDivId);
    if(!targetDiv||!targetDiv.subcategories[0])return;
    const targetSubId=targetDiv.subcategories[0].id;
    snapshot();
    // Collect rows to move, in order they appear in source divs
    const rowsToMove:any[]=[];
    divisions.forEach(div=>div.subcategories.forEach(sub=>{
      [...walkItems(sub.rows)].forEach(item=>{if(idsToMove.has(item.id))rowsToMove.push(item);});
    }));
    if(!rowsToMove.length)return;
    setDivisions(prev=>{
      // Remove from source
      const cleaned=prev.map(d=>({...d,subcategories:d.subcategories.map(s=>({...s,rows:s.rows
        .filter(r=>!idsToMove.has(r.id))
        .map(r=>r.type==="group"?{...r,children:r.children.filter(c=>!idsToMove.has(c.id))}:r)
      }))}));
      // Append to first sub of target div
      return cleaned.map(d=>{
        if(d.id!==targetDivId)return d;
        return{...d,subcategories:d.subcategories.map((s,si)=>si===0?{...s,rows:[...s.rows,...rowsToMove]}:s)};
      });
    });
    setActiveDiv(targetDivId);
    clearRowSel();
    showToast(`Moved ${idsToMove.size} row(s) to ${targetDiv.code}`);
  },[rowSelIds,divisions,snapshot,setDivisions,clearRowSel]);

  const handleDuplicate=useCallback((targetRowId?:string)=>{
    // Determine which rows to duplicate: explicit target, or current selection
    const ids=targetRowId?new Set([targetRowId]):rowSelIds;
    if(!ids.size)return;
    snapshot();
    const orderedIds=visibleRowIds.filter(id=>ids.has(id));
    const groupIds=new Set<string>();
    const items:any[]=[];
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type==="group"){
        groupIds.add(id);
        const groupClone=JSON.parse(JSON.stringify(found.row));
        const itemDataMap={};
        [...walkItems(found.row.children)].forEach(item=>{itemDataMap[item.id]=JSON.parse(JSON.stringify(itemState[item.id]||{}));});
        items.push({rowObj:groupClone,itemDataMap,isGroup:true,origTarget:found});
      }
    });
    orderedIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type!=="group"&&(!found.parentGroupId||!groupIds.has(found.parentGroupId))){
        items.push({rowObj:JSON.parse(JSON.stringify(found.row)),itemData:JSON.parse(JSON.stringify(itemState[id]||{})),isGroup:false,origTarget:found});
      }
    });
    if(!items.length)return;
    // Clone with new IDs and insert after originals
    const idMap:Record<string,string>={};
    const newItemStateEntries:Record<string,any>={};
    const newRows=items.map(entry=>{
      if(entry.isGroup){
        const newGroupId=uid();
        const newChildren=entry.rowObj.children.map(child=>{
          const newId=uid();idMap[child.id]=newId;
          newItemStateEntries[newId]={...entry.itemDataMap[child.id]};
          return{...child,id:newId};
        });
        return{type:"group" as const,id:newGroupId,name:entry.rowObj.name+" (copy)",children:newChildren};
      }else{
        const newId=uid();idMap[entry.rowObj.id]=newId;
        newItemStateEntries[newId]={...entry.itemData};
        return{...entry.rowObj,id:newId};
      }
    });
    Object.values(newItemStateEntries).forEach((data:any)=>{
      if(data.pctTargetIds)data.pctTargetIds=data.pctTargetIds.map(id=>idMap[id]||id);
    });
    // Insert after the last selected row
    const lastId=orderedIds[orderedIds.length-1];
    const lastFound=findRowById(lastId);
    if(lastFound)insertRowsAfter(lastFound.divId,lastFound.subId,lastId,newRows);
    setItemState(p=>({...p,...newItemStateEntries}));
    const newIds=new Set(newRows.map(r=>r.id));
    setRowSelIds(newIds);
    showToast(`Duplicated ${items.length} row(s)`);
  },[rowSelIds,visibleRowIds,findRowById,itemState,snapshot,insertRowsAfter,setItemState]);

  const handleDeleteSelectedRows=useCallback(()=>{
    if(!rowSelIds.size)return;
    snapshot();
    const idsToRemove=new Set<string>();
    rowSelIds.forEach(id=>{
      idsToRemove.add(id);
      const found=findRowById(id);
      if(found?.row?.type==="group")found.row.children.forEach(c=>idsToRemove.add(c.id));
    });
    removeRowsByIds(idsToRemove);
    // Clean up itemState
    setItemState(p=>{const n={...p};idsToRemove.forEach(id=>delete n[id]);return n;});
    clearRowSel();
    showToast(`Deleted ${rowSelIds.size} row(s)`);
  },[rowSelIds,findRowById,snapshot,removeRowsByIds,setItemState]);

  // ── Drag-and-drop reorder ──
  const handleDragStart=(e:React.DragEvent,rowId:string)=>{
    // If the row isn't already in the selection, replace it; otherwise keep
    // the full multi-row selection so users can drag many rows at once.
    if(!rowSelIds.has(rowId))setRowSelIds(new Set([rowId]));
    const ids=rowSelIds.has(rowId)?new Set(rowSelIds):new Set([rowId]);
    dndSourceRef.current={ids};
    // Clear any stale drop target from a prior drag — keeps drops from
    // landing on a leftover position when this drag's first hover hits a source.
    setDndTargetId(null);
    e.dataTransfer.effectAllowed="move";
    // Non-empty payload — some browsers (esp. Firefox) abort drags otherwise.
    e.dataTransfer.setData("text/plain",`oe-rows:${ids.size}`);
    setDndActive(true);
    clearCellSel();
  };
  const handleDragOver=(e:React.DragEvent,rowId:string)=>{
    e.preventDefault();
    if(dndSourceRef.current?.ids.has(rowId)){e.dataTransfer.dropEffect="none";return;}
    e.dataTransfer.dropEffect="move";
    const rect=(e.currentTarget as HTMLElement).getBoundingClientRect();
    const t=(e.clientY-rect.top)/rect.height;
    // Three zones: top 25% = before, middle 50% = into (group), bottom 25% = after
    const pos:typeof dndPosition=t<0.25?"before":t>0.75?"after":"into";
    setDndTargetId(rowId);setDndPosition(pos);
    setDndTargetSubId(null); // row hover wins over sub-header hover
  };
  // Drag over a subcategory header — drop will append rows into that sub
  const handleSubDragOver=(e:React.DragEvent,subId:string)=>{
    if(!dndSourceRef.current)return;
    e.preventDefault();
    e.dataTransfer.dropEffect="move";
    setDndTargetSubId(subId);
    setDndTargetId(null); // sub-header hover wins over any stale row target
  };
  const resetDnd=()=>{setDndActive(false);setDndTargetId(null);setDndTargetSubId(null);dndSourceRef.current=null;};
  // Collect source rows from current state in visual order (groups first so
  // their children come along; non-group items are skipped if their parent
  // group is already in the move set).
  const collectDragRows=(sourceIds:Set<string>)=>{
    const orderedSourceIds=visibleRowIds.filter(id=>sourceIds.has(id));
    const groupIds=new Set<string>();
    const rowsToMove:any[]=[];
    orderedSourceIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type==="group"){groupIds.add(id);rowsToMove.push(JSON.parse(JSON.stringify(found.row)));}
    });
    orderedSourceIds.forEach(id=>{
      const found=findRowById(id);if(!found)return;
      if(found.row.type!=="group"&&(!found.parentGroupId||!groupIds.has(found.parentGroupId))){
        rowsToMove.push(JSON.parse(JSON.stringify(found.row)));
      }
    });
    const allSourceIds=new Set<string>();
    rowsToMove.forEach(r=>{allSourceIds.add(r.id);if(r.type==="group")r.children.forEach((c:any)=>allSourceIds.add(c.id));});
    return {rowsToMove,allSourceIds};
  };
  const handleDrop=(e:React.DragEvent)=>{
    e.preventDefault();
    const sourceRef=dndSourceRef.current;
    if(!sourceRef){resetDnd();return;}
    const sourceIds=sourceRef.ids;

    // Branch: dropping on a subcategory header → append rows to that sub
    if(dndTargetSubId&&!dndTargetId){
      const _subId=dndTargetSubId;
      let _divId:string|null=null;
      for(const d of divisions){if(d.subcategories.some(s=>s.id===_subId)){_divId=d.id;break;}}
      if(!_divId){resetDnd();return;}
      const {rowsToMove,allSourceIds}=collectDragRows(sourceIds);
      if(rowsToMove.length===0){resetDnd();return;}
      snapshot();
      const _moveCount=rowsToMove.length;
      setDivisions(prev=>{
        const cleaned=prev.map(d=>({...d,subcategories:d.subcategories.map(s=>({
          ...s,rows:s.rows.filter(r=>!allSourceIds.has(r.id))
            .map(r=>r.type==="group"?{...r,children:r.children.filter((c:any)=>!allSourceIds.has(c.id))}:r)
        }))}));
        return cleaned.map(d=>{
          if(d.id!==_divId)return d;
          return{...d,subcategories:d.subcategories.map(s=>{
            if(s.id!==_subId)return s;
            return{...s,rows:[...s.rows,...rowsToMove]};
          })};
        });
      });
      showToast(`Moved ${_moveCount} row${_moveCount!==1?"s":""}`);
      resetDnd();
      return;
    }

    if(!dndTargetId){resetDnd();return;}
    // Refuse drop on a source (covers stale dndTargetId carried from prior hover)
    if(sourceIds.has(dndTargetId)){resetDnd();return;}
    // Capture target position from the *original* state, before any cleanup.
    // The previous implementation searched the cleaned state for the target,
    // which silently fell back to "append to end" if the target's row index
    // shifted — making rows appear to vanish.
    const target=findRowById(dndTargetId);
    if(!target){resetDnd();return;}

    const {rowsToMove,allSourceIds}=collectDragRows(sourceIds);
    if(rowsToMove.length===0){resetDnd();return;}
    // Refuse drops onto rows that are themselves being moved (e.g. dropping
    // a group onto its own child).
    if(allSourceIds.has(dndTargetId)){resetDnd();return;}

    snapshot();
    const _targetId=dndTargetId;
    const _pos=dndPosition;
    const _targetDiv=target.divId;
    const _targetSub=target.subId;
    const _targetParentGroup=target.parentGroupId;
    let _newGroupId:string|null=null;
    const _moveCount=rowsToMove.length;

    setDivisions(prev=>{
      // 1) Remove source rows from everywhere
      const cleaned=prev.map(d=>({...d,subcategories:d.subcategories.map(s=>({
        ...s,rows:s.rows.filter(r=>!allSourceIds.has(r.id))
          .map(r=>r.type==="group"?{...r,children:r.children.filter((c:any)=>!allSourceIds.has(c.id))}:r)
      }))}));

      const itemsToAdd=rowsToMove.filter(r=>r.type!=="group");
      const groupsToAdd=rowsToMove.filter(r=>r.type==="group");

      // 2) Insert at the captured target position
      return cleaned.map(d=>{
        if(d.id!==_targetDiv)return d;
        return{...d,subcategories:d.subcategories.map(s=>{
          if(s.id!==_targetSub)return s;

          // Target was a group child → insert inside that group
          if(_targetParentGroup){
            const gi=s.rows.findIndex(r=>r.id===_targetParentGroup);
            if(gi<0){
              // Parent group itself was removed — append to subcategory end
              return{...s,rows:[...s.rows,...rowsToMove]};
            }
            const grp=s.rows[gi];
            const ci=grp.children.findIndex((c:any)=>c.id===_targetId);
            if(ci<0){
              // Target child got removed — append items to the group
              const updatedRows=[...s.rows];
              updatedRows[gi]={...grp,children:[...grp.children,...itemsToAdd]};
              if(groupsToAdd.length)updatedRows.splice(gi+1,0,...groupsToAdd);
              return{...s,rows:updatedRows};
            }
            const insertAt=_pos==="before"?ci:ci+1;
            const newChildren=[...grp.children];
            newChildren.splice(insertAt,0,...itemsToAdd);
            const updatedRows=[...s.rows];
            updatedRows[gi]={...grp,children:newChildren};
            if(groupsToAdd.length)updatedRows.splice(gi+1,0,...groupsToAdd);
            return{...s,rows:updatedRows};
          }

          // Target was a top-level row
          const ti=s.rows.findIndex(r=>r.id===_targetId);
          if(ti<0){
            // Target gone — append to subcategory end
            return{...s,rows:[...s.rows,...rowsToMove]};
          }
          const targetRow=s.rows[ti];

          if(_pos==="into"){
            if(targetRow.type==="group"){
              // Drop INTO existing group — append items as children
              const updatedRows=[...s.rows];
              updatedRows[ti]={...targetRow,children:[...targetRow.children,...itemsToAdd]};
              if(groupsToAdd.length)updatedRows.splice(ti+1,0,...groupsToAdd);
              return{...s,rows:updatedRows};
            } else {
              // Drop ONTO plain item — wrap target + dragged items in a new group
              const gid=`grp_${Math.random().toString(36).slice(2,9)}`;
              _newGroupId=gid;
              const newGroup={type:"group",id:gid,name:"Group",children:[targetRow,...itemsToAdd]};
              const updatedRows=[...s.rows];
              updatedRows[ti]=newGroup;
              if(groupsToAdd.length)updatedRows.splice(ti+1,0,...groupsToAdd);
              return{...s,rows:updatedRows};
            }
          }

          // before / after
          const insertAt=_pos==="before"?ti:ti+1;
          const nr=[...s.rows];
          nr.splice(insertAt,0,...rowsToMove);
          return{...s,rows:nr};
        })};
      });
    });
    if(_newGroupId){
      showToast("Group created — click the label to rename");
      setExpandedGrps(p=>({...p,[_newGroupId!]:true}));
    } else {
      showToast(`Moved ${_moveCount} row${_moveCount!==1?"s":""}`);
    }
    resetDnd();
  };
  const handleDragEnd=()=>{resetDnd();};

  // Row clipboard keyboard shortcuts (placed after handlers are defined)
  // ── Spreadsheet keys ─────────────────────────────────────────────
  // Estimators come from Excel, and every moment their hands leave the
  // keyboard is a moment they notice the software instead of the estimate.
  // The grid answered only to Tab, through two hand-written DOM walks that
  // disagreed with each other; arrows moved the text caret, and there was no
  // way back up a column at all.
  //
  // Addresses live on the cells themselves as data-oe-item / data-oe-field,
  // so "which cell is this and what is next to it" is answered by the DOM
  // that is actually on screen -- hidden columns and rows that carry no
  // quantity drop out for free, rather than by a second list of the grid
  // kept in step by hand.
  // Field names are this file's own literals; item ids are not, so they are
  // matched rather than interpolated into a selector.
  const gridCellEl=useCallback((c:GridCell):HTMLElement|null=>{
    const cells=document.querySelectorAll<HTMLElement>(`[data-oe-field="${c.field}"]`);
    for(const el of Array.from(cells))if(el.getAttribute("data-oe-item")===c.itemId)return el;
    return null;
  },[]);

  /** Move to a cell. Returns false if it is not on screen, so the caller can
   *  leave the keystroke alone rather than swallowing it. */
  const goToCell=useCallback((c:GridCell|null)=>{
    if(!c)return false;
    const el=gridCellEl(c);
    if(!el)return false;
    el.focus();
    // Arriving by keyboard selects the whole value, so the next thing typed
    // replaces it. Arriving by mouse places a caret instead. That is the
    // difference between moving through the grid and editing one cell of it,
    // and it is the whole of "type to replace".
    if(el instanceof HTMLInputElement)el.select();
    return true;
  },[gridCellEl]);

  /**
   * Ctrl+D, both of the things it means in a spreadsheet.
   *
   * Over a selected range it pushes the top value down the rest; on a single
   * cell it copies the one above, which is how a unit cost gets repeated down
   * a list without selecting anything first. The formula travels with the
   * value -- a cell holding "12*4" fills as "12*4", not as 48, so the next
   * person can still see where the number came from.
   */
  const fillDown=useCallback(()=>{
    const active=document.activeElement as HTMLElement|null;
    const field=selField||active?.getAttribute?.("data-oe-field")||null;
    if(!field||!isPasteable(field)){
      showToast("Fill down works on quantity, unit and unit cost");return;
    }
    const range=(selField&&selAnchorId)
      ?visibleItemIds.filter(id=>selectedIds.has(id))
      :[active?.getAttribute?.("data-oe-item")].filter(Boolean) as string[];
    const plan=planFillDown(visibleItemIds,range);
    if(!plan){showToast("Nothing above to fill from");return;}
    const carried=field==="qty"?["qty","qtyFormula"]
      :field==="unitCost"?["unitCost","unitCostFormula"]:[field];
    // A cell writes its own value back when it loses focus, so filling INTO
    // the one being edited would be undone a moment later by its own blur.
    // Commit it first, then write over the committed value.
    const here=active?.getAttribute?.("data-oe-item")
      ?{itemId:active.getAttribute("data-oe-item")!,field}:null;
    active?.blur?.();
    snapshot();
    setItemState(p=>{
      // Source read from the freshest state, not the render this ran in:
      // the blur above may have just changed it.
      const src=p[plan.from]||{};
      const next={...p};
      for(const id of plan.to){
        const row={...next[id]};
        for(const k of carried)row[k]=src[k];
        next[id]=row;
      }
      return next;
    });
    if(here)setTimeout(()=>goToCell(here),0);
    showToast(`Filled ${plan.to.length} cell${plan.to.length===1?"":"s"} — Ctrl+Z to undo`);
  },[selField,selAnchorId,selectedIds,visibleItemIds,setItemState,snapshot,goToCell]);

  useEffect(()=>{
    if(activeTab!=="estimate")return;
    const h=(e:KeyboardEvent)=>{
      const el=e.target as HTMLElement|null;
      const itemId=el?.getAttribute?.("data-oe-item");
      const field=el?.getAttribute?.("data-oe-field");
      if(!itemId||!field)return;
      const from:GridCell={itemId,field};
      const exists=(c:GridCell)=>!!gridCellEl(c);
      const isSelect=el!.tagName==="SELECT";

      // F2 drops the selection and puts the caret at the end: edit what is
      // there rather than replace it.
      if(e.key==="F2"&&el instanceof HTMLInputElement){
        e.preventDefault();
        const n=el.value.length;el.setSelectionRange(n,n);
        return;
      }
      if(e.key==="Tab"){
        // Tab wraps off the end of a row into the next, which is how a table
        // gets filled in. It reads the same column order the arrows do.
        if(goToCell(nextCell(visibleItemIds,NAV_FIELDS,from,e.shiftKey?"left":"right",{wrap:true,exists})))
          e.preventDefault();
        return;
      }
      // Shift+Arrow is selecting text inside the cell. Leave it alone.
      if(e.shiftKey)return;

      if(e.key==="ArrowUp"||e.key==="ArrowDown"){
        const dir=e.key==="ArrowDown"?"down":"up";
        const to=(e.ctrlKey||e.metaKey)
          ?blockEdge(visibleBlocks,from,dir)
          :nextCell(visibleItemIds,NAV_FIELDS,from,dir,{exists});
        // A unit is a dropdown, and arrowing down a column must never quietly
        // rewrite one. Swallow the key at the ends rather than let the select
        // change its own value.
        if(goToCell(to)||isSelect)e.preventDefault();
        return;
      }
      if(e.key==="ArrowLeft"||e.key==="ArrowRight"){
        if(e.ctrlKey||e.metaKey)return;   // jump by word, as in any text field
        const dir=e.key==="ArrowRight"?"right":"left";
        if(el instanceof HTMLInputElement&&
           !leavesCell(dir,{start:el.selectionStart??0,end:el.selectionEnd??0,length:el.value.length}))
          return;                          // walk the text until it runs out
        if(goToCell(nextCell(visibleItemIds,NAV_FIELDS,from,dir,{exists}))||isSelect)
          e.preventDefault();
      }
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[activeTab,visibleItemIds,visibleBlocks,gridCellEl,goToCell]);

  useEffect(()=>{
    const h=e=>{
      const tag=document.activeElement?.tagName;
      const inInput=tag==="INPUT"||tag==="TEXTAREA";
      if((e.ctrlKey||e.metaKey)&&e.key==="c"&&!e.shiftKey&&!inInput){
        // No preventDefault: suppressing the keydown's default also suppresses
        // the copy event below, which is what actually fills the system
        // clipboard. handleCopy only loads the in-app row clipboard.
        if(rowSelIds.size>0)handleCopy();
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="x"&&!e.shiftKey&&!inInput){
        if(rowSelIds.size>0){e.preventDefault();handleCut();}
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="v"&&!e.shiftKey&&!inInput){
        // A live cell selection means the user is pasting INTO cells, which
        // the paste event below handles with the clipboard's real contents.
        // Swallowing it here would re-paste whatever rows were copied hours
        // ago instead.
        const pastingIntoCells=!!(selField&&selAnchorId&&isPasteable(selField));
        if(clipboardRef.current&&!pastingIntoCells){e.preventDefault();handlePaste();}
      }
      // Ctrl+D fills down, as it does in every spreadsheet. Duplicating whole
      // rows -- which is what it used to do here, and which no spreadsheet
      // binds it to -- moved to Ctrl+Shift+D.
      if((e.ctrlKey||e.metaKey)&&e.key==="d"&&e.shiftKey){
        if(rowSelIds.size>0){e.preventDefault();handleDuplicate();}
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="d"&&!e.shiftKey&&activeTab==="estimate"){
        e.preventDefault();fillDown();
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="a"&&!e.shiftKey&&!inInput){
        if(activeTab==="estimate"){e.preventDefault();setRowSelIds(new Set(visibleRowIds));if(visibleRowIds.length)setRowSelAnchorId(visibleRowIds[0]);}
      }
      if((e.key==="Delete"||e.key==="Backspace")&&!inInput){
        if(rowSelIds.size>0){e.preventDefault();handleDeleteSelectedRows();}
      }
      // Up and down a column. Tab already moved across; nothing moved down,
      // and estimators work in columns. Shift extends the run instead of
      // moving it, the way a spreadsheet does.
      //
      // The head is tracked in a ref the handler writes itself, not read from
      // state. Holding the key down fires several keydowns inside one tick,
      // and reading state would make every one of them compute from the same
      // stale row -- so a held Shift+Down would extend by exactly one.
      if((e.key==="ArrowDown"||e.key==="ArrowUp")&&!inInput&&activeTab==="estimate"&&selField&&selAnchorId){
        const step=e.key==="ArrowDown"?1:-1;
        const from=e.shiftKey?(selHeadRef.current||selAnchorId):(selAnchorRef.current||selAnchorId);
        const i=visibleItemIds.indexOf(from);
        const next=i===-1?null:visibleItemIds[i+step];
        if(next){
          e.preventDefault();
          selHeadRef.current=next;
          if(e.shiftKey)setSelCurrentId(next);
          else{
            selAnchorRef.current=next;
            setSelAnchorId(next);setSelCurrentId(next);setSelExcluded(new Set());
          }
        }
      }
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[rowSelIds,handleCopy,handleCut,handlePaste,handleDeleteSelectedRows,visibleRowIds,activeTab,
     selField,selAnchorId,selCurrentId,visibleItemIds,orderedSelIds,handleDuplicate,fillDown]);

  // The paste event, rather than reading the clipboard on a keystroke: the
  // event carries the text with no permission prompt. Scoped to the estimate
  // grid with a live cell selection, so pasting into a text field anywhere
  // else in the app is untouched.
  useEffect(()=>{
    const onPaste=(e:ClipboardEvent)=>{
      const tag=document.activeElement?.tagName;
      if(tag==="INPUT"||tag==="TEXTAREA")return;
      if(activeTab!=="estimate"||isLocked)return;
      if(!selField||!selAnchorId)return;
      const text=e.clipboardData?.getData("text/plain")||"";
      if(!text)return;
      // Total and Contingency are selectable cells but not writable ones:
      // Total is qty x unitCost, Contingency is a mode plus a rate. Saying
      // so beats a paste that appears to do nothing.
      if(!isPasteable(selField)){
        e.preventDefault();
        showToast(selField==="total"
          ? "Total is Qty × Unit Cost — paste into Qty or Unit Cost instead"
          : "Contingency is set per line — paste into Qty or Unit Cost instead",3600);
        return;
      }
      if(applyExternalPaste(text))e.preventDefault();
    };
    window.addEventListener("paste",onPaste);
    return()=>window.removeEventListener("paste",onPaste);
  },[activeTab,isLocked,selField,selAnchorId,applyExternalPaste]);

  // Copy, through the copy event for the same reason as paste: it writes the
  // clipboard with no permission and no transient-activation requirement,
  // which navigator.clipboard.writeText from a keydown does not reliably have.
  useEffect(()=>{
    const onCopy=(e:ClipboardEvent)=>{
      const tag=document.activeElement?.tagName;
      if(tag==="INPUT"||tag==="TEXTAREA")return;   // let a normal text copy happen
      if(activeTab!=="estimate")return;
      let text="",what="";
      if(rowSelIds.size>0){
        const ids=visibleRowIds.filter(id=>rowSelIds.has(id));
        text=rowsAsText(ids);
        what=`${ids.length} row${ids.length===1?"":"s"}`;
      }else if(selField&&orderedSelIds.length){
        text=cellsAsText();
        what=`${orderedSelIds.length} cell${orderedSelIds.length===1?"":"s"}`;
      }
      if(!text)return;
      e.clipboardData?.setData("text/plain",text);
      e.preventDefault();
      showToast(`Copied ${what}`);
    };
    window.addEventListener("copy",onCopy);
    return()=>window.removeEventListener("copy",onCopy);
  },[activeTab,rowSelIds,visibleRowIds,selField,orderedSelIds,cellsAsText,rowsAsText]);

  const applyDivFilter=(val:string)=>{
    setGlobalFilter(val);
    // Apply across every division at once so no division is left in a stale filtered state
    setItemState(p=>{
      const n={...p};
      divisions.forEach(div=>
        div.subcategories.forEach(sub=>[...walkItems(sub.rows)].forEach(item=>{
          const prop=n[item.id]?.proposal||proposals[0]?.id||"p1";
          n[item.id]={...n[item.id],included:val==="All"||prop===val};
        }))
      );
      return n;
    });
  };

  /* ── row renderer ── */
  // ── Collaboration: cell presence helper ──
  const getCollabOnCell=useCallback((itemId:string,field:string)=>{
    return realtime.collaborators?.find(c=>c.activeItemId===itemId&&c.activeField===field)||null;
  },[realtime.collaborators]);
  const collabCellStyle=(itemId:string,field:string)=>{
    const c=getCollabOnCell(itemId,field);
    return c?{outline:`2px solid ${c.color}`,outlineOffset:-1,position:"relative" as const}:{};
  };
  // Capture the pre-edit undo snapshot on focus, clear it on blur — every
  // typed cell (qty/unitCost/description/…) routes focus/blur through here.
  const collabFocus=(itemId:string,field:string)=>()=>{beginCellEdit();realtime.trackCell?.(itemId,field);};
  const collabBlur=()=>{endCellEdit();realtime.trackCell?.(null,null);};
  const CollabLabel=({itemId,field}:{itemId:string,field:string})=>{
    const c=getCollabOnCell(itemId,field);
    if(!c)return null;
    return <div style={{position:"absolute",top:-13,left:0,background:c.color,color:"#fff",fontSize:10,padding:"1px 5px",borderRadius:"3px 3px 0 0",whiteSpace:"nowrap",zIndex:10,pointerEvents:"none",lineHeight:"12px"}}>{c.displayName}</div>;
  };

  const renderItemRowInner=(item,divId,subId,inGroupId=null,tradeGroupInfo?:any)=>{
    const li=itemState[item.id]||{};const tot=getItemTotal(li,itemState);
    const isClar=isClarification(li.costStatus||"");
    const isPct=li.unit==="%";
    const pctBase=isPct?(li.pctTargetIds||[]).reduce((s,id)=>{const t=itemState[id]||{};return t.unit==="%"?s:s+(t.qty||0)*(t.unitCost||0);},0):0;
    const pc=getPC(li.proposal,proposals);const cs=getCostStyle(li.costStatus);
    const isAccepted=li.altType==="Alt"&&li.altStatus==="Accepted";
    const altS=isAccepted?{rowBg:T.positive+"14",leftBar:T.positive,badgeText:T.positive,badge:T.positive}:(T.altStyle as any)[li.altType]||null;
    const altDropVal=isAccepted?"Alt-Accepted":li.altType||"";
    const _searchMatch=searchQuery&&li.included&&(li.description||"").toLowerCase().includes(searchQuery.toLowerCase());
    const rowBg=_searchMatch?"#f6e05e22":altS?altS.rowBg:cs?cs.rowBg:"transparent";
    const leftBar=_searchMatch&&!altS&&!cs?"#f6e05e":altS?altS.leftBar:cs?cs.leftBar:null;
    const V=visibleCols;
    const totColor = tot < 0 ? T.negative : tot > 0 ? T.positive : T.textMuted;
    const contMode=li.contMode||"project";
    const contAmt=calcItemCont(li,contingency,itemState);
    const _rowSel=isRowSel(item.id);const _cutPend=isCutPending(item.id);
    const _dndOver=dndActive&&dndTargetId===item.id;
    const _dndInto=_dndOver&&dndPosition==="into";
    return (
      <tr key={item.id} data-itemid={item.id}
        onDragOver={e=>handleDragOver(e,item.id)} onDrop={handleDrop} onDragEnd={handleDragEnd}
        onContextMenu={e=>{e.preventDefault();e.stopPropagation();if(!rowSelIds.has(item.id)){setRowSelIds(new Set([item.id]));setRowSelAnchorId(item.id);}setCtxMenu({x:e.clientX,y:e.clientY,itemId:item.id,divId,subId});}}
        style={{opacity:_cutPend?0.35:li.included?1:0.28,
          background:_dndInto?T.bgAccentSubtle:_rowSel?T.bgSelection:rowBg,
          outline:_dndInto?`2px solid ${T.borderAccent}`:_rowSel?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1,
          borderTop:_dndOver&&dndPosition==="before"?`2px solid ${T.borderAccent}`:undefined,
          borderBottom:_dndOver&&dndPosition==="after"?`2px solid ${T.borderAccent}`:`1px solid ${T.bgHeader}`}}>
        <td style={{padding:"0 2px",cursor:"grab",textAlign:"center",verticalAlign:"middle",color:T.textWeak,fontSize:10,userSelect:"none"}}
          onMouseDown={e=>startRowSel(item.id,e)} draggable onDragStart={e=>handleDragStart(e,item.id)}>
          <Icon.Menu size={11}/>
        </td>
        <td style={{padding:"3px 4px",textAlign:"center",verticalAlign:"middle",boxShadow:leftBar?`inset 3px 0 0 ${leftBar}`:"none"}}>
          <input type="checkbox" className="cb" checked={!!li.included} onChange={e=>upd(item.id,"included",e.target.checked)}/>
        </td>
        <td style={{padding:"3px 5px",verticalAlign:"middle",overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            {inGroupId
              ?<button className="indent-btn" title="Unindent from group" onClick={()=>unindentItemFromGroup(divId,subId,inGroupId,item.id)}><Icon.CornerUpLeft size={11}/></button>
              :<button className="indent-btn" title="Indent into group above" onClick={()=>indentItemToGroup(divId,subId,item.id)}><Icon.CornerDownRight size={11}/></button>}
            <div style={{flex:1,minWidth:0,position:"relative",...collabCellStyle(item.id,"description")}}>
              <CollabLabel itemId={item.id} field="description"/>
              <input className="ghost-input desc-input" value={li.description??""} onChange={e=>upd(item.id,"description",e.target.value)} placeholder="Description..."
                data-oe-item={item.id} data-oe-field="description"
                onFocus={collabFocus(item.id,"description")} onBlur={collabBlur}
                onKeyDown={e=>{
                  if(shortcuts.enterAddRow&&e.key==="Enter"){e.preventDefault();addItem(divId,subId,inGroupId||null);}
                  if(e.key==="Escape"){(e.target as HTMLInputElement).blur();}
                }}/>
              {showRowLine(li.notes,V.notes)&&<input className="ghost-input notes-input" value={li.notes??""} onChange={e=>upd(item.id,"notes",e.target.value)}
                title="Printed note — this appears on exports and client documents. For internal notes, use the notes icon on the row."
                placeholder="Printed note..."/>}
              {(()=>{
                const linked=rfis.filter(r=>r.status!=="void"&&r.linkedItemIds?.includes(item.id));
                if(linked.length===0)return null;
                const hasOpen=linked.some(r=>r.status==="open");
                const hasAnswered=linked.some(r=>r.status==="answered");
                const dotColor=hasOpen?"#e8a04a":hasAnswered?T.positive:T.textMuted;
                const badgeBg=hasOpen?"#e8a04a22":hasAnswered?`${T.positive}22`:`${T.textMuted}22`;
                const badgeBorder=hasOpen?"#e8a04a55":hasAnswered?T.positive+"55":T.textMuted+"55";
                const badgeColor=hasOpen?"#e8a04a":hasAnswered?T.positive:T.textMuted;
                const tip=linked.map(r=>`RFI-${String(r.number).padStart(3,"0")} (${r.status}): ${r.subject||"Untitled"}`).join("\n");
                return(
                  <span
                    onClick={e=>{e.stopPropagation();setActiveTab("rfi");setActiveRfiId(linked[0].id);setRfiSearch("");setRfiStatusFilter("all");}}
                    title={`${linked.length} linked RFI${linked.length!==1?"s":""} — click to view\n\n${tip}`}
                    style={{marginLeft:5,fontSize:10,fontWeight:700,letterSpacing:".06em",padding:"1px 5px",borderRadius:3,cursor:"pointer",
                      background:badgeBg,border:`1px solid ${badgeBorder}`,color:badgeColor,flexShrink:0,lineHeight:1.4,
                      display:"inline-flex",alignItems:"center",gap:3}}>
                    <span style={{width:5,height:5,borderRadius:"50%",background:dotColor,display:"inline-block"}}/>
                    RFI{linked.length>1?` ${linked.length}`:""}
                  </span>
                );
              })()}
              {/* Internal notes on this line. Shown only when there are some,
                  or on hover via the row -- a permanent icon on every row of a
                  dense estimate is noise. */}
              {notesEnabled&&(()=>{
                const mine=openNotesFor(notes,item.id);
                if(mine.length===0)return null;
                return(
                  <span onClick={e=>{e.stopPropagation();openNotes({kind:"item",id:item.id,label:li.description||"Line item"});}}
                    title={mine.length===1?mine[0].body:`${mine.length} open notes`}
                    style={{marginLeft:5,fontSize:10,fontWeight:700,letterSpacing:".06em",padding:"1px 5px",borderRadius:3,cursor:"pointer",
                      background:`${T.borderAccent}18`,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,flexShrink:0,lineHeight:1.4,
                      display:"inline-flex",alignItems:"center",gap:3}}>
                    <Icon.MessageSquare size={9}/>{mine.length>1?mine.length:""}
                  </span>
                );
              })()}
            </div>
          </div>
        </td>
        {V.status&&<td style={{padding:"3px 5px",verticalAlign:"middle",position:"relative"}}>
          {tradeGroupInfo?.isMember?(
            <div style={{display:"flex",alignItems:"center",gap:3,padding:"1px 0"}}>
              <span style={{flex:1,fontSize:10,color:T.textWeak,fontStyle:"italic" as any,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                {(()=>{const _li0=itemState[tradeGroupInfo.groupItemIds[0]]||{};const _bid0=_li0.sourceBidId?bids.find((b:any)=>b.id===_li0.sourceBidId):null;const _p0=_bid0?findPartner(_bid0.partnerId):null;return "↳ "+(_p0?.name||_li0.tradeName||"grouped");})()}
              </span>
              <button title="Ungroup — restore separate trade assignments" onClick={()=>{snapshot();setItemState(prev=>{const n={...prev};tradeGroupInfo.groupItemIds.forEach((id:string)=>{n[id]={...n[id],tradeName:undefined,tradeDate:undefined,sourceBidId:undefined};});return n;});}}
                style={{fontSize:10,lineHeight:1,padding:"2px 5px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",flexShrink:0,fontFamily:FONT}}><Icon.X size={10}/></button>
            </div>
          ):(
          <>
          <StatusSelect id={item.id+"_status"} value={li.costStatus||""}
            onChange={v=>{
              upd(item.id,"costStatus",v);
              // Clearing the status retracts the claim the status was making,
              // so the bid it credited stops reading as this line's basis.
              const drop=statusLinkPatch(v);
              if(drop)setItemState(prev=>({...prev,[item.id]:{...prev[item.id],...drop}}));
              if((v==="Hard Bid"||v==="Allowance"||v==="Needs Review")&&bids.filter(b=>b.status==="Active").length>0){
                setBidLinkFor(item.id);
                setBidLinkOtherOpen(false);
              } else {
                setBidLinkFor(null);
              }
            }}/>
          {/* Re-link button — only shown when status is already a bid status and there are active bids */}
          {(li.costStatus==="Hard Bid"||li.costStatus==="Allowance"||li.costStatus==="Needs Review")
            &&bids.filter(b=>b.status==="Active").length>0
            &&bidLinkFor!==item.id&&(
            <button title="Link to a bid" onClick={()=>{setBidLinkFor(item.id);setBidLinkOtherOpen(false);}}
              style={{position:"absolute",right:2,top:"50%",transform:"translateY(-50%)",
                fontSize:10,padding:"1px 4px",background:"none",border:"none",
                color:li.sourceBidId?T.borderAccent:T.textWeak,cursor:"pointer",lineHeight:1,opacity:.7}}>
              <Icon.Link size={12}/>
            </button>
          )}
          {bidLinkFor===item.id&&(()=>{
            const activeBids=bids.filter(b=>b.status==="Active");
            // Segment: bids whose scope covers the current division vs. everything else
            const divisionBids=activeBids.filter(b=>{
              const sc=bidScopes.find(s=>s.id===b.scopeId);
              return sc?.divisionIds?.includes(divId);
            });
            const otherBids=activeBids.filter(b=>!divisionBids.includes(b));

            const renderBidRow=(b:any)=>{
              const p=findPartner(b.partnerId);
              const sc=bidScopes.find((x:any)=>x.id===b.scopeId);
              const dateStr=b.date?new Date(b.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",year:"2-digit"}):"";
              const label=(p?.name||"Unknown")+(b.version>1?` v${b.version}`:"")+(sc?" · "+sc.name:"")+(dateStr?" ("+dateStr+")":"");
              return(
                <button key={b.id}
                  onClick={()=>{snapshot();setItemState(prev=>({...prev,[item.id]:{...prev[item.id],sourceBidId:b.id,tradeName:undefined,tradeDate:undefined}}));setBidLinkFor(null);}}
                  style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",color:T.textSecondary,fontSize:10,fontFamily:FONT,padding:"5px 8px",cursor:"pointer",borderRadius:3,lineHeight:1.4}}
                  onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                  onMouseLeave={e=>(e.currentTarget.style.background="none")}>
                  {label}
                </button>
              );
            };

            return(<>
              <div style={{position:"fixed",inset:0,zIndex:299}} onClick={()=>setBidLinkFor(null)}/>
              <div style={{position:"absolute",right:0,top:"100%",zIndex:300,background:T.bgModal,border:`1px solid ${T.borderAccent}55`,borderRadius:3,padding:"6px 8px",boxShadow:"0 4px 16px #0008",width:260}}>
                {/* Header row */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6,gap:4}}>
                  <div style={{fontSize:10,color:T.borderAccent,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase"}}>Link a Bid</div>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={()=>setBidLinkFor(null)}
                      style={{fontSize:10,color:T.textWeak,background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,padding:"2px 7px",cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap" as any}}>
                      Skip
                    </button>
                    <button
                      onClick={()=>{
                        const matchingScope=bidScopes.find((s:any)=>s.divisionIds?.includes(divId));
                        if(matchingScope)setBidImportScopeId(matchingScope.id);
                        setShowBidImport(true);
                        setBidLinkFor(null);
                      }}
                      style={{fontSize:10,fontWeight:600,color:T.borderAccent,background:T.bgAccentSubtle,border:`1px solid ${T.borderAccentFaint}`,borderRadius:3,padding:"2px 7px",cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap" as any}}>
                      + Import Bid
                    </button>
                  </div>
                </div>

                {/* This Division */}
                {divisionBids.length>0&&(
                  <>
                    <div style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",padding:"2px 8px 2px",marginBottom:1,borderTop:`1px solid ${T.borderSecondary}`}}>
                      This Division
                    </div>
                    {divisionBids.map(renderBidRow)}
                  </>
                )}

                {/* Other Divisions — collapsed by default */}
                {otherBids.length>0&&(
                  <>
                    <button
                      onClick={()=>setBidLinkOtherOpen(v=>!v)}
                      style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:"none",border:"none",borderTop:`1px solid ${T.borderSecondary}`,
                        padding:"5px 8px 3px",cursor:"pointer",fontFamily:FONT}}>
                      <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any}}>
                        Other Divisions ({otherBids.length})
                      </span>
                      <span style={{fontSize:10,color:T.textWeak,transition:"transform .15s",display:"inline-block",transform:bidLinkOtherOpen?"rotate(180deg)":"none"}}>▾</span>
                    </button>
                    {bidLinkOtherOpen&&otherBids.map(renderBidRow)}
                  </>
                )}

                {activeBids.length===0&&(
                  <div style={{fontSize:10,color:T.textWeak,padding:"4px 8px"}}>No active bids — use "+ Import Bid" to add one.</div>
                )}

              </div>
            </>);
          })()}
          {(()=>{
            // Bid Leveling embedded source (newer system — sourceBidId on itemState)
            const bidSrc=li.sourceBidId?bids.find(b=>b.id===li.sourceBidId):null;
            const bidPartnerSrc=bidSrc?findPartner(bidSrc.partnerId):null;
            if(bidSrc&&bidPartnerSrc){
              const bidScopeSrc=bidScopes.find(s=>s.id===bidSrc.scopeId);
              const bidDateLabel=bidSrc.date?new Date(bidSrc.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"}):"";
              const chipLabel=`${bidPartnerSrc.name}${bidSrc.version>1?` v${bidSrc.version}`:""}${bidDateLabel?` · ${bidDateLabel}`:""}`;
              const tipText=`${bidPartnerSrc.name}${bidSrc.version>1?` · v${bidSrc.version}`:""}${bidDateLabel?` · ${bidDateLabel}`:""}${bidScopeSrc?` · ${bidScopeSrc.name}`:""}  — Click to view in Bids`;
              return(
                <div style={{display:"flex",alignItems:"center",gap:3,marginTop:4}}>
                  <button
                    title={tipText}
                    onClick={()=>{
                      setActiveTab("bids");
                      setBidSubTab("compare");
                      const tn=(li.tradeName||bidPartnerSrc.name||"").trim();
                      void tn; // partner filter removed — bids compare shows all
                    }}
                    style={{flex:1,fontSize:10,color:T.borderAccent,background:T.bgAccentSubtle,
                      border:`1px solid ${T.borderAccentFaint}`,borderRadius:3,padding:"2px 6px",
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                      cursor:"pointer",letterSpacing:".02em",fontFamily:FONT,textAlign:"left",
                      display:"flex",alignItems:"center",gap:4}}>
                    <span style={{opacity:.6,fontSize:10,flexShrink:0}}>↗</span>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{chipLabel}</span>
                  </button>
                  <button title="Change bid link" onClick={()=>setBidLinkFor(item.id)}
                    style={{fontSize:10,lineHeight:1,padding:"2px 5px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",flexShrink:0,fontFamily:FONT}}>
                    ↺
                  </button>
                  <button title="Remove bid link" onClick={()=>{snapshot();setItemState(p=>({...p,[item.id]:{...p[item.id],sourceBidId:undefined}}));}}
                    style={{fontSize:10,lineHeight:1,padding:"2px 5px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",flexShrink:0,fontFamily:FONT}}>
                    <Icon.X size={10}/>
                  </button>
                </div>
              );
            }
            return null;
          })()}
          {/* Who the price came from. Drawn once it says someone, or once
              the trade column is asked for -- an empty "Basis of bid..." on
              every priced row was a second line per row that named nobody. */}
          {(li.costStatus==="Hard Bid"||li.costStatus==="Allowance"||li.costStatus==="Needs Review")&&!li.sourceBidId
            &&showRowLine(li.tradeName,V.tradeName)&&(
            <div style={{display:"flex",alignItems:"center",gap:2,marginTop:2}}>
              <TradePartnerPicker tiny value={li.tradeName??""} partners={pickerPartners} T={T} FONT={FONT}
                placeholder={li.costStatus==="Hard Bid"?"Basis of bid…":li.costStatus==="Allowance"?"Basis of allowance…":"Trade partner…"}
                onChange={n=>{snapshot();upd(item.id,"tradeName",n||undefined);}}
                onRequestAdd={prefill=>setAddPartnerModal({prefill,onAdded:op=>{
                  addSupabasePartner(op);
                  snapshot();upd(item.id,"tradeName",op.name);
                  setAddPartnerModal(null);
                }})}/>
            </div>
          )}
          {li.sourceBidId&&li.tradeName&&(
            <div style={{display:"flex",alignItems:"center",gap:3,marginTop:3}}>
              <TradePartnerPicker tiny value={li.tradeName??""} partners={pickerPartners} T={T} FONT={FONT}
                onChange={n=>{snapshot();upd(item.id,"tradeName",n||undefined);}}
                onRequestAdd={prefill=>setAddPartnerModal({prefill,onAdded:op=>{
                  addSupabasePartner(op);
                  snapshot();upd(item.id,"tradeName",op.name);
                  setAddPartnerModal(null);
                }})}/>
            </div>
          )}
          {tradeGroupInfo?.isLeader&&(
            <div style={{display:"flex",alignItems:"center",gap:3,marginTop:3,paddingTop:3,borderTop:`1px dashed ${T.borderSecondary}`}}>
              <div style={{flex:1,fontSize:10,color:T.textWeak,letterSpacing:".03em"}}>⊕ {tradeGroupInfo.groupItemIds.length} grouped</div>
              <button title="Ungroup — restore separate trade assignments for all items" onClick={()=>{snapshot();setItemState(prev=>{const n={...prev};tradeGroupInfo.groupItemIds.forEach((id:string)=>{n[id]={...n[id],tradeName:undefined,tradeDate:undefined,sourceBidId:undefined};});return n;});}}
                style={{fontSize:10,lineHeight:1,padding:"2px 6px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",flexShrink:0,fontFamily:FONT,letterSpacing:".02em"}}>
                <Icon.X size={10}/> ungroup
              </button>
            </div>
          )}
          </>)}
        </td>}
        {V.qty&&<td onMouseDown={isClar?undefined:e=>startSel(item.id,"qty",e)} onMouseEnter={isClar?undefined:()=>extendSel(item.id,"qty")}
          style={{padding:"3px 4px",verticalAlign:"middle",userSelect:"none",
            background:isSel(item.id,"qty")?T.bgSelection:"transparent",
            outline:isSel(item.id,"qty")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
          {isClar
            ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"center"}}>—</span>
            :<div style={{position:"relative",...collabCellStyle(item.id,"qty")}}><CollabLabel itemId={item.id} field="qty"/>
            <NumberInput value={li.qty} formula={li.qtyFormula}
              onChange={(v,f)=>{upd(item.id,"qty",v);upd(item.id,"qtyFormula",f||"");}}
              onFocus={collabFocus(item.id,"qty")} onBlur={collabBlur}
              style={{textAlign:"center",fontSize:12,padding:"3px 5px"}}
              data-oe-item={item.id} data-oe-field="qty"
            />
            </div>}
        </td>}
        {V.unit&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
          {isClar
            ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"center"}}>—</span>
            :<select className="input-field" value={li.unit||"Ea"} onChange={e=>upd(item.id,"unit",e.target.value)} onFocus={collabFocus(item.id,"unit")} onBlur={collabBlur}
              data-oe-item={item.id} data-oe-field="unit"
              style={{fontSize:11,padding:"3px 18px 3px 5px",textAlign:"center",appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
                backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
              {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
              {li.unit&&!UNIT_OPTIONS.includes(li.unit)&&<option value={li.unit}>{li.unit}</option>}
            </select>}
        </td>}
        {V.unitCost&&<td onMouseDown={isClar?undefined:e=>startSel(item.id,"unitCost",e)} onMouseEnter={isClar?undefined:()=>extendSel(item.id,"unitCost")}
          style={{padding:"3px 4px",verticalAlign:"middle",userSelect:"none",
            textDecoration:li.altType==="Void"?"line-through":"none",opacity:li.altType==="Void"?0.45:1,
            background:isSel(item.id,"unitCost")?T.bgSelection:"transparent",
            outline:isSel(item.id,"unitCost")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
          {isClar
            ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"right"}}>—</span>
            :isPct?(
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <button onClick={()=>{setPickerSel(li.pctTargetIds||[]);setPickingPctForId(item.id);}}
                  style={{fontSize:10,padding:"2px 5px",borderRadius:3,background:T.versionA.bg,border:`1px solid ${T.versionA.bar}`,color:T.versionA.bar,cursor:"pointer",fontFamily:FONT,flexShrink:0,whiteSpace:"nowrap"}}>
                  {(li.pctTargetIds||[]).length?`${(li.pctTargetIds||[]).length} items`:"Select..."}
                </button>
                <span style={{flex:1,textAlign:"right",fontSize:11,color:(li.pctTargetIds||[]).length?T.textTertiary:T.textWeak,whiteSpace:"nowrap"}}>
                  {(li.pctTargetIds||[]).length?fmt(pctBase):"base"}
                </span>
              </div>
            ):(
              <div style={{position:"relative",...collabCellStyle(item.id,"unitCost")}}><CollabLabel itemId={item.id} field="unitCost"/>
              <div style={{display:"flex",alignItems:"center",gap:2}}>
                <CurrencyInput value={li.unitCost??0} formula={li.unitCostFormula}
                  onChange={(val,f)=>{upd(item.id,"unitCost",val);upd(item.id,"unitCostFormula",f||"");}}
                  style={{fontSize:12,padding:"3px 6px",flex:1,minWidth:0}} onEnter={()=>shortcuts.enterAddRow&&addItem(divId,subId,inGroupId||null)} negColor={T.negative} onFocus={collabFocus(item.id,"unitCost")} onBlur={collabBlur}
                  data-oe-item={item.id} data-oe-field="unitCost"
                />
              </div>
              </div>
            )}
        </td>}
        {V.total&&<td onMouseDown={isClar?undefined:e=>startSel(item.id,"total",e)} onMouseEnter={isClar?undefined:()=>extendSel(item.id,"total")}
          style={{padding:"3px 6px",textAlign:"right",verticalAlign:"middle",fontSize:13,fontWeight:tot!==0?500:400,
            color:isClar?T.textWeak:totColor,
            cursor:"default",userSelect:"none",
            textDecoration:li.altType==="Void"?"line-through":"none",opacity:li.altType==="Void"?0.45:isClar?0.5:1,
            background:isSel(item.id,"total")?T.bgSelection:"transparent",
            outline:isSel(item.id,"total")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:5}}>
            {li.excludeFromMarkup&&<span title="Excluded from OH&P markup" style={{fontSize:10,padding:"1px 4px",borderRadius:3,background:"#c08040",color:"#fff",fontWeight:700,letterSpacing:.5,flexShrink:0}}>NO MU</span>}
            {isClar?"—":tot!==0?fmt(tot):"--"}
          </div>
        </td>}
        {V.proposal&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
          <select className="input-field" value={li.proposal||proposals[0]?.id||"p1"} onChange={e=>{
              if(e.target.value==="__new__"){
                snapshot();
                const newId=uid();const nextColor=proposals.length%6;
                setProposals(p=>[...p,{id:newId,name:`Package ${p.length+1}`,color:nextColor}]);
                upd(item.id,"proposal",newId);
              }else{upd(item.id,"proposal",e.target.value);}
            }}
            style={{fontSize:10,padding:"3px 18px 3px 6px",color:pc.text,background:pc.bg,border:`1px solid ${pc.text}35`,fontWeight:500,
              appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
              backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
              backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
            {proposals.map(p=><option key={p.id} value={p.id} style={{background:T.bgInput,color:T.textSecondary}}>{p.name}</option>)}
            <option value="__new__" style={{background:T.bgInput,color:T.borderAccent}}>+ New Package</option>
          </select>
        </td>}
        {V.altType&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
          <select className="input-field" value={altDropVal} onChange={e=>{
              const newAlt=e.target.value;
              const clean=(li.description||"").replace(/^(ADD:|DEDUCT:|REJECTED:)\s*/i,"");
              const pos=(li.qty||0)*(li.unitCost||0)>=0;
              if(newAlt==="Alt-Accepted"){upd(item.id,"altType","Alt");upd(item.id,"altStatus","Accepted");upd(item.id,"description",(pos?"ADD: ":"DEDUCT: ")+clean);}
              else if(newAlt==="Alt"){upd(item.id,"altType","Alt");upd(item.id,"altStatus","TBD");upd(item.id,"description",(pos?"ADD: ":"DEDUCT: ")+clean);}
              else if(newAlt==="Void"){upd(item.id,"altType","Void");upd(item.id,"altStatus","Void");upd(item.id,"description","REJECTED: "+clean);}
              else{upd(item.id,"altType","");upd(item.id,"altStatus","");upd(item.id,"description",clean);}
            }}
            style={{fontSize:10,padding:"3px 18px 3px 6px",color:altS?altS.badgeText:T.textWeak,border:altS?`1px solid ${altS.leftBar}55`:`1px solid ${T.borderSecondary}`,fontWeight:altS?600:400,
              appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
              backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
              backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
            <option value="">Base Bid</option>
            <option value="Alt" style={{background:T.bgInput,color:T.textSecondary}}>Alternate — TBD</option>
            <option value="Alt-Accepted" style={{background:T.bgInput,color:T.textSecondary}}>Accepted ✓</option>
            <option value="Void" style={{background:T.bgInput,color:T.textSecondary}}>Rejected ✕</option>
          </select>
        </td>}
        {V.contingency&&(!li.altType?(
          <td onMouseDown={e=>startSel(item.id,"contingency",e)} onMouseEnter={()=>extendSel(item.id,"contingency")} title={contLive?undefined:contDeadNote}
            style={{padding:"3px 4px",verticalAlign:"middle",
              opacity:contLive?1:.4,background:isSel(item.id,"contingency")?T.bgSelection:"transparent",
              outline:isSel(item.id,"contingency")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1,
              userSelect:"none"}}>
            <div style={{display:"flex",alignItems:"center",gap:3}}>
              <select value={contMode} onChange={e=>upd(item.id,"contMode",e.target.value)}
                style={{fontSize:10,padding:"2px 14px 2px 3px",flex:"0 0 auto",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
                  color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT,outline:"none",maxWidth:80,
                  appearance:"none" as any,WebkitAppearance:"none" as any,
                  backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                  backgroundRepeat:"no-repeat",backgroundPosition:"right 4px center"}}>
                <option value="project">Proj. {contingency}%</option>
                <option value="pct">Custom %</option>
                <option value="dollar">Custom $</option>
                <option value="none">None</option>
              </select>
              {contMode==="pct"&&<input type="number" className="input-field" value={li.contValue??""}
                onChange={e=>upd(item.id,"contValue",+e.target.value)}
                style={{width:34,fontSize:10,padding:"2px 3px",textAlign:"right"}} placeholder="%"/>}
              {contMode==="dollar"
                ?<CurrencyInput value={li.contValue??0} onChange={val=>upd(item.id,"contValue",val)} style={{flex:1,fontSize:11,padding:"2px 4px"}} negColor={T.negative}/>
                :contMode==="none"
                  ?<span style={{flex:1,textAlign:"right",fontSize:11,color:T.textWeak}}>--</span>
                  :<span style={{flex:1,textAlign:"right",fontSize:12,color:T.textMuted,fontWeight:500,whiteSpace:"nowrap"}}>
                    {contAmt!==0?fmt(contAmt):"--"}
                  </span>
              }
            </div>
          </td>
        ):<td/>)}
        <td style={{padding:"3px 2px",textAlign:"center",verticalAlign:"middle",whiteSpace:"nowrap"}}>
          <button title="Duplicate item" onClick={e=>{e.stopPropagation();cloneItem(divId,subId,item.id);}}
            style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:11,padding:"2px 4px",opacity:0.6}}
            onMouseEnter={e=>(e.currentTarget.style.opacity="1")}
            onMouseLeave={e=>(e.currentTarget.style.opacity="0.6")}><Icon.Duplicate size={11}/></button>
          <button className="del-btn" onClick={()=>removeItem(divId,subId,item.id)}>x</button>
        </td>
      </tr>
    );
  };

  const renderGroupRowsInner=(group,divId,subId)=>{
    const isOpen=expandedGrps[group.id]!==false;const gTotal=rowsTotal(group.children);
    const V=visibleCols;
    const colspan=3+[V.status,V.qty,V.unit,V.unitCost,V.total,V.proposal,V.altType,V.contingency].filter(Boolean).length+1;
    const gContTotal=V.contingency?rowsContTotal(group.children):0;
    const gDisplay=gTotal+gContTotal;
    const gTotColor = gDisplay < 0 ? T.negative : gDisplay > 0 ? T.positive : T.textMuted;
    const _grpSel=isRowSel(group.id);const _grpCutPend=isCutPending(group.id);
    return (
      <React.Fragment key={group.id}>
        <tr onDragOver={e=>handleDragOver(e,group.id)} onDrop={handleDrop} onDragEnd={handleDragEnd}
          style={{opacity:_grpCutPend?0.35:1,
            background:dndActive&&dndTargetId===group.id&&dndPosition==="into"?T.bgAccentSubtle:_grpSel?T.bgSelection:undefined,
            outline:dndActive&&dndTargetId===group.id&&dndPosition==="into"?`2px solid ${T.borderAccent}`:_grpSel?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1,
            borderTop:dndActive&&dndTargetId===group.id&&dndPosition==="before"?`2px solid ${T.borderAccent}`:undefined,
            borderBottom:dndActive&&dndTargetId===group.id&&dndPosition==="after"?`2px solid ${T.borderAccent}`:undefined}}>
          <td colSpan={colspan} style={{padding:0,borderTop:`2px solid ${T.borderAccentFaint}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0 5px 0",background:_grpSel?T.bgSelection:T.bgApp}}>
              <div style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                <span style={{cursor:"grab",color:T.textWeak,fontSize:10,userSelect:"none",padding:"0 2px",flexShrink:0}}
                  onMouseDown={e=>startRowSel(group.id,e)} draggable onDragStart={e=>handleDragStart(e,group.id)}><Icon.Menu size={11}/></span>
                <button onClick={()=>setExpandedGrps(p=>({...p,[group.id]:!isOpen}))}
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,padding:"0 2px",flexShrink:0}}>
                  {isOpen?"v":">"}
                </button>
                <input className="ghost-input group-name-input" value={group.name}
                  onChange={e=>renameGroup(divId,subId,group.id,e.target.value)}
                  style={{fontWeight:700,fontSize:11,color:T.borderAccent,letterSpacing:".06em",textTransform:"uppercase",flex:1,maxWidth:380,padding:"1px 4px"}}/>
                <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",flexShrink:0}}>Roll-up</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,paddingRight:2}}>
                <span style={{fontSize:12,color:gTotColor,fontWeight:600}}>{gDisplay!==0?fmt(gDisplay):"--"}</span>
                <button className="plus-btn" style={{width:18,height:18,fontSize:13}} onClick={()=>addItem(divId,subId,group.id)}>+</button>
                <button className="del-btn" style={{opacity:.55}} title="Ungroup — move items back to subcategory" onClick={()=>removeGroup(divId,subId,group.id)}><Icon.CornerUpLeft size={10}/></button>
              </div>
            </div>
          </td>
        </tr>
        {isOpen&&group.children.map(child=>{
          const li=itemState[child.id]||{};const tot=getItemTotal(li,itemState);
          const isPct2=li.unit==="%";
          const pctBase2=isPct2?(li.pctTargetIds||[]).reduce((s,id)=>{const t=itemState[id]||{};return t.unit==="%"?s:s+(t.qty||0)*(t.unitCost||0);},0):0;
          const cp=getPC(li.proposal,proposals);const cs2=getCostStyle(li.costStatus);
          const isAccepted2=li.altType==="Alt"&&li.altStatus==="Accepted";
          const altS2=isAccepted2?{rowBg:T.positive+"14",leftBar:T.positive,badgeText:T.positive,badge:T.positive}:(T.altStyle as any)[li.altType]||null;
          const altDropVal2=isAccepted2?"Alt-Accepted":li.altType||"";
          const leftBar=altS2?altS2.leftBar:cs2?cs2.leftBar:T.borderAccentFaint;
          const totColor2 = tot < 0 ? T.negative : tot > 0 ? T.positive : T.textMuted;
          const _childSel=isRowSel(child.id);const _childCutPend=isCutPending(child.id);
          const _childDndOver=dndActive&&dndTargetId===child.id;
          const _childDndInto=_childDndOver&&dndPosition==="into";
          const isClar2=isClarification(li.costStatus||"");
          return (
            <tr key={child.id} data-itemid={child.id}
              onDragOver={e=>handleDragOver(e,child.id)} onDrop={handleDrop} onDragEnd={handleDragEnd}
              style={{borderBottom:_childDndOver&&dndPosition==="after"?`2px solid ${T.borderAccent}`:`1px solid ${T.bgHeader}`,
                borderTop:_childDndOver&&dndPosition==="before"?`2px solid ${T.borderAccent}`:undefined,
                opacity:_childCutPend?0.35:li.included?1:0.28,
                background:_childDndInto?T.bgAccentSubtle:_childSel?T.bgSelection:altS2?altS2.rowBg:cs2?cs2.rowBg:T.bgAccentFaint,
                outline:_childDndInto?`2px solid ${T.borderAccent}`:_childSel?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
              <td style={{padding:"0 2px",cursor:"grab",textAlign:"center",verticalAlign:"middle",color:T.textWeak,fontSize:10,userSelect:"none"}}
                onMouseDown={e=>startRowSel(child.id,e)} draggable onDragStart={e=>handleDragStart(e,child.id)}>
                <Icon.Menu size={11}/>
              </td>
              <td style={{padding:"3px 4px",textAlign:"center",verticalAlign:"middle",boxShadow:`inset 3px 0 0 ${leftBar}`}}>
                <input type="checkbox" className="cb" checked={!!li.included} onChange={e=>upd(child.id,"included",e.target.checked)}/>
              </td>
              <td style={{padding:"3px 5px",verticalAlign:"middle",overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:4,paddingLeft:14}}>
                  <button className="indent-btn" title="Unindent from group" onClick={()=>unindentItemFromGroup(divId,subId,group.id,child.id)}><Icon.CornerUpLeft size={11}/></button>
                  <div style={{flex:1,minWidth:0}}>
                    <input className="ghost-input desc-input" value={li.description??""} onChange={e=>upd(child.id,"description",e.target.value)} placeholder="Description..."
                      data-oe-item={child.id} data-oe-field="description"
                      onKeyDown={e=>{
                        if(shortcuts.enterAddRow&&e.key==="Enter"){e.preventDefault();addItem(divId,subId,group.id);}
                        if(e.key==="Escape"){(e.target as HTMLInputElement).blur();}
                      }}/>
                    {showRowLine(li.notes,V.notes)&&<input className="ghost-input notes-input" value={li.notes??""} onChange={e=>upd(child.id,"notes",e.target.value)}
                    title="Printed note — this appears on exports and client documents. For internal notes, use the notes icon on the row."
                    placeholder="Printed note..."/>}
                  </div>
                </div>
              </td>
              {V.status&&<td style={{padding:"3px 5px",verticalAlign:"middle"}}>
                <StatusSelect id={child.id+"_status"} value={li.costStatus||""} onChange={v=>{
                  upd(child.id,"costStatus",v);
                  const drop=statusLinkPatch(v);
                  if(drop)setItemState(prev=>({...prev,[child.id]:{...prev[child.id],...drop}}));
                }}/>
                {showRowLine(li.tradeName,V.tradeName)&&(
                  <div style={{display:"flex",alignItems:"center",gap:3,marginTop:3}}>
                    <TradePartnerPicker tiny value={li.tradeName??""} partners={pickerPartners} T={T} FONT={FONT}
                      onChange={n=>{snapshot();upd(child.id,"tradeName",n||undefined);}}
                      onRequestAdd={prefill=>setAddPartnerModal({prefill,onAdded:op=>{
                        addSupabasePartner(op);
                        snapshot();upd(child.id,"tradeName",op.name);
                        setAddPartnerModal(null);
                      }})}/>
                  </div>
                )}
              </td>}
              {V.qty&&<td onMouseDown={isClar2?undefined:e=>startSel(child.id,"qty",e)} onMouseEnter={isClar2?undefined:()=>extendSel(child.id,"qty")}
                style={{padding:"3px 4px",verticalAlign:"middle",userSelect:"none",
                  background:isSel(child.id,"qty")?T.bgSelection:"transparent",
                  outline:isSel(child.id,"qty")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
                {isClar2
                  ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"center"}}>—</span>
                  :<NumberInput value={li.qty} formula={li.qtyFormula}
                    onChange={(v,f)=>{upd(child.id,"qty",v);upd(child.id,"qtyFormula",f||"");}}
                    data-oe-item={child.id} data-oe-field="qty"
                    style={{textAlign:"center",fontSize:12,padding:"3px 5px"}}/>}
              </td>}
              {V.unit&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
                {isClar2
                  ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"center"}}>—</span>
                  :<select className="input-field" value={li.unit||"Ea"} onChange={e=>upd(child.id,"unit",e.target.value)}
                    data-oe-item={child.id} data-oe-field="unit"
                    style={{fontSize:11,padding:"3px 18px 3px 5px",textAlign:"center",appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
                      backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                      backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
                    {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
                    {li.unit&&!UNIT_OPTIONS.includes(li.unit)&&<option value={li.unit}>{li.unit}</option>}
                  </select>}
              </td>}
              {V.unitCost&&<td onMouseDown={isClar2?undefined:e=>startSel(child.id,"unitCost",e)} onMouseEnter={isClar2?undefined:()=>extendSel(child.id,"unitCost")}
                style={{padding:"3px 4px",verticalAlign:"middle",userSelect:"none",
                  textDecoration:li.altType==="Void"?"line-through":"none",opacity:li.altType==="Void"?0.45:isClar2?0.5:1,
                  background:isSel(child.id,"unitCost")?T.bgSelection:"transparent",
                  outline:isSel(child.id,"unitCost")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
                {isClar2
                  ?<span style={{fontSize:11,color:T.textWeak,display:"block",textAlign:"right"}}>—</span>
                  :isPct2?(
                    <div style={{display:"flex",alignItems:"center",gap:3}}>
                      <button onClick={()=>{setPickerSel(li.pctTargetIds||[]);setPickingPctForId(child.id);}}
                        style={{fontSize:10,padding:"2px 5px",borderRadius:3,background:T.versionA.bg,border:`1px solid ${T.versionA.bar}`,color:T.versionA.bar,cursor:"pointer",fontFamily:FONT,flexShrink:0,whiteSpace:"nowrap"}}>
                        {(li.pctTargetIds||[]).length?`${(li.pctTargetIds||[]).length} items`:"Select..."}
                      </button>
                      <span style={{flex:1,textAlign:"right",fontSize:11,color:(li.pctTargetIds||[]).length?T.textTertiary:T.textWeak,whiteSpace:"nowrap"}}>
                        {(li.pctTargetIds||[]).length?fmt(pctBase2):"base"}
                      </span>
                    </div>
                  ):(
                    <CurrencyInput value={li.unitCost??0} formula={li.unitCostFormula}
                      onChange={(val,f)=>{upd(child.id,"unitCost",val);upd(child.id,"unitCostFormula",f||"");}}
                      data-oe-item={child.id} data-oe-field="unitCost"
                      style={{fontSize:12,padding:"3px 6px"}} onEnter={()=>shortcuts.enterAddRow&&addItem(divId,subId,group.id)} negColor={T.negative}/>
                  )}
              </td>}
              {V.total&&<td onMouseDown={isClar2?undefined:e=>startSel(child.id,"total",e)} onMouseEnter={isClar2?undefined:()=>extendSel(child.id,"total")}
                style={{padding:"3px 6px",textAlign:"right",verticalAlign:"middle",fontSize:13,fontWeight:tot!==0?500:400,
                  color:isClar2?T.textWeak:totColor2,
                  cursor:"default",userSelect:"none",
                  textDecoration:li.altType==="Void"?"line-through":"none",opacity:li.altType==="Void"?0.45:isClar2?0.5:1,
                  background:isSel(child.id,"total")?T.bgSelection:"transparent",
                  outline:isSel(child.id,"total")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1}}>
                {isClar2?"—":tot!==0?fmt(tot):"--"}
              </td>}
              {V.proposal&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
                <select className="input-field" value={li.proposal||proposals[0]?.id||"p1"} onChange={e=>{
                    if(e.target.value==="__new__"){
                      snapshot();
                      const newId=uid();const nextColor=proposals.length%6;
                      setProposals(p=>[...p,{id:newId,name:`Package ${p.length+1}`,color:nextColor}]);
                      upd(child.id,"proposal",newId);
                    }else{upd(child.id,"proposal",e.target.value);}
                  }}
                  style={{fontSize:10,padding:"3px 18px 3px 6px",color:cp.text,background:cp.bg,border:`1px solid ${cp.text}35`,fontWeight:500,
                    appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
                    backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                    backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
                  {proposals.map(pp=><option key={pp.id} value={pp.id} style={{background:T.bgInput,color:T.textSecondary}}>{pp.name}</option>)}
                  <option value="__new__" style={{background:T.bgInput,color:T.borderAccent}}>+ New Package</option>
                </select>
              </td>}
              {V.altType&&<td style={{padding:"3px 4px",verticalAlign:"middle"}}>
                <select className="input-field" value={altDropVal2} onChange={e=>{
                    const newAlt=e.target.value;
                    const clean=(li.description||"").replace(/^(ADD:|DEDUCT:|REJECTED:)\s*/i,"");
                    const pos=(li.qty||0)*(li.unitCost||0)>=0;
                    if(newAlt==="Alt-Accepted"){upd(child.id,"altType","Alt");upd(child.id,"altStatus","Accepted");upd(child.id,"description",(pos?"ADD: ":"DEDUCT: ")+clean);}
                    else if(newAlt==="Alt"){upd(child.id,"altType","Alt");upd(child.id,"altStatus","TBD");upd(child.id,"description",(pos?"ADD: ":"DEDUCT: ")+clean);}
                    else if(newAlt==="Void"){upd(child.id,"altType","Void");upd(child.id,"altStatus","Void");upd(child.id,"description","REJECTED: "+clean);}
                    else{upd(child.id,"altType","");upd(child.id,"altStatus","");upd(child.id,"description",clean);}
                  }}
                  style={{fontSize:10,padding:"3px 18px 3px 6px",color:altS2?altS2.badgeText:T.textWeak,border:altS2?`1px solid ${altS2.leftBar}55`:`1px solid ${T.borderSecondary}`,fontWeight:altS2?600:400,
                    appearance:"none" as any,WebkitAppearance:"none" as any,cursor:"pointer",
                    backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                    backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
                  <option value="">Base Bid</option>
                  <option value="Alt" style={{background:T.bgInput,color:T.textSecondary}}>Alternate — TBD</option>
                  <option value="Alt-Accepted" style={{background:T.bgInput,color:T.textSecondary}}>Accepted ✓</option>
                  <option value="Void" style={{background:T.bgInput,color:T.textSecondary}}>Rejected ✕</option>
                </select>
              </td>}
              {V.contingency&&(!li.altType?(()=>{
                const cMode=li.contMode||"project";
                const cAmt=calcItemCont(li,contingency,itemState);
                return (
                  <td onMouseDown={e=>startSel(child.id,"contingency",e)} onMouseEnter={()=>extendSel(child.id,"contingency")} title={contLive?undefined:contDeadNote}
                    style={{padding:"3px 4px",verticalAlign:"middle",
                      opacity:contLive?1:.4,background:isSel(child.id,"contingency")?T.bgSelection:"transparent",
                      outline:isSel(child.id,"contingency")?`1px solid ${T.borderAccent}55`:undefined,outlineOffset:-1,
                      userSelect:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:3}}>
                      <select value={cMode} onChange={e=>upd(child.id,"contMode",e.target.value)}
                        style={{fontSize:10,padding:"2px 14px 2px 3px",flex:"0 0 auto",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
                          color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT,outline:"none",maxWidth:80,
                          appearance:"none" as any,WebkitAppearance:"none" as any,
                          backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                          backgroundRepeat:"no-repeat",backgroundPosition:"right 4px center"}}>
                        <option value="project">Proj. {contingency}%</option>
                        <option value="pct">Custom %</option>
                        <option value="dollar">Custom $</option>
                        <option value="none">None</option>
                      </select>
                      {cMode==="pct"&&<input type="number" className="input-field" value={li.contValue??""}
                        onChange={e=>upd(child.id,"contValue",+e.target.value)}
                        style={{width:34,fontSize:10,padding:"2px 3px",textAlign:"right"}} placeholder="%"/>}
                      {cMode==="dollar"
                        ?<CurrencyInput value={li.contValue??0} onChange={val=>upd(child.id,"contValue",val)} style={{flex:1,fontSize:11,padding:"2px 4px"}} negColor={T.negative}/>
                        :cMode==="none"
                          ?<span style={{flex:1,textAlign:"right",fontSize:11,color:T.textWeak}}>--</span>
                          :<span style={{flex:1,textAlign:"right",fontSize:12,color:T.textMuted,fontWeight:500,whiteSpace:"nowrap"}}>
                            {cAmt!==0?fmt(cAmt):"--"}
                          </span>
                      }
                    </div>
                  </td>
                );
              })():<td/>)}
              <td style={{padding:"3px 2px",textAlign:"center",verticalAlign:"middle",whiteSpace:"nowrap"}}>
                <button title="Duplicate item" onClick={e=>{e.stopPropagation();cloneItem(divId,subId,child.id);}}
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:11,padding:"2px 4px",opacity:0.6}}
                  onMouseEnter={e=>(e.currentTarget.style.opacity="1")}
                  onMouseLeave={e=>(e.currentTarget.style.opacity="0.6")}><Icon.Duplicate size={11}/></button>
                <button className="del-btn" onClick={()=>removeItem(divId,subId,child.id)}>x</button>
              </td>
            </tr>
          );
        })}
        {isOpen&&(
          <tr><td colSpan={colspan} style={{padding:"3px 0 5px 26px",borderBottom:`2px solid ${T.borderAccentFaint}`}}>
            <button className="add-row-btn" style={{fontSize:10}} onClick={()=>addItem(divId,subId,group.id)}>+ Add to Group</button>
          </td></tr>
        )}
      </React.Fragment>
    );
  };

  // ── Memoized row wrappers (perf): keep the latest closures in refs; pass only
  // the data each row's output depends on so unchanged rows skip re-rendering.
  const renderItemRowRef=useRef<any>(null);renderItemRowRef.current=renderItemRowInner;
  const renderGroupRowsRef=useRef<any>(null);renderGroupRowsRef.current=renderGroupRowsInner;
  // Table-wide state read by row renders/handlers — any change busts every row.
  // These change on clicks/toggles (rare), never per keystroke in a cell.
  const _rowShared={T,visibleCols,proposals,contingency,shortcuts,isLocked,
    bids,bidScopes,rfis,pickerPartners,bidPartners,
    bidLinkFor,bidLinkOtherOpen,statusDropdownId,
    rowSelIds,rowSelAnchorId,visibleRowKey:visibleRowIds.join("|"),
    selectedIds,selField,selAnchorId,selDragging,
    dndActive,dndTargetId,dndPosition,expandedGrps};
  const renderItemRow=(item,divId,subId,inGroupId=null,tradeGroupInfo?:any)=>{
    const li=itemState[item.id];const _li=li||{};
    const pctBase=_li.unit==="%"?(_li.pctTargetIds||[]).reduce((s,id)=>{const t=itemState[id]||{};return t.unit==="%"?s:s+(t.qty||0)*(t.unitCost||0);},0):0;
    return <MemoItemRow key={item.id} renderRef={renderItemRowRef}
      item={item} divId={divId} subId={subId} inGroupId={inGroupId} tradeGroupInfo={tradeGroupInfo}
      li={li}
      tot={getItemTotal(_li,itemState)}
      contAmt={calcItemCont(_li,contingency,itemState)}
      pctBase={pctBase}
      searchMatch={!!(searchQuery&&_li.included&&(_li.description||"").toLowerCase().includes(searchQuery.toLowerCase()))}
      collabKey={(realtime.collaborators||[]).filter((c:any)=>c.activeItemId===item.id).map((c:any)=>`${c.color}|${c.activeField}|${c.displayName}`).join(";")}
      cutPend={isCutPending(item.id)}
      tgKey={tradeGroupInfo?`${tradeGroupInfo.isLeader?"L":"M"}:${tradeGroupInfo.groupItemIds.join(",")}`:""}
      tgLeaderLi={tradeGroupInfo?itemState[tradeGroupInfo.groupItemIds[0]]:undefined}
      {..._rowShared}/>;
  };
  const renderGroupRows=(group,divId,subId)=>{
    return <MemoGroupRow key={group.id} renderRef={renderGroupRowsRef}
      group={group} divId={divId} subId={subId}
      childLis={group.children.map((c:any)=>itemState[c.id])}
      childTots={group.children.map((c:any)=>getItemTotal(itemState[c.id]||{},itemState))}
      childConts={group.children.map((c:any)=>calcItemCont(itemState[c.id]||{},contingency,itemState))}
      childPctBases={group.children.map((c:any)=>{const cl=itemState[c.id]||{};return cl.unit==="%"?(cl.pctTargetIds||[]).reduce((s:number,id:string)=>{const t=itemState[id]||{};return t.unit==="%"?s:s+(t.qty||0)*(t.unitCost||0);},0):0;})}
      gTot={rowsTotal(group.children)}
      gCont={rowsContTotal(group.children)}
      cutKey={[group.id,...group.children.map((c:any)=>c.id)].map(id=>isCutPending(id)?"1":"0").join("")}
      {..._rowShared}/>;
  };

  const visColHeaders=()=>{
    const V=visibleCols,h=[];
    if(V.status)h.push(["Status","center"]);
    if(V.qty)h.push(["Qty","center"]);
    if(V.unit)h.push(["Unit","center"]);
    if(V.unitCost)h.push(["Unit Cost","right"]);
    if(V.total)h.push(["Total","right"]);
    if(V.proposal)h.push(["Package","left"]);
    if(V.altType)h.push(["Alt Type","left"]);
    if(V.contingency)h.push(["Contingency","left"]);
    h.push(["","left"]);
    return h;
  };

  const CSS=`
    *{box-sizing:border-box;margin:0;padding:0;}
    ::-webkit-scrollbar{width:5px;height:5px;}
    ::-webkit-scrollbar-track{background:${T.bgScrollTrack};}
    ::-webkit-scrollbar-thumb{background:${T.bgScrollThumb};border-radius:3px;}
    input[type=number]{-moz-appearance:textfield;}
    input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;}
    .input-field{background:${T.bgInput};border:1px solid ${T.borderSecondary};color:${T.textSecondary};font-family:${FONT};
      font-size:12px;padding:5px 8px;border-radius:3px;transition:border-color .18s;width:100%;}
    .input-field:focus{outline:none;border-color:${T.borderAccent};background:${T.bgInputFocus};}
    .input-field::placeholder{color:${T.textMuted};}
    select.input-field{cursor:pointer;}
    select.input-field option{background:${T.bgInput};color:${T.textSecondary};}
    .gantt-date-input{background:transparent;border:none;color:${T.textSecondary};font-family:${FONT};
      font-size:10px;outline:none;width:100%;cursor:pointer;padding:0 4px;}
    .gantt-date-input::-webkit-calendar-picker-indicator{opacity:0;width:0;padding:0;margin:0;}
    .gantt-date-input:hover{color:${T.borderAccent};}
    .gantt-date-input:focus{color:${T.textPrimary};border-bottom:1px solid ${T.borderAccent};}
    /* An unscheduled row previews the bar a drag would leave, under the
       pointer, instead of every such row repeating the same sentence. The
       ghost is moved and shown by writing to its style directly, so sweeping
       the pointer down a long schedule costs no React renders.

       Shown from the component rather than by :hover -- a pointer capture, a
       pointer leaving the window, or a row re-rendering mid-gesture can all
       leave a CSS hover stuck on, and a ghost left behind reads as a bar that
       is actually scheduled. */
    .gantt-empty{cursor:ew-resize;}
    .gantt-empty:hover{background:${T.borderAccent}08;}
    .gantt-ghost{position:absolute;top:50%;transform:translateY(-50%);height:18px;
      border-radius:3px;pointer-events:none;opacity:0;transition:opacity .12s ease;}
    @media (prefers-reduced-motion: reduce){.gantt-ghost{transition:none;}}
    /* .45 put textInverse on bgHeader at 3.65:1 light / 3.83:1 dark, under the
       4.5:1 11px text needs. .6 gives 5.37 and 5.79, and stays brighter than
       the .55 sub-tabs below so the primary row still reads as primary. */
    .group-btn{cursor:pointer;padding:0 16px;height:100%;border:none;background:transparent;font-family:${FONT};
      font-size:11px;letter-spacing:.13em;text-transform:uppercase;transition:color .15s,opacity .15s;font-weight:700;
      color:${T.textInverse};white-space:nowrap;border-bottom:2px solid transparent;opacity:.6;display:flex;align-items:center;}
    .group-btn:hover{opacity:.85;}
    .group-btn.active{color:${T.borderAccent};cursor:default;border-bottom-color:${T.borderAccent};opacity:1;}
    /* Unselected sub-tabs sat at .4, which puts textInverse on this darkened
       strip at 3.2:1 -- under the 4.5:1 that 10px text needs to be read.
       .55 measures 4.9:1 and still reads as plainly unselected next to the
       active tab's full weight, bold and underline. */
    .subtab-btn{cursor:pointer;padding:0 14px;height:100%;border:none;background:transparent;font-family:${FONT};
      font-size:10px;letter-spacing:.1em;text-transform:uppercase;transition:all .15s;font-weight:500;
      color:${T.textInverse};white-space:nowrap;border-bottom:2px solid transparent;opacity:.55;display:flex;align-items:center;}
    .subtab-btn:hover{opacity:.8;}
    .subtab-btn.active{color:${T.textInverse};border-bottom-color:${T.textInverse};font-weight:700;opacity:1;}
    .tab-btn{cursor:pointer;padding:8px 18px;border:none;background:transparent;font-family:${FONT};
      font-size:11px;letter-spacing:.06em;text-transform:uppercase;transition:all .18s;font-weight:600;
      color:#7a8290;border-bottom:2px solid transparent;white-space:nowrap;}
    .tab-btn:hover{color:#c8b870;}
    .tab-btn.active{color:#e8d898;border-bottom-color:${T.borderAccent};}
    .div-btn{cursor:pointer;padding:10px 14px;border:none;text-align:left;width:100%;
      background:transparent;color:${T.divisionTabInactive};border-left:2px solid transparent;
      font-family:${FONT};font-size:11px;transition:all .15s;}
    .div-btn:hover{color:${T.divisionTabActive};background:${T.bgHover};}
    .div-btn.active{color:${T.divisionTabActive};border-left-color:${T.divisionTabActive};background:${T.bgAccentSubtle};}
    .sub-toggle{cursor:pointer;user-select:none;}
    .sub-toggle:hover .sub-lbl{color:${T.borderAccent};}
    tbody tr:hover td{background:${T.bgRowHover};}
    .cb{cursor:pointer;accent-color:${T.borderAccent};width:13px;height:13px;}
    /* A snapshot is a record of what was true on a date. The lock was
       enforced at the state layer -- setItemState simply returned when
       locked -- so a field accepted the click, accepted the keystroke, and
       then snapped back when React re-rendered with the old value. That
       reads as the app fighting you, or worse, as an edit that silently did
       not save. Nothing here takes a pointer or a caret now. Chevrons,
       scrolling and tab navigation are untouched: reading an old version is
       the point of opening one. */
    .oe-readonly table input,.oe-readonly table textarea,.oe-readonly table select,
    .oe-readonly .ghost-input,.oe-readonly .input-field,
    .oe-readonly [contenteditable]{pointer-events:none!important;user-select:none;}
    /* Search and filter boxes are deliberately NOT in that list. Reading an
       old version is the whole reason to open one, and searching it is
       reading. Only the fields that hold the estimate's values are frozen. */
    .oe-readonly table input::selection,.oe-readonly .input-field::selection{background:transparent;}
    .ghost-input{background:transparent;border:1px solid transparent;color:${T.textSubLabel};font-family:${FONT};
      font-size:12px;padding:3px 5px;border-radius:3px;width:100%;transition:border-color .15s,background .15s;}
    .ghost-input:hover{border-color:${T.borderHover};background:${T.bgInput}20;}
    .ghost-input:focus{outline:none;border-color:${T.borderAccent};background:${T.bgInput};}
    .ghost-input::placeholder{color:${T.textMuted};}
    .ghost-input.notes-input{color:#7a8090;font-size:10px;margin-top:1px;}
    .ghost-input.notes-input::placeholder{color:#5e7080;}
    .ghost-input.group-name-input:hover{background:transparent;}
    .add-row-btn{background:none;border:1px dashed ${T.borderDashed};color:#7a8090;cursor:pointer;font-family:${FONT};
      font-size:11px;padding:4px 10px;border-radius:3px;transition:all .15s;}
    .add-row-btn:hover{border-color:${T.borderAccent};color:${T.borderAccent};}
    .del-btn{background:none;border:none;color:${T.textMuted};cursor:pointer;font-size:14px;line-height:1;padding:0 2px;transition:color .15s;}
    .del-btn:hover{color:${T.dangerText};}
    .plus-btn{background:none;border:1px solid ${T.borderHover};color:${T.textTertiary};cursor:pointer;font-size:14px;
      width:20px;height:20px;border-radius:3px;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
    .plus-btn:hover{border-color:${T.borderAccent};color:${T.borderAccent};background:${T.bgAccentSubtle};}
    .filter-select{background:${T.bgInput};border:1px solid ${T.borderSecondary};color:${T.textTertiary};font-family:${FONT};
      font-size:10px;padding:3px 20px 3px 8px;border-radius:3px;cursor:pointer;appearance:none;-webkit-appearance:none;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E");
      background-repeat:no-repeat;background-position:right 6px center;}
    .filter-select:focus{outline:none;border-color:${T.borderAccent};}
    .filter-select option{background:${T.bgInput};}
    .add-sub-btn{background:none;border:1px dashed ${T.borderDashed}66;color:#6a7080;cursor:pointer;font-family:${FONT};
      font-size:10px;padding:5px 14px;border-radius:3px;transition:all .15s;width:100%;}
    .add-sub-btn:hover{border-color:${T.borderAccent}66;color:${T.borderAccent};}
    .sub-name-input{background:transparent;border:none;color:${T.textTertiary};font-size:13px;font-family:${FONT};
      font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:0 3px;
      border-bottom:1px solid ${T.borderAccent}66;outline:none;min-width:60px;}
    .sub-name-input:focus{color:${T.borderAccent};}
    .indent-btn{background:none;border:none;color:${T.textMuted};cursor:pointer;font-size:12px;padding:0 3px;transition:color .15s;flex-shrink:0;}
    .indent-btn:hover{color:${T.borderAccent};}
    .fade-in{animation:fadeUp .2s ease both;}
    /* Ends on transform:none, not translateY(0). With fill-mode "both" the
       last keyframe's value is held forever, and *any* transform other than
       none makes the element a containing block for position:fixed
       descendants. Every tab is wrapped in .fade-in, so every fixed overlay
       in the app -- menus, backdrops, dialogs -- was being positioned
       relative to the tab instead of the viewport. The status dropdown on
       the last row of a subcategory was the visible symptom: it measured
       its space against a box 129px down the page and opened off the
       bottom of the screen. */
    @keyframes fadeUp{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
    @keyframes aiPulse{0%{width:20%}50%{width:80%}100%{width:20%}}
    /* A ring that turns while something is being read or rendered. Reduced
       motion gets a steady ring: the point is that work is happening, and a
       spinner is not worth making someone unwell over. */
    .oe-spin{display:inline-block;width:14px;height:14px;border-radius:999px;
      border:2px solid ${T.borderSecondary};border-top-color:${T.borderAccent};
      animation:oeSpin .7s linear infinite;flex-shrink:0;}
    @keyframes oeSpin{to{transform:rotate(360deg)}}
    @media (prefers-reduced-motion: reduce){.oe-spin{animation:none;}}
    .sf-input{background:${T.bgPanel};border:1px solid ${T.borderSecondary};color:${T.textTertiary};font-family:${FONT};
      font-size:11px;padding:2px 6px;border-radius:3px;width:72px;text-align:right;}
    .sf-input:focus{outline:none;border-color:${T.borderAccent};}
    .project-name-input{background:transparent;border:none;outline:none;font-family:${FONT};
      font-size:12px;color:${T.textTertiary};padding:0;width:180px;border-bottom:1px solid transparent;transition:color .15s,border-color .15s;}
    .project-name-input:hover{color:${T.textTertiary};border-bottom-color:${T.borderHover};}
    .project-name-input:focus{color:${T.textSecondary};border-bottom-color:${T.borderAccent};}
    .file-menu{position:absolute;top:100%;left:0;background:${T.bgInput};border:1px solid ${T.borderSecondary};
      border-radius:3px;min-width:280px;z-index:100;box-shadow:0 8px 24px #00000060;padding:6px 0;}
    .file-menu-item{padding:8px 16px;cursor:pointer;font-size:12px;color:${T.textTertiary};transition:all .15s;display:flex;align-items:center;gap:8px;}
    .file-menu-item:hover{background:${T.bgAccentSubtle};color:${T.borderAccent};}
    .modal-backdrop{position:fixed;inset:0;background:${T.bgModalBackdrop};z-index:200;display:flex;align-items:center;justify-content:center;}
    .modal{background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:6px;padding:24px;min-width:0;max-width:min(calc(100vw - 32px),520px);width:100%;}
    .prop-section{background:${T.bgPanel};border:1px solid ${T.borderPrimary};border-radius:3px;padding:16px 20px;margin-bottom:16px;}
    .prop-label{font-size:10px;color:${T.textWeak};letter-spacing:.12em;text-transform:uppercase;margin-bottom:8px;}
    .toggle-switch{width:32px;height:18px;border-radius:10px;position:relative;cursor:pointer;transition:background .2s;flex-shrink:0;}
    .toggle-thumb{width:14px;height:14px;border-radius:6px;background:#fff;position:absolute;top:2px;transition:left .2s;}
    .toast{position:fixed;bottom:18px;left:50%;transform:translateX(-50%);background:${T.bgToast};
      border:1px solid ${T.borderHover};color:${T.textTertiary};font-size:11px;padding:6px 18px;border-radius:999px;
      pointer-events:none;z-index:999;animation:fadeUp .2s ease both;
      display:flex;align-items:center;gap:10px;}
    /* A toast you can click back out of has to accept the click. */
    .toast.has-action{pointer-events:auto;}
    .toast-undo{background:none;border:1px solid ${T.borderHover};color:${T.textSecondary};
      font-family:${FONT};font-size:11px;padding:2px 10px;border-radius:999px;cursor:pointer;}
    .toast-undo:hover{border-color:${T.borderAccent};color:${T.borderAccent};}
    .snap-card{background:${T.bgPanel};border:1px solid ${T.borderPrimary};border-radius:3px;padding:12px 16px;
      cursor:pointer;min-width:180px;max-width:240px;transition:all .2s;position:relative;}
    .snap-card:hover{border-color:#4a8fbf55;}
    .snap-card.sel-left{background:${T.versionA.bg};border-color:#4a8fbf88;}
    .snap-card.sel-right{background:${T.versionB.bg};border-color:#4abf6a88;}
    .note-input{background:transparent;border:1px solid transparent;color:${T.textTertiary};font-size:11px;
      padding:3px 6px;border-radius:3px;width:100%;font-family:${FONT};}
    .note-input:hover{border-color:${T.borderHover};}
    .note-input:focus{outline:none;border-color:${T.borderAccent};background:${T.bgInput};}
    .note-input::placeholder{color:#5e7080;}
    .col-menu{position:absolute;top:100%;right:0;background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:3px;
      min-width:180px;z-index:100;box-shadow:0 8px 24px #00000060;padding:8px 0;}
    .sf-header-cell .sf-breakdown-tooltip{display:none;}
    .sf-header-cell:hover .sf-breakdown-tooltip{display:block;}
    .col-menu-item{padding:6px 14px;cursor:pointer;font-size:11px;color:${T.textTertiary};display:flex;align-items:center;gap:8px;transition:background .12s;}
    .col-menu-item:hover{background:${T.bgAccentFaint};}
    .col-check{width:12px;height:12px;border-radius:3px;border:1px solid #4a5060;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    .col-check.on{background:${T.borderAccent};border-color:${T.borderAccent};}
    .proj-card{background:${T.bgPanel};border:1px solid ${T.borderPrimary};border-radius:3px;padding:16px 18px;cursor:pointer;transition:all .18s;}
    .proj-card:hover{border-color:${T.borderAccent}55;background:#161a22;}
    .home-action-btn{background:${T.bgPanel};border:1px solid ${T.borderSecondary};color:${T.textTertiary};cursor:pointer;padding:8px 18px;border-radius:3px;font-size:12px;font-family:${FONT};display:inline-flex;align-items:center;gap:6px;transition:all .15s;text-decoration:none;}
    .home-action-btn:hover{border-color:${T.borderAccent}66;color:${T.borderAccent};}
    .home-action-btn.primary{background:${T.bgAccentLight};border-color:${T.borderAccent}55;color:${T.borderAccent};font-weight:600;}
    .home-action-btn.primary:hover{background:${T.bgAccentMedium};}
    .typology-chip{background:none;border:1px solid ${T.borderSecondary};color:${T.textWeak};cursor:pointer;padding:5px 12px;border-radius:3px;font-family:${FONT};font-size:11px;transition:all .15s;}
    .typology-chip.active{background:${T.bgAccentLight};border-color:${T.borderAccent}55;color:${T.borderAccent};font-weight:600;}
    .icon-btn{background:none;border:1px solid ${T.borderSecondary};color:${T.textTertiary};cursor:pointer;padding:4px 10px;border-radius:3px;font-size:11px;font-family:${FONT};transition:all .15s;}
    .icon-btn:hover{border-color:${T.borderAccent};color:${T.borderAccent};}
    kbd{background:${T.bgToast};border:1px solid ${T.borderHover};padding:2px 6px;border-radius:3px;font-size:10px;color:${T.textTertiary};}
    .shortcut-row{display:flex;justify-content:space-between;align-items:center;padding:7px 0;border-bottom:1px solid ${T.bgToast};font-size:11px;gap:12px;}
    .analysis-ver-card{background:${T.bgPanel};border:1px solid ${T.borderSecondary};border-radius:3px;padding:10px 14px;cursor:pointer;transition:all .18s;min-width:160px;}
    .analysis-ver-card:hover{border-color:#4a8fbf55;background:${T.bgHeader};}
    .analysis-ver-card.sel-left{border-color:#4a8fbf;background:${T.versionA.bg};}
    .analysis-ver-card.sel-right{border-color:#4abf6a;background:${T.versionB.bg};}
    .account-avatar{width:32px;height:32px;border-radius:50%;background:${T.borderSecondary};border:2px solid ${T.borderHover};color:${T.textTertiary};
      cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;font-family:${FONT};
      transition:all .15s;flex-shrink:0;padding:0;}
    .account-avatar:hover{border-color:${T.borderAccent};color:${T.borderAccent};}
    .account-menu{position:absolute;top:calc(100% + 6px);left:0;background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:6px;
      min-width:220px;z-index:200;box-shadow:0 8px 24px #00000060;padding:0;overflow:hidden;}
    .account-menu-header{padding:14px 16px;border-bottom:1px solid ${T.borderPrimary};}
    .account-menu-item{padding:9px 16px;cursor:pointer;font-size:12px;color:${T.textTertiary};display:flex;align-items:center;gap:10px;transition:background .12s;}
    .account-menu-item:hover{background:${T.bgAccentFaint};color:${T.textSecondary};}
    .account-menu-divider{height:1px;background:${T.borderPrimary};margin:0;}
    .settings-field{display:flex;flex-direction:column;gap:5px;margin-bottom:20px;}
    .settings-label{font-size:10px;color:${T.textWeak};letter-spacing:.1em;text-transform:uppercase;}
    .settings-input{background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:3px;color:${T.textSecondary};padding:10px 12px;font-size:13px;font-family:${FONT};width:100%;box-sizing:border-box;}
    .settings-input:focus{outline:none;border-color:${T.borderAccent};}
    .settings-input:read-only{color:${T.textWeak};cursor:default;}
    .settings-card{background:${T.bgPanel};border:1px solid ${T.borderPrimary};border-radius:6px;padding:24px 28px;max-width:480px;}
    .settings-save-btn{background:${T.borderAccent};border:none;color:${T.textOnGold};cursor:pointer;padding:8px 20px;border-radius:3px;font-size:12px;font-family:${FONT};font-weight:700;}
    .settings-save-btn:hover{background:#d4b87a;}
    .settings-save-btn:disabled{opacity:.5;cursor:wait;}
    .settings-back{background:none;border:1px solid ${T.borderSecondary};color:${T.textTertiary};cursor:pointer;padding:5px 10px;border-radius:3px;font-size:14px;font-family:${FONT};line-height:1;transition:border-color .15s;display:inline-flex;align-items:center;justify-content:center;}
    .settings-back:hover{border-color:${T.borderAccent};}
    .org-card{background:${T.bgPanel};border:1px solid ${T.borderPrimary};border-radius:6px;padding:20px 24px;margin-bottom:16px;}
    .org-card-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;}
    .org-card-title{font-size:14px;font-weight:700;color:${T.textSecondary};}
    .org-role-badge{font-size:10px;color:${T.borderAccent};background:${T.bgAccentLight};border:1px solid ${T.borderAccent}44;padding:2px 10px;border-radius:10px;text-transform:uppercase;letter-spacing:.08em;font-weight:600;}
    .member-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid ${T.bgToast};font-size:12px;}
    .member-row:last-child{border-bottom:none;}
    .member-avatar{width:28px;height:28px;border-radius:50%;background:${T.borderSecondary};display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:${T.textTertiary};flex-shrink:0;}
    .member-name{flex:1;min-width:0;color:${T.textTertiary};}
    .member-name .email{font-size:10px;color:${T.textWeak};margin-top:1px;}
    .role-select{background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:3px;color:${T.textTertiary};font-size:10px;padding:3px 6px;font-family:${FONT};cursor:pointer;}
    .role-select:focus{outline:none;border-color:${T.borderAccent};}
    .invite-row{display:flex;align-items:center;gap:8px;padding:10px 0;margin-top:4px;}
    .invite-input{background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:3px;color:${T.textSecondary};padding:6px 10px;font-size:12px;font-family:${FONT};flex:1;min-width:0;}
    .invite-input:focus{outline:none;border-color:${T.borderAccent};}
    .invite-input::placeholder{color:#506878;}
    .invite-btn{background:${T.borderAccent};border:none;color:${T.textOnGold};cursor:pointer;padding:6px 14px;border-radius:3px;font-size:11px;font-family:${FONT};font-weight:700;white-space:nowrap;}
    .invite-btn:hover{background:#d4b87a;}
    .invite-btn:disabled{opacity:.5;cursor:default;}
    .remove-btn{background:none;border:none;color:#5e7080;cursor:pointer;font-size:14px;padding:0 4px;line-height:1;transition:color .15s;}
    .remove-btn:hover{color:${T.negative};}
    .share-panel{border-top:1px solid ${T.borderPrimary};background:${T.bgApp};padding:12px 16px 12px 42px;}
    .create-org-row{display:flex;align-items:center;gap:8px;margin-bottom:24px;}
    .danger-btn{background:${T.dangerBg};border:1px solid ${T.dangerBorder};color:${T.dangerText};cursor:pointer;padding:8px 16px;border-radius:3px;font-size:11px;font-family:inherit;font-weight:600;letter-spacing:.04em;transition:background .15s,border-color .15s;}
    .danger-btn:hover{background:${T.dangerHoverBg};border-color:#7a3030;}
    .danger-btn:disabled{opacity:.4;cursor:not-allowed;}
    .danger-section{margin-top:16px;padding-top:16px;border-top:1px solid ${T.dangerBg};}
    .confirm-input{width:100%;background:${T.bgInput};border:1px solid ${T.borderSecondary};border-radius:3px;color:${T.textSecondary};padding:8px 10px;font-size:13px;font-family:inherit;box-sizing:border-box;margin-top:10px;}
    .confirm-input:focus{outline:none;border-color:${T.borderAccent};}
    .request-banner{background:#2a2510;border:1px solid #4a3d1a;border-radius:3px;padding:10px 14px;margin-bottom:12px;}
    .request-row{display:flex;align-items:center;gap:10px;padding:6px 0;}
    .request-row .request-name{flex:1;font-size:12px;color:${T.textSecondary};}
    .request-row .request-email{font-size:10px;color:${T.textWeak};}
    .approve-btn{background:#1a2a1a;border:1px solid #2a4a2a;color:${T.positive};cursor:pointer;padding:4px 10px;border-radius:3px;font-size:10px;font-family:inherit;font-weight:600;}
    .approve-btn:hover{background:#2a3a2a;border-color:#3a5a3a;}
    .deny-btn{background:none;border:1px solid #3a2020;color:${T.negative};cursor:pointer;padding:4px 10px;border-radius:3px;font-size:10px;font-family:inherit;}
    .deny-btn:hover{background:#2a1515;}
    .request-ownership-btn{background:none;border:1px solid #4a3d1a;color:${T.borderAccent};cursor:pointer;padding:5px 12px;border-radius:3px;font-size:10px;font-family:inherit;font-weight:600;letter-spacing:.04em;}
    .request-ownership-btn:hover{background:#2a2510;border-color:#6a5a2a;}
    .request-ownership-btn:disabled{opacity:.5;cursor:default;}

    /* Keyboard focus you can see.
       Every input in here turns its own outline off and signals focus with a
       border colour instead, which reads fine -- but buttons, tabs and the
       rest were left with whatever the browser draws by default, and on these
       backgrounds that is nothing you can find. Tabbing through the app gave
       no clue where you were.

       :focus-visible rather than :focus, so a clicked button does not keep a
       ring the mouse user never asked for. These come last so they beat the
       outline:none set on the input classes above, which has the same
       specificity. */
    /* Sliders drawn by us rather than by the browser.
       accent-color only tints the filled part and the thumb; the rest of the
       track is left to the user agent, which paints it from the operating
       system's own light or dark setting -- so a light panel on a machine set
       to dark mode got a near-black bar across it. Each slider's colour comes
       in as --oe-accent. */
    .oe-range{-webkit-appearance:none;appearance:none;background:transparent;
      height:14px;cursor:pointer;}
    .oe-range:disabled{cursor:default;opacity:.5;}
    .oe-range::-webkit-slider-runnable-track{height:4px;border-radius:999px;
      background:${T.borderSecondary};}
    .oe-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
      width:12px;height:12px;margin-top:-4px;border:none;border-radius:50%;
      background:var(--oe-accent,${T.borderAccent});
      box-shadow:0 0 0 1px ${T.bgPanel};}
    .oe-range::-moz-range-track{height:4px;border-radius:999px;background:${T.borderSecondary};}
    .oe-range::-moz-range-progress{height:4px;border-radius:999px;background:var(--oe-accent,${T.borderAccent});}
    .oe-range::-moz-range-thumb{width:12px;height:12px;border:none;border-radius:50%;
      background:var(--oe-accent,${T.borderAccent});}

    :focus-visible{outline:2px solid ${T.borderAccent};outline-offset:2px;border-radius:3px;}
    .input-field:focus-visible,.ghost-input:focus-visible,.filter-select:focus-visible,
    .sf-input:focus-visible,.note-input:focus-visible,.settings-input:focus-visible,
    .role-select:focus-visible,.invite-input:focus-visible,.sub-name-input:focus-visible,
    .project-name-input:focus-visible,.gantt-date-input:focus-visible{
      outline:2px solid ${T.borderAccent};outline-offset:1px;}
  `;

  // ── ORG / COLLABORATION helpers ──
  const loadOrgs = useCallback(async () => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase || !user) return;
    setOrgsLoading(true);
    try {
      // Get user's org memberships
      const {data: memberships} = await supabase.from("org_members")
        .select("org_id, role, organizations(id, name)")
        .eq("user_id", user.id);
      if (!memberships) { setOrgsLoading(false); return; }
      // For each org, get members + pending invitations
      const orgsWithMembers = await Promise.all(memberships.map(async (m:any) => {
        const org = m.organizations as {id:string;name:string};
        const {data: members} = await supabase.from("org_members")
          .select("user_id, role, profiles(email, display_name)")
          .eq("org_id", org.id);
        const {data: pending} = await supabase.from("org_invitations")
          .select("id, email, role")
          .eq("org_id", org.id)
          .is("accepted_at", null);
        return {
          id: org.id, name: org.name, myRole: m.role,
          members: (members || []).map((mem:any) => ({
            userId: mem.user_id, role: mem.role,
            email: (mem.profiles as any)?.email || "", displayName: (mem.profiles as any)?.display_name || ""
          })),
          pendingInvites: (pending || []),
        };
      }));
      setOrgs(orgsWithMembers);
    } catch (e) { console.error("loadOrgs", e); }
    setOrgsLoading(false);
  }, [user]);

  useEffect(() => { if (activeTab === "settings-team") { loadOrgs(); loadOwnershipRequests(); } }, [activeTab, loadOrgs]);

  const createOrg = async () => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase || !user || !newOrgName.trim()) return;
    setOrgMsg("");
    const orgId = crypto.randomUUID();
    const {error} = await supabase.from("organizations").insert({id: orgId, name: newOrgName.trim()});
    if (error) { setOrgMsg(error.message); return; }
    const {error: memErr} = await supabase.from("org_members").insert({org_id: orgId, user_id: user.id, role: "owner"});
    if (memErr) { setOrgMsg(memErr.message); return; }
    setNewOrgName("");
    loadOrgs();
  };

  const inviteMember = async (orgId) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase) return;
    const email = (inviteEmails[orgId] || "").trim();
    const role = inviteRoles[orgId] || "member";
    if (!email) return;
    setOrgMsg("");
    // Get current session token to authenticate API call
    const {data: {session}} = await supabase.auth.getSession();
    if (!session) { setOrgMsg("Not authenticated"); return; }
    try {
      const res = await fetch("/api/account?action=invite", {
        method: "POST",
        headers: {"Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}`},
        body: JSON.stringify({orgId, email, role}),
      });
      const json = await res.json();
      if (!res.ok) { setOrgMsg(json.error || "Invite failed"); return; }
      setOrgMsg(json.message || "Invitation sent");
      setInviteEmails(prev => ({...prev, [orgId]: ""}));
      loadOrgs();
    } catch (e) {
      setOrgMsg("Network error sending invite");
    }
  };

  const revokeInvite = async (orgId, inviteId) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase) return;
    await supabase.from("org_invitations").delete().eq("id", inviteId);
    loadOrgs();
  };

  const changeOrgRole = async (orgId, userId, newRole) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase) return;
    await supabase.from("org_members").update({role: newRole}).eq("org_id", orgId).eq("user_id", userId);
    loadOrgs();
  };

  const removeOrgMember = async (orgId, userId) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase) return;
    await supabase.from("org_members").delete().eq("org_id", orgId).eq("user_id", userId);
    loadOrgs();
  };

  const confirmRemoveMember = (orgId, orgName, memberName, memberId) => {
    setConfirmAction({title:"Remove Member",message:`Remove ${memberName} from ${orgName}? They will lose access to all shared projects.`,confirmText:"Remove",onConfirm:()=>{removeOrgMember(orgId,memberId);setConfirmAction(null);}});
    setConfirmInput("");
  };

  const deleteOrg = async (orgId) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase) return;
    setOrgMsg("");
    const {error} = await supabase.from("organizations").delete().eq("id", orgId);
    if (error) { setOrgMsg(error.message); return; }
    setConfirmAction(null);
    loadOrgs();
  };

  const confirmDeleteOrg = (orgId, orgName) => {
    setConfirmAction({title:"Delete Organization",message:`This will permanently delete "${orgName}" and remove all members. Type the organization name to confirm.`,confirmText:"Delete Organization",inputRequired:orgName,onConfirm:()=>deleteOrg(orgId)});
    setConfirmInput("");
  };

  // Ownership transfer requests
  const loadOwnershipRequests = async () => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase || !user) return;
    const {data} = await supabase.from("ownership_requests").select("id, org_id, requester_id, status, created_at, profiles(email, display_name)").eq("status","pending");
    if (!data) return;
    const byOrg:Record<string,any[]> = {};
    data.forEach((r:any) => {
      if (!byOrg[r.org_id]) byOrg[r.org_id] = [];
      byOrg[r.org_id].push({id:r.id, requesterId:r.requester_id, name:(r.profiles as any)?.display_name||"", email:(r.profiles as any)?.email||"", createdAt:r.created_at});
    });
    setOwnershipRequests(byOrg);
  };

  const requestOwnership = async (orgId) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase || !user) return;
    setOrgMsg("");
    const {error} = await supabase.from("ownership_requests").insert({org_id: orgId, requester_id: user.id});
    if (error) { setOrgMsg(error.message); return; }
    loadOwnershipRequests();
  };

  const respondToRequest = async (requestId, orgId, requesterId, approve) => {
    const {supabase} = await import("./lib/supabase");
    if (!supabase || !user) return;
    setOrgMsg("");
    if (approve) {
      // Transfer: make requester owner, demote current owner to admin
      const {error:e1} = await supabase.from("org_members").update({role:"owner"}).eq("org_id",orgId).eq("user_id",requesterId);
      if (e1) { setOrgMsg(e1.message); return; }
      const {error:e2} = await supabase.from("org_members").update({role:"admin"}).eq("org_id",orgId).eq("user_id",user.id);
      if (e2) { setOrgMsg(e2.message); return; }
      await supabase.from("ownership_requests").update({status:"approved"}).eq("id",requestId);
    } else {
      await supabase.from("ownership_requests").update({status:"denied"}).eq("id",requestId);
    }
    setConfirmAction(null);
    loadOrgs();
    loadOwnershipRequests();
  };

  const confirmApproveTransfer = (requestId, orgId, requesterId, requesterName) => {
    setConfirmAction({title:"Transfer Ownership",message:`Transfer ownership to ${requesterName||"this user"}? You will be demoted to admin.`,confirmText:"Transfer Ownership",onConfirm:()=>respondToRequest(requestId,orgId,requesterId,true)});
    setConfirmInput("");
  };

  // Project sharing helpers
  const loadShares = async (projectName) => {
    // For localStorage mode, shares aren't supported yet
    // This is a placeholder — will integrate when projects move to Supabase
    setProjectShares(prev => ({...prev, [projectName]: []}));
  };

  // ── ANALYSIS helpers ──
  const getVersionSummary = id => {
    if(id==="current") return currentSummary;
    const snap = projectSnapshots.find(s=>s.id===id);
    return snap ? summarizeState(snap.state) : null;
  };
  const getVersionLabel = id => {
    if(id==="current") return `Current — ${projectName}`;
    return projectSnapshots.find(s=>s.id===id)?.label || "Unknown";
  };
  const getVersionTotal = (summary:any):number => Object.values(summary||{}).reduce<number>((a,v:any)=>a+(v?.total||0),0);
  // Returns {divisions, itemState} for either the live estimate or a snapshot
  const getVersionState = (id:string):{divisions:any[];itemState:Record<string,any>}|null => {
    if(id==="current") return {divisions,itemState};
    const snap=projectSnapshots.find(s=>s.id===id);
    return snap?.state||null;
  };

  if(cloudLoading)return(
    <div style={{fontFamily:FONT,background:T.bgApp,color:T.textPrimary,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}><OELogo height={52} circleColor="white" barColor={T.borderAccent}/></div>
        <div style={{fontSize:12,color:T.textMuted}}>Loading project...</div>
      </div>
    </div>
  );


  // The avatar menu, rendered in both the main header and the settings header.
  // These were two hand-written copies that had to be edited in lockstep, drawn
  // with OS emoji rather than the icon set -- so the same menu looked like a
  // different product depending on where you opened it. Items come from
  // lib/accountMenu, shared with the project picker.
  const accountMenuRows = (
    <>
      <div className="account-menu-divider"/>
      {accountMenuEntries({ surface: "project" }).map(({ id, label, icon, settingsTab })=>(
        <div key={id} className="account-menu-item" onClick={()=>{
          setShowAccountMenu(false);
          if(settingsTab){
            setActiveTab(settingsTab);
            if(id==="profile")setEditDisplayName(user?.displayName||"");
          }
          else if(id==="help")setShowHelp(true);
          else if(id==="shortcuts")setShowShortcuts(true);
        }}>
          <span style={{width:16,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icon}</span> {label}
        </div>
      ))}
    </>
  );

  return (
    <div style={{fontFamily:FONT,background:T.bgApp,color:T.textPrimary,height:"100vh",display:"flex",flexDirection:"column",overflow:"hidden"}}
      onClick={()=>{if(showAccountMenu)setShowAccountMenu(false);if(showFileMenu)setShowFileMenu(false);if(showColMenu)setShowColMenu(false);}}>
      <style>{CSS}</style>
      <style>{`html,body{overflow:hidden!important;height:100%!important;}`}</style>
      {/* Trade partner autocomplete datalist — bid import still uses free-text */}
      <datalist id="oe-trade-partners">
        {[...new Set([...bidPartners.map(p=>p.name),...orgPartners.map(p=>p.name)])].sort().map(n=><option key={n} value={n}/>)}
      </datalist>
      {addPartnerModal&&<AddPartnerToDirectoryModal T={T} FONT={FONT}
        prefillName={addPartnerModal.prefill}
        onSave={op=>{addPartnerModal.onAdded(op);}}
        onCancel={()=>setAddPartnerModal(null)}/>}
      {showPrintDialog&&<PrintModal activeTab={activeTab} initialSheets={printInitialSheets}
        bidScopes={bidScopes} bidPartners={bidPartners} bids={bids} levelingAdjustments={levelingAdjustments} rfis={rfis}
        projectName={projectName} buildingArea={buildingArea} duration={duration}
        overhead={overhead} profit={profit} contingency={contingency} markupMode={markupMode} proposals={proposals} proposalAreas={proposalAreas}
        divisions={divisions} itemState={itemState} final={final} grandTotal={grandTotal} divTotals={divTotals}
        contingencyByProp={contingencyByProp} altTotals={altTotals} projectSnapshots={projectSnapshots}
        currentSummary={currentSummary} getPC={getPC} orgLogo={activeLogo} costSFCents={costSFCents}
        onClose={()=>{setShowPrintDialog(false);setPrintInitialSheets(null);}}/>}

      {/* ── SOW Editor Modal ── */}
      {showSowEditor&&(()=>{
        const divId=showSowEditor.divisionId;
        const div=divisions.find(d=>d.id===divId);
        // Collect estimate line item descriptions for this division (exclude alternates + allowances — each goes to its own SOW section)
        const estLineItems:string[]=[];
        if(div){
          for(const sub of div.subcategories||[]){
            const walkR=(rows:any[])=>{for(const r of rows){const it=itemState[r.id];if(it&&it.included&&it.description&&!it.altType&&it.costStatus!=="Allowance")estLineItems.push(it.description);if(r.rows)walkR(r.rows);}};
            walkR(sub.rows||[]);
          }
        }
        // Collect allowance items for this division
        const divAllowances:string[]=[];
        if(div){
          for(const sub of div.subcategories||[]){
            const walkR=(rows:any[])=>{for(const r of rows){const it=itemState[r.id];if(it&&it.included&&it.costStatus==="Allowance"&&it.description){const amt=(it.qty||0)*(it.unitCost||0);divAllowances.push(amt>0?`${it.description} — $${Math.round(amt).toLocaleString()} Allowance`:`${it.description} — Allowance TBD`);}if(r.rows)walkR(r.rows);}};
            walkR(sub.rows||[]);
          }
        }
        // Collect alternates for this division from itemState
        const divAlts:{description:string;value:string}[]=[];
        if(div){
          for(const sub of div.subcategories||[]){
            const walkR=(rows:any[])=>{for(const r of rows){const it=itemState[r.id];if(it&&it.included&&(it.altType==="Alt"||it.altType==="Add Alt"||it.altType==="Ded Alt")){divAlts.push({description:it.description||"",value:it.unitCost?(it.qty||0)*(it.unitCost||0)?`$${Math.round((it.qty||0)*(it.unitCost||0)).toLocaleString()}`:"":"" });}if(r.rows)walkR(r.rows);}};
            walkR(sub.rows||[]);
          }
        }
        // Proposal totals for this division
        const propTotals:Record<string,number>={};
        const dt=divTotals[divId];
        if(dt&&dt.byProp)Object.entries(dt.byProp).forEach(([pid,amt])=>{propTotals[pid]=amt as number;});
        // Submittals for this division from the Submittals tab (description strings)
        const divSubmittals=submittals
          .filter(s=>s.divisionId===divId&&s.status!=="not-required")
          .map(s=>s.specSection?`${s.specSection} — ${s.description}`:s.description);
        return(
          <SowEditorModal
            projectName={projectName}
            address={address}
            divisionId={divId}
            divisionName={showSowEditor.divisionName}
            tradeName={showSowEditor.tradeName}
            tradeContact={showSowEditor.tradeContact}
            estimateLineItems={estLineItems}
            alternates={divAlts}
            proposals={proposals}
            proposalTotals={propTotals}
            scheduleStart={""}
            scheduleEnd={""}
            scheduleData={schedule}
            submittalsForDivision={divSubmittals}
            allowancesForDivision={divAllowances}
            projectContextDoc={projectContextDoc}
            T={T}
            FONT={FONT}
            onClose={()=>setShowSowEditor(null)}
          />
        );
      })()}

      {/* ── Bulk Edit Toolbar ── appears when 2+ rows selected in estimate */}
      {activeTab==="estimate"&&rowSelIds.size>=2&&(
        <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",zIndex:8000,background:T.bgToast,border:`1px solid ${T.borderAccent}55`,borderRadius:6,padding:"10px 16px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 8px 32px #00000060",fontFamily:FONT,whiteSpace:"nowrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:T.borderAccent,marginRight:4}}>{rowSelIds.size} rows</span>
          {/* Cost Status */}
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Status</span>
            <select defaultValue="" onChange={e=>{if(e.target.value!=="")bulkUpd("costStatus",e.target.value);e.target.value="";}}
              style={{fontSize:10,padding:"3px 6px",borderRadius:3,background:T.bgInput,color:T.textSecondary,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,outline:"none"}}>
              <option value="" disabled>— set —</option>
              <option value="">Clear</option>
              <option value="Hard Bid">Hard Bid</option>
              <option value="Allowance">Allowance</option>
              <option value="Needs Review">Needs Review</option>
              <option value="By Others">By Others</option>
              <option value="Excluded">Excluded</option>
            </select>
          </div>
          <div style={{width:1,height:20,background:T.borderSecondary}}/>
          {/* Package */}
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Package</span>
            <select defaultValue="" onChange={e=>{if(e.target.value!=="")bulkUpd("proposal",e.target.value);e.target.value="";}}
              style={{fontSize:10,padding:"3px 18px 3px 6px",borderRadius:3,background:T.bgInput,color:T.textSecondary,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,outline:"none",
                appearance:"none" as any,WebkitAppearance:"none" as any,
                backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
              <option value="" disabled>— set —</option>
              {proposals.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{width:1,height:20,background:T.borderSecondary}}/>
          {/* Alt Type */}
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Alt Type</span>
            <select defaultValue="" onChange={e=>{if(e.target.value!=="__placeholder")bulkAltUpd(e.target.value);e.target.value="__placeholder";}}
              style={{fontSize:10,padding:"3px 18px 3px 6px",borderRadius:3,background:T.bgInput,color:T.textSecondary,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,outline:"none",
                appearance:"none" as any,WebkitAppearance:"none" as any,
                backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4L7 0z' fill='%23708898' opacity='0.5'/%3E%3C/svg%3E\")",
                backgroundRepeat:"no-repeat",backgroundPosition:"right 5px center"}}>
              <option value="__placeholder" disabled>— set —</option>
              <option value="">Base Bid</option>
              <option value="Alt">Alternate — TBD</option>
              <option value="Alt-Accepted">Accepted ✓</option>
              <option value="Void">Rejected ✕</option>
            </select>
          </div>
          <div style={{width:1,height:20,background:T.borderSecondary}}/>
          {/* Trade Partner bulk assign */}
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Trade Partner</span>
            <div style={{width:160}}>
              <TradePartnerPicker value={bulkTradeInput} partners={pickerPartners} T={T} FONT={FONT}
                placeholder="— assign partner —"
                onChange={n=>{
                  setBulkTradeInput(n);
                  if(n){snapshot();setItemState(prev=>{const next={...prev};rowSelIds.forEach(id=>{next[id]={...(next[id]||{}),tradeName:n};});return next;});}
                }}
                onRequestAdd={prefill=>setAddPartnerModal({prefill,onAdded:op=>{
                  addSupabasePartner(op);
                  setBulkTradeInput(op.name);
                  snapshot();setItemState(prev=>{const next={...prev};rowSelIds.forEach(id=>{next[id]={...(next[id]||{}),tradeName:op.name};});return next;});
                  setAddPartnerModal(null);
                }})}/>
            </div>
          </div>
          {/* Bid bulk link — filtered to matching trade partner when one is set */}
          {(()=>{
            const activeBids=bids.filter(b=>b.status==="Active");
            const filteredBids=bulkTradeInput.trim()
              ? activeBids.filter(b=>{
                  const p=findPartner(b.partnerId);
                  return (p?.name||"").toLowerCase().includes(bulkTradeInput.trim().toLowerCase());
                })
              : activeBids;
            if(activeBids.length===0)return null;
            return(
              <>
                <div style={{width:1,height:20,background:T.borderSecondary}}/>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Bid</span>
                  <select defaultValue="" onChange={e=>{
                    const bidId=e.target.value;
                    if(bidId==="__clear"){snapshot();setItemState(prev=>{const next={...prev};rowSelIds.forEach(id=>{if(next[id])next[id]={...next[id],sourceBidId:undefined};});return next;});}
                    else if(bidId){snapshot();setItemState(prev=>{const next={...prev};rowSelIds.forEach(id=>{next[id]={...(next[id]||{}),sourceBidId:bidId,tradeName:undefined,tradeDate:undefined};});return next;});}
                    e.target.value="";
                  }} style={{fontSize:10,padding:"3px 6px",borderRadius:3,background:T.bgInput,color:T.textSecondary,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,outline:"none",maxWidth:180}}>
                    <option value="" disabled>{filteredBids.length===0?"No bids match":"— link bid —"}</option>
                    {filteredBids.map(b=>{
                      const p=findPartner(b.partnerId);
                      const sc=bidScopes.find((x:any)=>x.id===b.scopeId);
                      return <option key={b.id} value={b.id}>{(p?.name||"Unknown")}{sc?" · "+sc.name:""}</option>;
                    })}
                    <option value="__clear">— clear bid —</option>
                  </select>
                </div>
              </>
            );
          })()}
          <div style={{width:1,height:20,background:T.borderSecondary}}/>
          <button onClick={clearRowSel} style={{fontSize:10,padding:"3px 8px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>✕ Clear</button>
        </div>
      )}

      {/* Proposal Import Modal */}
      {importTarget&&(
        <ProposalImportModal
          divId={importTarget.divId}
          divName={importTarget.divName}
          subId={importTarget.subId}
          subName={importTarget.subName}
          proposals={proposals}
          proposalColors={T.proposalColors}
          defaultProposal={proposals[0]?.id||"p1"}
          tradeSources={tradeSources}
          T={T}
          font={FONT}
          onEmbed={(items,sourceInfo)=>handleProposalEmbed(importTarget.divId,importTarget.subId,items,sourceInfo)}
          onClose={()=>setImportTarget(null)}
        />
      )}

      {/* Share dialog */}
      {showShareDialog&&cloudProjectId&&cloudStorage&&(
        <ShareDialog
          projectId={cloudProjectId}
          projectName={projectName}
          ownerId={user?.id||""}
          ownerName={user?.displayName||user?.email||""}
          currentOrgId={null}
          currentOrgName={null}
          storage={cloudStorage}
          userOrgs={userOrgs}
          theme={T}
          isOwner={true}
          onClose={()=>setShowShareDialog(false)}
          onChanged={()=>{}}
        />
      )}

      {showProjectChat&&(
        <>
          <div onClick={()=>setShowProjectChat(false)} style={{position:"fixed",inset:0,zIndex:299}}/>
          <ProjectChatPanel
            contextDoc={projectContextDoc}
            projectName={projectName}
            onClose={()=>setShowProjectChat(false)}
          />
        </>
      )}

      {/* HEADER */}
      {!activeTab.startsWith("settings-")&&activeTab!=="home"&&(<div style={{background:T.bgHeader,borderBottom:`1px solid ${T.borderPrimary}`,padding:`10px ${hPad}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,flexWrap:"wrap",gap:isMobile?6:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
          <button onClick={()=>onBackToProjects?onBackToProjects():setActiveTab("home")} title="Home"
            style={{background:"none",border:"none",padding:"0 4px 0 0",cursor:"pointer",display:"inline-flex",alignItems:"center",transition:"opacity .15s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity="0.65"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
            <OELogo height={24} circleColor="white" barColor={T.borderAccent}/>
          </button>
          <div style={{position:"relative"}}>
            <button onClick={e=>{e.stopPropagation();setShowAccountMenu(false);setShowFileMenu(v=>{if(v)setShowExportSub(false);return!v;});}}
              style={{background:"none",border:`1px solid ${T.textInverse}44`,color:T.textInverse,cursor:"pointer",padding:"5px 12px",borderRadius:3,fontSize:12,fontFamily:FONT,letterSpacing:".06em"}}>
              File
            </button>
            {showFileMenu&&(
              <div className="file-menu">
                <div className="file-menu-item" onClick={()=>{setShowNewProjDialog(true);setShowFileMenu(false);}}><Icon.Plus size={11}/> New Project</div>
                {cloudStorage&&(
                  <div className="file-menu-item" onClick={handleSaveAsNewProject}><Icon.Copy size={11}/> Copy to New Project...</div>
                )}
                <div className="file-menu-item" onClick={()=>{setShowSnapDialog(true);setShowFileMenu(false);}}><Icon.Pin size={11}/> Save Milestone Snapshot...</div>
                <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
                {entitlements.canUseAI&&<div className="file-menu-item" onClick={()=>{aiFileRef.current?.click();setShowFileMenu(false);}}><Icon.Bot size={11}/> Import with AI...</div>}
                <div className="file-menu-item" onClick={()=>{csvImportRef.current?.click();setShowFileMenu(false);}}><Icon.Clipboard size={11}/> Import CSV...</div>
                <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
                <div style={{position:"relative"}} onMouseEnter={()=>setShowExportSub(true)} onMouseLeave={()=>setShowExportSub(false)}>
                  <div className="file-menu-item" style={{justifyContent:"space-between"}}>
                    <span><Icon.Download size={11}/> Export</span><span style={{fontSize:10,opacity:.6}}>▸</span>
                  </div>
                  {showExportSub&&(
                    <div className="file-menu" style={{position:"absolute",left:"100%",top:0,minWidth:160,marginLeft:2}}>
                      <div className="file-menu-item" onClick={handleExportExcel}><Icon.BarChart size={11}/> Excel (.xlsx)</div>
                      <div className="file-menu-item" onClick={handleExportCSV}><Icon.Clipboard size={11}/> CSV (.csv)</div>
                      <div className="file-menu-item" onClick={handleExportQuickBooks}><Icon.DollarSign size={11}/> QuickBooks (.csv)</div>
                      <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
                      <div className="file-menu-item" onClick={handleProcoreExport}><Icon.Upload size={11}/> Procore</div>
                    </div>
                  )}
                </div>
                <div className="file-menu-item" onClick={()=>{setActiveTab("scope");setShowFileMenu(false);}}><Icon.FileText size={11}/> Scope of Work...</div>
                <div className="file-menu-item" onClick={()=>{setShowPrintDialog(true);setShowFileMenu(false);}}><Icon.Printer size={11}/> Print / PDF View...</div>
              </div>
            )}
          </div>
          {cloudProjectId&&cloudStorage&&(
            <button onClick={()=>setShowShareDialog(true)}
              style={{background:"none",border:`1px solid ${T.textInverse}44`,color:T.textInverse,cursor:"pointer",padding:"5px 12px",borderRadius:3,fontSize:12,fontFamily:FONT,letterSpacing:".06em",transition:"border-color .15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.borderAccent} onMouseLeave={e=>e.currentTarget.style.borderColor=`${T.textInverse}44`}>
              Share
            </button>
          )}
          <div style={{flex:1,minWidth:isMobile?0:200,display:"flex",flexDirection:"column",justifyContent:"center"}}>
            <input className="project-name-input" value={projectName} onChange={e=>setProjectName(e.target.value)} placeholder="Project name..."
              style={{fontSize:isMobile?14:18,fontWeight:700,color:T.textInverse,background:"transparent",border:"1px solid transparent",borderRadius:3,padding:"2px 8px",fontFamily:FONT,width:"100%",transition:"border-color .15s"}}
              onFocus={e=>e.currentTarget.style.borderColor=T.borderAccent} onBlur={e=>e.currentTarget.style.borderColor="transparent"}/>
            {address&&<div style={{fontSize:10,color:`${T.textInverse}70`,paddingLeft:9,marginTop:1,letterSpacing:".02em",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{address}</div>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {isSandbox
            ?<div style={{fontSize:10,fontWeight:600,color:T.borderAccent,background:T.bgAccentLight,border:`1px solid ${T.borderAccent}44`,borderRadius:3,padding:"2px 8px",letterSpacing:".06em"}}>SANDBOX</div>
            :<div style={{fontSize:10,display:"flex",alignItems:"center",gap:6,
              color:cloudSaveStatus==="saving"?`${T.borderAccent}88`:cloudSaveStatus==="saved"?T.saveIndicator:cloudSaveStatus==="failed"?"#e05050":`${T.textInverse}66`}}>
              {cloudProjectId?(
                cloudSaveStatus==="saving"?"Saving…":
                cloudSaveStatus==="failed"?(
                  <><span>Save failed</span>
                  <button onClick={()=>{setCloudSaveStatus("saving");cloudStorage?.saveProjectById(cloudProjectId,{}).catch(()=>setCloudSaveStatus("failed"));}}
                    style={{fontSize:10,background:"none",border:`1px solid #e0505088`,color:"#e05050",borderRadius:3,padding:"1px 5px",cursor:"pointer",fontFamily:FONT}}>Retry</button></>
                ):
                cloudSaveStatus==="saved"&&lastSavedAt?(
                  (()=>{
                    void saveAgoTick;
                    const secs=Math.round((Date.now()-lastSavedAt)/1000);
                    return secs<10?"Saved ✓":secs<60?`Saved ${secs}s ago`:`Saved ${Math.round(secs/60)}m ago`;
                  })()
                ):"Auto-saved"
              ):"Auto-saved"}
            </div>
          }
          {!isMobile&&cloudProjectId&&!isLocked&&(
            <button onClick={()=>setShowSnapDialog(true)} title="Save Milestone Snapshot (capture the current estimate state)"
              style={{background:"none",border:`1px solid ${T.textInverse}33`,color:`${T.textInverse}88`,cursor:"pointer",
                padding:"4px 10px",borderRadius:3,fontSize:11,fontFamily:FONT,display:"flex",alignItems:"center",gap:5,transition:"border-color .15s,color .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=`${T.textInverse}33`;e.currentTarget.style.color=`${T.textInverse}88`;}}>
              <Icon.Pin size={12}/> Snapshot
            </button>
          )}
          <button onClick={()=>setShowProjectChat(v=>!v)} title="Ask AI about this project"
            style={{background:showProjectChat?`${T.borderAccent}22`:"none",
              border:`1px solid ${showProjectChat?T.borderAccent:T.textInverse+"33"}`,
              color:showProjectChat?T.borderAccent:`${T.textInverse}88`,cursor:"pointer",
              padding:"4px 10px",borderRadius:3,fontSize:11,fontFamily:FONT,
              display:"flex",alignItems:"center",gap:5,transition:"border-color .15s,color .15s,background .15s"}}
            onMouseEnter={e=>{if(!showProjectChat){e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}}
            onMouseLeave={e=>{if(!showProjectChat){e.currentTarget.style.borderColor=`${T.textInverse}33`;e.currentTarget.style.color=`${T.textInverse}88`;}}}>
            <Icon.Bot size={12}/> Ask AI
          </button>
          {/* ── Collaborator avatars ── */}
          {!isMobile&&realtime.collaborators?.length>0&&(
            <div style={{display:"flex",alignItems:"center",gap:0,marginLeft:4}}>
              {realtime.isConnected&&<div style={{width:6,height:6,borderRadius:"50%",background:"#4ade80",marginRight:6}} title="Live"/>}
              {realtime.collaborators.map(c=>(
                <div key={c.userId} title={c.displayName} style={{width:24,height:24,borderRadius:"50%",background:c.color,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,border:`2px solid ${T.bgApp}`,marginLeft:-4,cursor:"default"}}>
                  {(c.displayName||"?")[0].toUpperCase()}
                </div>
              ))}
            </div>
          )}
          {!isMobile&&<div style={{width:1,height:28,background:`${T.textInverse}22`}}/>}
          <button
            onClick={()=>{setActiveTab("summary");window.history.pushState({oeTab:"summary"},"",window.location.hash);}}
            title="Go to Summary"
            style={{textAlign:"right",background:"none",border:"none",cursor:"pointer",padding:"2px 4px",borderRadius:6,transition:"opacity 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity=".75"}
            onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
            <div style={{fontSize:10,color:`${T.textInverse}88`,letterSpacing:".14em",textTransform:"uppercase",marginBottom:2}}>Project Total</div>
            <div style={{fontSize:isMobile?16:22,fontWeight:700,color:final.total<0?T.negative:T.borderAccent}}>{fmt(final.total)}</div>
          </button>
          {!isMobile&&<>
          <div style={{width:1,height:28,background:`${T.textInverse}22`}}/>
          <div style={{textAlign:"right",position:"relative"}} className="sf-header-cell">
            <div style={{fontSize:10,color:`${T.textInverse}88`,letterSpacing:".14em",textTransform:"uppercase",marginBottom:2}}>Cost / SF</div>
            <div style={{fontSize:14,fontWeight:500,color:final.costPerSF<0?T.negative:T.positive,cursor:proposals.length>1?"default":"default"}}>{blendedSF>0?fmtSF(final.costPerSF):"—"}</div>
            {proposals.length>1&&blendedSF>0&&(
              <div className="sf-breakdown-tooltip" style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"8px 12px",zIndex:200,whiteSpace:"nowrap",boxShadow:"0 4px 16px #0008",pointerEvents:"none"}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Per Package</div>
                {proposals.map(p=>{
                  const pSF=proposalAreas[p.id]||0;
                  if(!pSF)return null;
                  const pCSF=((grandTotal.byProp[p.id]||0)+(contingencyByProp.byProp[p.id]||0))/pSF;
                  const pc=getPC(p.id,proposals);
                  return(
                    <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:3}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:pc.text,flexShrink:0,display:"inline-block"}}/>
                      <span style={{fontSize:10,color:T.textSecondary,flex:1}}>{p.name}</span>
                      <span style={{fontSize:10,fontWeight:600,color:pc.text}}>{fmtSF(pCSF)}</span>
                      <span style={{fontSize:10,color:T.textWeak}}>{pSF.toLocaleString()} SF</span>
                    </div>
                  );
                })}
                <div style={{borderTop:`1px solid ${T.borderPrimary}`,marginTop:6,paddingTop:5,display:"flex",justifyContent:"space-between",gap:16}}>
                  <span style={{fontSize:10,color:T.textWeak}}>Blended</span>
                  <span style={{fontSize:10,fontWeight:600,color:T.positive}}>{fmtSF(final.costPerSF)} / {blendedSF.toLocaleString()} SF</span>
                </div>
              </div>
            )}
          </div>
          </>}
          {/* Project setup moved to the Setup tab group — a gear next to the
              account avatar read as app preferences, not project data. */}
          {user&&(<>
            <div style={{width:1,height:28,background:`${T.textInverse}22`}}/>
            <div style={{position:"relative"}}>
              <button className="account-avatar" onClick={e=>{e.stopPropagation();setShowFileMenu(false);setShowExportSub(false);setShowAccountMenu(v=>!v);}} title={user.displayName}>
                {user.avatarUrl?<img src={user.avatarUrl} alt="" style={{width:28,height:28,borderRadius:"50%"}}/>
                  :(user.displayName||user.email||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}
              </button>
              {showAccountMenu&&(<div className="account-menu" style={{right:0,left:"auto"}} onClick={e=>e.stopPropagation()}>
                <div className="account-menu-header">
                  <div style={{fontSize:13,fontWeight:700,color:T.textSecondary}}>{user.displayName}</div>
                  <div style={{fontSize:11,color:T.textWeak,marginTop:2}}>{user.email}</div>
                </div>
                <div style={{padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div onClick={()=>{const next=!isDarkMode;setIsDarkMode(next);try{sSetRaw("oe_dark_mode",String(next));}catch{};}} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none"}}>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:!isDarkMode?T.textSecondary:T.textMuted,transition:"color .2s"}}>LIGHT</span>
                    <div style={{width:44,height:22,borderRadius:999,background:isDarkMode?"#3a3d44":"#c8cad0",position:"relative",transition:"background .25s",flexShrink:0}}>
                      <div style={{width:18,height:18,borderRadius:999,background:"#fff",position:"absolute",top:2,left:isDarkMode?24:2,transition:"left .25s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}}/>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:isDarkMode?T.textSecondary:T.textMuted,transition:"color .2s"}}>DARK</span>
                  </div>
                </div>
                {accountMenuRows}
                <div className="account-menu-item" onClick={()=>{setShowAccountMenu(false);window.location.href="mailto:feedback@open-estimate.com?subject=OpenEstimate%20Feedback";}}>
                  <span style={{width:16,textAlign:"center",fontSize:13}}>&#9993;</span> Send Feedback
                </div>
                <div className="account-menu-divider"/>
                <div className="account-menu-item" style={{color:T.negative}} onClick={()=>{setShowAccountMenu(false);signOut&&signOut();}}>
                  <span style={{width:16,textAlign:"center",fontSize:13}}>&#8617;</span> Sign Out
                </div>
              </div>)}
            </div>
          </>)}
        </div>
      </div>)}

      {/* SETTINGS HEADER — shown when a settings tab is active (main header is hidden) */}
      {activeTab.startsWith("settings-")&&(
        <div style={{background:T.bgHeader,borderBottom:`1px solid ${T.borderPrimary}`,padding:`10px ${hPad}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}} onClick={()=>setShowAccountMenu(false)}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <button onClick={()=>onBackToProjects?onBackToProjects():setActiveTab("home")} title="Back to Projects"
              style={{background:"none",border:"none",padding:"0",cursor:"pointer",display:"inline-flex",alignItems:"center",transition:"opacity .15s"}}
              onMouseEnter={e=>e.currentTarget.style.opacity="0.65"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
              <OELogo height={26} circleColor="white" barColor={T.borderAccent}/>
            </button>
            {settingsReturnRef.current&&(
              <button onClick={()=>setActiveTab(settingsReturnRef.current as string)}
                title={`Return to ${TAB_LABELS[settingsReturnRef.current]||"the project"}`}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,
                  color:T.textSecondary,cursor:"pointer",fontFamily:FONT,fontSize:11,fontWeight:600,
                  padding:"4px 10px",display:"inline-flex",alignItems:"center",gap:6}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.borderSecondary;e.currentTarget.style.color=T.textSecondary;}}>
                <Icon.ChevronLeft size={11}/>
                Back to {TAB_LABELS[settingsReturnRef.current]||"the project"}
              </button>
            )}
          </div>
          {user&&(
            <div style={{position:"relative"}} onClick={e=>e.stopPropagation()}>
              <button className="account-avatar" onClick={e=>{e.stopPropagation();setShowFileMenu(false);setShowAccountMenu(v=>!v);}} title={user.displayName}>
                {user.avatarUrl?<img src={user.avatarUrl} alt="" style={{width:28,height:28,borderRadius:"50%"}}/>
                  :(user.displayName||user.email||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}
              </button>
              {showAccountMenu&&(<div className="account-menu" style={{right:0,left:"auto"}} onClick={e=>e.stopPropagation()}>
                <div className="account-menu-header">
                  <div style={{fontSize:13,fontWeight:700,color:T.textSecondary}}>{user.displayName}</div>
                  <div style={{fontSize:11,color:T.textWeak,marginTop:2}}>{user.email}</div>
                </div>
                <div style={{padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div onClick={()=>{const next=!isDarkMode;setIsDarkMode(next);try{sSetRaw("oe_dark_mode",String(next));}catch{};}} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none"}}>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:!isDarkMode?T.textSecondary:T.textMuted,transition:"color .2s"}}>LIGHT</span>
                    <div style={{width:44,height:22,borderRadius:999,background:isDarkMode?"#3a3d44":"#c8cad0",position:"relative",transition:"background .25s",flexShrink:0}}>
                      <div style={{width:18,height:18,borderRadius:999,background:"#fff",position:"absolute",top:2,left:isDarkMode?24:2,transition:"left .25s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}}/>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:isDarkMode?T.textSecondary:T.textMuted,transition:"color .2s"}}>DARK</span>
                  </div>
                </div>
                {accountMenuRows}
                <div className="account-menu-item" onClick={()=>{setShowAccountMenu(false);window.location.href="mailto:feedback@open-estimate.com?subject=OpenEstimate%20Feedback";}}>
                  <span style={{width:16,textAlign:"center",fontSize:13}}>&#9993;</span> Send Feedback
                </div>
                <div className="account-menu-divider"/>
                <div className="account-menu-item" style={{color:T.negative}} onClick={()=>{setShowAccountMenu(false);signOut&&signOut();}}>
                  <span style={{width:16,textAlign:"center",fontSize:13}}>&#8617;</span> Sign Out
                </div>
              </div>)}
            </div>
          )}
        </div>
      )}

      {/* LOCK BANNER */}
      {isLocked&&!activeTab.startsWith("settings-")&&activeTab!=="home"&&(
        <div style={{background:T.bgHeader,borderBottom:`2px solid #f6ad5566`,padding:`10px ${hPad}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:16,lineHeight:1}}>&#128274;</span>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"#f6ad55",lineHeight:1.2}}>
                Viewing Snapshot — Read Only
              </div>
              <div style={{fontSize:10,color:"#f6ad5599",marginTop:2}}>
                {lockedLabel||"Archived"}{lockedDate?` · ${new Date(lockedDate).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}`:""} &nbsp;·&nbsp; Changes are not saved
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <button onClick={handleReturnToCurrentEstimate}
              style={{background:"#f6ad55",border:"none",color:"#1a1200",cursor:"pointer",
                padding:"6px 16px",borderRadius:3,fontSize:11,fontFamily:FONT,fontWeight:700,
                display:"flex",alignItems:"center",gap:6}}>
              ↩ Return to Current Estimate
            </button>
            <button
              title="Removes write protection — be careful not to save changes over the live estimate"
              onClick={()=>setConfirmAction({
                title:"Unlock Snapshot for Editing?",
                message:"This removes write protection. Any changes you make will overwrite the live estimate the next time the project auto-saves. Are you sure?",
                onConfirm:()=>{setIsLocked(false);setLockedLabel(null);setLockedDate(null);isViewingSnapshot.current=false;setConfirmAction(null);setConfirmInput("");}
              })}
              style={{background:"none",border:`1px solid #f6ad5533`,color:"#f6ad5577",
                cursor:"pointer",padding:"5px 12px",borderRadius:3,fontSize:10,fontFamily:FONT}}>
              Unlock to Edit
            </button>
          </div>
        </div>
      )}

      {/* ── SHARED CHART COMPONENTS ── */}
      {/* DivBarChart: horizontal bar chart of division costs */}
      {/* WaterfallChart: alternate comparison waterfall */}

      {/* TABS — single row, active group expands inline */}
      {!activeTab.startsWith("settings-")&&activeTab!=="home"&&(()=>{
        const GROUPS=[
          // Summary leads: most visits to this group are to read the number,
          // not to change it. The grid is still where a new project opens --
          // see resolveLandingTab -- because an empty summary looks broken.
          // Scope of Work sits here, not under Procurement. It is the
          // contract exhibit handed to the OWNER -- recapitulation,
          // exclusions, allowances, unit prices -- so it belongs beside the
          // summary the owner is also given, not beside the bids and buyout
          // that are how the work gets let. The per-trade bid form is a
          // different document and stays in the Bids tab where it is used.
          {id:"estimate",    label:"Estimate",    tabs:["summary","decisions","estimate","alternates","analysis","scope","history"]},
          // "Bids", not "Procurement". Procurement promises purchase orders,
          // vendor management and commitment tracking -- the half of the line
          // this product deliberately does not cross. A user who clicks it
          // expecting buyout management finds two tabs that do not do that,
          // and the label has spent trust the rest of the app has to earn
          // back. Buyout stays under it because the award flow already points
          // there: it is the tail of the bid, not a parallel activity.
          ...(entitlements.canUseBids?[{id:"procurement",label:"Bids",tabs:["bids","buyout"]}]:[]),
          // "Project" named nothing -- it was where everything that was not
          // money ended up, so somebody hunting for the schedule had no
          // reason to look under it. These six are the precon deliverables:
          // a schedule, a logistics plan, a cash flow, and the submittal,
          // RFI and decision logs.
          {id:"project",     label:"Planning",    tabs:["submittals","rfi","schedule","cashflow","logistics",...(BUDGET_ENABLED?["budget"]:[])]},
          // Setup holds project-scoped configuration (markup, divisions, cost
          // codes, drawings) — peer-level with the other groups, not a user
          // preference. Lives here rather than behind a header gear icon.
          {id:"setup",       label:"Setup",       tabs:["properties","properties-defaults"]},
        ];
        // What each group SHOWS, as opposed to what it owns. The two lists
        // drifted the moment one of them was edited -- GROUPS decides which
        // group a tab belongs to, this decides the buttons, and changing only
        // GROUPS changed nothing on screen.
        const SUB_DISPLAY:Record<string,string[]>={
          // Decisions sits second, right under Summary. It is assembled from
          // the estimate -- allowances, Needs Review, alternates still open --
          // and it is the sentence after the number: this is what it is, and
          // this is what is still soft about it. The app already OPENS here
          // when a project has any, and a landing page two groups away from
          // the figure it qualifies was the wrong place for it.
          estimate:["summary","decisions","estimate","alternates","analysis","scope","history"],
          ...(entitlements.canUseBids?{procurement:["bids","buyout"]}:{}),
          project:["submittals",...(entitlements.canUseRfi?["rfi"]:[]),"schedule",...(entitlements.canUseCashflow?["cashflow"]:[]),"logistics",...(BUDGET_ENABLED?["budget"]:[])],
          setup:["properties","properties-defaults"],
        };
        const activeGroup=GROUPS.find(g=>g.tabs.includes(activeTab))??GROUPS[0];
        const goTab=(t:string)=>{setLinkReturn(null);setActiveTab(t);window.history.pushState({oeTab:t},"",window.location.hash);};

        const back=linkReturn;
        return(
          <div style={{flexShrink:0,background:T.bgHeader,borderBottom:`1px solid ${T.borderPrimary}`}}>
            {/* The way back from a link.
                In the chrome rather than on each page, so it is in the same
                place whatever followed the link, and so a tab that gains a
                link later does not have to remember to grow a return of its
                own. Absent unless a link put you here -- offering "back" to
                somebody who chose this tab is noise. */}
            {back&&(
              <div style={{display:"flex",alignItems:"center",gap:8,padding:`4px ${hPad}`,
                background:T.bgAccentFaint,borderBottom:`1px solid ${T.borderPrimary}44`}}>
                <button onClick={()=>{setLinkReturn(null);setActiveTab(back.tab);
                    window.history.pushState({oeTab:back.tab},"",window.location.hash);}}
                  style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",border:"none",
                    padding:0,cursor:"pointer",fontFamily:FONT,fontSize:11,fontWeight:600,color:T.borderAccent}}>
                  ← Back to {TAB_LABELS[back.tab]||"where you were"}
                </button>
                <span style={{fontSize:10,color:T.textWeak}}>you followed a link here</span>
              </div>
            )}
            {/* ── Row 1: Group selector ── */}
            <div role="tablist" aria-label="Sections" style={{display:"flex",alignItems:"stretch",height:30,padding:`0 ${hPad}`,borderBottom:`1px solid ${T.borderPrimary}44`}}>
              {GROUPS.map((g,gi)=>(
                <React.Fragment key={g.id}>
                  {gi>0&&<span style={{width:1,background:T.borderPrimary,margin:"auto 6px",height:10,flexShrink:0,opacity:.35}}/>}
                  <button role="tab" aria-selected={activeGroup.id===g.id} className={`group-btn${activeGroup.id===g.id?" active":""}`}
                    onClick={()=>goTab(SUB_DISPLAY[g.id][0]??g.tabs[0])}>
                    {g.label}
                  </button>
                </React.Fragment>
              ))}
              <div style={{flex:1}}/>
              {/* ── Notes ── */}
              {notesEnabled&&(
                <button
                  onClick={()=>openNotes(null)}
                  title={notesUnread>0?`${notesUnread} new note${notesUnread===1?"":"s"} from someone else`:"Internal notes — never printed"}
                  style={{
                    flexShrink:0,display:"flex",alignItems:"center",gap:5,position:"relative",
                    padding:"0 12px",background:"none",
                    border:"none",borderLeft:`1px solid ${T.borderPrimary}44`,cursor:"pointer",
                    color:notesUnread>0?T.borderAccent:`${T.textInverse}55`,
                    fontFamily:FONT,fontSize:10,fontWeight:700,letterSpacing:".08em",
                    alignSelf:"stretch",
                  }}>
                  <Icon.MessageSquare size={13}/>
                  {!isMobile&&"NOTES"}
                  {notesUnread>0&&(
                    <span style={{background:T.borderAccent,color:T.textOnGold,borderRadius:999,fontSize:10,
                      padding:"0 5px",lineHeight:1.5,fontWeight:700}}>{notesUnread}</span>
                  )}
                </button>
              )}
              {/* ── Help button ── */}
              <button
                onClick={()=>setShowHelp(true)}
                title="Help & Guides"
                style={{
                  flexShrink:0,display:"flex",alignItems:"center",gap:5,
                  padding:"0 12px",background:"none",
                  border:"none",borderLeft:`1px solid ${T.borderPrimary}44`,cursor:"pointer",
                  color:`${T.textInverse}55`,
                  fontFamily:FONT,fontSize:10,fontWeight:700,letterSpacing:".08em",
                  textTransform:"uppercase" as any,transition:"all 0.15s",
                }}
                onMouseEnter={e=>e.currentTarget.style.color=T.textInverse}
                onMouseLeave={e=>e.currentTarget.style.color=`${T.textInverse}55`}
              >
                <span style={{fontSize:13,fontWeight:700}}>?</span>
                {!isMobile&&<span>Help</span>}
              </button>
            </div>
            {/* ── Row 2: Sub-tabs for active group ── */}
            <div ref={tabBarRef} role="tablist" aria-label={`${activeGroup.label} tabs`}
              style={{display:"flex",alignItems:"stretch",height:34,padding:`0 ${hPad}`,
                background:"rgba(0,0,0,0.18)",
                overflowX:"auto",WebkitOverflowScrolling:"touch" as any,cursor:"grab",userSelect:"none"}}
              onMouseDown={e=>{const el=tabBarRef.current;if(!el)return;tabDrag.current={active:true,didDrag:false,startX:e.pageX-el.offsetLeft,scrollLeft:el.scrollLeft};el.style.cursor="grabbing";}}
              onMouseMove={e=>{if(!tabDrag.current.active||!tabBarRef.current)return;e.preventDefault();const x=e.pageX-tabBarRef.current.offsetLeft;const delta=x-tabDrag.current.startX;if(Math.abs(delta)>4)tabDrag.current.didDrag=true;tabBarRef.current.scrollLeft=tabDrag.current.scrollLeft-delta;}}
              onMouseUp={()=>{tabDrag.current.active=false;if(tabBarRef.current)tabBarRef.current.style.cursor="grab";}}
              onMouseLeave={()=>{tabDrag.current.active=false;if(tabBarRef.current)tabBarRef.current.style.cursor="grab";}}>
              {SUB_DISPLAY[activeGroup.id].map(t=>(
                <button key={t} role="tab" aria-selected={activeTab===t} className={`subtab-btn${activeTab===t?" active":""}`}
                  onClick={e=>{if(tabDrag.current.didDrag){tabDrag.current.didDrag=false;return;}goTab(t);}}>
                  {TAB_LABELS[t]??(t.charAt(0).toUpperCase()+t.slice(1))}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* BODY */}
      <div className={isLocked?"oe-readonly":undefined} style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}} onClick={()=>{if(showFileMenu)setShowFileMenu(false);if(showColMenu)setShowColMenu(false);if(showAccountMenu)setShowAccountMenu(false);}}>

        {activeTab==="estimate"&&!isMobile&&(
          <div style={{width:sidebarCollapsed?36:205,background:T.bgPanel,borderRight:`1px solid ${T.borderPrimary}`,overflow:"hidden",flexShrink:0,paddingTop:6,transition:"width .18s ease",display:"flex",flexDirection:"column" as any}}>
            {/* Collapse toggle */}
            <button onClick={()=>setSidebarCollapsed(c=>!c)} title={sidebarCollapsed?"Expand sidebar":"Collapse sidebar"}
              style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",padding:"4px 0",marginBottom:4,
                display:"flex",alignItems:"center",justifyContent:sidebarCollapsed?"center":"flex-end",paddingRight:sidebarCollapsed?0:8,fontSize:13,opacity:.5,flexShrink:0}}
              onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".5"}>
              {sidebarCollapsed?<Icon.ChevronRight size={13}/>:<Icon.ChevronLeft size={13}/>}
            </button>
            {/* Scrollable divisions list */}
            <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
            {divisions.map(div=>{
              if(sidebarCollapsed){return(
                <button key={div.id} ref={sidebarDiv===div.id?(sidebarActiveRef as any):undefined} title={`${div.code} — ${div.name}\n${fmt(divTotals[div.id]?.total||0)}`}
                  onClick={()=>goToDiv(div.id)}
                  style={{background:sidebarDiv===div.id?T.bgAccentFaint:"none",border:"none",borderLeft:`2px solid ${sidebarDiv===div.id?T.borderAccent:"transparent"}`,
                    cursor:"pointer",padding:"8px 0",display:"flex",flexDirection:"column" as any,alignItems:"center",gap:3,width:"100%",flexShrink:0}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:".05em",color:sidebarDiv===div.id?T.borderAccent:T.textWeak}}>{div.code.replace("Div ","")}</span>
                </button>
              );}
              return(
                <div key={div.id} style={{position:"relative"}}>
                  <div role="button" tabIndex={0} ref={sidebarDiv===div.id?sidebarActiveRef:undefined} className={`div-btn${sidebarDiv===div.id?" active":""}`}
                    title={CSI_DIV_DESC[div.id]||div.name}
                    onClick={()=>goToDiv(div.id)}
                    onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){e.preventDefault();goToDiv(div.id);} }}>
                    <div style={{fontSize:10,color:T.textSubLabel,fontWeight:700,marginBottom:2,letterSpacing:".1em"}}>
                      {div.code}
                    </div>
                    <div style={{lineHeight:1.35}}>{div.name}</div>
                    <div style={{fontSize:11,marginTop:3}}>
                      <span style={{color:divTotals[div.id]?.total<0?T.negative:divTotals[div.id]?.total>0?T.positive:T.textMuted}}>{fmt(divTotals[div.id]?.total)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>{/* end scrollable divisions list */}
            {/* No totals here. Every division used to carry a second line --
                "+$12,400 cont" -- under its subtotal, which put two figures
                per row in the narrowest column in the app to answer a
                question nobody asks while choosing a division. The project
                total that replaced it was no better: it is already in the
                header, larger and where the eye goes first. */}
            {showChecklist&&(
              <GetStartedChecklist
                T={T} FONT={FONT}
                hasProjectName={checklistHasName}
                hasLineItem={checklistHasItem}
                hasPackage={checklistHasPackage}
                hasMarkup={checklistHasMarkup}
                onDismiss={()=>{setChecklistDismissed(true);lsSet("oe_checklist_dismissed_v1",true);}}
              />
            )}
          </div>
        )}

        {/* Most tabs are documents and scroll as one. A few lay themselves
            out to the viewport and scroll their own panels -- Logistics keeps
            the drawing still while the tool rails move, the Gantt keeps its
            header and frozen column still while the bars move. Letting the
            page scroll underneath those drags the thing you are working on
            out from under you, and the 60px of bottom padding guaranteed it
            by making the content taller than the box on every one of them. */}
        <div style={FULL_BLEED_TABS.has(activeTab)
          ?{flex:1,overflow:"hidden",display:"flex",flexDirection:"column" as const,minHeight:0}
          :{flex:1,overflowY:"auto",paddingBottom:60}}>
          {/* ── Recovery banner ── shows when a local backup has more data than the saved state */}
          {recoveryBackup&&(
            <div style={{background:"#2d2a1a",borderBottom:`1px solid #c9a227`,padding:"9px 20px",display:"flex",alignItems:"center",gap:12,fontFamily:FONT,fontSize:12,color:"#f0d080",flexWrap:"wrap",zIndex:20}}>
              <span style={{fontWeight:600}}>⚠ Unsaved backup found</span>
              <span style={{color:"#c4a84a",fontSize:11}}>
                {new Date(recoveryBackup.ts).toLocaleString()} · {recoveryBackup.projectName||"Untitled"}
              </span>
              <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
                <button onClick={()=>restoreFromBackup(recoveryBackup)}
                  style={{background:"#c9a227",color:"#1a1600",border:"none",borderRadius:3,padding:"4px 12px",cursor:"pointer",fontFamily:FONT,fontWeight:700,fontSize:11}}>
                  Restore backup
                </button>
                <button onClick={()=>setRecoveryBackup(null)}
                  style={{background:"none",color:"#c4a84a",border:`1px solid #c4a84a55`,borderRadius:3,padding:"4px 10px",cursor:"pointer",fontFamily:FONT,fontSize:11}}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {/* Mobile division selector */}
          {activeTab==="estimate"&&isMobile&&(
            <div style={{display:"flex",overflowX:"auto",WebkitOverflowScrolling:"touch" as any,background:T.bgPanel,borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0}}>
              {divisions.map(div=>(
                <button key={div.id} onClick={()=>setActiveDiv(div.id)}
                  style={{flexShrink:0,padding:"7px 11px",background:"none",border:"none",borderBottom:`2px solid ${activeDiv===div.id?T.borderAccent:"transparent"}`,cursor:"pointer",fontFamily:FONT,textAlign:"left",color:activeDiv===div.id?T.borderAccent:T.textTertiary,transition:"color .15s"}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",marginBottom:1}}>{div.code}</div>
                  <div style={{fontSize:11,lineHeight:1.2,whiteSpace:"nowrap"}}>{div.name}</div>
                </button>
              ))}
            </div>
          )}

          {/* ── SEARCH BAR ── */}
          {searchOpen&&activeTab==="estimate"&&(()=>{
            // Collect matches across ALL divisions with location context so
            // results are clickable even when they live outside the active view.
            const q=searchQuery.toLowerCase();
            const results:{id:string;desc:string;divCode:string;divName:string;subName:string;amt:number}[]=[];
            if(q){
              divisions.forEach(d=>d.subcategories.forEach(s=>{
                const walk=(rows:any[])=>{for(const r of rows||[]){
                  if(r.type==="item"||(!r.type&&r.id)){
                    const li=itemState[r.id];
                    if(li&&li.included&&(li.description||"").toLowerCase().includes(q))
                      results.push({id:r.id,desc:li.description||"",divCode:d.code,divName:d.name,subName:s.name,amt:(li.qty||0)*(li.unitCost||0)});
                  }
                  if(r.children)walk(r.children);
                }};
                walk(s.rows||[]);
              }));
            }
            return(
              <div style={{background:T.bgPanel,borderBottom:`1px solid ${T.borderSecondary}`}}>
                <div style={{padding:"8px 16px",display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:12,color:T.textMuted}}>Search</span>
                  <input id="oe-search-input" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                    placeholder="Search items across all divisions..."
                    onKeyDown={e=>{
                      if(e.key==="Escape"){setSearchOpen(false);setSearchQuery("");}
                      if(e.key==="Enter"&&results.length>0)navigateToItem(results[0].id);
                    }}
                    style={{flex:1,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textSecondary,padding:"6px 10px",fontSize:12,fontFamily:FONT,outline:"none"}} />
                  {searchQuery&&<span style={{fontSize:11,color:T.textMuted}}>{results.length} match{results.length!==1?"es":""}</span>}
                  <button onClick={()=>{setSearchOpen(false);setSearchQuery("");}} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:14,padding:"2px 6px"}}>x</button>
                </div>
                {q&&results.length>0&&(
                  <div style={{maxHeight:240,overflowY:"auto",borderTop:`1px solid ${T.borderPrimary}`}}>
                    {results.slice(0,25).map(r=>(
                      <div key={r.id} onClick={()=>navigateToItem(r.id)}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"7px 16px",cursor:"pointer",fontSize:12}}
                        onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                        onMouseLeave={e=>(e.currentTarget.style.background="")}>
                        <span style={{fontSize:10,fontWeight:700,color:T.borderAccent,background:`${T.borderAccent}18`,padding:"1px 6px",borderRadius:3,flexShrink:0,letterSpacing:".04em"}}>{r.divCode}</span>
                        <span style={{fontSize:10,color:T.textWeak,flexShrink:0,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>{r.subName}</span>
                        <span style={{flex:1,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>{r.desc}</span>
                        <span style={{color:T.textMuted,fontSize:11,flexShrink:0,fontVariantNumeric:"tabular-nums" as any}}>{fmt(r.amt)}</span>
                      </div>
                    ))}
                    {results.length>25&&<div style={{padding:"6px 16px",fontSize:10,color:T.textWeak}}>+{results.length-25} more — refine your search</div>}
                  </div>
                )}
                {q&&results.length===0&&(
                  <div style={{padding:"10px 16px",fontSize:11,color:T.textWeak,borderTop:`1px solid ${T.borderPrimary}`}}>No items match "{searchQuery}"</div>
                )}
              </div>
            );
          })()}

          {/* ── HOME ── */}
          {/* ── HOME ── */}
          {activeTab==="home"&&(
            <ErrorBoundary tabName="Home">
            <HomeTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            cloudLoading={cloudLoading}
            isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}
            user={user} signOut={signOut}
            showAccountMenu={showAccountMenu} setShowAccountMenu={setShowAccountMenu}
            onBackToProjects={onBackToProjects}
            setShowNewProjDialog={setShowNewProjDialog}
            handleOpenSandbox={handleOpenSandbox}
            setActiveTab={setActiveTab}
            setProjectName={setProjectName}
            setBuildingArea={setBuildingArea}
            setDuration={setDuration}
            setOverhead={setOverhead}
            setProfit={setProfit}
            setContingency={setContingency}
            setMarkupMode={setMarkupMode}
            setProposals={setProposals}
            setProposalAreas={setProposalAreas}
            setDivisionSettings={setDivisionSettings}
            setSubSettings={setSubSettings}
            setDivisionsRaw={setDivisionsRaw}
            setActiveDiv={setActiveDiv}
            setItemStateRaw={setItemStateRaw}
            setProjectSnapshots={setProjectSnapshots}
            aiFileRef={aiFileRef}
            getFileSummary={getFileSummary}
            fmt={fmt} fmtSF={fmtSF}
            setEditDisplayName={setEditDisplayName}
            DEFAULT_PROPOSALS={DEFAULT_PROPOSALS}
            />
            </ErrorBoundary>
          )}

          {/* ── SETTINGS SHELL ── */}
          {activeTab.startsWith("settings-")&&(
            <ErrorBoundary tabName="Settings">
            <SettingsPanel
            T={T} isMobile={isMobile} isTablet={isTablet}
            activeTab={activeTab} setActiveTab={setActiveTab}
            settingsFrom={settingsReturnRef.current}
            settingsFromLabel={TAB_LABELS[settingsReturnRef.current||""]}
            user={user} cloudStorage={cloudStorage} cloudProjectId={cloudProjectId}
            orgPartners={orgPartners} orgPartnerSearch={orgPartnerSearch} setOrgPartnerSearch={setOrgPartnerSearch}
            orgs={orgs}
            inviteEmails={inviteEmails} setInviteEmails={setInviteEmails}
            inviteRoles={inviteRoles} setInviteRoles={setInviteRoles}
            newOrgName={newOrgName} setNewOrgName={setNewOrgName}
            ownershipRequests={ownershipRequests}
            editDisplayName={editDisplayName} setEditDisplayName={setEditDisplayName}
            profileMsg={profileMsg} setProfileMsg={setProfileMsg}
            profileSaving={profileSaving} setProfileSaving={setProfileSaving}
            orgLogos={orgLogos} setOrgLogos={setOrgLogos}
            setOrgLogo={setOrgLogo} clearOrgLogo={clearOrgLogo}
            orgExclusions={orgExclusions} setOrgExclusions={setOrgExclusions}
            laborRates={laborRates} setLaborRates={setLaborRates}
            bestPractices={bestPractices} setBestPractices={updateBestPractices}
            bpOrgEditItem={bpOrgEditItem} setBpOrgEditItem={setBpOrgEditItem}
            bpOrgDeleteConfirmId={bpOrgDeleteConfirmId} setBpOrgDeleteConfirmId={setBpOrgDeleteConfirmId}
            bpOrgFilterCat={bpOrgFilterCat} setBpOrgFilterCat={setBpOrgFilterCat}
            presets={presets} editingPresetId={editingPresetId} setEditingPresetId={setEditingPresetId}
            newPresetName={newPresetName} setNewPresetName={setNewPresetName}
            showNewPresetForm={showNewPresetForm} setShowNewPresetForm={setShowNewPresetForm}
            procoreConnected={procoreConnected} setProcoreConnected={setProcoreConnected}
            orgMsg={orgMsg} orgsLoading={orgsLoading}
            procoreConnecting={procoreConnecting}
            showToast={showToast} uid={uid}
            createOrg={createOrg} inviteMember={inviteMember}
            changeOrgRole={changeOrgRole} requestOwnership={requestOwnership}
            confirmApproveTransfer={confirmApproveTransfer} respondToRequest={respondToRequest}
            confirmRemoveMember={confirmRemoveMember} revokeInvite={revokeInvite}
            confirmDeleteOrg={confirmDeleteOrg}
            addPreset={addPreset} deletePreset={deletePreset}
            duplicatePreset={duplicatePreset} savePreset={savePreset}
            handleProcoreConnect={handleProcoreConnect}
            phaseGroups={phaseGroups} setPhaseGroups={updatePhaseGroups}
            isAdmin={isAdmin}
            accessRequests={accessRequests}
            accessRequestsLoading={accessRequestsLoading}
            onApproveRequest={handleApproveRequest}
            onRejectRequest={handleRejectRequest}
            onInviteUser={handleInviteUser}
            />
            </ErrorBoundary>
          )}

          {/* ── ESTIMATE ── */}
          {/* ── ESTIMATE ── */}
          {activeTab==="estimate"&&activeDivData&&(
            <ErrorBoundary tabName="Estimate">
            <EstimateTab
            onOpenStaffingPlan={()=>setShowStaffingPlan(true)}
            staffPlanStale={staffPlanStale.length}
            T={T} isMobile={isMobile} FONT={FONT}
            activeDivData={activeDivData} divisions={divisions}
            continuous={continuousEstimate} setContinuous={setContinuousEstimate}
            onVisibleDivChange={setVisibleDiv}
            confidenceRollup={confidenceRollup}
            divTotals={divTotals}
            proposals={proposals} getPC={getPC}
            globalFilter={globalFilter} applyDivFilter={applyDivFilter}
            showColMenu={showColMenu} setShowColMenu={setShowColMenu}
            fmt={fmt} setActiveTab={setActiveTab} setAnalysisStatusFilter={setAnalysisStatusFilter}
            visibleCols={visibleCols} setVisibleCols={setVisibleCols}
            bpByDiv={bpByDiv} bpNotedSet={bpNotedSet} setBpNotedSet={setBpNotedSet}
            bpPanelCollapsed={bpPanelCollapsed} setBpPanelCollapsed={setBpPanelCollapsed}
            handleAddBpToEstimate={handleAddBpToEstimate}
            expandedSubs={expandedSubs} setExpandedSubs={setExpandedSubs}
            collapsedDivs={collapsedDivs} setCollapsedDivs={setCollapsedDivs}
            rowsTotal={rowsTotal} rowsContTotal={rowsContTotal}
            itemState={itemState}
            expandedMobileRow={expandedMobileRow} setExpandedMobileRow={setExpandedMobileRow}
            isLocked={isLocked}
            upd={upd} addItem={addItem} addSub={addSub} removeSub={removeSub} addGroup={addGroup} addLaborItem={addLaborItem}
            orgCostCodes={orgCostCodes}
            laborRates={laborRates} laborPickerSub={laborPickerSub} setLaborPickerSub={setLaborPickerSub}
            editSubId={editSubId} setEditSubId={setEditSubId} renameSub={renameSub}
            descWidth={descWidth} setDescWidth={setDescWidth}
            visColHeaders={visColHeaders}
            renderGroupRows={renderGroupRows} renderItemRow={renderItemRow}
            setImportTarget={setImportTarget}
            dndActive={dndActive} dndTargetSubId={dndTargetSubId} setDndTargetSubId={setDndTargetSubId}
            handleSubDragOver={handleSubDragOver} handleDrop={handleDrop}
            setHoveredSubId={setHoveredSubId}
            snapshot={snapshot} uid={uid} setProposals={setProposals}
            UNIT_OPTIONS={UNIT_OPTIONS}
            />
            </ErrorBoundary>
          )}

          {/* ── SUMMARY ── */}
          {activeTab==="summary"&&(
            <ErrorBoundary tabName="Summary">
            <SummaryTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            divisions={divisions} itemState={itemState}
            proposals={proposals} proposalAreas={proposalAreas}
            final={final} overhead={overhead} profit={profit}
            contingency={contingency} markupMode={markupMode}
            buildingArea={buildingArea} duration={duration}
            projectName={projectName} address={address}
            grandTotal={grandTotal} divTotals={divTotals}
            contingencyByProp={contingencyByProp} contingencyPolicy={contingencyPolicy} onEditSetting={goSetting} altTotals={altTotals}
            drawingList={drawingList} orgExclusions={orgExclusions}
            budgetRange={budgetRange}
            projectExclusions={projectExclusions} setProjectExclusions={setProjectExclusions}
            newExclusionText={newExclusionText} setNewExclusionText={setNewExclusionText}
            contingencyDisplay={contingencyDisplay} setContingencyDisplay={setContingencyDisplay}
            showAllowancePanel={showAllowancePanel} setShowAllowancePanel={setShowAllowancePanel}
            showBudgetPicker={showBudgetPicker} setShowBudgetPicker={setShowBudgetPicker}
            bdCollapsed={bdCollapsed} setBdCollapsed={setBdCollapsed}
            setActiveTab={setActiveTab} setActiveDiv={setActiveDiv}
            setPrintInitialSheets={setPrintInitialSheets} setShowPrintDialog={setShowPrintDialog}
            fmt={fmt} fmtDec={fmtDec} fmtSF={fmtSF} costSFCents={costSFCents}
            getPC={getPC} navigateToItem={navigateToItem} subTotals={subTotals}
            />
            </ErrorBoundary>
          )}

          {/* ── EXECUTIVE SUMMARY (removed — merged into Summary tab) ── */}
          {false&&(()=>{
            const SK_EX=["Hard Bid","Allowance","Needs Review","Other"];
            const SC_EX={"Hard Bid":{fill:"#4abf6a",text:"#7ad49a",label:"Hard Bid"},"Allowance":{fill:"#4a8fbf",text:"#7ab8d4",label:"Allowance"},"Needs Review":{fill:"#bfb04a",text:"#d4c87a",label:"Needs Review"},"Other":{fill:"#666888",text:"#9999bb",label:"Other"}};
            const CW_EX={"Hard Bid":1,"Allowance":.5,"Needs Review":0,"Other":.25};
            const allItemsEx=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].map(item=>{
              const li=itemState[item.id]||{};
              const total=li.included!==false&&!li.altType?(li.qty||0)*(li.unitCost||0):0;
              const status=li.costStatus&&SK_EX.includes(li.costStatus)?li.costStatus:"Other";
              return{total,status,proposal:li.proposal};
            }).filter(i=>i.total>0)));
            const bkts=(items)=>{const b:Record<string,number>={};SK_EX.forEach(k=>{b[k]=0;});items.forEach(i=>{b[i.status]=(b[i.status]||0)+i.total;});return{buckets:b,grandSum:Object.values(b).reduce((a,v)=>a+v,0)};};
            const{buckets:allBkt,grandSum:allSum}=bkts(allItemsEx);
            const confScore=(items)=>{const{buckets:b,grandSum:gs}=bkts(items);if(gs===0)return null;return Math.round((SK_EX.reduce((a,k)=>a+(b[k]||0)*CW_EX[k],0)/gs)*100);};
            const os=confScore(allItemsEx);
            const sc=os===null?T.textMuted:os>=75?T.positive:os>=45?T.costStatus["Needs Review"].badgeText:T.negative;
            const BAR_COLORS_EX=["#7ab8d4","#4abf6a","#d4c87a","#bf8a4a","#bf6abf","#4a8fbf","#d4906a","#8fbf4a","#6ab8bf","#d4b87a"];
            const divRows=divisions.map((div,i)=>({div,total:divTotals[div.id]?.total||0,color:BAR_COLORS_EX[i%BAR_COLORS_EX.length]})).filter(r=>r.total>0).sort((a,b)=>b.total-a.total).slice(0,8);
            const maxDivVal=Math.max(...divRows.map(r=>r.total),1);
            // mini confidence donut
            const donutSize=120;const cx2=donutSize/2,cy2=donutSize/2,r2=donutSize/2-8,ri2=r2*.52;
            let ang2=-Math.PI/2;const segs2=[];
            SK_EX.forEach(key=>{const val=allBkt[key]||0;if(!val||!allSum)return;const sw=(val/allSum)*2*Math.PI,ea=ang2+sw;
              const x1=cx2+r2*Math.cos(ang2),y1=cy2+r2*Math.sin(ang2),x2=cx2+r2*Math.cos(ea),y2=cy2+r2*Math.sin(ea);
              const ix1=cx2+ri2*Math.cos(ang2),iy1=cy2+ri2*Math.sin(ang2),ix2=cx2+ri2*Math.cos(ea),iy2=cy2+ri2*Math.sin(ea);
              const lg=sw>Math.PI?1:0;
              segs2.push({key,fill:SC_EX[key].fill,pct:val/allSum*100,d:`M${ix1},${iy1} L${x1},${y1} A${r2},${r2} 0 ${lg},1 ${x2},${y2} L${ix2},${iy2} A${ri2},${ri2} 0 ${lg},0 ${ix1},${iy1} Z`});
              ang2=ea;});
            return (
              <div className="fade-in" style={{padding:isMobile?"14px 16px":isTablet?"20px 24px":"28px 36px",maxWidth:1060,overflowY:"auto"}}>
                {/* Header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:28,paddingBottom:16,borderBottom:`1px solid ${T.borderPrimary}`}}>
                  <div>
                    <div style={{fontSize:24,fontWeight:700,color:T.textPrimary,letterSpacing:"-.01em"}}>{projectName}</div>
                    {address&&<div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{address}</div>}
                    <div style={{fontSize:11,color:T.textWeak,marginTop:4}}>Executive Summary · {new Date().toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"})}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:8}}>
                    <button onClick={()=>{setPrintInitialSheets(["summary"]);setShowPrintDialog(true);}}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"7px 16px",fontSize:12,fontWeight:600,
                        background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textTertiary,
                        cursor:"pointer",fontFamily:FONT,transition:"all .15s",whiteSpace:"nowrap" as any}}
                      onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.borderColor=T.borderAccent;(e.currentTarget as HTMLElement).style.color=T.borderAccent;}}
                      onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.borderColor=T.borderSecondary;(e.currentTarget as HTMLElement).style.color=T.textTertiary;}}>
                      <Icon.Printer size={12}/> Export PDF
                    </button>
                    <div style={{textAlign:"right",fontSize:11,color:T.textMuted}}>
                      {buildingArea>0&&<div>{buildingArea.toLocaleString()} SF</div>}
                      <div>{duration} Month{duration!==1?"s":""}</div>
                    </div>
                  </div>
                </div>
                {/* KPI cards row */}
                <div style={{display:"flex",gap:16,marginBottom:28,flexWrap:"wrap"}}>
                  {[
                    {label:"Total Estimate",value:fmt(final.total),sub:null,col:T.borderAccent},
                    {label:"Cost per SF",value:buildingArea>0?fmtSF(final.costPerSF)+" / SF":"—",sub:buildingArea>0?`${buildingArea.toLocaleString()} SF`:null,col:T.positive},
                    {label:"Project Duration",value:`${duration} Month${duration!==1?"s":""}`,sub:null,col:T.textSecondary},
                  ].map(({label,value,sub,col})=>(
                    <div key={label} style={{flex:1,minWidth:180,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"20px 22px"}}>
                      <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase",marginBottom:10}}>{label}</div>
                      <div style={{fontSize:24,fontWeight:700,color:col,lineHeight:1.1}}>{value}</div>
                      {sub&&<div style={{fontSize:10,color:T.textMuted,marginTop:6}}>{sub}</div>}
                    </div>
                  ))}
                  {/* Conceptual Budget KPI card */}
                  {(()=>{
                    const est=final.total;
                    const withinBudget=budgetRange&&est>=budgetRange.low&&est<=budgetRange.high;
                    const overBudget=budgetRange&&est>budgetRange.high;
                    const underBudget=budgetRange&&est<budgetRange.low;
                    const statusColor=withinBudget?T.positive:overBudget?T.negative:"#d4a76a";
                    const statusLabel=withinBudget?"Within Budget":overBudget?"Over Budget":"Under Budget";
                    const pctFromMid=budgetRange?(est-((budgetRange.low+budgetRange.high)/2))/((budgetRange.low+budgetRange.high)/2)*100:0;
                    return(
                      <div style={{flex:1,minWidth:200,background:T.bgPanel,border:`1px solid ${budgetRange?`${statusColor}44`:T.borderPrimary}`,borderRadius:6,padding:"20px 22px",position:"relative"}}>
                        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase" as any,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <span>Conceptual Budget</span>
                          <button onClick={()=>setShowBudgetPicker(true)}
                            style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,cursor:"pointer",fontFamily:FONT,letterSpacing:".06em",textTransform:"uppercase" as any}}>
                            {linkedBudgetId?"Change":"Link"}
                          </button>
                        </div>
                        {budgetRange?(
                          <>
                            <div style={{fontSize:12,fontWeight:700,color:statusColor,marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:16}}>{withinBudget?"✓":overBudget?"▲":"▼"}</span>
                              {statusLabel}
                              <span style={{fontSize:10,fontWeight:400,color:T.textMuted}}>
                                ({pctFromMid>0?"+":""}{pctFromMid.toFixed(1)}% of mid)
                              </span>
                            </div>
                            <div style={{fontSize:11,color:T.textTertiary,marginBottom:8}}>
                              Range: {fmt(budgetRange.low)} – {fmt(budgetRange.high)}
                            </div>
                            {/* Range bar */}
                            {(()=>{
                              const lo=budgetRange.low,hi=budgetRange.high,span=hi-lo||1;
                              const pad=span*0.15;
                              const barMin=Math.min(lo-pad,est-pad);
                              const barMax=Math.max(hi+pad,est+pad);
                              const barSpan=barMax-barMin||1;
                              const loX=((lo-barMin)/barSpan)*100;
                              const hiX=((hi-barMin)/barSpan)*100;
                              const estX=Math.max(0,Math.min(100,((est-barMin)/barSpan)*100));
                              return(
                                <div style={{position:"relative",height:20,marginTop:4}}>
                                  {/* Budget range band */}
                                  <div style={{position:"absolute",left:`${loX}%`,width:`${hiX-loX}%`,top:6,height:8,background:`${T.positive}30`,border:`1px solid ${T.positive}55`,borderRadius:3}}/>
                                  {/* Estimate marker */}
                                  <div style={{position:"absolute",left:`${estX}%`,top:2,transform:"translateX(-50%)",width:3,height:16,background:statusColor,borderRadius:3}}/>
                                  <div style={{position:"absolute",left:`${loX}%`,bottom:-14,transform:"translateX(-50%)",fontSize:10,color:T.textWeak,whiteSpace:"nowrap" as any}}>{fmt(lo)}</div>
                                  <div style={{position:"absolute",left:`${hiX}%`,bottom:-14,transform:"translateX(-50%)",fontSize:10,color:T.textWeak,whiteSpace:"nowrap" as any}}>{fmt(hi)}</div>
                                </div>
                              );
                            })()}
                            <div style={{marginTop:20,fontSize:10,color:T.textMuted,fontStyle:"italic"}}>{budgetRange.name}</div>
                          </>
                        ):(
                          <div style={{fontSize:12,color:T.textWeak,marginTop:4}}>
                            <div style={{marginBottom:8}}>No budget linked.</div>
                            <button onClick={()=>setShowBudgetPicker(true)}
                              style={{fontSize:10,padding:"5px 14px",borderRadius:3,background:T.bgAccentFaint,border:`1px solid ${T.borderAccentFaint}`,color:T.borderAccent,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
                              Link a Conceptual Budget →
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {/* Proposal cards */}
                {proposals.length>0&&(
                  <div style={{marginBottom:28}}>
                    <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:12}}>Packages</div>
                    <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                      {proposals.map(prop=>{
                        const pc=getPC(prop.id,proposals);
                        const ptotal=grandTotal.byProp[prop.id]||0;
                        const sf=sfFor(prop.id);
                        const csf=sf>0&&ptotal!==0?ptotal/sf:0;
                        const pct=grandTotal.subtotal!==0?(ptotal/Math.abs(grandTotal.subtotal))*100:0;
                        return (
                          <div key={prop.id} style={{flex:1,minWidth:180,background:T.bgPanel,borderRadius:6,padding:"16px 18px",borderTop:`3px solid ${pc.text}`,border:`1px solid ${pc.text}28`,borderTopColor:pc.text}}>
                            <div style={{fontSize:10,color:pc.text,letterSpacing:".1em",textTransform:"uppercase",fontWeight:600,marginBottom:8}}>{prop.name}</div>
                            <div style={{fontSize:20,fontWeight:700,color:ptotal<0?T.negative:pc.text,marginBottom:4}}>{fmt(ptotal)}</div>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textMuted}}>
                              {csf>0&&<span>{fmtSF(csf)}/sf</span>}
                              <span>{pct.toFixed(0)}% of total</span>
                            </div>
                            <div style={{height:3,background:T.bgToast,borderRadius:3,marginTop:10,overflow:"hidden"}}>
                              <div style={{width:`${Math.min(100,Math.abs(pct))}%`,height:"100%",background:pc.text,borderRadius:3}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Bottom row: Division bars + Confidence */}
                <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"flex-start"}}>
                  {/* Division bar chart */}
                  {divRows.length>0&&(
                    <div style={{flex:"1 1 400px",background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"20px 22px"}}>
                      <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:16}}>Cost by Division</div>
                      {(()=>{
                        const rowH=26,gap=5,lW=160,vW=120,bW=240,tW=lW+bW+vW,tH=divRows.length*(rowH+gap);
                        return (
                          <svg width="100%" viewBox={`0 0 ${tW} ${tH}`} style={{overflow:"visible",display:"block"}}>
                            {divRows.map(({div,total,color},i)=>{
                              const y=i*(rowH+gap);
                              const barW=Math.max(2,(total/maxDivVal)*bW);
                              const csf=buildingArea>0?total/buildingArea:0;
                              const lbl=`${div.code} — ${div.name}`;
                              return (
                                <g key={div.id} style={{cursor:"pointer"}} onClick={()=>{setActiveTab("estimate");setActiveDiv(div.id);}}>
                                  <text x={lW-6} y={y+rowH/2} textAnchor="end" dominantBaseline="middle" fill={T.textMuted} fontSize={10.5} fontFamily={FONT}>{lbl.length>26?lbl.slice(0,25)+"…":lbl}</text>
                                  <rect x={lW} y={y+4} width={bW} height={rowH-8} fill={T.bgInput} rx={2}/>
                                  <rect x={lW} y={y+4} width={barW} height={rowH-8} fill={color} rx={2} opacity={0.82}/>
                                  <text x={lW+bW+8} y={y+rowH/2-4} dominantBaseline="middle" fill={T.textSecondary} fontSize={10.5} fontFamily={FONT} fontWeight={600}>{fmt(total)}</text>
                                  {csf>0&&<text x={lW+bW+8} y={y+rowH/2+8} dominantBaseline="middle" fill={T.textWeak} fontSize={10.5} fontFamily={FONT}>{fmtSF(csf)}/sf</text>}
                                </g>
                              );
                            })}
                          </svg>
                        );
                      })()}
                    </div>
                  )}
                  {/* Confidence donut */}
                  {allSum>0&&(
                    <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"20px 22px",minWidth:220}}>
                      <div style={{fontSize:10,color:T.textMuted,letterSpacing:".12em",textTransform:"uppercase",marginBottom:12}}>Cost Confidence</div>
                      <div style={{display:"flex",alignItems:"center",gap:18}}>
                        <svg width={donutSize+20} height={donutSize+20} style={{overflow:"visible",flexShrink:0}}>
                          <g transform="translate(10,10)">
                            {segs2.map(s=><path key={s.key} d={s.d} fill={s.fill} stroke={T.bgApp} strokeWidth={1.5}/>)}
                            {os!==null&&<>
                              <text x={cx2} y={cy2-7} textAnchor="middle" fill={sc} fontSize={20} fontWeight={700} fontFamily={FONT}>{os}</text>
                              <text x={cx2} y={cy2+10} textAnchor="middle" fill={T.textWeak} fontSize={10} fontFamily={FONT}>{os>=75?"HIGH":os>=45?"MED":"LOW"}</text>
                            </>}
                          </g>
                        </svg>
                        <div style={{flex:1}}>
                          {segs2.map(s=>(
                            <div key={s.key} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                              <span style={{width:8,height:8,borderRadius:3,background:s.fill,flexShrink:0,display:"inline-block"}}/>
                              <span style={{fontSize:10,color:T.textMuted,flex:1}}>{SC_EX[s.key].label}</span>
                              <span style={{fontSize:10,color:SC_EX[s.key].text,fontWeight:600}}>{s.pct.toFixed(0)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {os!==null&&(
                        <div style={{marginTop:14,paddingTop:10,borderTop:`1px solid ${T.bgInput}`}}>
                          <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Confidence Score</div>
                          <div style={{height:6,background:T.bgToast,borderRadius:3,overflow:"hidden"}}>
                            <div style={{width:`${os}%`,height:"100%",background:sc,borderRadius:3,transition:"width .4s ease"}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginTop:4,fontSize:10,color:T.textWeak}}>
                            <span>0</span><span style={{color:sc,fontWeight:700}}>{os}</span><span>100</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── ALTERNATES ── */}
          {activeTab==="alternates"&&(
            <ErrorBoundary tabName="Alternates">
            <AlternatesTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            rollups={altRollups} final={final} fmt={fmt}
            setActiveTab={setActiveTab} setVisibleCols={setVisibleCols}
            navigateToItem={navigateToItem}
            onStatus={onAltStatus} onRename={onAltRename}
            onCombine={onAltCombine} onSplit={onAltSplit}
            />
            </ErrorBoundary>
          )}

          {/* ── ANALYSIS (Cost Confidence) ── */}
          {activeTab==="analysis"&&(
            <ErrorBoundary tabName="Analysis">
            <AnalysisTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            divisions={divisions} itemState={itemState} proposals={proposals}
            grandTotal={grandTotal} final={final} projectName={projectName} fmt={fmt}
            getPC={getPC}
            collapsedAnalysisDivs={collapsedAnalysisDivs} setCollapsedAnalysisDivs={setCollapsedAnalysisDivs}
            collapsedAnalysisSubs={collapsedAnalysisSubs} setCollapsedAnalysisSubs={setCollapsedAnalysisSubs}
            analysisStatusFilter={analysisStatusFilter} setAnalysisStatusFilter={setAnalysisStatusFilter}
            navigateToItem={navigateToItem}
            />
            </ErrorBoundary>
          )}

          {/* ── BID LEVELING ── */}
          {activeTab==="bids"&&(
            <ErrorBoundary tabName="Bids">
            <BidsTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            divisions={divisions} itemState={itemState}
            bids={bids} setBids={setBids}
            bidScopes={bidScopes} setBidScopes={setBidScopes}
            bidPartners={effectiveBidPartners} setBidPartners={setBidPartners}
            buyoutItems={buyoutItems}
            onAwardBid={awardBid}
            orgPartnersCount={pickerPartners.length}
            levelingAdjustments={levelingAdjustments} setLevelingAdjustments={setLevelingAdjustments}
            bidAlignments={bidAlignments} setBidAlignments={setBidAlignments}
            activeBidScope={activeBidScope} setActiveBidScope={setActiveBidScope}
            bidSubTab={bidSubTab} setBidSubTab={setBidSubTab}
            bidNavMode={bidNavMode} setBidNavMode={setBidNavMode}
            bidSnapModalId={bidSnapModalId} setBidSnapModalId={setBidSnapModalId}
            snapExpandedDivs={snapExpandedDivs} setSnapExpandedDivs={setSnapExpandedDivs}
            tradeSources={tradeSources} setTradeSources={setTradeSources}
            bidsSnapshot={bidsSnapshot} onImportBcLeveling={()=>setShowBcImport(true)}
            onPrintComparison={()=>{setPrintInitialSheets(["bids"]);setShowPrintDialog(true);}} showToast={showToast} uid={uid} fmt={fmt}
            setAnalysisLeft={setAnalysisLeft} setAnalysisRight={setAnalysisRight}
            setActiveTab={setActiveTab}
            projectSnapshots={projectSnapshots} snapshot={snapshot} divTotals={divTotals}
            editingScopeId={editingScopeId} setEditingScopeId={setEditingScopeId}
            editingScopeName={editingScopeName} setEditingScopeName={setEditingScopeName}
            showNewScope={showNewScope} setShowNewScope={setShowNewScope}
            newScopeName={newScopeName} setNewScopeName={setNewScopeName}
            newScopeDivIds={newScopeDivIds} setNewScopeDivIds={setNewScopeDivIds}
            showPartnersPanel={showPartnersPanel} setShowPartnersPanel={setShowPartnersPanel}
            bidTreeExpanded={bidTreeExpanded} setBidTreeExpanded={setBidTreeExpanded}
            bidPdfCache={bidPdfCache}
            bidChartHover={bidChartHover} setBidChartHover={setBidChartHover}
            bidChartScopeFilter={bidChartScopeFilter} setBidChartScopeFilter={setBidChartScopeFilter}
            bidChartRangeMode={bidChartRangeMode} setBidChartRangeMode={setBidChartRangeMode}
            bidChartCustomStart={bidChartCustomStart} setBidChartCustomStart={setBidChartCustomStart}
            bidChartCustomEnd={bidChartCustomEnd} setBidChartCustomEnd={setBidChartCustomEnd}
            bidChartW={bidChartW} setBidChartW={setBidChartW}
            collapsedHistoryPartners={collapsedHistoryPartners} setCollapsedHistoryPartners={setCollapsedHistoryPartners}
            setBidImportScopeId={setBidImportScopeId} setShowBidImport={setShowBidImport}
            showDocScopePicker={showDocScopePicker} setShowDocScopePicker={setShowDocScopePicker}
            comparePdfIds={comparePdfIds} setComparePdfIds={setComparePdfIds}
            hiddenPartners={hiddenPartners} setHiddenPartners={setHiddenPartners}
            dragBidItemIds={dragBidItemIds} setDragBidItemIds={setDragBidItemIds}
            dropTargetEstItemId={dropTargetEstItemId} setDropTargetEstItemId={setDropTargetEstItemId}
            expandedAlignRows={expandedAlignRows} setExpandedAlignRows={setExpandedAlignRows}
            openBidPdfViewer={openBidPdfViewer} viewBidPdf={viewBidPdf}
            setShowSowEditor={setShowSowEditor}
            setItemState={setItemState} setDivisionsRaw={setDivisionsRaw}
            />
            </ErrorBoundary>
          )}
          {/* ── BID IMPORT MODAL ── */}
          {showBidImport&&(
            <div className="modal-backdrop" onClick={()=>{setShowBidImport(false);setBidImportStage("idle");}}>
              <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:660,maxHeight:"88vh",display:"flex",flexDirection:"column",padding:0,overflow:"hidden"}}>
                {/* Header */}
                <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.borderSecondary}`}}>
                  <div style={{fontSize:14,fontWeight:600,color:T.borderAccent}}>Import Bid</div>
                  <div style={{fontSize:11,color:T.textWeak}}>{bidScopes.find(s=>s.id===bidImportScopeId)?.name||""}</div>
                </div>
                <div style={{flex:1,overflow:"auto",padding:"16px 24px"}}>
                  {/* Subcontractor selection */}
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Subcontractor</div>
                    <select value={bidImportPartnerId} onChange={e=>setBidImportPartnerId(e.target.value)}
                      style={{width:"100%",fontSize:12,padding:"6px 8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT}}>
                      <option value="">— Select subcontractor —</option>
                      {pickerPartners.map(p=>(
                        <option key={p.id} value={p.id}>{p.name}{p.trade?` · ${p.trade}`:""}</option>
                      ))}
                      <option value="__new__">+ Add new subcontractor…</option>
                    </select>
                    {bidImportPartnerId==="__new__"&&(
                      <input className="input-field" placeholder="Subcontractor name..." value={newBidPartnerName} onChange={e=>setNewBidPartnerName(e.target.value)}
                        style={{width:"100%",marginTop:8,fontSize:12,boxSizing:"border-box" as any}}/>
                    )}
                  </div>

                  {/* ── Stage: IDLE — mode picker + drop zone ── */}
                  {bidImportStage==="idle"&&(
                    <div>
                      {/* Mode selector */}
                      <div style={{display:"flex",gap:8,marginBottom:16}}>
                        {([
                          {id:"ai" as const,icon:<Icon.Bot size={14}/>,label:"Parse with AI",desc:"Claude extracts line items from the PDF automatically"},
                          {id:"attach" as const,icon:<Icon.Paperclip size={14}/>,label:"Attach PDF only",desc:"Store for viewing — enter totals manually or skip"},
                        ]).map(opt=>{
                          const active=bidImportMode===opt.id;
                          return(
                            <button key={opt.id} onClick={()=>setBidImportMode(opt.id)}
                              style={{flex:1,padding:"12px 14px",border:`2px solid ${active?T.borderAccent:T.borderSecondary}`,borderRadius:6,
                                background:active?T.bgAccentSubtle:"transparent",cursor:"pointer",textAlign:"left",fontFamily:FONT,transition:"all .15s"}}>
                              <div style={{fontSize:16,marginBottom:4}}>{opt.icon}</div>
                              <div style={{fontSize:11,fontWeight:700,color:active?T.borderAccent:T.textSecondary,marginBottom:2}}>{opt.label}</div>
                              <div style={{fontSize:10,color:T.textWeak,lineHeight:1.4}}>{opt.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                      {/* Drop zone */}
                      <div
                        onDragOver={e=>{e.preventDefault();setBidImportIsDragging(true);}}
                        onDragLeave={()=>setBidImportIsDragging(false)}
                        onDrop={async e=>{e.preventDefault();setBidImportIsDragging(false);const f=e.dataTransfer.files[0];if(f)await handleBidPdf(f,bidImportMode);}}
                        onClick={()=>bidImportFileRef.current?.click()}
                        style={{border:`2px dashed ${bidImportIsDragging?T.borderAccent:T.borderDashed}`,borderRadius:6,padding:"28px 24px",textAlign:"center",cursor:"pointer",
                          background:bidImportIsDragging?T.bgAccentFaint:"transparent",transition:"all .15s"}}>
                        <div style={{fontSize:11,color:T.textSecondary,fontWeight:600,marginBottom:3}}>
                          {bidImportMode==="ai"?"Drop PDF here to parse with AI":"Drop PDF here to attach"}
                        </div>
                        <div style={{fontSize:10,color:T.textWeak}}>or click to browse</div>
                      </div>
                      <input ref={bidImportFileRef} type="file" accept=".pdf" style={{display:"none"}}
                        onChange={async e=>{const f=e.target.files?.[0];if(f)await handleBidPdf(f,bidImportMode);e.target.value="";}}/>
                      {bidImportMode==="ai"&&(
                        <div style={{textAlign:"center",marginTop:12}}>
                          <button onClick={()=>{setBidImportStage("manual");setManualBidItems([{id:uid(),description:"",qty:1,unit:"LS",unitCost:0,notes:"",altType:""}]);}}
                            style={{fontSize:10,color:T.textWeak,background:"none",border:"none",cursor:"pointer",fontFamily:FONT,textDecoration:"underline"}}>
                            Enter line items manually instead
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Stage: SCANNING ── */}
                  {bidImportStage==="scanning"&&(
                    <div style={{padding:"48px 24px",textAlign:"center"}}>
                      <div style={{fontSize:24,marginBottom:14}}><Icon.Hourglass size={28}/></div>
                      <div style={{fontSize:13,color:T.textSecondary,fontWeight:600,marginBottom:6}}>Parsing bid with Claude…</div>
                      <div style={{fontSize:11,color:T.textWeak}}>{bidImportPdfName}</div>
                    </div>
                  )}

                  {/* ── Stage: ERROR ── */}
                  {bidImportStage==="error"&&(
                    <div style={{padding:"20px",background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,borderRadius:6}}>
                      <div style={{fontSize:12,color:T.dangerText,marginBottom:14,lineHeight:1.5}}>{bidImportError||"Failed to parse PDF."}</div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>setBidImportStage("idle")} style={{padding:"5px 14px",fontSize:10,border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Try Another File</button>
                        <button onClick={()=>{setBidImportStage("manual");setManualBidItems([{id:uid(),description:"",qty:1,unit:"LS",unitCost:0,notes:"",altType:""}]);}}
                          style={{padding:"5px 14px",fontSize:10,border:`1px solid ${T.borderAccent}`,borderRadius:3,background:"none",color:T.borderAccent,cursor:"pointer",fontFamily:FONT}}>Enter Manually</button>
                      </div>
                    </div>
                  )}

                  {/* ── Stage: ATTACH CONFIRM ── */}
                  {bidImportStage==="attach"&&(
                    <div>
                      {/* File confirmation banner */}
                      <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:T.bgAccentFaint,border:`1px solid ${T.borderAccentFaint}`,borderRadius:6,marginBottom:18}}>
                        <span style={{fontSize:20,lineHeight:1,flexShrink:0}}><Icon.Paperclip size={22}/></span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{bidImportPdfName}</div>
                          <div style={{fontSize:10,color:T.textWeak,marginTop:2}}>PDF attached — will be stored and viewable from the Bids section</div>
                        </div>
                        <button onClick={()=>setBidImportStage("idle")}
                          style={{fontSize:10,color:T.textWeak,background:"none",border:"none",cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap",flexShrink:0}}>🔄 Change file</button>
                      </div>
                      {/* Optional: bid total */}
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Bid Total <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
                        <CurrencyInput value={bidAttachTotal} onChange={setBidAttachTotal}
                          style={{fontSize:13,padding:"6px 10px",width:"100%",boxSizing:"border-box"as any}} negColor={T.negative}/>
                        <div style={{fontSize:10,color:T.textWeak,marginTop:4}}>Enter the proposal total if known. You can always update this later.</div>
                      </div>
                      {/* Notes */}
                      <div>
                        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Notes <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
                        <textarea value={bidAttachNotes} onChange={e=>setBidAttachNotes(e.target.value)}
                          placeholder="Scope inclusions, exclusions, clarifications…"
                          rows={3}
                          style={{width:"100%",fontSize:11,padding:"7px 10px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textSecondary,fontFamily:FONT,resize:"vertical",boxSizing:"border-box"as any,outline:"none"}}/>
                      </div>
                    </div>
                  )}

                  {/* ── Stage: PREVIEW or MANUAL — item table ── */}
                  {(bidImportStage==="preview"||bidImportStage==="manual")&&(
                    <div>
                      {bidImportStage==="preview"&&(
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"8px 12px",background:T.bgAccentFaint,border:`1px solid ${T.borderAccentFaint}`,borderRadius:3}}>
                          <span style={{fontSize:16,lineHeight:1}}>✓</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,color:T.textSecondary,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{bidImportPdfName}</div>
                            <div style={{fontSize:10,color:T.textWeak}}>Parsed by Claude — review and edit below before saving</div>
                          </div>
                          <button onClick={()=>setBidImportStage("idle")}
                            style={{fontSize:10,color:T.textWeak,background:"none",border:"none",cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap"}}>🔄 Different file</button>
                        </div>
                      )}
                      <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead>
                          <tr style={{borderBottom:`1px solid ${T.borderSecondary}`}}>
                            {["Description","Qty","Unit","Unit Cost","Alt",""].map(h=><th key={h} style={{fontSize:10,color:T.textWeak,padding:"4px 6px",textAlign:h==="Qty"||h==="Unit Cost"?"right":"left"}}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {manualBidItems.map((it,i)=>(
                            <tr key={it.id} style={{borderBottom:`1px solid ${T.bgInput}`}}>
                              <td style={{padding:"3px 4px"}}><input className="ghost-input" value={it.description} onChange={e=>{const v=[...manualBidItems];v[i]={...v[i],description:e.target.value};setManualBidItems(v);}} placeholder="Description..." style={{width:"100%",fontSize:11}}/></td>
                              <td style={{padding:"3px 4px",width:60}}><input className="ghost-input" type="number" value={it.qty} onChange={e=>{const v=[...manualBidItems];v[i]={...v[i],qty:+e.target.value||0};setManualBidItems(v);}} style={{textAlign:"right",fontSize:11,width:"100%"}}/></td>
                              <td style={{padding:"3px 4px",width:60}}><select value={it.unit} onChange={e=>{const v=[...manualBidItems];v[i]={...v[i],unit:e.target.value};setManualBidItems(v);}} style={{fontSize:10,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,padding:"2px 4px",fontFamily:FONT}}>
                                {["LS","EA","SF","LF","CY","CF","SY","MBF","TON","HR","DAY","GAL","LB","MO"].map(u=><option key={u} value={u}>{u}</option>)}
                              </select></td>
                              <td style={{padding:"3px 4px",width:100}}><CurrencyInput value={it.unitCost} onChange={val=>{const v=[...manualBidItems];v[i]={...v[i],unitCost:val};setManualBidItems(v);}} style={{fontSize:11,padding:"3px 6px",width:"100%",boxSizing:"border-box"as any}} negColor={T.negative}/></td>
                              <td style={{padding:"3px 4px",width:80}}><select value={it.altType||""} onChange={e=>{const v=[...manualBidItems];v[i]={...v[i],altType:e.target.value};setManualBidItems(v);}} style={{fontSize:10,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:it.altType==="AddAlt"?T.borderAccent:it.altType==="DedAlt"?"#e06c75":T.textSecondary,borderRadius:3,padding:"2px 4px",fontFamily:FONT,width:"100%"}}>
                                <option value="">Base</option>
                                <option value="AddAlt">Add Alt</option>
                                <option value="DedAlt">Ded Alt</option>
                              </select></td>
                              <td style={{padding:"3px 4px",width:28}}><button onClick={()=>setManualBidItems(p=>p.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:12}}><Icon.X size={10}/></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button onClick={()=>setManualBidItems(p=>[...p,{id:uid(),description:"",qty:1,unit:"LS",unitCost:0,notes:"",altType:""}])}
                        style={{marginTop:6,fontSize:10,color:T.borderAccent,background:"none",border:"none",cursor:"pointer",fontFamily:FONT}}>+ Add line item</button>
                    </div>
                  )}
                </div>

                {/* Footer — changes based on stage */}
                <div style={{padding:"14px 24px",borderTop:`1px solid ${T.borderSecondary}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  {/* ── Attach-only footer ── */}
                  {bidImportStage==="attach"&&(
                    <>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:10,color:T.textWeak}}>Bid Date:</span>
                        <input type="date" value={bidImportDate} onChange={e=>setBidImportDate(e.target.value)}
                          style={{fontSize:10,padding:"3px 7px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontFamily:FONT,outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>{setShowBidImport(false);setBidImportStage("idle");}}
                          style={{padding:"6px 14px",fontSize:11,border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
                        <button onClick={async()=>{
                          let pid=bidImportPartnerId;
                          if(pid==="__new__"&&newBidPartnerName.trim()){
                            const newOp:OrgPartner={id:uid(),name:newBidPartnerName.trim(),trade:"",contact:"",phone:"",email:"",notes:""};
                            pid=await addOrgPartner(newOp);
                          }
                          if(!pid||pid==="__new__"){showToast("Select or add a subcontractor");return;}
                          const sid=bidImportScopeId;if(!sid)return;
                          const existing=bids.find(b=>b.partnerId===pid&&b.scopeId===sid&&b.status==="Active");
                          if(existing)setBids(prev=>prev.map(b=>b.id===existing.id?{...b,status:"Superseded" as const}:b));
                          const version=existing?(existing.version+1):1;
                          // Build items: one lump-sum entry if total entered, else empty
                          const attachItems:BidItem[]=bidAttachTotal>0
                            ?[{id:uid(),description:"Proposal Total",qty:1,unit:"LS",unitCost:bidAttachTotal,notes:bidAttachNotes}]
                            :[];
                          const newBid:Bid={id:uid(),partnerId:pid,scopeId:sid,version,date:bidImportDate||new Date().toISOString(),
                            status:"Active",items:attachItems,pdfName:bidImportPdfName,notes:bidAttachNotes};
                          setBids(p=>[...p,newBid]);
                          if(pendingBidFileRef.current){
                            _revokeBlob(bidPdfCache.current.get(newBid.id));bidPdfCache.current.set(newBid.id,URL.createObjectURL(pendingBidFileRef.current));
                            uploadProposalToStorage(pendingBidFileRef.current,newBid);
                            pendingBidFileRef.current=null;
                          }
                          setShowBidImport(false);setBidImportStage("idle");setBidAttachTotal(0);setBidAttachNotes("");
                          showToast(`Proposal from ${pName(pid)||newBidPartnerName} attached`);
                        }}
                          style={{padding:"6px 16px",fontSize:11,border:"none",borderRadius:3,background:T.borderAccent,color:T.textOnGold,cursor:"pointer",fontWeight:600,fontFamily:FONT}}>
                          Attach Proposal
                        </button>
                      </div>
                    </>
                  )}
                  {(bidImportStage==="preview"||bidImportStage==="manual")?(
                    <>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{fontSize:12,color:T.textWeak}}>
                          {manualBidItems.length} items · Total: <span style={{color:T.positive,fontWeight:600}}>{fmt(manualBidItems.reduce((s,it)=>s+(it.qty||0)*(it.unitCost||0),0))}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:10,color:T.textWeak}}>Bid Date:</span>
                          <input type="date" value={bidImportDate} onChange={e=>setBidImportDate(e.target.value)}
                            style={{fontSize:10,padding:"3px 7px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontFamily:FONT,outline:"none"}}/>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>{setShowBidImport(false);setBidImportStage("idle");}}
                          style={{padding:"6px 14px",fontSize:11,border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
                        <button onClick={async()=>{
                          let pid=bidImportPartnerId;
                          if(pid==="__new__"&&newBidPartnerName.trim()){
                            const newOp:OrgPartner={id:uid(),name:newBidPartnerName.trim(),trade:"",contact:"",phone:"",email:"",notes:""};
                            pid=await addOrgPartner(newOp);
                          }
                          if(!pid||pid==="__new__"){showToast("Select or add a subcontractor");return;}
                          const sid=bidImportScopeId;if(!sid)return;
                          const items=manualBidItems.filter(it=>it.description.trim());
                          if(!items.length){showToast("Add at least one line item");return;}
                          const existing=bids.find(b=>b.partnerId===pid&&b.scopeId===sid&&b.status==="Active");
                          if(existing){
                            // Supersede old bid and archive the new one
                            setBids(prev=>prev.map(b=>b.id===existing.id?{...b,status:"Superseded" as const}:b));
                          }
                          const version=existing?(existing.version+1):1;
                          const newBid:Bid={id:uid(),partnerId:pid,scopeId:sid,version,date:bidImportDate||new Date().toISOString(),status:"Active",
                            items:items.map(it=>({...it,id:uid()})),pdfName:bidImportPdfName,notes:""};
                          setBids(p=>[...p,newBid]);setShowBidImport(false);setBidImportStage("idle");
                          // Cache PDF in memory and upload to cloud storage
                          if(pendingBidFileRef.current){
                            _revokeBlob(bidPdfCache.current.get(newBid.id));bidPdfCache.current.set(newBid.id,URL.createObjectURL(pendingBidFileRef.current));
                            uploadProposalToStorage(pendingBidFileRef.current,newBid);
                            pendingBidFileRef.current=null;
                          }
                          // Auto-match bid lines to estimate items (conservative —
                          // only unambiguous matches; drag remains the correction tool)
                          const scopeDef=bidScopes.find(s=>s.id===sid);
                          let matchCount=0;
                          if(scopeDef){
                            const estItems:{itemId:string;desc:string}[]=[];
                            (scopeDef.divisionIds||[]).forEach(dvId=>{
                              const div=divisions.find(d=>d.id===dvId);if(!div)return;
                              div.subcategories.forEach(sub=>{[...walkItems(sub.rows)].forEach(item=>{
                                const li=itemState[item.id];if(!li?.included)return;
                                estItems.push({itemId:item.id,desc:li.description||""});
                              });});
                            });
                            const matches=suggestAlignments(newBid.items,estItems);
                            matchCount=Object.keys(matches).length;
                            if(matchCount>0)setBidAlignments(prev=>({...prev,[sid]:{...(prev[sid]||{}),...matches}}));
                          }
                          showToast(matchCount>0
                            ?`Bid from ${pName(pid)||newBidPartnerName} saved — ${matchCount} of ${newBid.items.length} lines auto-matched to estimate`
                            :`Bid from ${pName(pid)||newBidPartnerName} saved`,4500);
                        }}
                          style={{padding:"6px 16px",fontSize:11,border:"none",borderRadius:3,background:T.borderAccent,color:T.textOnGold,cursor:"pointer",fontWeight:600,fontFamily:FONT}}>Save Bid</button>
                      </div>
                    </>
                  ):(
                    <div style={{marginLeft:"auto"}}>
                      <button onClick={()=>{setShowBidImport(false);setBidImportStage("idle");}}
                        style={{padding:"6px 14px",fontSize:11,border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bid embed modal removed — estimate items are the source of truth */}

          {/* ── PARTNERS MANAGEMENT PANEL ── */}
          {showPartnersPanel&&(
            <div style={{position:"fixed",inset:0,zIndex:8900,display:"flex",alignItems:"center",justifyContent:"center",background:"#00000088"}}
              onClick={e=>{if(e.target===e.currentTarget){setShowPartnersPanel(false);setEditingPartnerId(null);}}}>
              <div style={{width:560,maxHeight:"80vh",background:T.bgPanel,borderRadius:6,border:`1px solid ${T.borderPrimary}`,display:"flex",flexDirection:"column"as any,overflow:"hidden",boxShadow:"0 8px 40px #0008"}}>
                {/* Header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}><Icon.Users size={13}/> Trade Partner Roster</div>
                    <div style={{fontSize:10,color:T.borderAccent,marginTop:2}}>Org-wide · shared across all projects</div>
                  </div>
                  <button onClick={()=>{setShowPartnersPanel(false);setEditingPartnerId(null);}} style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:16,padding:0,lineHeight:1}}><Icon.X size={10}/></button>
                </div>
                {/* Migration banner — project-level partners not yet in org roster */}
                {(()=>{
                  const orphans=bidPartners.filter(bp=>!pickerPartners.some(op=>op.name.toLowerCase()===bp.name.toLowerCase()));
                  if(!orphans.length)return null;
                  return(
                    <div style={{padding:"10px 20px",background:`${T.borderAccent}15`,borderBottom:`1px solid ${T.borderAccent}40`,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                      <span style={{fontSize:11,color:T.borderAccent,flex:1}}>
                        {orphans.length} partner{orphans.length!==1?"s":""} in this project aren't in the org roster yet. Promote them so they're available on every project.
                      </span>
                      <button onClick={async()=>{
                        for(const bp of orphans){
                          const op:OrgPartner={id:uid(),name:bp.name,trade:bp.trade||"",contact:bp.contact||"",phone:bp.phone||"",email:bp.email||"",notes:bp.notes||""};
                          await addOrgPartner(op);
                        }
                        showToast(`${orphans.length} partner${orphans.length!==1?"s":""} added to org roster`);
                      }} style={{fontSize:10,padding:"4px 12px",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"as any}}>
                        Promote All
                      </button>
                    </div>
                  );
                })()}
                {/* Partner list — org-level */}
                <div style={{flex:1,overflowY:"auto",padding:"10px 0"}}>
                  {pickerPartners.length===0&&(
                    <div style={{padding:"28px 20px",textAlign:"center"as any}}>
                      <div style={{fontSize:24,marginBottom:8}}>🏗</div>
                      <div style={{fontSize:12,color:T.textMuted,fontWeight:600,marginBottom:4}}>No trade partners yet</div>
                      <div style={{fontSize:11,color:T.textWeak}}>Add your subs and GC partners below. They'll be available on every project.</div>
                    </div>
                  )}
                  {pickerPartners.map(p=>{
                    const pBids=bids.filter(b=>b.partnerId===p.id);
                    const isEditing=editingPartnerId===p.id;
                    return(
                      <div key={p.id} style={{borderBottom:`1px solid ${T.borderPrimary}`}}>
                        {isEditing?(
                          <div style={{padding:"12px 20px",background:T.bgAccentFaint}}>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Company Name *</div>
                                <input value={partnerEditDraft.name} onChange={e=>setPartnerEditDraft(d=>({...d,name:e.target.value}))}
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderAccent}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Trade / Division</div>
                                <input value={partnerEditDraft.trade} onChange={e=>setPartnerEditDraft(d=>({...d,trade:e.target.value}))}
                                  placeholder="e.g. Electrical, Concrete…"
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Contact Name</div>
                                <input value={partnerEditDraft.contact||""} onChange={e=>setPartnerEditDraft(d=>({...d,contact:e.target.value}))}
                                  placeholder="Contact person"
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Phone</div>
                                <input value={partnerEditDraft.phone||""} onChange={e=>setPartnerEditDraft(d=>({...d,phone:e.target.value}))}
                                  placeholder="(555) 000-0000"
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Email</div>
                                <input value={partnerEditDraft.email||""} onChange={e=>setPartnerEditDraft(d=>({...d,email:e.target.value}))}
                                  placeholder="email@company.com"
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,marginBottom:3,letterSpacing:".08em",textTransform:"uppercase"as any}}>Notes</div>
                                <input value={partnerEditDraft.notes||""} onChange={e=>setPartnerEditDraft(d=>({...d,notes:e.target.value}))}
                                  style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"5px 8px",fontFamily:FONT,boxSizing:"border-box"as any}}/>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button onClick={async()=>{
                                if(!partnerEditDraft.name.trim())return;
                                await updateOrgPartner({id:p.id,name:partnerEditDraft.name,trade:partnerEditDraft.trade||"",contact:partnerEditDraft.contact||"",phone:partnerEditDraft.phone||"",email:partnerEditDraft.email||"",notes:partnerEditDraft.notes||""});
                                setEditingPartnerId(null);
                                showToast("Partner updated");
                              }} style={{fontSize:11,padding:"5px 16px",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700}}>Save</button>
                              <button onClick={()=>setEditingPartnerId(null)} style={{fontSize:11,padding:"5px 12px",background:"none",color:T.textWeak,border:`1px solid ${T.borderSecondary}`,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
                              <button onClick={async()=>{
                                if(!window.confirm(`Remove "${p.name}" from the org roster? Existing bids referencing them will still display their name.`))return;
                                await deleteOrgPartner(p.id);
                                setEditingPartnerId(null);
                              }} style={{fontSize:11,padding:"5px 12px",background:"none",color:T.negative,border:`1px solid ${T.negative}44`,borderRadius:3,cursor:"pointer",fontFamily:FONT,marginLeft:"auto"}}>Remove</button>
                            </div>
                          </div>
                        ):(
                          <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 20px",transition:"background .15s"}}
                            onMouseEnter={e=>e.currentTarget.style.background=T.bgAccentFaint}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <div style={{width:36,height:36,borderRadius:999,background:`${T.borderAccent}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:T.borderAccent,flexShrink:0}}>
                              {p.name.slice(0,1).toUpperCase()}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{p.name}</div>
                              <div style={{fontSize:10,color:T.textWeak,marginTop:1}}>
                                {p.trade&&<span style={{marginRight:8}}><Icon.Folder size={10}/> {p.trade}</span>}
                                {p.contact&&<span style={{marginRight:8,color:T.textMuted}}><Icon.User size={10}/> {p.contact}</span>}
                              </div>
                              {(p.email||p.phone)&&(
                                <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                  {p.email&&<span style={{marginRight:8}}>✉ {p.email}</span>}
                                  {p.phone&&<span>📞 {p.phone}</span>}
                                </div>
                              )}
                            </div>
                            {pBids.length>0&&<div style={{fontSize:10,color:T.textMuted,flexShrink:0}}>{pBids.length} bid{pBids.length!==1?"s":""} here</div>}
                            <button onClick={()=>{setEditingPartnerId(p.id);setPartnerEditDraft({id:p.id,name:p.name,trade:p.trade||"",contact:(p as any).contact||"",phone:p.phone||"",email:p.email||"",notes:(p as any).notes||"",});}}
                              style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,cursor:"pointer",padding:"3px 10px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Edit</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Footer — add new org partner */}
                <div style={{borderTop:`1px solid ${T.borderPrimary}`,padding:"14px 20px",flexShrink:0}}>
                  <div style={{fontSize:10,color:T.textWeak,marginBottom:8,letterSpacing:".08em",textTransform:"uppercase"as any}}>Add to Org Roster</div>
                  <div style={{display:"flex",gap:8}}>
                    <input id="new-org-partner-name" placeholder="Company name…"
                      style={{flex:1,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"6px 10px",fontFamily:FONT}}/>
                    <input id="new-org-partner-trade" placeholder="Trade (optional)"
                      style={{width:120,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"6px 10px",fontFamily:FONT}}/>
                    <button onClick={async()=>{
                      const nameEl=document.getElementById("new-org-partner-name") as HTMLInputElement;
                      const tradeEl=document.getElementById("new-org-partner-trade") as HTMLInputElement;
                      const name=(nameEl?.value||"").trim();
                      if(!name)return;
                      await addOrgPartner({id:uid(),name,trade:tradeEl?.value||"",contact:"",phone:"",email:"",notes:""});
                      if(nameEl)nameEl.value="";if(tradeEl)tradeEl.value="";
                      showToast(`${name} added to org roster`);
                    }} style={{fontSize:11,padding:"6px 16px",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700,flexShrink:0}}>
                      + Add
                    </button>
                  </div>
                  <div style={{fontSize:10,color:T.textWeak,marginTop:8}}>Partners added here are available on <em>all</em> projects in your org.</div>
                </div>
              </div>
            </div>
          )}

          {/* ── PDF VIEWER MODAL ── full-screen, 1 or 2 panes side by side */}
          {pdfViewerOpen&&(
            <div style={{position:"fixed",inset:0,zIndex:9100,display:"flex",flexDirection:"column",background:T.bgApp,fontFamily:FONT}}>
              {/* Header */}
              <div style={{height:52,display:"flex",alignItems:"center",padding:"0 20px",background:T.bgHeader,borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0,gap:16}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                  <span style={{fontSize:13,fontWeight:700,color:T.textInverse}}><Icon.FileText size={13}/> Proposal Viewer</span>
                  <span style={{fontSize:10,color:`${T.textInverse}66`,letterSpacing:".08em"}}>
                    {pdfViewerItems.length===2?"Side-by-side comparison":"Single proposal view"}
                  </span>
                </div>
                {/* Swap / add pane: quick-add from all scope bids with PDFs */}
                {pdfViewerItems.length===1&&(()=>{
                  const currentBid=bids.find(b=>b.id===pdfViewerItems[0].bidId);
                  const otherBidsWithPdf=bids.filter(b=>b.id!==pdfViewerItems[0].bidId&&b.scopeId===currentBid?.scopeId&&(b.pdfUrl||b.pdfStoragePath||bidPdfCache.current.has(b.id)));
                  if(otherBidsWithPdf.length===0)return null;
                  return(
                    <select defaultValue="" onChange={async e=>{
                        const otherId=e.target.value;e.target.value="";
                        if(!otherId)return;
                        const other=bids.find(b=>b.id===otherId);if(!other)return;
                        const op=findPartner(other.partnerId);
                        const url=await resolveBidPdfUrl(other);
                        if(!url){showToast("PDF not available for that bid");return;}
                        setPdfViewerItems(prev=>[...prev,{bidId:other.id,url,title:`${op?.name||"?"} · v${other.version} · ${other.date.slice(0,10)}`}]);
                      }}
                      style={{fontSize:10,padding:"4px 8px",background:T.bgPanel,border:`1px solid ${T.borderAccent}55`,color:T.textInverse,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
                      <option value="" disabled>⇔ Compare with…</option>
                      {otherBidsWithPdf.map(b=>{
                        const p=findPartner(b.partnerId);
                        return <option key={b.id} value={b.id}>{p?.name||"?"} · v{b.version} · {b.date.slice(0,10)}</option>;
                      })}
                    </select>
                  );
                })()}
                {pdfViewerItems.length===2&&(
                  <button onClick={()=>setPdfViewerItems([pdfViewerItems[0]])}
                    style={{fontSize:10,padding:"4px 10px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textInverse,borderRadius:3,cursor:"pointer",fontFamily:FONT,opacity:.7}}>
                    Single view
                  </button>
                )}
                <button onClick={()=>{setPdfViewerOpen(false);setComparePdfIds([]);}}
                  style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textInverse,cursor:"pointer",fontSize:16,opacity:.8,flexShrink:0}}>
                  <Icon.X size={10}/>
                </button>
              </div>
              {/* PDF pane(s) */}
              <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                {pdfViewerItems.map(({bidId,url,title},i)=>{
                  const bid=bids.find(b=>b.id===bidId);
                  const partner=bid&&findPartner(bid.partnerId);
                  const isLow=pdfViewerItems.length===2&&bid&&(()=>{
                    const myTotal=bidTotal(bid);
                    const other=bids.find(b=>b.id===pdfViewerItems[1-i]?.bidId);
                    return other&&myTotal<bidTotal(other);
                  })();
                  return(
                    <div key={bidId} style={{flex:1,display:"flex",flexDirection:"column",borderRight:i<pdfViewerItems.length-1?`1px solid ${T.borderPrimary}`:"none",minWidth:0}}>
                      {/* Pane header */}
                      <div style={{flexShrink:0,padding:"10px 16px",background:T.bgPanel,borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"center",gap:10}}>
                        {pdfViewerItems.length===2&&(
                          <span style={{fontSize:11,fontWeight:700,color:T.textWeak,width:20,flexShrink:0}}>{i===0?"A":"B"}</span>
                        )}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{title}</div>
                          {bid&&<div style={{fontSize:10,color:T.textWeak,marginTop:1}}>{fmt(bidTotal(bid))} total · {bid.items?.length||0} line items</div>}
                        </div>
                        {isLow&&<span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:`${T.positive}22`,color:T.positive,fontWeight:700,flexShrink:0}}>LOW BID</span>}
                        {/* Swap this pane to a different bid */}
                        {pdfViewerItems.length===2&&(()=>{
                          const thisBid=bids.find(b=>b.id===bidId);
                          const othersWithPdf=bids.filter(b=>b.id!==bidId&&b.scopeId===thisBid?.scopeId&&(b.pdfUrl||b.pdfStoragePath||bidPdfCache.current.has(b.id)));
                          if(!othersWithPdf.length)return null;
                          return(
                            <select defaultValue="" onChange={async e=>{
                                const newId=e.target.value;e.target.value="";if(!newId)return;
                                const nb=bids.find(b=>b.id===newId);if(!nb)return;
                                const np=findPartner(nb.partnerId);
                                const url=await resolveBidPdfUrl(nb);
                                if(!url){showToast("PDF not available");return;}
                                const newItem={bidId:newId,url,title:`${np?.name||"?"} · v${nb.version} · ${nb.date.slice(0,10)}`};
                                setPdfViewerItems(prev=>prev.map((x,idx)=>idx===i?newItem:x));
                              }}
                              style={{fontSize:10,padding:"2px 4px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>
                              <option value="" disabled>Swap…</option>
                              {othersWithPdf.map(b=>{
                                const p=findPartner(b.partnerId);
                                return <option key={b.id} value={b.id}>{p?.name||"?"} v{b.version}</option>;
                              })}
                            </select>
                          );
                        })()}
                      </div>
                      {/* PDF renderer */}
                      <div style={{flex:1,overflow:"hidden"}}>
                        <PdfPane url={url}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── SCOPE OF WORK ── */}
          {activeTab==="scope"&&(
            <ErrorBoundary tabName="Scope">
            <ScopeTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            divisions={divisions} divTotals={divTotals}
            handleGenerateScope={handleGenerateScope} handleDownloadScope={handleDownloadScope}
            scopeLoading={scopeLoading} scopeData={scopeData}
            scopeExclusions={scopeExclusions} setScopeExclusions={setScopeExclusions}
            projectName={projectName} address={address} buildingArea={buildingArea}
            final={final} fmtSF={fmtSF} fmt={fmt}
            bidPartners={bidPartners} projectExclusions={projectExclusions}
            />
            </ErrorBoundary>
          )}

          {/* ── PROPERTIES ── */}
          {/* ── PROPERTIES ── */}
          {(activeTab==="properties"||activeTab==="properties-defaults")&&(
            <ErrorBoundary tabName="Properties">
            <PropertiesTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            projectName={projectName} setProjectName={setProjectName}
            address={address} setAddress={setAddress}
            buildingArea={buildingArea} setBuildingArea={setBuildingArea}
            duration={duration} setDuration={setDuration}
            overhead={overhead} setOverhead={auditedSetOverhead}
            profit={profit} setProfit={auditedSetProfit}
            contingency={contingency} setContingency={auditedSetContingency}
            contingencyPolicy={contingencyPolicy} setContingencyPolicy={setContingencyPolicy} final={final}
            escalation={escalation} setEscalation={setEscalation} escalationResult={escLive}
            markupMode={markupMode} setMarkupMode={setMarkupMode}
            proposals={proposals} setProposals={setProposals}
            proposalAreas={proposalAreas} setProposalAreas={setProposalAreas}
            divisions={divisions} setDivisionsRaw={setDivisionsRaw}
            divisionSettings={divisionSettings} setDivisionSettings={setDivisionSettings}
            drawingList={drawingList} setDrawingList={setDrawingList}
            propSubTab={activeTab==="properties-defaults"?"settings":"info"}
            setPropSubTab={(v:any)=>{const t=v==="settings"?"properties-defaults":"properties";setActiveTab(t);window.history.pushState({oeTab:t},"",window.location.hash);}}
            geoQuery={geoQuery} setGeoQuery={setGeoQuery}
            jurisdictionInfo={jurisdictionInfo} setJurisdictionInfo={setJurisdictionInfo}
            divisionMarkups={divisionMarkups} setDivisionMarkups={setDivisionMarkups}
            divMarkupExpanded={divMarkupExpanded} setDivMarkupExpanded={setDivMarkupExpanded}
            orgPartners={orgPartners} setOrgPartners={setOrgPartners}
            orgPartnerSearch={orgPartnerSearch} setOrgPartnerSearch={setOrgPartnerSearch}
            showOrgPartnerImport={showOrgPartnerImport} setShowOrgPartnerImport={setShowOrgPartnerImport}
            orgPartnerImportStep={orgPartnerImportStep} setOrgPartnerImportStep={setOrgPartnerImportStep}
            orgPartnerImportRows={orgPartnerImportRows} setOrgPartnerImportRows={setOrgPartnerImportRows}
            orgPartnerImportError={orgPartnerImportError} setOrgPartnerImportError={setOrgPartnerImportError}
            isLocked={isLocked} sumPropAreas={sumPropAreas} getPC={getPC}
            divTotals={divTotals} divMarkupableSubtotals={divMarkupableSubtotals}
            projectExclusions={projectExclusions} subSettings={subSettings}
            snapshot={snapshot} addSub={addSub} removeSub={removeSub}
            renameSub={renameSub} updateSubCostCode={updateSubCostCode}
            orgCostCodes={orgCostCodes}
            setActiveTab={setActiveTab}
            fmt={fmt} fmtDec={fmtDec} costSFCents={costSFCents}
            user={user} cloudStorage={cloudStorage} showToast={showToast} uid={uid}
            />
            </ErrorBoundary>
          )}

          {/* ── DRAWING REVIEW / INTAKE ── */}
          {/* ── DECISIONS ── */}
          {activeTab==="decisions"&&(()=>{
            // Most recent saved snapshot, for the "since we last met" line.
            const snaps=[...(projectSnapshots||[])].filter((s:any)=>s.date||s.createdAt)
              .sort((a:any,b:any)=>((a.date||a.createdAt) < (b.date||b.createdAt) ? 1 : -1));
            const last=snaps[0];
            // summarizeState returns a per-cost-code map; sum it. Both sides use
            // the same raw line-item basis (no markup), so the delta compares like
            // with like.
            const sumSummary=(m:Record<string,{total:number}>)=>Object.values(m||{}).reduce((s,v)=>s+(v.total||0),0);
            const lastSnapshot=last
              ? {label:last.label||"last snapshot",date:(last.date||last.createdAt||"").slice(0,10),total:sumSummary(summarizeState(last.state))}
              : null;
            // What changed about *when*, not just how much. Snapshots carry the
            // schedule only from 140fc4a on; older ones are reported as not
            // comparable rather than as "nothing moved".
            const scheduleDelta=last
              ? summarizeScheduleDelta(last.state||{},{duration,schedule,costSchedule,divisions,itemState})
              : null;
            return (
              <ErrorBoundary tabName="Decisions">
                <DecisionsTab
                  T={T} isMobile={isMobile} isTablet={isTablet}
                  divisions={divisions} itemState={itemState}
                  altTotals={altTotals} rfis={rfis}
                  projectName={projectName} fmt={fmt}
                  navigateToItem={navigateToItem} setActiveTab={setActiveTab}
                  navigateToRfi={entitlements.canUseRfi?(id:string)=>{
                    // Clear the filters on the way. The RFI tab remembers the
                    // last search and status filter, so arriving from Decisions
                    // with "closed" still selected lands you on a tab that does
                    // not contain the row you clicked -- the link appears to do
                    // nothing, which is worse than not linking.
                    setRfiSearch("");
                    setRfiStatusFilter("all");
                    setActiveRfiId(id);
                    navTo("rfi");
                  }:undefined}
                  onPrint={()=>{setPrintInitialSheets(["decisions"]);setShowPrintDialog(true);}}
                  lastSnapshot={lastSnapshot} currentTotal={sumSummary(currentSummary)}
                  scheduleDelta={scheduleDelta}
                />
              </ErrorBoundary>
            );
          })()}

          {/* ── SUBMITTALS ── */}
          {activeTab==="submittals"&&(
            <ErrorBoundary tabName="Submittals">
            <SubmittalsTab
            T={T} divisions={divisions}
            submittals={submittals} setSubmittals={setSubmittals}
            onExportProcore={cloudProjectId&&!isSandbox?()=>setProcoreExportKind("submittal"):undefined}
            submittalTemplates={submittalTemplates} setSubmittalTemplates={setSubmittalTemplates}
            subDraft={subDraft} setSubDraft={setSubDraft}
            subEditId={subEditId} setSubEditId={setSubEditId}
            subFilterStatus={subFilterStatus} setSubFilterStatus={setSubFilterStatus}
            subFilterDiv={subFilterDiv} setSubFilterDiv={setSubFilterDiv}
            subTemplateEditorExpanded={subTemplateEditorExpanded} setSubTemplateEditorExpanded={setSubTemplateEditorExpanded}
            subTemplateExpandedDiv={subTemplateExpandedDiv} setSubTemplateExpandedDiv={setSubTemplateExpandedDiv}
            setSubAutoPopDone={setSubAutoPopDone}
            submittalsSnapshot={submittalsSnapshot} showToast={showToast}
            subTemplateImportRef={subTemplateImportRef}
            />
            </ErrorBoundary>
          )}

          {/* ── RFI LOG ── */}
          {activeTab==="rfi"&&(
            <ErrorBoundary tabName="RFI">
            <RfiTab
            T={T} rfis={rfis} setRfis={setRfis}
            rfiSearch={rfiSearch} setRfiSearch={setRfiSearch}
            rfiStatusFilter={rfiStatusFilter} setRfiStatusFilter={setRfiStatusFilter}
            activeRfiId={activeRfiId} setActiveRfiId={setActiveRfiId}
            rfiItemPickerFor={rfiItemPickerFor} setRfiItemPickerFor={setRfiItemPickerFor}
            rfiItemSearch={rfiItemSearch} setRfiItemSearch={setRfiItemSearch}
            rfiAssigneeOptions={rfiAssigneeOptions} setRfiJumpHighlight={setRfiJumpHighlight}
            divisions={divisions} itemState={itemState}
            snapshot={snapshot} uid={uid} fmt={fmt} setActiveTab={setActiveTab}
            onExportProcore={cloudProjectId&&!isSandbox?()=>setProcoreExportKind("rfi"):undefined}
            />
            </ErrorBoundary>
          )}

          {/* ── BUYOUT LOG ── */}
          {activeTab==="buyout"&&(
            <ErrorBoundary tabName="Buyout">
            <BuyoutTab
            T={T} buyoutItems={buyoutItems} setBuyoutItems={setBuyoutItems}
            editingBuyoutCell={editingBuyoutCell} setEditingBuyoutCell={setEditingBuyoutCell}
            divisions={divisions} bids={bids} bidScopes={bidScopes} bidPartners={bidPartners}
            itemState={itemState} snapshot={snapshot} uid={uid} showToast={showToast}
            fmt={fmt} setShowSowEditor={setShowSowEditor}
            />
            </ErrorBoundary>
          )}

          {/* ── SITE LOGISTICS ── */}
          {/* ── SITE LOGISTICS ── */}
          {BUDGET_ENABLED&&activeTab==="budget"&&(
            <ErrorBoundary tabName="Budget">
            <BudgetView
              projectId={cloudProjectId||"local"}
              projectName={projectName}
              darkMode={isDarkMode}
              embedded
              onClose={()=>setActiveTab("estimate")}
            />
            </ErrorBoundary>
          )}

          {activeTab==="logistics"&&(
            <ErrorBoundary tabName="Logistics">
            <LogisticsTab
            T={T}
            siteLogistics={siteLogistics} setSiteLogistics={setSiteLogistics}
            logisticsTool={logisticsTool} setLogisticsTool={setLogisticsTool}
            selectedLogId={selectedLogId} setSelectedLogId={setSelectedLogId}
            lineInProgress={lineInProgress} setLineInProgress={setLineInProgress}
            linePreview={linePreview} setLinePreview={setLinePreview}
            logSvgRef={logSvgRef} logDragRef={logDragRef} logVertexDragRef={logVertexDragRef}
            logResizeRef={logResizeRef}
            logRotateRef={logRotateRef} logLabelRef={logLabelRef} showToast={showToast}
            areaInProgress={areaInProgress} setAreaInProgress={setAreaInProgress}
            areaPreview={areaPreview} setAreaPreview={setAreaPreview}
            circleCenter={circleCenter} setCircleCenter={setCircleCenter}
            circlePreviewR={circlePreviewR} setCirclePreviewR={setCirclePreviewR}
            calibPoints={calibPoints} setCalibPoints={setCalibPoints}
            calibInput={calibInput} setCalibInput={setCalibInput}
            logDrawStyle={logDrawStyle} setLogDrawStyle={setLogDrawStyle}
            logPanelCollapsed={logPanelCollapsed} setLogPanelCollapsed={setLogPanelCollapsed}
            logSvgDims={logSvgDims}
            logisticsView={logisticsView} setLogisticsView={setLogisticsView}
            logisticsSnapshot={logisticsSnapshot} uid={uid}
            address={address} geoQuery={geoQuery} mapLocLabel={mapLocLabel} setMapLocLabel={setMapLocLabel}
            mapViewMode={mapViewMode} setMapViewMode={setMapViewMode}
            mapLocked={mapLocked} setMapLocked={setMapLocked}
            schedule={{projectStartDate:schedule.projectStartDate,phases:schedule.phases}}
            projectName={projectName}
            />
            </ErrorBoundary>
          )}

          {/* ── SCHEDULE ── */}
          {/* ── SCHEDULE ── */}
          {activeTab==="schedule"&&(
            <ErrorBoundary tabName="Schedule">
            <ScheduleTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            onOpenCashflow={entitlements.canUseCashflow?()=>{setActiveTab("cashflow");window.history.pushState({oeTab:"cashflow"},"",window.location.hash);}:undefined}
            duration={duration} setDuration={setDuration}
            divisions={divisions} itemState={itemState}
            subTotals={subTotals} final={final} fmt={fmt}
            schedule={schedule} setSchedule={setSchedule}
            uid={uid} setActiveTab={setActiveTab} showToast={showToast}
            scheduleSnapshot={scheduleSnapshot}
            costSchedule={costSchedule} setCostSchedule={setCostSchedule}
            proposals={proposals}
            phaseGroups={phaseGroups}
            setSubPhase={setSubPhase}
            />
            </ErrorBoundary>
          )}

          {/* ── CASH FLOW ── */}
          {activeTab==="cashflow"&&(
            <ErrorBoundary tabName="Cash Flow">
            <CashflowTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            schedule={schedule} divisions={divisions} subTotals={subTotals}
            final={final} fmt={fmt} setActiveTab={setActiveTab}
            cfDetail={cfDetail} setCfDetail={setCfDetail} setSchedDetail={setSchedDetail}
            costSchedule={costSchedule} duration={duration}
            overhead={overhead} profit={profit} contingency={contingency}
            markupMode={markupMode} divisionMarkups={divisionMarkups}
            proposals={proposals} itemState={itemState}
            phaseGroups={phaseGroups}
            />
            </ErrorBoundary>
          )}

          {/* Budget tab removed — now a standalone view (components/BudgetView.tsx) accessed from ProjectPicker. */}

          {/* ── HISTORY ── */}
          {/* ── HISTORY ── */}
          {activeTab==="history"&&(
            <ErrorBoundary tabName="History">
            <HistoryTab
            T={T} isMobile={isMobile} isTablet={isTablet}
            projectSnapshots={projectSnapshots} deletedSnapshots={deletedSnapshots} setDeletedSnapshots={setDeletedSnapshots}
            summarizeState={summarizeState} currentSummary={currentSummary} analysisOptions={analysisOptions}
            analysisLeft={analysisLeft} setAnalysisLeft={setAnalysisLeft}
            analysisRight={analysisRight} setAnalysisRight={setAnalysisRight}
            snapPreviewId={snapPreviewId} setSnapPreviewId={setSnapPreviewId}
            renamingSnapId={renamingSnapId} setRenamingSnapId={setRenamingSnapId}
            renameSnapLabel={renameSnapLabel} setRenameSnapLabel={setRenameSnapLabel}
            renameSnapDate={renameSnapDate} setRenameSnapDate={setRenameSnapDate}
            deltaPopupId={deltaPopupId} setDeltaPopupId={setDeltaPopupId}
            deltaPopupPos={deltaPopupPos} setDeltaPopupPos={setDeltaPopupPos}
            showToast={showToast} setShowSnapDialog={setShowSnapDialog}
            deleteSnapshot={deleteSnapshot} renameSnapshot={renameSnapshot}
            restoreSnapshot={restoreSnapshot} handleOpenArchive={handleOpenArchive}
            getVersionSummary={getVersionSummary} getVersionTotal={getVersionTotal}
            getVersionLabel={getVersionLabel}
            projectOwnerId={projectOwnerId} user={user} fmt={fmt}
            collapsedAnalysisDivs={collapsedAnalysisDivs} setCollapsedAnalysisDivs={setCollapsedAnalysisDivs}
            historyNotes={historyNotes} projectName={projectName} importVersion={importVersion} currentOrgId={currentOrgId} setHistoryNotes={setHistoryNotes}
            divisions={divisions}
            showDeletedSnaps={showDeletedSnaps} setShowDeletedSnaps={setShowDeletedSnaps}
            selectedArchivedIds={selectedArchivedIds} setSelectedArchivedIds={setSelectedArchivedIds}
            setConfirmAction={setConfirmAction} setConfirmInput={setConfirmInput}
            cloudStorage={cloudStorage} setLineItemPopup={setLineItemPopup}
            />
            </ErrorBoundary>
          )}
        </div>
      </div>

      {/* SELECTION STATUS BAR */}
      {selStatus&&(()=>{
        const asNumber=selStatus.field==="qty";
        const show=(n:number)=>asNumber?n.toLocaleString("en-US",{maximumFractionDigits:2}):fmt(n);
        return <SelectionStatus T={T} count={selStatus.count}
          valueLabel={asNumber?"Sum":"Total"} value={show(selStatus.sum)}
          average={selStatus.average==null?null:show(selStatus.average)}/>;
      })()}

      {/* PCT PICKER MODAL */}
      {pickingPctForId&&(()=>{
        const pctLi=itemState[pickingPctForId]||{};
        const pctRate=pctLi.qty||0;
        const selSet=new Set(pickerSel);
        const baseTotal=pickerSel.reduce((sum,id)=>{const t=itemState[id]||{};return t.unit==="%"?sum:sum+(t.qty||0)*(t.unitCost||0);},0);
        const pctTotal=baseTotal*(pctRate/100);
        // Find which div/sub contains the % line item, then only show peers from that same subcategory
        const groups=[];
        divisions.forEach(div=>div.subcategories.forEach(sub=>{
          const allInSub=[...walkItems(sub.rows)];
          if(!allInSub.some(r=>r.id===pickingPctForId))return; // skip other subcategories
          const items=allInSub.filter(r=>{
            const li=itemState[r.id]||{};
            return li.included&&!li.altType&&li.unit!=="%"&&r.id!==pickingPctForId;
          });
          if(items.length)groups.push({divName:div.name,subName:sub.name,items});
        }));
        return(
          <div className="modal-backdrop" onClick={()=>setPickingPctForId(null)}>
            <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column",padding:0,overflow:"hidden"}}>
              <div style={{padding:"20px 24px 14px",borderBottom:`1px solid ${T.borderSecondary}`}}>
                <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:4}}>
                  <span style={{fontSize:14,fontWeight:600,color:T.borderAccent}}>Select Base for % Calculation</span>
                  <span style={{fontSize:11,color:T.textWeak}}>— rate:</span>
                  <input type="number" value={pctRate} min={0} step={0.1}
                    onChange={e=>upd(pickingPctForId,"qty",parseFloat(e.target.value)||0)}
                    onClick={e=>e.stopPropagation()}
                    style={{width:58,fontSize:13,fontWeight:600,color:T.borderAccent,background:T.bgInput,border:`1px solid ${T.borderAccent}55`,borderRadius:3,padding:"2px 4px",textAlign:"right",fontFamily:FONT,outline:"none"}}/>
                  <span style={{fontSize:13,color:T.borderAccent,fontWeight:600}}>%</span>
                </div>
                <div style={{fontSize:11,color:T.textWeak}}>Check the items whose totals will serve as the base for this rate.</div>
              </div>
              <div style={{flex:1,overflow:"auto",background:T.bgPanel}}>
                {groups.length===0&&<div style={{padding:20,color:T.textWeak,fontSize:11,textAlign:"center"}}>No eligible items in this subcategory.</div>}
                {groups.map((g,gi)=>(
                  <div key={gi}>
                    <div style={{padding:"6px 16px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",background:T.bgApp,borderBottom:`1px solid ${T.bgInput}`}}>
                      {g.divName} / {g.subName}
                    </div>
                    {g.items.map(r=>{
                      const li=itemState[r.id]||{};
                      const t=(li.qty||0)*(li.unitCost||0);
                      const sel=selSet.has(r.id);
                      return(
                        <div key={r.id} onClick={()=>setPickerSel(p=>sel?p.filter(id=>id!==r.id):[...p,r.id])}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"7px 16px",cursor:"pointer",
                            background:sel?T.bgAccentFaint:"transparent",borderBottom:`1px solid ${T.bgInput}`,transition:"background .1s"}}>
                          <input type="checkbox" checked={sel} readOnly style={{cursor:"pointer",accentColor:T.borderAccent}}/>
                          <span style={{flex:1,fontSize:12,color:sel?T.costStatus["Needs Review"].badgeText:T.textTertiary}}>{li.description||"—"}</span>
                          <span style={{fontSize:11,color:T.textWeak,fontVariantNumeric:"tabular-nums"}}>{t!==0?fmt(t):"--"}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={{padding:"14px 24px",borderTop:`1px solid ${T.borderSecondary}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bgInput}}>
                <div style={{fontSize:12,color:T.textWeak}}>
                  Base: <span style={{color:T.textTertiary,fontWeight:500}}>{fmt(baseTotal)}</span>
                  <span style={{margin:"0 6px",color:T.borderSecondary}}>×</span>
                  <span style={{color:T.textTertiary}}>{pctRate}%</span>
                  <span style={{margin:"0 6px",color:T.borderSecondary}}>=</span>
                  <span style={{color:T.borderAccent,fontWeight:600}}>{fmt(pctTotal)}</span>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPickingPctForId(null)}
                    style={{padding:"6px 14px",fontSize:12,border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>
                    Cancel
                  </button>
                  <button onClick={()=>{upd(pickingPctForId,"pctTargetIds",pickerSel);setPickingPctForId(null);}}
                    style={{padding:"6px 16px",fontSize:12,border:"none",borderRadius:3,background:T.borderAccent,color:T.textOnGold,cursor:"pointer",fontWeight:600,fontFamily:FONT}}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* OPEN ITEMS PANEL */}
      {/* ── Allowance → Hard Bid Panel ── */}
      {showAllowancePanel&&(()=>{
        const FONT_P="'Inter','Avenir Next','Century Gothic',sans-serif";
        const allowanceRows=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].filter(item=>{
          const li=itemState[item.id]||{};
          return li.included!==false&&!li.altType&&li.costStatus==="Allowance";
        }).map(item=>{
          const li=itemState[item.id]||{};
          return{id:item.id,desc:li.description||item.description,qty:li.qty||0,unit:li.unit||"LS",unitCost:li.unitCost||0,total:(li.qty||0)*(li.unitCost||0),divName:div.name,subName:sub.name};
        })));
        const totalAllowance=allowanceRows.reduce((s,r)=>s+r.total,0);
        return (
          <>
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.25)",zIndex:1099}} onClick={()=>setShowAllowancePanel(false)}/>
            <div style={{
              position:"fixed",top:0,right:0,bottom:0,width:520,maxWidth:"100vw",
              background:T.bgPanel,borderLeft:`1px solid ${T.borderPrimary}`,
              boxShadow:"-8px 0 32px rgba(0,0,0,0.28)",display:"flex",flexDirection:"column" as any,
              zIndex:1100,fontFamily:FONT_P,
            }}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",padding:"14px 18px",borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0,gap:10}}>
                <span style={{fontSize:16}}>💲</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.textSecondary,fontFamily:FONT_P}}>Allowances to Price</div>
                  <div style={{fontSize:10,color:T.textWeak,marginTop:1}}>{allowanceRows.length} item{allowanceRows.length!==1?"s":""} · {fmt(totalAllowance)} in allowances</div>
                </div>
                <button onClick={()=>setShowAllowancePanel(false)}
                  style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontSize:16,padding:"2px 4px",lineHeight:1}}>×</button>
              </div>
              {/* Body */}
              <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
                {allowanceRows.length===0?(
                  <div style={{padding:"40px 24px",textAlign:"center",fontFamily:FONT_P,fontSize:12,color:T.textWeak,lineHeight:1.8}}>
                    No allowance items in the estimate.<br/>
                    Mark items as <strong>Allowance</strong> in the estimate to track them here.
                  </div>
                ):allowanceRows.map(row=>(
                  <div key={row.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 18px",borderBottom:`1px solid ${T.borderSecondary}`}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,color:T.textSecondary,fontFamily:FONT_P,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>{row.desc}</div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{row.divName} · {row.subName}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#5aaad4",fontFamily:FONT_P}}>{fmt(row.total)}</div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{row.qty} {row.unit} × {fmt(row.unitCost)}</div>
                    </div>
                    <button
                      onClick={()=>{
                        snapshot();
                        setItemState(prev=>({...prev,[row.id]:{...prev[row.id],costStatus:"Hard Bid"}}));
                        showToast("Converted to Hard Bid");
                      }}
                      disabled={isLocked}
                      style={{
                        fontFamily:FONT_P,fontSize:11,fontWeight:700,padding:"6px 12px",
                        background:T.positive+"22",border:`1px solid ${T.positive}55`,
                        color:T.positive,borderRadius:6,cursor:isLocked?"not-allowed":"pointer",
                        flexShrink:0,whiteSpace:"nowrap" as any,
                      }}>
                      ✓ Hard Bid
                    </button>
                    <button
                      onClick={()=>{setShowAllowancePanel(false);navigateToItem(row.id);}}
                      style={{
                        fontFamily:FONT_P,fontSize:11,padding:"6px 10px",
                        background:"none",border:`1px solid ${T.borderSecondary}`,
                        color:T.textWeak,borderRadius:6,cursor:"pointer",
                        flexShrink:0,
                      }}
                      title="Edit in estimate">
                      Edit
                    </button>
                  </div>
                ))}
              </div>
              {/* Footer */}
              {allowanceRows.length>0&&!isLocked&&(
                <div style={{borderTop:`1px solid ${T.borderPrimary}`,padding:"12px 18px",flexShrink:0}}>
                  <button
                    onClick={()=>{
                      snapshot();
                      const ids=allowanceRows.map(r=>r.id);
                      setItemState(prev=>{
                        const next={...prev};
                        ids.forEach(id=>{next[id]={...next[id],costStatus:"Hard Bid"};});
                        return next;
                      });
                      showToast(`${ids.length} items converted to Hard Bid`);
                      setShowAllowancePanel(false);
                    }}
                    style={{
                      width:"100%",fontFamily:FONT_P,fontSize:12,fontWeight:700,padding:"10px",
                      background:T.positive,border:"none",color:"#fff",borderRadius:6,cursor:"pointer",
                    }}>
                    Convert All to Hard Bid
                  </button>
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* CONFIRMATION MODAL */}
      {confirmAction&&(
        <div className="modal-backdrop" onClick={()=>{setConfirmAction(null);setConfirmInput("");}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
            <div style={{fontSize:16,fontWeight:600,color:confirmAction.title.includes("Delete")?T.negative:T.borderAccent,marginBottom:10}}>{confirmAction.title}</div>
            <div style={{fontSize:13,color:T.textTertiary,lineHeight:1.6}}>{confirmAction.message}</div>
            {confirmAction.inputRequired&&(
              <input className="confirm-input" value={confirmInput} onChange={e=>setConfirmInput(e.target.value)}
                placeholder={`Type "${confirmAction.inputRequired}" to confirm`} autoFocus/>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
              <button onClick={()=>{setConfirmAction(null);setConfirmInput("");}}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textTertiary,cursor:"pointer",padding:"8px 16px",borderRadius:3,fontSize:12,fontFamily:"inherit"}}>
                Cancel
              </button>
              <button className="danger-btn"
                disabled={confirmAction.inputRequired&&confirmInput!==confirmAction.inputRequired}
                onClick={confirmAction.onConfirm}>
                {confirmAction.confirmText||"Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Budget Picker Modal ── */}
      {showBudgetPicker&&(()=>{
        let budgets:CBudget[]=[];
        try{const raw=sGetRaw(CB_KEY);budgets=raw?JSON.parse(raw):[];}catch{}
        return(
          <div style={{position:"fixed",inset:0,background:T.bgModalBackdrop,zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div style={{background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,width:"100%",maxWidth:420,maxHeight:"60vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>Link Conceptual Budget</div>
                <button onClick={()=>setShowBudgetPicker(false)} style={{background:"none",border:"none",color:T.textWeak,fontSize:16,cursor:"pointer",lineHeight:1}}><Icon.X size={10}/></button>
              </div>
              <div style={{padding:"12px 20px",overflowY:"auto",flex:1}}>
                {budgets.length===0&&<div style={{fontSize:11,color:T.textMuted,padding:20,textAlign:"center"}}>No conceptual budgets found. Create one from the home screen.</div>}
                {budgets.map(b=>{
                  const groups=b.areaGroups||[];const siteItems=b.siteItems||[];
                  let low=0,high=0;
                  groups.forEach(g=>{const sf=g.subAreas.reduce((s,a)=>s+(a.sf||0),0);low+=sf*(g.lowRate||0);high+=sf*(g.highRate||0);});
                  siteItems.forEach(si=>{low+=(si.low||0);high+=(si.high||0);});
                  const selected=linkedBudgetId===b.id;
                  return(
                    <div key={b.id} onClick={()=>{setLinkedBudgetId(selected?null:b.id);setShowBudgetPicker(false);}}
                      style={{padding:"10px 14px",borderRadius:6,border:`1px solid ${selected?T.borderAccent:T.borderSecondary}`,
                        background:selected?T.bgAccentLight:T.bgInput,marginBottom:8,cursor:"pointer"}}>
                      <div style={{fontSize:12,fontWeight:600,color:selected?T.borderAccent:T.textPrimary}}>{b.name}</div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>
                        {low>0||high>0?`${fmt(low)} – ${fmt(high)}`:"No data"} · {new Date(b.savedAt).toLocaleDateString()}
                      </div>
                    </div>
                  );
                })}
              </div>
              {linkedBudgetId&&<div style={{padding:"10px 20px",borderTop:`1px solid ${T.borderPrimary}`}}>
                <button onClick={()=>{setLinkedBudgetId(null);setShowBudgetPicker(false);}}
                  style={{fontSize:10,padding:"4px 12px",borderRadius:3,border:`1px solid ${T.borderSecondary}`,background:"transparent",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>
                  Unlink Budget
                </button>
              </div>}
            </div>
          </div>
        );
      })()}

      {/* ── Row Context Menu ── */}
      {ctxMenu&&(
        <>
          <div style={{position:"fixed",inset:0,zIndex:1999}} onClick={()=>{setCtxMenu(null);setCtxShowMoveSub(false);}} onContextMenu={e=>{e.preventDefault();setCtxMenu(null);setCtxShowMoveSub(false);}}/>
          <div style={{position:"fixed",left:ctxMenu.x,top:Math.min(ctxMenu.y,window.innerHeight-200),
            background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
            boxShadow:"0 4px 24px #0009",zIndex:2000,minWidth:168,padding:"4px 0",fontFamily:FONT}}>
            <div className="file-menu-item" onClick={()=>{setCtxMenu(null);handleCopy();}}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>⎘</span> Copy
              <span style={{marginLeft:"auto",fontSize:10,opacity:.4}}>Ctrl+C</span>
            </div>
            <div className="file-menu-item" onClick={()=>{setCtxMenu(null);handleCut();}}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>✂</span> Cut
              <span style={{marginLeft:"auto",fontSize:10,opacity:.4}}>Ctrl+X</span>
            </div>
            <div className="file-menu-item" onClick={()=>{setCtxMenu(null);if(clipboardRef.current)handlePaste();else showToast("Nothing to paste — copy or cut a row first");}}
              style={{opacity:clipboardRef.current?1:0.45}}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>⎗</span> Paste below
              <span style={{marginLeft:"auto",fontSize:10,opacity:.4}}>Ctrl+V</span>
            </div>
            <div className="file-menu-item" onClick={()=>{setCtxMenu(null);handleDuplicate(ctxMenu.itemId);}}>
              <span style={{width:18,display:"inline-block",opacity:.7}}><Icon.Duplicate size={12}/></span> Duplicate
              <span style={{marginLeft:"auto",fontSize:10,opacity:.4}}>Ctrl+D</span>
            </div>
            <div className="file-menu-item" onClick={()=>{
              const li=itemState[ctxMenu.itemId]||{};
              snapshot();setItemState(p=>({...p,[ctxMenu.itemId]:{...p[ctxMenu.itemId],excludeFromMarkup:!li.excludeFromMarkup}}));
              setCtxMenu(null);
            }}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>{(itemState[ctxMenu.itemId]||{}).excludeFromMarkup?"✓":"⊘"}</span>
              {(itemState[ctxMenu.itemId]||{}).excludeFromMarkup?"Include in Markup":"Exclude from Markup"}
            </div>
            <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
            {notesEnabled&&(()=>{
              const li=itemState[ctxMenu.itemId]||{};
              const div=divisions.find(d=>d.id===ctxMenu.divId);
              const divShort=(div?.name||"").replace(/^Division \d+ [–-] /,"").replace(/^Div \d+ [–-] /,"");
              const desc=li.description||"Line item";
              const open=openNotesFor(notes,ctxMenu.itemId).length;
              return(
                <div className="file-menu-item" onClick={()=>{
                  const id=ctxMenu.itemId;
                  setCtxMenu(null);
                  // Anchored, so the note hangs on the number it is about and
                  // the row carries a badge from here on.
                  openNotes({kind:"item",id,label:divShort?`${divShort} · ${desc}`:desc});
                }}>
                  <span style={{width:18,display:"inline-block",opacity:.7}}><Icon.MessageSquare size={11}/></span>
                  {open>0?`Notes on this item (${open})`:"Add a note to this item"}
                </div>
              );
            })()}
            <div className="file-menu-item" onClick={()=>{
              const li=itemState[ctxMenu.itemId]||{};
              const div=divisions.find(d=>d.id===ctxMenu.divId);
              const sub=div?.subcategories?.find((s:any)=>s.id===ctxMenu.subId);
              const divShort=(div?.name||"").replace(/^Division \d+ [–-] /,"").replace(/^Div \d+ [–-] /,"");
              const itemDesc=li.description||"—";
              // Pre-fill division from the source line item's division.
              const divPrefill=div?`${(div as any).code||""}${(div as any).code&&div.name?" — ":""}${div.name||""}`.trim():"";
              setCtxMenu(null);
              setRfiQuickCreate({itemId:ctxMenu.itemId,divId:ctxMenu.divId,subId:ctxMenu.subId,divLabel:divShort,subLabel:sub?.name||"",itemDesc,
                subject:`Clarification needed — ${itemDesc}`,question:"",assignedTo:"",costImpactType:"tbd",costImpact:"",priority:"normal",
                specSection:divPrefill,drawingRef:""});
            }}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>📝</span> Create RFI for this item
            </div>
            {(()=>{
              const linked=rfis.filter(r=>r.linkedItemIds?.includes(ctxMenu.itemId));
              if(linked.length===0)return null;
              return(
                <div className="file-menu-item" onClick={()=>{
                  setCtxMenu(null);
                  setActiveTab("rfi");
                  setActiveRfiId(linked[0].id);
                  setRfiSearch("");
                  setRfiStatusFilter("all");
                }}>
                  <span style={{width:18,display:"inline-block",opacity:.7}}>🔗</span> View linked RFIs
                  <span style={{marginLeft:"auto",fontSize:10,opacity:.55}}>{linked.length}</span>
                </div>
              );
            })()}
            <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
            <div style={{position:"relative"}}>
              <div className="file-menu-item" onClick={()=>setCtxShowMoveSub(v=>!v)}>
                <span style={{width:18,display:"inline-block",opacity:.7}}>↷</span> Move to Division
                <span style={{marginLeft:"auto",fontSize:10,opacity:.6}}>▸</span>
              </div>
              {ctxShowMoveSub&&(
                <div style={{position:"absolute",left:"100%",top:0,background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
                  boxShadow:"0 4px 24px #0009",minWidth:200,padding:"4px 0",zIndex:2100}}>
                  {divisions.filter(d=>d.id!==ctxMenu.divId).map(d=>(
                    <div key={d.id} className="file-menu-item" onClick={()=>{handleMoveToDivision(ctxMenu.itemId,d.id);setCtxMenu(null);setCtxShowMoveSub(false);}}>
                      <span style={{fontSize:10,color:T.textWeak,fontWeight:700,width:32,flexShrink:0}}>{d.code}</span>
                      {d.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
            <div className="file-menu-item" onClick={()=>{setCtxMenu(null);removeItem(ctxMenu.divId,ctxMenu.subId,ctxMenu.itemId);}}
              style={{color:T.dangerText}}>
              <span style={{width:18,display:"inline-block",opacity:.7}}>✕</span> Delete row
            </div>
          </div>
        </>
      )}

      {/* ── Quick-Create RFI from Line Item ── */}
      {rfiQuickCreate&&(
        <div style={{position:"fixed",inset:0,zIndex:2200,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={()=>setRfiQuickCreate(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,width:"100%",maxWidth:540,display:"flex",flexDirection:"column" as any,overflow:"hidden",fontFamily:FONT,boxShadow:"0 12px 40px #000a"}}>
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,marginBottom:3}}>New RFI · Linked to estimate item</div>
                <div style={{fontSize:14,fontWeight:700,color:T.borderAccent}}>Create RFI</div>
              </div>
              <button onClick={()=>setRfiQuickCreate(null)} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",display:"flex"}}><Icon.X size={14}/></button>
            </div>
            <div style={{padding:"16px 20px",display:"flex",flexDirection:"column" as any,gap:14,maxHeight:"70vh",overflowY:"auto" as any}}>
              {/* Breadcrumb chip */}
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",background:`${T.borderAccent}10`,border:`1px solid ${T.borderAccentFaint}`,borderRadius:6,fontSize:11}}>
                <span style={{color:T.textMuted,fontSize:10}}>Linked to:</span>
                <span style={{color:T.textMuted}}>{rfiQuickCreate.divLabel}</span>
                {rfiQuickCreate.subLabel&&<><span style={{color:T.textMuted,opacity:.5}}>›</span><span style={{color:T.textMuted}}>{rfiQuickCreate.subLabel}</span></>}
                <span style={{color:T.textMuted,opacity:.5}}>›</span>
                <span style={{color:T.textPrimary,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any,minWidth:0}}>{rfiQuickCreate.itemDesc}</span>
              </div>
              {/* Subject */}
              <div>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Subject</div>
                <input value={rfiQuickCreate.subject} autoFocus
                  onChange={e=>setRfiQuickCreate(p=>p?{...p,subject:e.target.value}:p)}
                  style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"7px 10px",borderRadius:3,fontSize:12,fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}/>
              </div>
              {/* Question */}
              <div>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Question / Clarification needed</div>
                <textarea value={rfiQuickCreate.question} rows={5}
                  onChange={e=>setRfiQuickCreate(p=>p?{...p,question:e.target.value}:p)}
                  placeholder="What is unclear about this scope or assumption? What information do you need from the design team?"
                  style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"8px 10px",borderRadius:3,fontSize:12,fontFamily:FONT,outline:"none",resize:"vertical" as any,lineHeight:1.5,boxSizing:"border-box" as any}}/>
              </div>
              {/* Assigned To + Priority */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 140px",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Assigned To</div>
                  <input value={rfiQuickCreate.assignedTo} list="rfi-quick-assignee-list"
                    onChange={e=>setRfiQuickCreate(p=>p?{...p,assignedTo:e.target.value}:p)}
                    placeholder="Architect, owner's rep, etc."
                    style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 10px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}/>
                  <datalist id="rfi-quick-assignee-list">
                    {rfiAssigneeOptions.map((o,i)=>(
                      <option key={i} value={o.value}>{o.hint}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Priority</div>
                  <select value={rfiQuickCreate.priority}
                    onChange={e=>setRfiQuickCreate(p=>p?{...p,priority:e.target.value as any}:p)}
                    style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 8px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none"}}>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              {/* Spec Section + Drawing Ref */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Division</div>
                  <select value={rfiQuickCreate.specSection}
                    onChange={e=>setRfiQuickCreate(p=>p?{...p,specSection:e.target.value}:p)}
                    title="Pre-filled from this item's division — change if the RFI applies to a different scope."
                    style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 8px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}>
                    <option value="">— Select division —</option>
                    {divisions.map((d:any)=>{
                      const lbl=`${d.code||""}${d.code&&d.name?" — ":""}${d.name||""}`.trim();
                      return <option key={d.id} value={lbl}>{lbl}</option>;
                    })}
                    {rfiQuickCreate.specSection&&!divisions.some((d:any)=>`${d.code||""}${d.code&&d.name?" — ":""}${d.name||""}`.trim()===rfiQuickCreate.specSection)&&(
                      <option value={rfiQuickCreate.specSection}>{rfiQuickCreate.specSection}</option>
                    )}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Drawing Ref</div>
                  <input value={rfiQuickCreate.drawingRef}
                    onChange={e=>setRfiQuickCreate(p=>p?{...p,drawingRef:e.target.value}:p)}
                    placeholder="e.g. A2.01, S-301"
                    style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 10px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}/>
                </div>
              </div>
              {/* Cost impact */}
              <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Cost Impact</div>
                  <select value={rfiQuickCreate.costImpactType}
                    onChange={e=>setRfiQuickCreate(p=>p?{...p,costImpactType:e.target.value as any}:p)}
                    style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 8px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none"}}>
                    <option value="tbd">TBD — pending response</option>
                    <option value="none">No cost impact</option>
                    <option value="add">Add (cost increase)</option>
                    <option value="deduct">Deduct (cost decrease)</option>
                  </select>
                </div>
                {(rfiQuickCreate.costImpactType==="add"||rfiQuickCreate.costImpactType==="deduct")&&(
                  <div>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,marginBottom:4}}>Estimated $ Impact</div>
                    <input type="number" value={rfiQuickCreate.costImpact} placeholder="0"
                      onChange={e=>setRfiQuickCreate(p=>p?{...p,costImpact:e.target.value}:p)}
                      style={{width:"100%",background:T.bgApp,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,padding:"6px 10px",borderRadius:3,fontSize:11,fontFamily:FONT,outline:"none",boxSizing:"border-box" as any}}/>
                  </div>
                )}
              </div>
            </div>
            <div style={{padding:"12px 20px",borderTop:`1px solid ${T.borderPrimary}`,display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setRfiQuickCreate(null)}
                style={{fontSize:11,padding:"7px 14px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
              <button onClick={()=>{
                if(!rfiQuickCreate)return;
                const qc=rfiQuickCreate;
                const li=itemState[qc.itemId]||{};
                const total=(li.qty||0)*(li.unitCost||0);
                const snap={description:li.description||qc.itemDesc,divLabel:qc.divLabel,subLabel:qc.subLabel,
                  qty:li.qty||0,unit:li.unit||"",unitCost:li.unitCost||0,total,capturedAt:new Date().toISOString()};
                let costImpactNum=0;
                if(qc.costImpactType==="add")costImpactNum=Math.abs(parseFloat(qc.costImpact)||0);
                else if(qc.costImpactType==="deduct")costImpactNum=-Math.abs(parseFloat(qc.costImpact)||0);
                snapshot();
                const newRfi:RFIItem={id:uid(),
                  number:rfis.length>0?Math.max(...rfis.map(r=>r.number))+1:1,
                  subject:qc.subject||`Clarification — ${qc.itemDesc}`,status:"open",priority:qc.priority,
                  submittedBy:"",assignedTo:qc.assignedTo,question:qc.question,response:"",
                  drawingRef:qc.drawingRef||"",specSection:qc.specSection||"",
                  createdAt:new Date().toISOString().split("T")[0],dueDate:"",answeredAt:"",
                  linkedItemIds:[qc.itemId],
                  linkedItemSnapshots:{[qc.itemId]:snap},
                  costImpact:costImpactNum,scheduleImpact:0};
                setRfis(prev=>[...prev,newRfi]);
                setRfiQuickCreate(null);
                showToast(`RFI-${String(newRfi.number).padStart(3,"0")} created and linked`);
              }}
                style={{fontSize:11,padding:"7px 18px",background:T.borderAccent,border:"none",color:T.textOnGold,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700}}>
                Create RFI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Org Trade Partner Import Modal ── */}
      {showOrgPartnerImport&&(
        <div style={{position:"fixed",inset:0,background:T.bgModalBackdrop,zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:T.bgModal,border:`1px solid ${T.borderPrimary}`,borderRadius:6,width:"100%",maxWidth:640,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Header */}
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>Import Trade Partners</div>
              <button onClick={()=>setShowOrgPartnerImport(false)} style={{background:"none",border:"none",color:T.textWeak,fontSize:16,cursor:"pointer",lineHeight:1}}><Icon.X size={10}/></button>
            </div>
            <div style={{flex:1,overflow:"auto",padding:"20px"}}>
              {orgPartnerImportStep==="upload"&&(
                <div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:16,lineHeight:1.6}}>
                    Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file with your trade partner list.
                    Columns are auto-detected — supported column names include:
                    <em style={{color:T.textTertiary}}> Name/Company, Trade/Division, Contact, Phone, Email, Notes</em>.
                  </div>
                  <div
                    onClick={()=>orgPartnerFileRef.current?.click()}
                    style={{border:`2px dashed ${T.borderDashed}`,borderRadius:6,padding:"36px 24px",textAlign:"center",cursor:"pointer",background:"transparent",transition:"all .15s",marginBottom:16}}
                    onMouseEnter={e=>(e.currentTarget.style.borderColor=T.borderAccent)}
                    onMouseLeave={e=>(e.currentTarget.style.borderColor=T.borderDashed)}>
                    <div style={{fontSize:24,marginBottom:10}}><Icon.Clipboard size={28}/></div>
                    <div style={{fontSize:13,color:T.textSecondary,fontWeight:600,marginBottom:4}}>Click to browse or drop a file</div>
                    <div style={{fontSize:11,color:T.textWeak}}>CSV or Excel (.xlsx) — exported from Excel, SmartSheet, or your ERP</div>
                  </div>
                  <input ref={orgPartnerFileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={async e=>{
                    const f=e.target.files?.[0];
                    if(!f)return;
                    e.target.value="";
                    setOrgPartnerImportError("");
                    try{
                      const parsed=await parsePartnerFile(f);
                      if(parsed.length===0){setOrgPartnerImportError("No valid partner rows found. Make sure the file has a header row and a 'Name' or 'Company' column.");return;}
                      setOrgPartnerImportRows(parsed);
                      setOrgPartnerImportStep("preview");
                    }catch(err){setOrgPartnerImportError("Failed to parse file: "+String(err));}
                  }}/>
                  {orgPartnerImportError&&<div style={{fontSize:11,color:T.dangerText,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,borderRadius:3,padding:"8px 12px"}}>{orgPartnerImportError}</div>}
                  <div style={{marginTop:20,borderTop:`1px solid ${T.borderPrimary}`,paddingTop:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                    <div style={{fontSize:11,color:T.textWeak}}>Not sure about the format? Download the template, fill it in, and re-upload.</div>
                    <button
                      onClick={()=>{
                        const rows=[
                          ["Name","Trade","Contact","Phone","Email","Notes"],
                          ["ABC Concrete","Division 03","John Smith","555-0100","john@abcconcrete.com","Preferred sub for footings"],
                          ["XYZ Framing","Division 06","Jane Doe","555-0200","jane@xyzframing.com",""],
                          ["Summit MEP","Division 15","Carlos Rivera","555-0300","carlos@summitmep.com",""],
                        ];
                        const csv=rows.map(r=>r.map(c=>c.includes(",")||c.includes('"')?`"${c.replace(/"/g,'""')}"`:c).join(",")).join("\r\n");
                        const a=document.createElement("a");
                        a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
                        a.download="trade_partners_template.csv";
                        a.click();
                        URL.revokeObjectURL(a.href);
                      }}
                      style={{flexShrink:0,fontSize:11,padding:"6px 14px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
                        color:T.textSecondary,borderRadius:3,cursor:"pointer",fontFamily:FONT,
                        display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap" as any}}>
                      <Icon.Download size={11}/> Download Template
                    </button>
                  </div>
                </div>
              )}
              {orgPartnerImportStep==="preview"&&(
                <div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:12}}>
                    Found <strong style={{color:T.textPrimary}}>{orgPartnerImportRows.length}</strong> partners to import. Review below, then confirm.
                  </div>
                  <div style={{overflowX:"auto" as any,marginBottom:16}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{background:T.bgApp}}>
                          {["Name","Trade","Contact","Phone","Email"].map(h=>(
                            <th key={h} style={{padding:"6px 10px",textAlign:"left",color:T.textWeak,fontWeight:600,fontSize:10,letterSpacing:".08em",textTransform:"uppercase",borderBottom:`1px solid ${T.borderPrimary}`}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orgPartnerImportRows.map((p,i)=>(
                          <tr key={p.id} style={{background:i%2===0?T.bgPanel:T.bgApp}}>
                            <td style={{padding:"5px 10px",color:T.textPrimary,fontWeight:600}}>{p.name}</td>
                            <td style={{padding:"5px 10px",color:T.textTertiary}}>{p.trade}</td>
                            <td style={{padding:"5px 10px",color:T.textTertiary}}>{p.contact}</td>
                            <td style={{padding:"5px 10px",color:T.textTertiary}}>{p.phone}</td>
                            <td style={{padding:"5px 10px",color:T.textTertiary}}>{p.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{padding:"12px 20px",borderTop:`1px solid ${T.borderPrimary}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
              {orgPartnerImportStep==="preview"&&(
                <button onClick={()=>setOrgPartnerImportStep("upload")} style={{fontSize:11,padding:"6px 14px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>← Back</button>
              )}
              <div style={{flex:1}}/>
              <button onClick={()=>setShowOrgPartnerImport(false)} style={{fontSize:11,padding:"6px 14px",background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
              {orgPartnerImportStep==="preview"&&(
                <button onClick={()=>{
                  // Merge: skip duplicates by name (case-insensitive)
                  const existing=new Set(orgPartners.map(p=>p.name.toLowerCase().trim()));
                  const toAdd=orgPartnerImportRows.filter(p=>!existing.has(p.name.toLowerCase().trim()));
                  const dupes=orgPartnerImportRows.length-toAdd.length;
                  setOrgPartners(prev=>[...prev,...toAdd]);
                  setShowOrgPartnerImport(false);
                  showToast(`Imported ${toAdd.length} partner${toAdd.length!==1?"s":""}${dupes>0?` (${dupes} duplicate${dupes!==1?"s":""} skipped)`:""}`);
                }} style={{fontSize:11,padding:"6px 18px",background:T.borderAccent,border:"none",color:T.textOnGold,borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600}}>
                  Import {orgPartnerImportRows.length} Partners
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SHORTCUTS MODAL */}
      {showBcImport&&(
        <BcLevelingImportModal
          T={T} fmt={fmt} scopes={bidScopes} partners={bidPartners} uid={uid}
          divisions={divisions} itemState={itemState}
          onClose={()=>setShowBcImport(false)}
          onApply={({scope,isNewScope,newPartners,bids:newBids,adjustments})=>{
            bidsSnapshot();
            if(isNewScope)setBidScopes(p=>[...p,scope]);
            if(newPartners.length)setBidPartners(p=>[...p,...newPartners]);
            setBids(p=>[...p,...newBids]);
            setLevelingAdjustments(p=>[...p,...adjustments]);
            setActiveBidScope(scope.id);
            setShowBcImport(false);
            showToast(`Imported ${newBids.length} bid${newBids.length===1?"":"s"} into ${scope.name}`,3200);
          }}/>
      )}
      {showStaffingPlan&&(
        <StaffingPlanModal
          T={T} fmt={fmt} duration={duration} laborRates={laborRates}
          existing={staffPlan} onApply={applyStaffingPlan}
          onClose={()=>setShowStaffingPlan(false)}/>
      )}
      {showHelp&&<HelpCenter T={T} onClose={()=>setShowHelp(false)}/>}
      {notesOpen&&notesEnabled&&user&&(
        <NotesPanel T={T} notes={notes} userId={user.id} people={notesPeople}
          anchor={notesAnchor} error={notesError} onClose={()=>setNotesOpen(false)}
          onAdd={handleAddNote} onResolve={handleResolveNote} onDelete={handleDeleteNote}/>
      )}
      {procoreExportKind&&(
        <ProcoreExport T={T} kind={procoreExportKind} rfis={rfis} submittals={submittals}
          cloudProjectId={cloudProjectId||null}
          onClose={()=>setProcoreExportKind(null)} showToast={showToast}/>
      )}
      {showShortcuts&&(
        <div className="modal-backdrop" onClick={()=>setShowShortcuts(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{minWidth:460}}>
            <div style={{fontSize:16,fontWeight:600,color:T.borderAccent,marginBottom:6}}>Keyboard Shortcuts</div>
            <div style={{fontSize:10,color:T.textWeak,marginBottom:18}}>Toggle shortcuts on or off using the switches below.</div>
            {[
              {key:"enterAddRow",   label:"Enter (Description / Unit Cost)", desc:"Add a new row below"},
              {key:"ctrlZUndo",     label:"Ctrl+Z / ⌘Z · Ctrl+Y",          desc:"Undo and redo — reaches whatever the screen you are on shows"},
              {key:"questionShortcuts", label:"? key",                       desc:"Open/close this shortcuts panel"},
            ].map(({key,label,desc})=>(
              <div key={key} className="shortcut-row">
                <div style={{flex:1}}>
                  <div style={{color:T.textSecondary,marginBottom:2}}>{desc}</div>
                  <kbd style={{fontSize:10,padding:"2px 8px"}}>{label}</kbd>
                </div>
                <div onClick={()=>setShortcuts(p=>({...p,[key]:!p[key]}))}
                  className="toggle-switch" style={{background:shortcuts[key]?T.borderAccent:T.borderSecondary,flexShrink:0,cursor:"pointer"}}>
                  <div className="toggle-thumb" style={{left:shortcuts[key]?"16px":"2px"}}/>
                </div>
              </div>
            ))}
            <div style={{marginTop:4,padding:"10px 0 4px",borderTop:`1px solid ${T.borderPrimary}`,fontSize:10,color:T.textMuted}}>
              <div style={{fontWeight:600,color:T.textTertiary,marginBottom:6}}>In the grid</div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>← ↑ ↓ →</kbd> Move between cells. Arriving by keyboard selects the value, so typing replaces it</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Tab</kbd> Next cell, wrapping to the start of the row below &nbsp; <kbd style={{fontSize:10,padding:"1px 5px"}}>Shift+Tab</kbd> Back</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>F2</kbd> Edit in place instead of replacing</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+↑ / ↓</kbd> Jump to the edge of the subcategory, then the next one</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+D</kbd> Fill down — the top of a selection, or the cell above</div>
                <div>Use the ↦ / ↤ indent buttons to group items.</div>
              </div>
            </div>
            <div style={{padding:"6px 0 4px",fontSize:10,color:T.textMuted}}>
              <div style={{fontWeight:600,color:T.textTertiary,marginBottom:6}}>Row Operations (click ☰ grip to select rows first)</div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+C</kbd> Copy selected rows</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+X</kbd> Cut selected rows</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+V</kbd> Paste after selected row</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+A</kbd> Select all rows</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+Shift+D</kbd> Duplicate selected rows</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Delete</kbd> Delete selected rows</div>
                <div><kbd style={{fontSize:10,padding:"1px 5px"}}>Shift+Click</kbd> Range select &nbsp; <kbd style={{fontSize:10,padding:"1px 5px"}}>Ctrl+Click</kbd> Multi-select</div>
                <div>Drag ☰ handle to reorder rows</div>
              </div>
            </div>
            <div style={{marginTop:16,textAlign:"right"}}>
              <button onClick={()=>setShowShortcuts(false)}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"6px 16px",borderRadius:3,fontFamily:FONT,fontSize:12}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* NEW PROJECT DIALOG */}
      {showNewProjDialog&&(
        <div className="modal-backdrop" onClick={()=>{setShowNewProjDialog(false);setNewProjName("");}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:460}}>
            <div style={{fontSize:16,fontWeight:600,color:T.borderAccent,marginBottom:20}}>New Project</div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Project Name</div>
              <input className="input-field" value={newProjName} onChange={e=>setNewProjName(e.target.value)}
                placeholder="e.g. 123 Main St." autoFocus
                onKeyDown={e=>{if(e.key==="Enter")handleNewProject();if(e.key==="Escape"){setShowNewProjDialog(false);setNewProjName("");}}}
                style={{width:"100%"}}/>
            </div>
            <div style={{marginBottom:22}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Preset</div>
                <button onClick={()=>{setShowNewProjDialog(false);setActiveTab("settings-presets");}}
                  style={{background:"none",border:"none",color:T.borderAccent,cursor:"pointer",fontSize:10,padding:0,fontFamily:FONT}}>Manage Presets →</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto"}}>
                {presets.map(p=>{
                  const isOn=newProjPresetId===p.id;
                  return (
                    <div key={p.id} onClick={()=>setNewProjPresetId(p.id)}
                      style={{padding:"10px 14px",borderRadius:3,cursor:"pointer",transition:"all .15s",
                        border:`1px solid ${isOn?`${T.borderAccent}66`:T.borderSecondary}`,background:isOn?T.bgAccentSubtle:T.bgApp,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{fontSize:12,fontWeight:isOn?600:400,color:isOn?T.borderAccent:T.textMuted}}>{p.name}</div>
                      <div style={{fontSize:10,color:T.textWeak,whiteSpace:"nowrap"}}>
                        OH {p.overhead}% · Profit {p.profit}% · Cont {p.contingency}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>{setShowNewProjDialog(false);setNewProjName("");}}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"6px 16px",borderRadius:3,fontFamily:FONT,fontSize:12}}>Cancel</button>
              <button onClick={handleNewProject}
                style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"6px 18px",borderRadius:3,fontFamily:FONT,fontSize:12,fontWeight:600}}>Create Project</button>
            </div>
          </div>
        </div>
      )}

      {/* SETTINGS DIALOG removed — replaced by settings-presets page */}

      {/* Labor rate popover removed — labor added via "+ Add Labor" button in subcategory footer */}
      {laborRatePopover&&null}

      {/* ── Delta breakdown popup ── */}
      {deltaPopupId&&(()=>{
        const snap=projectSnapshots.find(s=>s.id===deltaPopupId);
        if(!snap)return null;
        const snapSum=summarizeState(snap.state);
        const allCodes=new Set([...Object.keys(snapSum),...Object.keys(currentSummary)]);
        const rows=[...allCodes].map(code=>{
          const snapTotal=(snapSum[code]?.total)||0;
          const curTotal2=(currentSummary[code]?.total)||0;
          const d=curTotal2-snapTotal;
          const label=currentSummary[code]?.label||snapSum[code]?.label||code;
          return{code,label,snapTotal,curTotal:curTotal2,delta:d};
        }).filter(r=>Math.abs(r.delta)>0.5||Math.abs(r.snapTotal)>0.5||Math.abs(r.curTotal)>0.5)
          .sort((a,b)=>Math.abs(b.delta)-Math.abs(a.delta));
        const totalDelta=rows.reduce((s,r)=>s+r.delta,0);
        const popupMaxH=420,popupW=460,margin=8;
        const showAbove=deltaPopupPos.top-popupMaxH>=margin;
        const popupTop=showAbove?deltaPopupPos.top-6:deltaPopupPos.bottom+6;
        const popupTransform=showAbove?"translateY(-100%)":"translateY(0)";
        // Clamp horizontal so popup never clips left or right viewport edge
        const rawLeft=deltaPopupPos.x-popupW/2;
        const clampedLeft=Math.max(margin,Math.min((typeof window!=="undefined"?window.innerWidth:1200)-popupW-margin,rawLeft));
        return(
          <>
            {/* Click-outside overlay */}
            <div style={{position:"fixed",inset:0,zIndex:9998}} onClick={()=>setDeltaPopupId(null)}/>
            <div style={{position:"fixed",left:clampedLeft,top:popupTop,transform:popupTransform,
              zIndex:9999,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
              boxShadow:"0 8px 32px rgba(0,0,0,.55)",width:popupW,maxHeight:popupMaxH,display:"flex",flexDirection:"column" as const}}>
              {/* Header */}
              <div style={{padding:"10px 14px 8px",borderBottom:`1px solid ${T.borderSecondary}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                <div>
                  <span style={{fontSize:11,fontWeight:700,color:T.textPrimary}}>{snap.label}</span>
                  {snap.date&&<span style={{fontSize:10,color:T.textMuted,marginLeft:8}}>{new Date(snap.date).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}</span>}
                </div>
                <span style={{fontSize:10,color:T.textWeak,fontStyle:"italic"}}>vs. Current</span>
              </div>
              {/* Column headers */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 90px",gap:0,padding:"5px 14px 4px",borderBottom:`1px solid ${T.borderSecondary}`,flexShrink:0}}>
                {["Subdivision","Snapshot","Current","Change"].map(h=>(
                  <div key={h} style={{fontSize:10,color:T.textWeak,textTransform:"uppercase" as const,letterSpacing:".08em",textAlign:h==="Subdivision"?"left":"right"}}>{h}</div>
                ))}
              </div>
              {/* Rows */}
              <div style={{overflowY:"auto" as const,flex:1}}>
                {rows.map(r=>{
                  const changed=Math.abs(r.delta)>0.5;
                  // parse "01-100 — Permit Fees (Div 01)" → "Permit Fees (Div 01)"
                  const shortLabel=r.label.replace(/^[\d\-]+\s*—\s*/,"");
                  return(
                    <div key={r.code} style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 90px",gap:0,
                      padding:"5px 14px",borderBottom:`1px solid ${T.borderSecondary}22`,
                      background:changed?"transparent":"transparent"}}>
                      <div style={{fontSize:10,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as const,paddingRight:8}} title={r.label}>{shortLabel}</div>
                      <div style={{fontSize:10,color:T.textMuted,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{r.snapTotal===0?"—":fmt(r.snapTotal)}</div>
                      <div style={{fontSize:10,color:T.textMuted,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{r.curTotal===0?"—":fmt(r.curTotal)}</div>
                      <div style={{fontSize:10,fontWeight:changed?600:400,textAlign:"right",fontVariantNumeric:"tabular-nums",
                        color:r.delta>0.5?T.negative:r.delta<-0.5?T.positive:T.textWeak}}>
                        {!changed?"—":r.delta>0?`+${fmt(r.delta)}`:fmt(r.delta)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Footer total */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 90px",gap:0,padding:"7px 14px",
                borderTop:`1px solid ${T.borderSecondary}`,background:T.bgAccentFaint,flexShrink:0}}>
                <div style={{fontSize:10,fontWeight:700,color:T.textPrimary}}>Total change</div>
                <div/><div/>
                <div style={{fontSize:11,fontWeight:700,textAlign:"right",fontVariantNumeric:"tabular-nums",
                  color:totalDelta>0.5?T.negative:totalDelta<-0.5?T.positive:T.textWeak}}>
                  {Math.abs(totalDelta)<0.5?"—":totalDelta>0?`+${fmt(totalDelta)}`:fmt(totalDelta)}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Line-item breakdown popup (subcategory delta drill-down) ── */}
      {lineItemPopup&&(()=>{
        const {code,label,x,top:chipTop,bottom:chipBottom}=lineItemPopup;
        const stateA=getVersionState(analysisLeft);
        const stateB=getVersionState(analysisRight||"current");
        if(!stateA||!stateB)return null;
        // Find the subcategory in each state by cost code
        const findItems=(st:{divisions:any[];itemState:Record<string,any>})=>{
          for(const div of st.divisions){
            for(const sub of div.subcategories){
              if((sub.costCode||"00-000")===code){
                const out:[{id:string;desc:string;qty:number;unit:string;unitCost:number;total:number}]=[] as any;
                [...walkItems(sub.rows)].forEach(item=>{
                  const li=st.itemState[item.id]||{};
                  if(li.included===false)return;
                  const qty=Number(li.qty)||0;
                  const uc=Number(li.unitCost)||0;
                  out.push({id:item.id,desc:li.description||item.description||"",qty,unit:li.unit||"",unitCost:uc,total:qty*uc});
                });
                return out;
              }
            }
          }
          return [];
        };
        const itemsA=findItems(stateA);
        const itemsB=findItems(stateB);
        // Merge by item id, fall back to description matching for new/removed items
        const allIds=Array.from(new Set([...itemsA.map(i=>i.id),...itemsB.map(i=>i.id)]));
        const rows=allIds.map(id=>{
          const a=itemsA.find(i=>i.id===id);
          const b=itemsB.find(i=>i.id===id);
          const desc=a?.desc||b?.desc||id;
          const totalA=a?.total||0;
          const totalB=b?.total||0;
          return{id,desc,totalA,totalB,delta:totalB-totalA};
        }).filter(r=>r.totalA||r.totalB);
        const totalDelta=rows.reduce((s,r)=>s+r.delta,0);
        const labelA=analysisLeft==="current"?"Current":(projectSnapshots.find(s=>s.id===analysisLeft)?.label||"Version A");
        const labelB=(!analysisRight||analysisRight==="current")?"Current":(projectSnapshots.find(s=>s.id===analysisRight)?.label||"Version B");
        const popupMaxH=460,popupW=540,margin=8;
        const showAbove=chipTop-popupMaxH>=margin;
        const popupTop=showAbove?chipTop-6:chipBottom+6;
        const popupTransform=showAbove?"translateY(-100%)":"translateY(0)";
        const rawLeft=x-popupW/2;
        const clampedLeft=Math.max(margin,Math.min((typeof window!=="undefined"?window.innerWidth:1400)-popupW-margin,rawLeft));
        return(
          <>
            <div style={{position:"fixed",inset:0,zIndex:9998}} onClick={()=>setLineItemPopup(null)}/>
            <div style={{position:"fixed",left:clampedLeft,top:popupTop,transform:popupTransform,
              zIndex:9999,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
              boxShadow:"0 8px 32px rgba(0,0,0,.6)",width:popupW,maxHeight:popupMaxH,display:"flex",flexDirection:"column" as const}}>
              {/* Header */}
              <div style={{padding:"10px 14px 8px",borderBottom:`1px solid ${T.borderSecondary}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexShrink:0}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.borderAccent,marginBottom:1}}>{label.replace(/^[\d\-]+\s*—\s*/,"")}</div>
                  <div style={{fontSize:10,color:T.textWeak,fontFamily:"monospace"}}>{code}</div>
                </div>
                <button onClick={()=>setLineItemPopup(null)}
                  style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:14,lineHeight:1,padding:"2px 4px",fontFamily:FONT}}><Icon.X size={10}/></button>
              </div>
              {/* Column headers */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 90px",padding:"5px 14px 4px",borderBottom:`1px solid ${T.borderSecondary}`,flexShrink:0}}>
                {[["Line Item","left"],["Version A","right"],["Version B","right"],["Change","right"]].map(([h,a])=>(
                  <div key={h} style={{fontSize:10,color:T.textWeak,textTransform:"uppercase" as const,letterSpacing:".08em",textAlign:a as any}}>{h==="Version A"?labelA:h==="Version B"?labelB:h}</div>
                ))}
              </div>
              {/* Rows */}
              <div style={{overflowY:"auto" as const,flex:1}}>
                {rows.length===0?(
                  <div style={{padding:"20px 14px",fontSize:11,color:T.textWeak,textAlign:"center"}}>No line items found</div>
                ):rows.map(r=>{
                  const changed=Math.abs(r.delta)>0.5;
                  return(
                    <div key={r.id} style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 90px",
                      padding:"6px 14px",borderBottom:`1px solid ${T.borderSecondary}22`,
                      background:!changed?"transparent":r.delta>0?`${T.negative}08`:`${T.positive}08`}}>
                      <div style={{fontSize:10,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as const,paddingRight:8}} title={r.desc}>{r.desc}</div>
                      <div style={{fontSize:10,color:r.totalA?T.versionA.bar:T.textWeak,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{r.totalA?fmt(r.totalA):"—"}</div>
                      <div style={{fontSize:10,color:r.totalB?T.versionB.bar:T.textWeak,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{r.totalB?fmt(r.totalB):"—"}</div>
                      <div style={{fontSize:10,fontWeight:changed?600:400,textAlign:"right",fontVariantNumeric:"tabular-nums",
                        color:r.delta>0.5?T.negative:r.delta<-0.5?T.positive:T.textWeak}}>
                        {!changed?"—":r.delta>0?`+${fmt(r.delta)}`:fmt(r.delta)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Footer */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 90px",padding:"7px 14px",
                borderTop:`1px solid ${T.borderSecondary}`,background:T.bgAccentFaint,flexShrink:0}}>
                <div style={{fontSize:10,fontWeight:700,color:T.textPrimary}}>Subcategory total</div>
                <div style={{fontSize:10,fontWeight:600,textAlign:"right",color:T.versionA.bar,fontVariantNumeric:"tabular-nums"}}>{fmt(rows.reduce((s,r)=>s+r.totalA,0))}</div>
                <div style={{fontSize:10,fontWeight:600,textAlign:"right",color:T.versionB.bar,fontVariantNumeric:"tabular-nums"}}>{fmt(rows.reduce((s,r)=>s+r.totalB,0))}</div>
                <div style={{fontSize:11,fontWeight:700,textAlign:"right",fontVariantNumeric:"tabular-nums",
                  color:totalDelta>0.5?T.negative:totalDelta<-0.5?T.positive:T.textWeak}}>
                  {Math.abs(totalDelta)<0.5?"—":totalDelta>0?`+${fmt(totalDelta)}`:fmt(totalDelta)}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* SNAPSHOT DIALOG */}
      {/* ── Snapshot Quick-Reference Modal ── */}
      {snapPreviewId&&(()=>{
        const snap=projectSnapshots.find(s=>s.id===snapPreviewId);
        if(!snap)return null;
        const snapSum=summarizeState(snap.state);
        const curSum=currentSummary;
        const snapTotal=Object.values(snapSum).reduce((a,v)=>a+v.total,0);
        const curTotal=Object.values(curSum).reduce((a,v)=>a+v.total,0);
        const delta=curTotal-snapTotal;
        // Build division-level rows by grouping subcategories
        const divRows:{code:string;name:string;snapAmt:number;curAmt:number}[]=[];
        snap.state.divisions?.forEach(div=>{
          let snapAmt=0,curAmt=0;
          div.subcategories.forEach(sub=>{
            [...walkItems(sub.rows)].forEach(item=>{
              const sli=snap.state.itemState?.[item.id]||{};
              if(sli.included!==false)snapAmt+=(sli.qty||0)*(sli.unitCost||0);
            });
          });
          // current version same division
          const curDiv=divisions.find(d=>d.id===div.id);
          curDiv?.subcategories.forEach(sub=>{
            [...walkItems(sub.rows)].forEach(item=>{
              const cli=itemState[item.id]||{};
              if(cli.included!==false)curAmt+=(cli.qty||0)*(cli.unitCost||0);
            });
          });
          if(snapAmt||curAmt)divRows.push({code:div.code,name:div.name,snapAmt,curAmt});
        });
        // also catch divisions in current that weren't in snap
        divisions.forEach(div=>{
          if(divRows.find(r=>r.code===div.code))return;
          let curAmt=0;
          div.subcategories.forEach(sub=>{
            [...walkItems(sub.rows)].forEach(item=>{
              const cli=itemState[item.id]||{};
              if(cli.included!==false)curAmt+=(cli.qty||0)*(cli.unitCost||0);
            });
          });
          if(curAmt)divRows.push({code:div.code,name:div.name,snapAmt:0,curAmt});
        });
        divRows.sort((a,b)=>b.snapAmt-a.snapAmt);
        return(
          <div className="modal-backdrop" onClick={()=>setSnapPreviewId(null)} style={{zIndex:220}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:T.bgModal,border:`1px solid ${T.borderAccent}44`,borderRadius:6,
                width:"min(640px,92vw)",maxHeight:"82vh",display:"flex",flexDirection:"column",
                boxShadow:"0 20px 60px #00000066",overflow:"hidden"}}>
              {/* Header */}
              <div style={{padding:"20px 24px 16px",borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                  <div>
                    <div style={{fontSize:11,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:4}}>Snapshot</div>
                    <div style={{fontSize:16,fontWeight:700,color:T.textSecondary,lineHeight:1.2}}>{snap.label}</div>
                    <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{new Date(snap.date).toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"})}</div>
                  </div>
                  <button onClick={()=>setSnapPreviewId(null)}
                    style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:20,lineHeight:1,padding:"0 2px",flexShrink:0}}>&times;</button>
                </div>
                {/* Totals bar */}
                <div style={{display:"flex",gap:12,marginTop:16,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:130,background:T.bgPanel,borderRadius:3,padding:"10px 14px",border:`1px solid ${T.borderPrimary}`}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>This snapshot</div>
                    <div style={{fontSize:20,fontWeight:700,color:snapTotal<0?T.negative:T.positive}}>{fmt(snapTotal)}</div>
                  </div>
                  <div style={{flex:1,minWidth:130,background:T.bgPanel,borderRadius:3,padding:"10px 14px",border:`1px solid ${T.borderPrimary}`}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>Current estimate</div>
                    <div style={{fontSize:20,fontWeight:700,color:curTotal<0?T.negative:T.borderAccent}}>{fmt(curTotal)}</div>
                  </div>
                  <div style={{flex:1,minWidth:130,background:T.bgPanel,borderRadius:3,padding:"10px 14px",border:`1px solid ${delta>0?T.dangerBorder:delta<0?"#2a4a3a":T.borderPrimary}`}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4}}>Change</div>
                    <div style={{fontSize:20,fontWeight:700,color:delta>0?T.negative:delta<0?T.positive:T.textWeak}}>{delta===0?"No change":(delta>0?"+":"")+fmt(delta)}</div>
                    {delta!==0&&snapTotal!==0&&<div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{((delta/Math.abs(snapTotal))*100).toFixed(1)}%</div>}
                  </div>
                </div>
              </div>
              {/* Division breakdown */}
              <div style={{flex:1,overflowY:"auto",padding:"0 24px"}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",padding:"14px 0 8px",position:"sticky",top:0,background:T.bgModal}}>Division Breakdown</div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{borderBottom:`1px solid ${T.borderPrimary}`}}>
                      <th style={{textAlign:"left",padding:"4px 0 8px",fontSize:10,color:T.textWeak,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase"}}>Division</th>
                      <th style={{textAlign:"right",padding:"4px 0 8px",fontSize:10,color:T.textWeak,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase",paddingRight:8}}>Snapshot</th>
                      <th style={{textAlign:"right",padding:"4px 0 8px",fontSize:10,color:T.textWeak,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase",paddingRight:8}}>Current</th>
                      <th style={{textAlign:"right",padding:"4px 0 8px",fontSize:10,color:T.textWeak,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase"}}>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {divRows.map(row=>{
                      const d=row.curAmt-row.snapAmt;
                      return(
                        <tr key={row.code} style={{borderBottom:`1px solid ${T.bgInput}`}}>
                          <td style={{padding:"8px 0",color:T.textSecondary,lineHeight:1.3}}>
                            <span style={{fontSize:10,color:T.textWeak,fontWeight:600,marginRight:6}}>{row.code}</span>
                            {row.name}
                          </td>
                          <td style={{textAlign:"right",paddingRight:8,color:T.textMuted,fontVariantNumeric:"tabular-nums"}}>{row.snapAmt?fmt(row.snapAmt):"—"}</td>
                          <td style={{textAlign:"right",paddingRight:8,color:T.textMuted,fontVariantNumeric:"tabular-nums"}}>{row.curAmt?fmt(row.curAmt):"—"}</td>
                          <td style={{textAlign:"right",fontWeight:d!==0?600:400,color:d>0?T.negative:d<0?T.positive:T.textWeak,fontVariantNumeric:"tabular-nums"}}>
                            {d===0?"—":(d>0?"+":"")+fmt(d)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{height:16}}/>
              </div>
              {/* Footer actions */}
              <div style={{padding:"14px 24px",borderTop:`1px solid ${T.borderPrimary}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
                <button onClick={()=>setSnapPreviewId(null)}
                  style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"7px 18px",borderRadius:3,fontFamily:FONT,fontSize:12}}>
                  Close
                </button>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <button onClick={()=>{setSnapPreviewId(null);setAnalysisLeft(snapPreviewId!);setAnalysisRight("current");setTimeout(()=>document.getElementById("history-comparison")?.scrollIntoView({behavior:"smooth",block:"start"}),80);}}
                    style={{background:"none",border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"7px 18px",borderRadius:3,fontFamily:FONT,fontSize:12,fontWeight:600}}>
                    Compare with Current →
                  </button>
                  <button onClick={()=>{setSnapPreviewId(null);handleOpenArchive(snap);}}
                    style={{background:T.borderAccent,border:"none",color:"#0f1117",cursor:"pointer",padding:"7px 20px",borderRadius:3,fontFamily:FONT,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                    <span>🔒</span> Open Full Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SNAPSHOT SUMMARY POPUP (opened from Bid Timeline) ── */}
      {bidSnapModalId&&(()=>{
        const snap=projectSnapshots.find(s=>s.id===bidSnapModalId);
        if(!snap)return null;
        type SubRow={name:string;total:number};
        type DivRow={code:string;name:string;total:number;subs:SubRow[]};
        const rows:DivRow[]=[];
        (snap.state?.divisions||[]).forEach((div:any)=>{
          const subs:SubRow[]=[];
          let divTotal=0;
          div.subcategories.forEach((sub:any)=>{
            let t=0;
            [...walkItems(sub.rows)].forEach((item:any)=>{
              const li=(snap.state.itemState||{})[item.id]||{};
              if(li.included!==false)t+=(li.qty||0)*(li.unitCost||0);
            });
            if(t!==0)subs.push({name:sub.name,total:t});
            divTotal+=t;
          });
          if(divTotal!==0)rows.push({code:div.code,name:div.name,total:divTotal,subs});
        });
        const total=rows.reduce((a,r)=>a+r.total,0);
        const dateStr=snap.date?new Date(snap.date).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"}):"";
        return(
          <>
            <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:301,
              width:340,background:T.bgPanel,border:`1px solid ${T.borderHover}`,borderRadius:6,
              boxShadow:"0 12px 40px rgba(0,0,0,.55)",overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"80vh"}}>
              {/* Header */}
              <div style={{padding:"12px 16px 10px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,flexShrink:0}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.textInverse,lineHeight:1.2}}>{snap.label}</div>
                  {dateStr&&<div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{dateStr}</div>}
                </div>
                <button onClick={()=>{setBidSnapModalId(null);setSnapExpandedDivs(new Set());}} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",padding:2,display:"flex",flexShrink:0}}>
                  <Icon.X size={13}/>
                </button>
              </div>
              {/* Total */}
              <div style={{padding:"10px 16px 8px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"baseline",justifyContent:"space-between",flexShrink:0}}>
                <span style={{fontSize:10,color:T.textWeak,textTransform:"uppercase" as any,letterSpacing:".08em"}}>Total</span>
                <span style={{fontSize:20,fontWeight:700,color:T.borderAccent,fontVariantNumeric:"tabular-nums"}}>{fmt(total)}</span>
              </div>
              {/* Division rows — collapsible */}
              <div style={{overflowY:"auto",flex:1}}>
                {rows.map(r=>{
                  const open=snapExpandedDivs.has(r.code);
                  const toggle=()=>setSnapExpandedDivs(prev=>{const next=new Set(prev);open?next.delete(r.code):next.add(r.code);return next;});
                  return(
                    <div key={r.code} style={{borderBottom:`1px solid ${T.borderSecondary}22`}}>
                      {/* Division header row */}
                      <div onClick={toggle}
                        style={{display:"flex",alignItems:"center",padding:"7px 14px",cursor:"pointer",gap:6,
                          background:open?`${T.borderAccent}08`:"transparent"}}
                        onMouseEnter={e=>(e.currentTarget.style.background=`${T.bgHover}`)}
                        onMouseLeave={e=>(e.currentTarget.style.background=open?`${T.borderAccent}08`:"transparent")}>
                        {/* Chevron */}
                        <span style={{fontSize:10,color:T.textWeak,flexShrink:0,transition:"transform .15s",
                          display:"inline-block",transform:open?"rotate(90deg)":"rotate(0deg)"}}>▶</span>
                        <div style={{flex:1,minWidth:0,display:"flex",alignItems:"baseline",gap:6}}>
                          <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".04em",flexShrink:0}}>{r.code}</span>
                          <span style={{fontSize:11,color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</span>
                        </div>
                        <span style={{fontSize:11,fontWeight:600,color:r.total<0?T.negative:T.textSubLabel,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{fmt(r.total)}</span>
                      </div>
                      {/* Subcategory rows */}
                      {open&&r.subs.map((s,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                          padding:"5px 14px 5px 32px",background:`${T.borderAccent}05`,gap:8,
                          borderTop:`1px solid ${T.borderSecondary}18`}}>
                          <span style={{fontSize:10,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{s.name}</span>
                          <span style={{fontSize:10,fontWeight:500,color:s.total<0?T.negative:T.textWeak,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{fmt(s.total)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}

      {/* ── COPY TO NEW PROJECT DIALOG ── */}
      {showCopyDialog&&(
        <div className="modal-backdrop" onClick={()=>setShowCopyDialog(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <div style={{fontSize:16,fontWeight:600,color:T.borderAccent,marginBottom:6}}>Copy to New Project</div>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:18,lineHeight:1.5}}>
              Creates a complete copy of this project — all line items, packages, bids, and settings. The original stays unchanged.
            </div>
            <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase",marginBottom:6}}>New project name</div>
            <input className="input-field" value={copyDialogName} onChange={e=>setCopyDialogName(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter")handleConfirmCopy();if(e.key==="Escape")setShowCopyDialog(false);}}
              style={{marginBottom:20,width:"100%",boxSizing:"border-box" as any}} autoFocus
              onFocus={e=>e.target.select()}/>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowCopyDialog(false)}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,cursor:"pointer",padding:"7px 16px",borderRadius:3,fontSize:12,fontFamily:FONT}}>
                Cancel
              </button>
              <button onClick={handleConfirmCopy} disabled={!copyDialogName.trim()}
                style={{background:T.borderAccent,border:"none",color:T.textOnGold,cursor:copyDialogName.trim()?"pointer":"default",
                  padding:"7px 20px",borderRadius:3,fontSize:12,fontFamily:FONT,fontWeight:600,
                  opacity:copyDialogName.trim()?1:0.5}}>
                <Icon.Copy size={11}/>&nbsp; Create Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {showSnapDialog&&(
        <div className="modal-backdrop" onClick={()=>setShowSnapDialog(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:600,color:T.borderAccent,marginBottom:16}}>Save Milestone Snapshot</div>
            <input className="input-field" value={snapLabel} onChange={e=>setSnapLabel(e.target.value)}
              placeholder={`${projectName} — ${new Date().toLocaleDateString()}`}
              onKeyDown={e=>{if(e.key==="Enter")saveSnapshot();}} style={{marginBottom:16}} autoFocus/>
            <div style={{fontSize:10,color:T.textMuted,marginBottom:16}}>
              Total at snapshot: <strong style={{color:T.positive}}>{fmt(Object.values(currentSummary).reduce((a,v)=>a+v.total,0))}</strong>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowSnapDialog(false)} style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"6px 16px",borderRadius:3,fontFamily:FONT,fontSize:12}}>Cancel</button>
              <button onClick={saveSnapshot} style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"6px 16px",borderRadius:3,fontFamily:FONT,fontSize:12,fontWeight:600}}>Save Snapshot</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Import: hidden file input */}
      <input ref={aiFileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" onChange={handleAiImport} style={{display:"none"}}/>
      {/* CSV Import: hidden file input */}
      <input ref={csvImportRef} type="file" accept=".csv,.txt" onChange={handleImportCSV} style={{display:"none"}}/>
      {/* Submittal Template Import */}
      <input ref={subTemplateImportRef} type="file" accept=".json" style={{display:"none"}} onChange={e=>{
        const f=e.target.files?.[0];if(!f)return;
        const reader=new FileReader();
        reader.onload=evt=>{try{const parsed=JSON.parse(evt.target?.result as string);if(parsed&&typeof parsed.custom==="object"&&typeof parsed.disabled==="object"){setSubmittalTemplates(parsed);showToast("Template customizations imported");}else{showToast("Invalid template file format");}}catch{showToast("Could not parse JSON file");}};
        reader.readAsText(f);e.target.value="";
      }}/>

      {/* AI Import: loading overlay */}
      {aiImporting&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:T.bgModalBackdrop,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:T.bgModal,border:`1px solid ${T.borderAccent}44`,borderRadius:6,padding:"40px 52px",textAlign:"center",maxWidth:360}}>
            <div style={{fontSize:24,marginBottom:16}}><Icon.Bot size={24}/></div>
            <div style={{fontSize:16,fontWeight:600,color:T.borderAccent,marginBottom:8}}>Analyzing Your Estimate</div>
            <div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>
              Claude AI is reading your file and mapping line items to CSI MasterFormat divisions. This usually takes 10–20 seconds.
            </div>
            <div style={{marginTop:20,height:2,background:T.borderPrimary,borderRadius:3,overflow:"hidden"}}>
              <div style={{height:"100%",width:"60%",background:T.borderAccent,borderRadius:3,animation:"aiPulse 2s ease-in-out infinite"}}/>
            </div>
          </div>
        </div>
      )}

      {/* ── Procore Export Dialog ── */}
      {showProcoreDialog&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:T.bgModalBackdrop,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>{if(!procoreExporting)setShowProcoreDialog(false);}}>
          <div style={{background:T.bgModal,border:`1px solid ${T.borderAccent}44`,borderRadius:6,padding:"32px 40px",maxWidth:440,width:"90%"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:700,color:T.textInverse,marginBottom:20}}>Export to Procore</div>
            <label style={{fontSize:11,color:T.textMuted,display:"block",marginBottom:4}}>PROCORE COMPANY</label>
            <select value={selectedCompanyId||""} onChange={e=>setSelectedCompanyId(Number(e.target.value))}
              style={{width:"100%",padding:"8px 10px",background:T.bgInput,color:T.textInverse,border:`1px solid ${T.borderSecondary}`,borderRadius:6,fontSize:13,marginBottom:16,fontFamily:FONT}}>
              {procoreCompanies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label style={{fontSize:11,color:T.textMuted,display:"block",marginBottom:4}}>PROCORE PROJECT</label>
            {procoreLoadingProjects?(
              <div style={{padding:"8px 10px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:6,fontSize:13,color:T.textMuted,marginBottom:8}}>Loading projects…</div>
            ):(
              <select value={procoreCreateNew?"__new__":(selectedProcoreProjectId||"")}
                onChange={e=>{
                  const v=e.target.value;
                  if(v==="__new__"){setProcoreCreateNew(true);setSelectedProcoreProjectId(null);}
                  else{setProcoreCreateNew(false);setSelectedProcoreProjectId(Number(v));}
                }}
                style={{width:"100%",padding:"8px 10px",background:T.bgInput,color:T.textInverse,border:`1px solid ${T.borderSecondary}`,borderRadius:6,fontSize:13,marginBottom:8,fontFamily:FONT}}>
                {procoreProjects.map(p=><option key={p.id} value={p.id}>{p.number?`${p.number} — `:""}{p.name}</option>)}
                <option value="__new__">+ Create a new Procore project</option>
              </select>
            )}
            {procoreCreateNew&&(
              <div style={{padding:"8px 10px",background:`${T.borderAccent}11`,border:`1px solid ${T.borderAccent}44`,borderRadius:6,fontSize:11,color:T.textMuted,marginBottom:16,lineHeight:1.5}}>
                A new project named <span style={{color:T.textInverse,fontWeight:600}}>{projectName}</span> will be created in Procore.
              </div>
            )}
            {!procoreCreateNew&&<div style={{height:8}}/>}
            <div style={{display:"flex",gap:24,marginBottom:20,fontSize:12,color:T.textMuted}}>
              <div><span style={{color:T.textInverse,fontWeight:600}}>{divisions.reduce((n,d)=>n+(d.subcategories?.filter(s=>s.rows?.some(r=>r.type==="item")).length||0),0)}</span> cost codes</div>
              <div><span style={{color:T.textInverse,fontWeight:600}}>{Object.keys(itemState).length}</span> line items</div>
              <div><span style={{color:T.borderAccent,fontWeight:600}}>${final.total?.toLocaleString()||"0"}</span> total</div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowProcoreDialog(false)} disabled={procoreExporting}
                style={{padding:"8px 20px",background:"transparent",color:T.textMuted,border:`1px solid ${T.borderSecondary}`,borderRadius:6,cursor:"pointer",fontSize:12,fontFamily:FONT}}>Cancel</button>
              <button onClick={handleProcoreSubmit} disabled={procoreExporting}
                style={{padding:"8px 20px",background:T.borderAccent,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FONT,opacity:procoreExporting?.6:1}}>
                {procoreExporting?"Exporting...":"Export to Procore"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock GMP dialogs removed — Budget is now a standalone view accessed from ProjectPicker. */}
      {/* Live region: always rendered so screen readers announce dynamic toast text */}
      <div role="status" aria-live="polite" aria-atomic="true"
        style={{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",clip:"rect(0 0 0 0)",whiteSpace:"nowrap",border:0}}>
        {toast}
      </div>
      {/* role=status rather than aria-hidden: a message carrying a button has
          to be reachable, and an action nobody can hear about is not one. */}
      {toast&&<div className={`toast${toastUndo?" has-action":""}`} role="status">
        <span>{toast}</span>
        {toastUndo&&<button className="toast-undo"
          onClick={()=>{setToast("");setToastUndo(false);undoRef.current();}}>Undo</button>}
      </div>}
    </div>
  );
}
